import { LoginAllievo, VistaAppAllievo } from './AppAllievo';
import { VERSIONE_BUILD, DATA_VERSIONE_BUILD } from './versioneBuild';
import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { createPortal } from 'react-dom';
import { EmailAuthProvider, onAuthStateChanged, reauthenticateWithCredential, sendPasswordResetEmail, signInWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { accediStaff, caricaProfiliStaff, esciDaFirebase, impostaPasswordStaff, intestazioniAutenticate, sessioneFirebaseValida } from './accessoServer';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { auth, db, firebaseApp } from './firebase';
import { ascoltaRaccolta, leggiRaccolta } from './raccolteCondivise';
import { collection, addDoc, arrayUnion, getDocs, deleteDoc, deleteField, doc, updateDoc, setDoc, onSnapshot, writeBatch, runTransaction, query, where } from 'firebase/firestore';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType } from 'docx';

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const VERSIONE_APP = VERSIONE_BUILD;
const DATA_VERSIONE_APP = DATA_VERSIONE_BUILD;
const URL_SERVIZIO_NOTIFICHE = 'https://academy-of-arts-push.academy-of-arts-push-11f97924.workers.dev/invia-avviso';
const URL_SERVIZIO_MESSAGGIO_ALLIEVO = 'https://academy-of-arts-push.academy-of-arts-push-11f97924.workers.dev/invia-messaggio-allievo';
const URL_SERVIZIO_ASSEGNAZIONE_LEZIONE = 'https://academy-of-arts-push.academy-of-arts-push-11f97924.workers.dev/invia-assegnazione-lezione';
const URL_SERVIZIO_ALLEGATI_EVENTI = 'https://academy-of-arts-push.academy-of-arts-push-11f97924.workers.dev/allegati-eventi';
const URL_ELENCO_COMUNI_ITALIANI = 'https://cdn.jsdelivr.net/gh/RP92/comuni-italiani/data/comuni.json';
const EMAIL_RECUPERO_ADMIN = 'raimondodimartino@gmail.com';
const CHIAVE_WEB_PUSH = 'BHuMqlPXlzHnuQOEfQrJs-4dIea2_YXzrVuUJ-x7FZTavvgTLQpcxYwpOKCba-OdbwKXzs-NozHbvl5Si-W7D-w';

// Restituisce la data di pagamento più recente tra quella salvata e quella
// nuova, così una ricevuta arretrata non fa risultare moroso chi ha già pagato.
function dataPagamentoPiuRecente(dataSalvata, dataNuova) {
  const formatoISO = /^\d{4}-\d{2}-\d{2}/;
  if (formatoISO.test(String(dataSalvata || '')) && formatoISO.test(String(dataNuova || '')) && String(dataSalvata) > String(dataNuova)) return dataSalvata;
  return dataNuova;
}

function dataOggiISO() {
  const adesso = new Date();
  const correzioneFuso = adesso.getTimezoneOffset() * 60 * 1000;
  return new Date(adesso.getTime() - correzioneFuso).toISOString().slice(0, 10);
}

const ORARI_SABATO_LEZIONI_PRIVATE = ['08:00', '08:45', '09:30', '10:15', '11:00', '11:45', '12:30', '13:15', '14:00', '14:45', '15:30', '16:15', '17:00', '17:45', '18:15', '19:00'];
const DISPONIBILITA_LEZIONI_PREDEFINITA = { 1: ['15:30', '16:15'], 2: ['15:00', '15:45'], 3: ['15:30', '16:15'], 4: ['15:00', '15:45'], 5: ['15:30', '16:15'], 6: ORARI_SABATO_LEZIONI_PRIVATE };
const NOMI_GIORNI_LEZIONI = { 1: 'Lunedì', 2: 'Martedì', 3: 'Mercoledì', 4: 'Giovedì', 5: 'Venerdì', 6: 'Sabato' };
// Ogni riga della griglia mostra due lezioni consecutive da 45 minuti.
const ORARI_SELEZIONABILI_LEZIONI = ['15:00', '15:45', '15:15', '16:00', '15:30', '16:15'];
// Nella stessa fascia possono esserci al massimo due lezioni private, purché
// abbiano allievi e docenti diversi. Un docente non può mai essere doppio.
const MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE = 2;

// Le vecchie impostazioni non contengono ancora il sabato. Le completiamo
// senza toccare le scelte già fatte nei giorni feriali. Un array vuoto è una
// scelta valida: significa che quel giorno non accetta richieste dall'app.
function completaDisponibilitaLezioniPrivate(fasce, fascePerData) {
  const disponibilita = fasce && typeof fasce === 'object' ? fasce : {};
  const completa = { ...DISPONIBILITA_LEZIONI_PREDEFINITA, ...disponibilita, 6: Array.isArray(disponibilita[6]) ? disponibilita[6] : ORARI_SABATO_LEZIONI_PRIVATE };
  if (fascePerData && typeof fascePerData === 'object') completa.perData = fascePerData;
  return completa;
}

// Orari validi per una data precisa: se la Segreteria ha pubblicato orari
// per quella data (anche nessun orario = giorno chiuso) valgono solo quelli,
// altrimenti valgono gli orari settimanali del giorno della settimana.
function orariLezioniPrivatePerData(data, disponibilita) {
  if (!data) return [];
  if (disponibilita?.perData && Array.isArray(disponibilita.perData[data])) return disponibilita.perData[data];
  const giorno = new Date(`${data}T12:00:00`).getDay();
  return Array.isArray(disponibilita?.[giorno]) ? disponibilita[giorno] : [];
}

function orarioConsentitoLezioniPrivate(data, ora, disponibilita = DISPONIBILITA_LEZIONI_PREDEFINITA) {
  if (!data || !ora) return false;
  return orariLezioniPrivatePerData(data, disponibilita).includes(ora);
}

function minutiDaOrario(orario) {
  const [ore, minuti] = String(orario || '').split(':').map(Number);
  return Number.isFinite(ore) && Number.isFinite(minuti) ? ore * 60 + minuti : null;
}

function oraFineLezionePrivata(orario) {
  const inizio = minutiDaOrario(orario);
  if (inizio === null) return '';
  const fine = inizio + 45;
  return `${String(Math.floor(fine / 60)).padStart(2, '0')}:${String(fine % 60).padStart(2, '0')}`;
}

function orariLezioneSiSovrappongono(inizioUno, fineUno, inizioDue, fineDue) {
  const inizioA = minutiDaOrario(inizioUno);
  const inizioB = minutiDaOrario(inizioDue);
  if (inizioA === null || inizioB === null) return false;
  const fineA = minutiDaOrario(fineUno) ?? inizioA + 45;
  const fineB = minutiDaOrario(fineDue) ?? inizioB + 45;
  return inizioA < fineB && inizioB < fineA;
}

// Un allievo non può seguire due lezioni nello stesso intervallo, nemmeno
// con due docenti diversi. I vecchi dati senza ID restano riconoscibili dal nome.
function lezioneSovrappostaPerAllievo(lezioni, { allievoId, allievoNome, data, oraInizio, oraFine, escludiId = '' }) {
  const idAllievo = String(allievoId || '').trim();
  const nomeAllievo = String(allievoNome || '').trim().toLocaleLowerCase('it-IT');
  return lezioni.find(lezione => {
    if (lezione.id === escludiId || lezione.Data !== data) return false;
    const stessoAllievo = idAllievo
      ? String(lezione.allievoId || '').trim() === idAllievo
      : String(lezione.Allievo || '').trim().toLocaleLowerCase('it-IT') === nomeAllievo;
    return stessoAllievo && orariLezioneSiSovrappongono(oraInizio, oraFine, lezione.OraInizio, lezione.OraFine);
  });
}

function docenteLezionePrivataSovrapposto(lezioni, { docente, data, oraInizio, oraFine, escludiId = '' }) {
  const nomeDocente = String(docente || '').trim().toLocaleLowerCase('it-IT');
  return lezioni.find(lezione => lezione.id !== escludiId
    && String(lezione.Maestro || '').trim().toLocaleLowerCase('it-IT') === nomeDocente
    && lezione.Data === data
    && orariLezioneSiSovrappongono(oraInizio, oraFine, lezione.OraInizio, lezione.OraFine));
}

function numeroLezioniPrivateSovrapposte(lezioni, { data, oraInizio, oraFine, escludiId = '' }) {
  return lezioni.filter(lezione => lezione.id !== escludiId && lezione.Data === data && orariLezioneSiSovrappongono(oraInizio, oraFine, lezione.OraInizio, lezione.OraFine)).length;
}

// Compatibile anche con i vecchi impegni salvati per un solo giorno (campo "data").
function periodiImpegniMaestri(impegni) {
  const ordinaPerData = periodi => [...periodi].sort((prima, dopo) => String(prima?.dataInizio || prima?.data || '').localeCompare(String(dopo?.dataInizio || dopo?.data || '')) || String(prima?.dataFine || '').localeCompare(String(dopo?.dataFine || '')));
  if (Array.isArray(impegni?.periodi)) return ordinaPerData(impegni.periodi);
  const inizio = String(impegni?.dataInizio || impegni?.data || '');
  const fine = String(impegni?.dataFine || inizio || '');
  return inizio ? ordinaPerData([{ id: `storico-${inizio}-${fine}`, dataInizio: inizio, dataFine: fine, maestri: Array.isArray(impegni?.maestri) ? impegni.maestri : [] }]) : [];
}

function maestroImpegnatoNelPeriodo(impegni, nomeMaestro, data) {
  if (!data) return false;
  return periodiImpegniMaestri(impegni).some(impegno => {
    const inizio = String(impegno?.dataInizio || '');
    const fine = String(impegno?.dataFine || inizio || '');
    return inizio && fine && data >= inizio && data <= fine && (impegno?.maestri || []).some(maestro => String(maestro).trim().toLocaleLowerCase('it-IT') === String(nomeMaestro || '').trim().toLocaleLowerCase('it-IT'));
  });
}

// Mantiene coerenti i testi della carriera: niente parole tutte maiuscole,
// prima lettera maiuscola e resto in minuscolo, come nello stile Academy.
function normalizzaTestoCarriera(valore) {
  const testo = String(valore || '').trim().replace(/\s+/g, ' ');
  if (!testo) return '';
  const minuscolo = testo.toLocaleLowerCase('it-IT');
  // Funziona anche con “1 Classificato” o “1° Classificato”: la prima
  // lettera effettiva, anche dopo un numero, resta sempre maiuscola.
  return minuscolo.replace(/^([^a-zà-öø-ÿ]*)([a-zà-öø-ÿ])/i, (_, prefisso, lettera) => `${prefisso}${lettera.toLocaleUpperCase('it-IT')}`);
}

function formattaFasciaDisciplina(valore) {
  const fascia = normalizzaFasciaCorso(valore);
  const nomiGruppi = { beginners: 'Beginners', syllabus: 'Syllabus', competition: 'Competition', teen: 'Teen', kid: 'Kid', advance: 'Advance', 'lady group': 'Lady Group', advanced: 'Advanced' };
  return nomiGruppi[fascia] || fascia
    .replace(/^under\b/i, 'Under')
    .replace(/^over\b/i, 'Over')
    .replace(/^adult latin\b/i, 'Adult Latin')
    .replace(/^adulti latin\b/i, 'Adult Latin')
    .replace(/^adulti\b/i, 'Adulti')
    .replace(/^ballo di coppia\b/i, 'Ballo di Coppia');
}

function normalizzaFasciaCorso(valore) {
  const fascia = String(valore || '').trim().toLocaleLowerCase('it-IT').replace(/\s+/g, ' ');
  return ['under 21', 'over 16', 'over 16 / under 21', 'under 21 / over 16'].includes(fascia) ? 'over 16 / under 21' : fascia;
}

// Mantiene valide le iscrizioni già salvate con i nomi delle vecchie fasce.
function gruppoCorsoRiorganizzato(corso, valore) {
  const fascia = normalizzaFasciaCorso(valore);
  const nomeCorso = String(corso || '').trim().toLocaleLowerCase('it-IT');
  if (nomeCorso === 'solo latin') {
    if (['under 12', 'under 15'].includes(fascia)) return 'syllabus';
    if (['under 4/7 anni', 'under 9 principiante', 'under 9 avanzato'].includes(fascia)) return 'beginners';
    if (['under 16', 'over 16 / under 21'].includes(fascia)) return 'competition';
  }
  if (nomeCorso === 'danze latino americane' && fascia === 'adult latin') return 'lady group';
  if (nomeCorso === 'danza moderna') {
    if (['under 4/7 anni', 'under 9 principiante'].includes(fascia)) return 'kid';
    if (['under 9 avanzato', 'under 12', 'under 15'].includes(fascia)) return 'teen';
    if (['under 16', 'over 16 / under 21'].includes(fascia)) return 'advance';
  }
  if (nomeCorso === 'hip-hop' && ['under 16', 'over 16 / under 21'].includes(fascia)) return 'advanced';
  if (nomeCorso === 'hip-hop' && ['under 4/7 anni', 'under 9 principiante', 'under 9 avanzato', 'under 12', 'under 15'].includes(fascia)) return 'teen';
  return fascia;
}

function formattaNomePersona(valore) {
  return String(valore || '')
    .trim()
    .toLocaleLowerCase('it-IT')
    .split(/([\s'-])/)
    .map(parte => {
      if (!parte || /^[\s'-]$/.test(parte)) return parte;
      return parte.charAt(0).toLocaleUpperCase('it-IT') + parte.slice(1);
    })
    .join('');
}

function letterePerCodiceFiscale(valore) {
  return String(valore || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
}

function treLettereCodiceFiscale(valore, eNome = false) {
  const lettere = letterePerCodiceFiscale(valore);
  const consonanti = lettere.replace(/[AEIOU]/g, '');
  const vocali = lettere.replace(/[^AEIOU]/g, '');
  // Per i nomi con almeno quattro consonanti la regola fiscale usa 1ª, 3ª e 4ª.
  const base = eNome && consonanti.length >= 4
    ? `${consonanti[0]}${consonanti[2]}${consonanti[3]}`
    : `${consonanti}${vocali}`;
  return `${base}XXX`.slice(0, 3);
}

function generaCodiceFiscaleRapido({ nome, cognome, sesso, dataNascita, codiceComune }) {
  const data = new Date(`${dataNascita}T12:00:00`);
  const luogo = String(codiceComune || '').trim().toUpperCase();
  if (!letterePerCodiceFiscale(nome) || !letterePerCodiceFiscale(cognome) || Number.isNaN(data.getTime())) {
    throw new Error('Inserisci prima nome, cognome e data di nascita.');
  }
  if (!/^[A-Z]\d{3}$/.test(luogo)) {
    throw new Error('Inserisci il codice catastale del Comune di nascita, composto da una lettera e 3 numeri (esempio: F839).');
  }
  const mesi = ['A', 'B', 'C', 'D', 'E', 'H', 'L', 'M', 'P', 'R', 'S', 'T'];
  const giorno = data.getDate() + (String(sesso).toUpperCase() === 'F' ? 40 : 0);
  const parziale = `${treLettereCodiceFiscale(cognome)}${treLettereCodiceFiscale(nome, true)}${String(data.getFullYear()).slice(-2)}${mesi[data.getMonth()]}${String(giorno).padStart(2, '0')}${luogo}`;
  const dispari = { 0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21, A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21, K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14, U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23 };
  const alfabeto = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const totale = [...parziale].reduce((somma, carattere, indice) => somma + (indice % 2 === 0 ? dispari[carattere] : (/[0-9]/.test(carattere) ? Number(carattere) : alfabeto.indexOf(carattere))), 0);
  return `${parziale}${alfabeto[totale % 26]}`;
}

function normalizzaRicercaComune(valore) {
  return String(valore || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it-IT')
    .replace(/[^a-z0-9]/g, '');
}

function verificaNomeECognome(nome, cognome) {
  const nomePulito = formattaNomePersona(nome);
  const cognomePulito = formattaNomePersona(cognome);
  const formatoValido = /^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[\s'’-][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/;
  if (nomePulito.length < 2 || !formatoValido.test(nomePulito)) {
    return 'Il Nome non è valido: usa solo lettere, spazi, apostrofi o trattini.';
  }
  if (cognomePulito.length < 2 || !formatoValido.test(cognomePulito)) {
    return 'Il Cognome non è valido: usa solo lettere, spazi, apostrofi o trattini.';
  }
  return '';
}

async function rilevaVersioneWebDisponibile() {
  // Confronta il JavaScript aperto con quello dell'ultimo deploy di Hosting,
  // ma non ricarica: la scelta resta sempre in mano all'utente che sta lavorando.
  if (process.env.NODE_ENV !== 'production') return false;
  try {
    const risposta = await fetch(`/asset-manifest.json?versione=${Date.now()}`, { cache: 'no-store' });
    if (!risposta.ok) return false;
    const manifest = await risposta.json();
    const scriptAggiornato = manifest?.files?.['main.js'];
    if (!scriptAggiornato) return false;
    const versioneGiaAperta = Array.from(document.scripts).some(script => {
      try { return script.src && new URL(script.src).pathname === scriptAggiornato; }
      catch { return false; }
    });
    return versioneGiaAperta ? false : scriptAggiornato;
  } catch (e) {
    console.warn('Impossibile verificare gli aggiornamenti della web app.', e);
    return false;
  }
}

function applicaAggiornamentoWeb(scriptAggiornato) {
  if (!scriptAggiornato) return;
  const indirizzo = new URL(window.location.href);
  indirizzo.searchParams.set('versione', scriptAggiornato.split('/').pop());
  // Il refresh è volontario: preserva la sessione e riapre la stessa schermata.
  sessionStorage.setItem('aggiornamento_webapp_in_corso', '1');
  window.location.replace(indirizzo.toString());
}

async function aggiornaPaginaManuale() {
  // Utile soprattutto quando la PWA è aperta dalla scorciatoia del telefono:
  // richiede al service worker l'ultima versione prima di riaprire la stessa pagina.
  try {
    const registrazione = await navigator.serviceWorker?.getRegistration();
    await registrazione?.update();
    const scriptAggiornato = await rilevaVersioneWebDisponibile();
    if (scriptAggiornato) {
      applicaAggiornamentoWeb(scriptAggiornato);
      return;
    }
  } catch (errore) {
    console.warn('Impossibile verificare la nuova versione prima dell’aggiornamento.', errore);
  }
  const indirizzo = new URL(window.location.href);
  indirizzo.searchParams.set('aggiorna', Date.now().toString());
  window.location.replace(indirizzo.toString());
}

async function aggiornaVersioneAllieviSeDisponibile() {
  // Quando l'app Allievi viene riaperta, controlla subito Hosting e carica
  // il JavaScript più recente senza richiedere un refresh manuale.
  if (process.env.NODE_ENV !== 'production' || Capacitor.isNativePlatform()) return false;
  try {
    const risposta = await fetch(`/asset-manifest.json?allievi=${Date.now()}`, { cache: 'no-store' });
    if (!risposta.ok) return false;
    const manifest = await risposta.json();
    const scriptAggiornato = manifest?.files?.['main.js'];
    if (!scriptAggiornato) return false;
    const versioneGiaAperta = Array.from(document.scripts).some(script => {
      try { return script.src && new URL(script.src).pathname === scriptAggiornato; }
      catch { return false; }
    });
    if (versioneGiaAperta) return false;

    const indirizzo = new URL(window.location.href);
    indirizzo.searchParams.set('versioneAllievi', scriptAggiornato.split('/').pop());
    window.location.replace(indirizzo.toString());
    return true;
  } catch (errore) {
    console.warn('Impossibile verificare gli aggiornamenti dell’app Allievi.', errore);
    return false;
  }
}

const UTENTI_DEFAULT = [
  { username: "Luisa Pagano", password: "123", ruolo: "operatore", docenteLezioniPrivate: true },
  { username: "Lia Di Martino", password: "123", ruolo: "master", docenteLezioniPrivate: true },
  { username: "Salvatore Renna", password: "123", ruolo: "master", docenteLezioniPrivate: true },
  { username: "Admin", password: "adminPassword123", ruolo: "admin" }
];

const PERMESSI_MENU_OPERATORE = [
  ['dashboard', '🏠 Dashboard'],
  ['ricerca', '🔍 Ricerca allievi'],
  ['iscrizione', '📝 Iscrizione allievo'],
  ['anagrafica', '📁 Anagrafica allievi'],
  ['presenze', '📋 Registro presenze'],
  ['corsi', '🎓 Corsi e orari'],
  ['compleanni', '🎂 Compleanni'],
  ['tesseramento', '📑 Tesseramento'],
  ['qr_allievi', '📱 QR App Allievi'],
  ['divise', '👕 Divise e materiali'],
  ['shop', '🛍️ Shop'],
  ['lezioni_private', '📅 Lezioni private'],
  ['pagamenti', '💳 Pagamenti'],
  ['comunicazioni', '🔔 Comunicazioni'],
  ['carriera', '🏆 Carriera'],
  ['calendario', '🗓️ Calendario eventi'],
  ['report', '📊 Report annuale']
];

const etichettaPermessoOperatore = chiave => PERMESSI_MENU_OPERATORE.find(([id]) => id === chiave)?.[1] || chiave;

// I profili già presenti mantengono il loro accesso attuale. Solo quando un
// utente ha una lista di permessi salvata viene limitato alle voci selezionate.
const utentePuoVedere = (utente, chiave) => {
  const ruolo = String(utente?.ruolo || '').toLowerCase();
  if (ruolo === 'admin' || ruolo === 'master') return true;
  return !Array.isArray(utente?.permessiMenu) || utente.permessiMenu.includes(chiave);
};

const DOCENTI_LEZIONI_PRIVATE_PREDEFINITI = ['luisa pagano', 'lia di martino', 'salvatore renna'];
const NOMI_MASTER_PRIORITARI = ['lia di martino', 'salvatore renna'];

function utenteEMasterPrioritario(utente) {
  return String(utente?.ruolo || '').trim().toLocaleLowerCase('it-IT') === 'master'
    && NOMI_MASTER_PRIORITARI.includes(String(utente?.username || '').trim().toLocaleLowerCase('it-IT'));
}

function utenteGestisceLezioniPrivate(utente) {
  if (String(utente?.ruolo || '').trim().toLocaleLowerCase('it-IT') === 'docente') return true;
  if (typeof utente?.docenteLezioniPrivate === 'boolean') return utente.docenteLezioniPrivate;
  return DOCENTI_LEZIONI_PRIVATE_PREDEFINITI.includes(String(utente?.username || '').trim().toLocaleLowerCase('it-IT'));
}

// In passato può essere stato creato più di un profilo tecnico per lo stesso
// docente. Per agenda e prenotazioni conta sempre una sola volta per nome.
function docentiLezioniPrivateSenzaDuplicati(docenti) {
  const perNome = new Map();
  (Array.isArray(docenti) ? docenti : []).forEach(docente => {
    const nome = String(docente?.username || '').trim();
    const chiave = nome.toLocaleLowerCase('it-IT');
    if (!chiave) return;
    const giaInserito = perNome.get(chiave);
    // Se sono presenti due schede, diamo priorità a un eventuale Master.
    if (!giaInserito || (utenteEMasterPrioritario(docente) && !utenteEMasterPrioritario(giaInserito))) {
      perNome.set(chiave, { ...docente, username: nome });
    }
  });
  return [...perNome.values()].sort((a, b) => String(a.username || '').localeCompare(String(b.username || ''), 'it'));
}

function qualificaDocenteLezioniPrivate(utente) {
  return utenteEMasterPrioritario(utente) ? 'M°' : 'Docente';
}

async function sincronizzaDocenteTemporaneo(allievoId, datiAllievo) {
  // In passato spuntare un allievo come docente poteva creare in automatico
  // un accesso nella schermata login. Non lo facciamo più: essere docente
  // della scuola e avere una credenziale del gestionale sono due cose distinte.
  // Gli accessi sono gestiti esplicitamente da Admin/Master.
  void allievoId;
  void datiAllievo;
}

function fotoProfiloOperatore(profilo) {
  if (profilo?.fotoUrl) return profilo.fotoUrl;
  const nome = String(profilo?.username || '').trim().toLowerCase();
  const fotoPredefinite = {
    'lia di martino': '/profili/lia-di-martino.png?v=20260905-3',
    'luisa pagano': '/profili/luisa-pagano.jpg?v=20260905-2',
    'salvatore renna': '/profili/salvatore-renna.jpg?v=20260905-2'
  };
  return fotoPredefinite[nome] || '/logo%20suola.jpeg';
}

const CORSI_INFO = [
  {
    nome: "Solo Latin", giorni: [1, 5], testoGiorni: "Lunedì e Venerdì",
    orari: {
      'beginners': ['Venerdì · 16:00 – 17:00'],
      'syllabus': ['Lunedì · 16:00 – 17:00'],
      'competition': ['Venerdì · 19:00 – 20:00']
    }
  },
  {
    nome: "Danze Latino Americane", giorni: [1, 2, 3, 4, 5], testoGiorni: "Dal Lunedì al Venerdì",
    orari: {
      'under 4/7 anni': ['Lunedì · 17:00 – 18:00', 'Giovedì · 16:30 – 17:30'],
      'under 9 principiante': ['Mercoledì e Venerdì · 17:00 – 18:00'],
      'under 9 avanzato': ['Martedì · 16:30 – 17:30', 'Giovedì · 17:30 – 18:30'],
      'under 12': ['Martedì · 17:30 – 18:30', 'Venerdì · 18:00 – 19:00'],
      'under 15': ['Martedì e Giovedì · 18:30 – 19:30'],
      'under 16': ['Martedì e Giovedì · 19:30 – 20:30'],
      'over 16 / under 21': ['Lunedì e Mercoledì · 20:00 – 21:00'],
      'lady group': ['Lunedì e Mercoledì · 21:00 – 22:00']
    }
  },
  {
    nome: "Danza Moderna", giorni: [1], testoGiorni: "Lunedì",
    orari: {
      'kid': ['Lunedì · 17:00 – 18:00'],
      'teen': ['Lunedì · 18:00 – 19:00'],
      'advance': ['Lunedì · 19:00 – 20:00']
    }
  },
  {
    nome: "Hip-hop", giorni: [3], testoGiorni: "Mercoledì",
    orari: {
      'kid': ['Mercoledì · 16:00 – 17:00'],
      'teen': ['Mercoledì · 18:00 – 19:00'],
      'advanced': ['Mercoledì · 19:00 – 20:00']
    }
  },
  { nome: "Ballo di Coppia", giorni: [3, 5], testoGiorni: "Mercoledì e Venerdì", orari: {} }
];

// Orari usati dall'appello manuale: corrispondono ai corsi pubblicati
// nell'App Allievi e permettono di creare la lista automaticamente.
const ORARI_PRESENZE_CORSI = {
  'Solo Latin': {
    'beginners': [{ giorni: [5], inizio: '16:00', fine: '17:00' }], 'syllabus': [{ giorni: [1], inizio: '16:00', fine: '17:00' }], 'competition': [{ giorni: [5], inizio: '19:00', fine: '20:00' }]
  },
  'Danze Latino Americane': {
    'under 4/7 anni': [{ giorni: [1], inizio: '17:00', fine: '18:00' }, { giorni: [4], inizio: '16:30', fine: '17:30' }], 'under 9 principiante': [{ giorni: [3, 5], inizio: '17:00', fine: '18:00' }], 'under 9 avanzato': [{ giorni: [2], inizio: '16:30', fine: '17:30' }, { giorni: [4], inizio: '17:30', fine: '18:30' }], 'under 12': [{ giorni: [2], inizio: '17:30', fine: '18:30' }, { giorni: [5], inizio: '18:00', fine: '19:00' }], 'under 15': [{ giorni: [2, 4], inizio: '18:30', fine: '19:30' }], 'under 16': [{ giorni: [2, 4], inizio: '19:30', fine: '20:30' }], 'over 16 / under 21': [{ giorni: [1, 3], inizio: '20:00', fine: '21:00' }], 'lady group': [{ giorni: [1, 3], inizio: '21:00', fine: '22:00' }]
  },
  'Danza Moderna': {
    'kid': [{ giorni: [1], inizio: '17:00', fine: '18:00' }], 'teen': [{ giorni: [1], inizio: '18:00', fine: '19:00' }], 'advance': [{ giorni: [1], inizio: '19:00', fine: '20:00' }]
  },
  'Hip-hop': {
    'kid': [{ giorni: [3], inizio: '16:00', fine: '17:00' }], 'teen': [{ giorni: [3], inizio: '18:00', fine: '19:00' }], 'advanced': [{ giorni: [3], inizio: '19:00', fine: '20:00' }]
  }
};

function appuntamentiPresenzeDelGiorno(allievo, giorno) {
  const corsi = Array.isArray(allievo?.Corsi) ? allievo.Corsi : (allievo?.Corso ? [allievo.Corso] : []);
  return corsi.flatMap(corsoAssegnato => {
    const [corso, ...partiFascia] = String(corsoAssegnato || '').split(' - ');
    const fascia = gruppoCorsoRiorganizzato(corso, partiFascia.join(' - '));
    return (ORARI_PRESENZE_CORSI[corso]?.[fascia] || []).filter(orario => orario.giorni.includes(giorno)).map(orario => ({ corso, fascia, ...orario }));
  });
}

function chiaveAppelloPresenze({ corso, fascia, inizio }) {
  return `${corso}-${fascia}-${inizio}`.toLocaleLowerCase('it-IT').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function chiaveCicloAssenzeDisciplina(corso) {
  return String(corso || '').trim().toLocaleLowerCase('it-IT').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'disciplina';
}

function resetCicloAssenzePerDisciplina(allievo, corso) {
  const reset = allievo?.resetAssenzePerDisciplina;
  return reset?.[chiaveCicloAssenzeDisciplina(corso)] || allievo?.resetAssenzeConsecutiveIl || '';
}

// Assenze CONSECUTIVE nella disciplina, guardando solo gli ultimi 15 giorni.
// Si parte dalla lezione più recente e si torna indietro: appena si trova una
// presenza il conteggio si ferma (una presenza azzera le assenze precedenti).
// Le lezioni più vecchie di 15 giorni non vengono mai sommate a quelle recenti.
const GIORNI_FINESTRA_ASSENZE = 15;
function contaAssenzeDopoReset(presenze, allievoId, resetIl, corso, riferimento = new Date()) {
  const momentoReset = Date.parse(resetIl || '') || 0;
  const disciplina = String(corso || '').trim().toLocaleLowerCase('it-IT');
  const inizioFinestra = new Date(riferimento);
  inizioFinestra.setHours(0, 0, 0, 0);
  inizioFinestra.setDate(inizioFinestra.getDate() - (GIORNI_FINESTRA_ASSENZE - 1));
  const dataInizio = `${inizioFinestra.getFullYear()}-${String(inizioFinestra.getMonth() + 1).padStart(2, '0')}-${String(inizioFinestra.getDate()).padStart(2, '0')}`;
  const registro = presenze
    .filter(presenza => {
      const momentoPresenza = Date.parse(presenza.aggiornataIl || presenza.pubblicataIl || '') || 0;
      return presenza.allievoId === allievoId
        && momentoPresenza > momentoReset
        && String(presenza.data || '') >= dataInizio
        && String(presenza.corso || '').trim().toLocaleLowerCase('it-IT') === disciplina
        && ['presente', 'assente', 'assente_programmata'].includes(presenza.stato);
    })
    .sort((a, b) => `${b.data || ''} ${b.oraInizio || ''}`.localeCompare(`${a.data || ''} ${a.oraInizio || ''}`));
  let consecutive = 0;
  for (const presenza of registro) {
    if (presenza.stato === 'presente') break;
    consecutive += 1;
  }
  return consecutive;
}

const DISCIPLINE_DISPONIBILI = [
  "kid",
  "beginners",
  "syllabus",
  "competition",
  "teen",
  "advance",
  "advanced",
  "lady group",
  "under 4/7 anni",
  "under 9 principiante",
  "under 9 avanzato",
  "under 12",
  "under 15",
  "under 16",
  "over 16 / under 21",
  "ballo di coppia"
];

const LISTA_CORSI = CORSI_INFO.map(c => c.nome);

const MAESTRI = [
  "Salvatore Renna",
  "Lia Di Martino",
  "Luisa Pagano"
];

const TAGLIE_ABBIGLIAMENTO = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const TAGLIE_SCARPE_EUR = Array.from({ length: 45 - 24 + 1 }, (_, i) => `EUR ${24 + i}`);

function formattaDataIT(dataStr) {
  if (!dataStr) return 'N/D';
  const dataSoloAnnoMeseGiorno = dataStr.split('T')[0];
  const parti = dataSoloAnnoMeseGiorno.split('-');
  if (parti.length === 3) {
    const [anno, mese, giorno] = parti;
    if (anno.length === 4) {
      return `${giorno.padStart(2, '0')}/${mese.padStart(2, '0')}/${anno}`;
    }
  }
  return dataStr;
}

async function registraLogOperazione(utente, azione) {
  if (utente && utente.toLowerCase() === 'admin') return;
  try {
    const oraAttuale = new Date().toLocaleString('it-IT');
    await addDoc(collection(db, 'log_operazioni'), {
      Utente: utente,
      Azione: azione,
      DataOra: oraAttuale,
      Timestamp: new Date().getTime()
    });
  } catch (e) {
    console.error("Errore registrazione log:", e);
  }
}

function ordinaAllieviPerCognomeNome(lista) {
  return [...lista].sort((a, b) => {
    const cogA = (a.Cognome || '').trim().toLowerCase();
    const cogB = (b.Cognome || '').trim().toLowerCase();
    if (cogA !== cogB) return cogA.localeCompare(cogB);
    const nomA = (a.Nome || '').trim().toLowerCase();
    const nomB = (b.Nome || '').trim().toLowerCase();
    return nomA.localeCompare(nomB);
  });
}

function isCompleannoOggi(dataNascita, cf) {
  const oggi = new Date();
  const giornoOggi = oggi.getDate();
  const meseOggi = oggi.getMonth() + 1;

  if (dataNascita) {
    const parti = dataNascita.split('-');
    if (parti.length === 3) {
      const meseNascita = parseInt(parti[1], 10);
      const giornoNascita = parseInt(parti[2], 10);
      return giornoNascita === giornoOggi && meseNascita === meseOggi;
    }
  }

  if (cf && cf.trim().length >= 11) {
    const cfClean = cf.trim().toUpperCase();
    const meseLettera = cfClean.charAt(8);
    let giorno = parseInt(cfClean.substring(9, 11), 10);

    if (!isNaN(giorno)) {
      if (giorno > 40) giorno -= 40;
      const mesiMappa = {
        'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'H': 6,
        'L': 7, 'M': 8, 'P': 9, 'R': 10, 'S': 11, 'T': 12
      };
      const mese = mesiMappa[meseLettera];
      return giorno === giornoOggi && mese === meseOggi;
    }
  }

  return false;
}

function getMeseGiornoCompleanno(dataNascita, cf) {
  if (dataNascita) {
    const parti = dataNascita.split('-');
    if (parti.length === 3) {
      return { mese: parseInt(parti[1], 10), giorno: parseInt(parti[2], 10) };
    }
  }
  if (cf && cf.trim().length >= 11) {
    const cfClean = cf.trim().toUpperCase();
    const meseLettera = cfClean.charAt(8);
    let giorno = parseInt(cfClean.substring(9, 11), 10);
    if (!isNaN(giorno)) {
      if (giorno > 40) giorno -= 40;
      const mesiMappa = {
        'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'H': 6,
        'L': 7, 'M': 8, 'P': 9, 'R': 10, 'S': 11, 'T': 12
      };
      const mese = mesiMappa[meseLettera];
      if (mese) return { mese, giorno };
    }
  }
  return null;
}

function calcolaEtaDaCF(cf) {
  if (!cf || cf.length < 11) return null;

  const cfClean = cf.trim().toUpperCase();
  const annoCifre = parseInt(cfClean.substring(6, 8), 10);
  const meseLettera = cfClean.charAt(8);
  let giorno = parseInt(cfClean.substring(9, 11), 10);

  if (isNaN(annoCifre) || isNaN(giorno)) return null;

  if (giorno > 40) giorno -= 40;

  const mesiMappa = {
    'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'H': 5,
    'L': 6, 'M': 7, 'P': 8, 'R': 9, 'S': 10, 'T': 11
  };

  const mese = mesiMappa[meseLettera];
  if (mese === undefined) return null;

  const annoCorrente2Cifre = new Date().getFullYear() % 100;
  const secolo = (annoCifre > annoCorrente2Cifre) ? 1900 : 2000;
  const annoNascita = secolo + annoCifre;

  const oggi = new Date();
  let eta = oggi.getFullYear() - annoNascita;
  if (oggi.getMonth() < mese || (oggi.getMonth() === mese && oggi.getDate() < giorno)) {
    eta--;
  }

  return (eta >= 0 && eta < 110) ? eta : null;
}

function calcolaEtaAllievo(dataNascita, codiceFiscale) {
  if (dataNascita) {
    const nascita = new Date(`${dataNascita}T12:00:00`);
    if (!Number.isNaN(nascita.getTime())) {
      const oggi = new Date();
      let eta = oggi.getFullYear() - nascita.getFullYear();
      const compleannoGiaPassato =
        oggi.getMonth() > nascita.getMonth() ||
        (oggi.getMonth() === nascita.getMonth() && oggi.getDate() >= nascita.getDate());
      if (!compleannoGiaPassato) eta -= 1;
      if (eta >= 0 && eta < 110) return eta;
    }
  }

  return calcolaEtaDaCF(codiceFiscale);
}

function AvvisoMinorenne({ telefonoGenitorePresente = false }) {
  const richiedeTelefono = !telefonoGenitorePresente;
  return (
    <>
      {richiedeTelefono && <style>{`
        @keyframes avvisoMinorenneLampeggia {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: .45; transform: scale(.985); }
        }
      `}</style>}
      <div style={{ animation: richiedeTelefono ? 'avvisoMinorenneLampeggia .9s ease-in-out infinite' : 'none', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', borderRadius: '8px', backgroundColor: richiedeTelefono ? '#fff5f5' : '#fff9db', border: `3px solid ${richiedeTelefono ? '#e03131' : '#fcc419'}`, color: richiedeTelefono ? '#c92a2a' : '#a66a00', fontWeight: 'bold', boxShadow: richiedeTelefono ? '0 0 12px rgba(224,49,49,.55)' : 'none' }}>
        <span aria-hidden="true" style={{ fontSize: '25px', lineHeight: 1 }}>▲</span>
        <div>
          <div style={{ fontSize: '16px', letterSpacing: '.6px' }}>MINORENNE</div>
          {richiedeTelefono && <small style={{ display: 'block', marginTop: '2px', color: '#862e2e' }}>Inserisci il numero del genitore o tutore.</small>}
        </div>
      </div>
    </>
  );
}

function calcolaFasciaEta(dataNascita, cf) {
  let eta = null;
  if (dataNascita) {
    const oggi = new Date();
    const nascita = new Date(dataNascita);
    eta = oggi.getFullYear() - nascita.getFullYear();
    const m = oggi.getMonth() - nascita.getMonth();
    if (m < 0 || (m === 0 && oggi.getDate() < nascita.getDate())) {
      eta--;
    }
  } else {
    eta = calcolaEtaDaCF(cf);
  }

  if (eta === null) return 'Non specificata';
  if (eta < 12) return `Bambini (${eta} anni)`;
  if (eta >= 12 && eta < 18) return `Ragazzi (${eta} anni)`;
  return `Adulti (${eta} anni)`;
}

function calcolaStatoPagamentoDinamico(dataUltimoPagamento) {
  const oggi = new Date();
  const annoCorrente = oggi.getFullYear();
  const meseCorrente = oggi.getMonth();
  const giornoMese = oggi.getDate();

  let haPagatoMeseCorrente = false;

  if (dataUltimoPagamento) {
    const dataPag = new Date(dataUltimoPagamento);
    if (dataPag.getFullYear() === annoCorrente && dataPag.getMonth() === meseCorrente) {
      haPagatoMeseCorrente = true;
    }
  }

  if (haPagatoMeseCorrente) {
    return {
      stato: 'Pagato (In Regola)',
      colore: '#2b8a3e',
      codice: 'VERDE',
      dettaglio: `Saldo effettuato il ${formattaDataIT(dataUltimoPagamento)}`
    };
  }

  if (giornoMese >= 1 && giornoMese <= 5) {
    return {
      stato: 'In Scadenza (1-5 del Mese)',
      colore: '#f08c00',
      codice: 'ARANCIONE',
      dettaglio: 'Pagamento atteso entro il 5'
    };
  }

  return {
    stato: 'Scaduto (Oltre il 5 del Mese)',
    colore: '#c92a2a',
    codice: 'ROSSO',
    dettaglio: 'Quota mensile non saldata'
  };
}

function allievoEsenteMensilita(allievo) {
  return Boolean(allievo?.isMaestro || allievo?.isTirocinante || allievo?.docenteScuola || allievo?.esenteMensilita);
}

function prossimaScadenzaQuota(allievo) {
  if (allievoEsenteMensilita(allievo)) {
    return { testo: 'Nessuna scadenza', dettaglio: 'Allievo esente dal pagamento', colore: '#1c7ed6' };
  }

  const oggi = new Date();
  const ultimoPagamento = allievo.DataUltimoPagamento ? new Date(`${String(allievo.DataUltimoPagamento).split('T')[0]}T12:00:00`) : null;
  const haPagatoQuestoMese = ultimoPagamento &&
    !Number.isNaN(ultimoPagamento.getTime()) &&
    ultimoPagamento.getFullYear() === oggi.getFullYear() &&
    ultimoPagamento.getMonth() === oggi.getMonth();
  const scadenza = haPagatoQuestoMese
    ? new Date(oggi.getFullYear(), oggi.getMonth() + 1, 5)
    : new Date(oggi.getFullYear(), oggi.getMonth(), 5);
  const scaduta = scadenza < new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate());

  const dataScadenza = `${scadenza.getFullYear()}-${String(scadenza.getMonth() + 1).padStart(2, '0')}-${String(scadenza.getDate()).padStart(2, '0')}`;
  return scaduta
    ? { testo: `Scaduta il ${formattaDataIT(dataScadenza)}`, dettaglio: 'La quota mensile deve essere regolarizzata', colore: '#c92a2a' }
    : { testo: `Entro il ${String(scadenza.getDate()).padStart(2, '0')}/${String(scadenza.getMonth() + 1).padStart(2, '0')}/${scadenza.getFullYear()}`, dettaglio: 'Prossima quota mensile', colore: '#f08c00' };
}

function calcolaScadenzaCertificatoMedico(dataCertificato) {
  if (!dataCertificato) return null;
  const data = new Date(`${dataCertificato}T12:00:00`);
  if (Number.isNaN(data.getTime())) return null;
  data.setFullYear(data.getFullYear() + 1);
  return data;
}

function certificatoMedicoScaduto(dataCertificato) {
  const scadenza = calcolaScadenzaCertificatoMedico(dataCertificato);
  if (!scadenza) return false;
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  return oggi >= scadenza;
}

function inviaMessaggioWhatsApp(telefono, nomeAllievo, utenteCorrente) {
  if (!telefono) {
    alert("⚠️ Numero di telefono non disponibile per questo allievo!");
    return;
  }

  let numeroPulito = telefono.replace(/\D/g, '');
  if ((numeroPulito.length === 9 || numeroPulito.length === 10)) {
    numeroPulito = '39' + numeroPulito;
  }

  const testoMessaggio = `*Buonasera ${nomeAllievo}!* 💃🕺
Sono *${utenteCorrente}* di *Academy Of Arts*.

Ti ricordo che la quota mensile per i tuoi corsi risulta in sospeso/scaduta! ⏳

Il saldo della mensilità è fondamentale per permetterci di organizzare al meglio tutte le lezioni, la sala e la musica, così da farvi ballare e divertire sempre al massimo! 🎶✨

Se hai già provveduto al saldo nelle ultime ore, ignora pure questo messaggio. In caso contrario, ti aspettiamo in segreteria prima dell'inizio della prossima lezione per regolarizzare la posizione.

Per qualsiasi informazione o dubbio puoi rispondere direttamente a questo messaggio.
Ci vediamo a lezione: energia alta e tanta voglia di ballare! 🚀✨`;

  registraLogOperazione(utenteCorrente, `Inviato sollecito WhatsApp a ${nomeAllievo}`);

  const url = `https://wa.me/${numeroPulito}?text=${encodeURIComponent(testoMessaggio)}`;
  window.open(url, '_blank');
}

function PallinoBluOnline() {
  const keyframesStyle = `
    @keyframes pulseBlue {
      0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(28, 126, 214, 0.7); }
      70% { transform: scale(1.1); box-shadow: 0 0 0 6px rgba(28, 126, 214, 0); }
      100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(28, 126, 214, 0); }
    }
  `;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '6px' }} title="Connesso ora in questa o altra postazione">
      <style>{keyframesStyle}</style>
      <span style={{
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: '#339af0',
        display: 'inline-block',
        animation: 'pulseBlue 1.8s infinite'
      }} />
    </span>
  );
}

function BadgeCertificatoMedico({ dataCertificato }) {
  if (!certificatoMedicoScaduto(dataCertificato)) return null;

  return (
    <>
      <style>{`
        @keyframes certificatoMedicoLampeggia {
          0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 rgba(220, 38, 38, 0); }
          50% { opacity: .5; transform: scale(.96); box-shadow: 0 0 13px rgba(220, 38, 38, .75); }
        }
      `}</style>
      <span title="Certificato medico scaduto" style={{ display: 'inline-block', marginLeft: '8px', marginTop: '5px', padding: '4px 8px', borderRadius: '5px', background: '#dc2626', color: '#fff', fontSize: '12px', fontWeight: 'bold', animation: 'certificatoMedicoLampeggia 1s ease-in-out infinite' }}>
        ⚠ Certificato medico scaduto
      </span>
    </>
  );
}

function BadgeStatoPagamento({ dataUltimoPagamento, isMaestro, isTirocinante, docenteScuola, esenteMensilita }) {
  if (docenteScuola || esenteMensilita) {
    return (
      <span style={{ backgroundColor: '#6d287f', color: 'white', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
        🧑‍🏫 Docente della scuola (Esente)
      </span>
    );
  }

  if (isMaestro) {
    return (
      <span style={{ backgroundColor: '#1c7ed6', color: 'white', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
        👩‍🏫 Docente (Esente Pagamento)
      </span>
    );
  }

  if (isTirocinante) {
    return (
      <span style={{ backgroundColor: '#fd7e14', color: 'white', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
        👨‍🏫 Tirocinante (Esente Pagamento)
      </span>
    );
  }

  const info = calcolaStatoPagamentoDinamico(dataUltimoPagamento);

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', margin: '5px 0' }}>
      <span style={{
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: info.colore,
        display: 'inline-block'
      }} />
      <span style={{
        backgroundColor: info.colore,
        color: 'white',
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {info.stato}
      </span>
    </div>
  );
}

function ModuloTesseramento({ utenteCorrente }) {
  const [allievi, setAllievi] = useState([]);
  const [allievoSelezionatoId, setAllievoSelezionatoId] = useState('');
  const [datiResponsabile, setDatiResponsabile] = useState({
    nomeResponsabile: '',
    cfResponsabile: ''
  });

  useEffect(() => {
    const caricaAllievi = async () => {
      const snap = await leggiRaccolta('allievi');
      const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllievi(ordinaAllieviPerCognomeNome(lista));
    };
    caricaAllievi();
  }, []);

  const allievo = allievi.find(a => a.id === allievoSelezionatoId);

  // Alcune anagrafiche più vecchie usano nomi di campo differenti. Il modulo
  // le legge tutte, senza dover riscrivere né perdere i dati esistenti.
  const leggiTelefonoAllievo = (a) => String(
    a?.Telefono || a?.telefono || a?.TelefonoAllievo || a?.telefonoAllievo || a?.Cellulare || a?.['Telefono / Cellulare'] || ''
  ).trim();
  const leggiTelefonoGenitore = (a) => String(
    a?.TelefonoGenitore || a?.telefonoGenitore || a?.TelefonoTutore || a?.telefonoTutore || a?.['Telefono Genitore'] || ''
  ).trim();

  const getCategoriaDisplay = (a) => {
    if (!a) return 'N/D';
    if (a.isMaestro || a.docenteTemporaneo) return 'DOCENTE';
    if (a.isTirocinante) return 'TIROCINANTE';
    const corsi = Array.isArray(a.Corsi) ? a.Corsi : (a.Corso ? [a.Corso] : []);
    return corsi.length > 0 ? corsi.join(', ').toUpperCase() : 'NON SPECIFICATA';
  };

  const dataOggiIT = formattaDataIT(dataOggiISO());

  const generaEDownloadWord = async () => {
    if (!allievoSelezionatoId || !allievo) {
      alert("⚠️ Seleziona un allievo dal database prima di scaricare il modulo Word!");
      return;
    }

    const cognomeNome = `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim() || '__________________';
    const categoria = getCategoriaDisplay(allievo);
    const cf = allievo.CodiceFiscale || '__________________';
    const dataNascita = allievo.DataNascita ? formattaDataIT(allievo.DataNascita) : '________';
    const sesso = allievo.Sesso || 'M';
    const telefono = leggiTelefonoAllievo(allievo) || '__________________';
    const telefonoGenitore = leggiTelefonoGenitore(allievo) || '__________________';
    const nomeGenitore = datiResponsabile.nomeResponsabile || '_____________________________________';
    const cfGenitore = datiResponsabile.cfResponsabile || '_____________________';

    const docxObj = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({ text: "A.S.D ACADEMY OF ARTS", bold: true, size: 28 }),
              new TextRun({ text: "\nC.F.: 92066660637 | https://www.academyofarts.it/ | s.renna@pec.it", size: 18 }),
            ],
          }),
          new Paragraph({ text: "\n" }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Cognome e Nome: ", bold: true }), new TextRun(cognomeNome)] })] }),
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Categoria: ", bold: true }), new TextRun(categoria)] })] }),
                ]
              }),
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Codice Fiscale: ", bold: true }), new TextRun(cf)] })] }),
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Data Nascita: ", bold: true }), new TextRun(`${dataNascita} (${sesso})`)] })] }),
                ]
              }),
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Telefono principale: ", bold: true }), new TextRun(telefono)] })] }),
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Telefono genitore: ", bold: true }), new TextRun(telefonoGenitore)] })] }),
                ]
              })
            ]
          }),
          new Paragraph({ text: "\n" }),
          new Paragraph({ children: [new TextRun({ text: "ADULTO AVENTE LA RESPONSABILITÀ GENITORIALE", bold: true, size: 20 })] }),
          new Paragraph({ children: [new TextRun({ text: "indicare l'adulto avente la responsabilità genitoriale a cui sarà intestata la fattura/ricevuta per la quota corsi.", italics: true, size: 16 })] }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Cognome e Nome: ", bold: true }), new TextRun(nomeGenitore)] })] }),
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Codice Fiscale Responsabile: ", bold: true }), new TextRun(cfGenitore)] })] }),
                ]
              })
            ]
          }),
          new Paragraph({ text: "\n" }),
          new Paragraph({
            children: [
              new TextRun({ text: "DICHIARAZIONE DI ISCRIZIONE ED ACCETTAZIONE\n", bold: true }),
              new TextRun({ text: "Il genitore e/o l'esercente la potestà genitoriale del minore o l'atleta maggiorenne con la firma del presente modulo dichiara volontà ad iscrivere l'atleta - ad iscriversi all'attività sportiva organizzata da: ", size: 18 }),
              new TextRun({ text: "A.S.D ACADEMY OF ARTS", bold: true, size: 18 }),
              new TextRun({ text: ", come tesserato per la stagione sportiva corrente, dichiarando inoltre di aver preso visione ed accettare senza condizioni quanto previsto dallo statuto e dal regolamento interno di A.S.D ACADEMY OF ARTS.", size: 18 })
            ]
          }),
          new Paragraph({ text: "\n" }),
          new Paragraph({ children: [new TextRun({ text: `Data: ${dataOggiIT}                     Firma: _____________________________________`, bold: true })] }),
          new Paragraph({ text: "\n" }),
          new Paragraph({
            children: [
              new TextRun({ text: "AUTORIZZAZIONE TRATTAMENTO DATI PERSONALI (GDPR 679/2016)\n", bold: true }),
              new TextRun({ text: "L'atleta o i genitori dichiarano di aver preso visione dell'informativa sulla Privacy e danno il consenso al trattamento dei dati personali per le finalità istituzionali, fiscali, sanitarie e legali.\n", size: 18 }),
              new TextRun({ text: "Si autorizza la fotografia e/o la ripresa del sottoscritto / del minore [SI] [NO]\nSi acconsente all'uso dei dati personali a fini di comunicazione e marketing [SI] [NO]\nSi acconsente alla pubblicazione di video/foto su sito web e social network [SI] [NO]\n", size: 18 })
            ]
          }),
          new Paragraph({
            children: [
              new TextRun({ text: "ESONERO RESPONSABILITÀ & DOCUMENTI\n", bold: true }),
              new TextRun({ text: "Il sottoscritto dichiara di accettare le condizioni di tesseramento ed esonera la A.S.D ACADEMY OF ARTS da ogni responsabilità per danni derivati da fatti imputabili all'atleta o da causa di forza maggiore.", size: 18 })
            ]
          }),
          new Paragraph({ text: "\n\n" }),
          new Paragraph({ children: [new TextRun({ text: `Data: ${dataOggiIT}                     Firma: _____________________________________`, bold: true })] })
        ]
      }]
    });

    const blob = await Packer.toBlob(docxObj);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Modulo_Tesseramento_${allievo.Cognome}_${allievo.Nome}.docx`;
    link.click();

    await registraLogOperazione(utenteCorrente, `Scaricato Modulo Tesseramento Word per ${allievo.Cognome} ${allievo.Nome}`);
  };

  const gestisciStampa = async () => {
    if (!allievoSelezionatoId) {
      alert("⚠️ Seleziona un allievo dal database prima di stampare!");
      return;
    }
    await registraLogOperazione(utenteCorrente, `Generato e stampato modulo tesseramento per ${allievo.Cognome} ${allievo.Nome}`);
    window.print();
  };

  return (
    <div style={{ color: '#f8f9fa' }}>
      <style>{`
        @keyframes richiamoDatiMancanti {
          0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 rgba(255, 107, 107, 0); }
          50% { opacity: .58; transform: scale(1.04); box-shadow: 0 0 10px rgba(255, 107, 107, .62); }
        }
        .richiamo-dati-mancanti { animation: richiamoDatiMancanti 1.5s ease-in-out infinite; }
        /* Palette Academy Of Arts anche nella modifica dell'anagrafica. */
        .modulo-allievo-academy { background: linear-gradient(145deg, #fffafd, #f7eff8) !important; border-color: #d9b8c5 !important; box-shadow: 0 10px 24px rgba(87,19,44,.10); }
        .modulo-allievo-academy h4, .modulo-allievo-academy > label { color: #57132c !important; }
        .modulo-allievo-academy input:not([type="checkbox"]), .modulo-allievo-academy select { background: #fff !important; color: #2d1721 !important; border: 1px solid #d9b8c5 !important; }
        .modulo-allievo-academy .ruoli-allievo-academy, .modulo-allievo-academy .elenco-corsi-allievo-academy { background: #f7eff8 !important; border-color: #d9b8c5 !important; color: #2d1721 !important; }
        .modulo-allievo-academy .ruoli-allievo-academy label { color: #57132c !important; }
        .modulo-allievo-academy .stato-app-allievo-academy { background: #f7eff8 !important; border-color: #a71243 !important; color: #57132c !important; }
        .modulo-allievo-academy .eta-allievo-academy { background: #f0e4f4 !important; border-color: #c5a4df !important; color: #57132c !important; }
        .modulo-allievo-academy .bottone-salva-allievo-academy { background: linear-gradient(110deg, #a71243, #d82d62) !important; color: #fff !important; }
      `}</style>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 7mm; }
          html, body { width: 210mm !important; height: 297mm !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; }
          body * { visibility: hidden; }
          /* L'app nascosta non deve continuare a occupare altezza: era la
             causa del secondo foglio bianco della stampa diretta. */
          #root { min-height: 0 !important; height: 0 !important; overflow: visible !important; }
          #modulo-stampa-pdf, #modulo-stampa-pdf * { visibility: visible; }
          #modulo-stampa-pdf {
            position: fixed !important;
            inset: 0 !important;
            width: auto !important;
            height: auto !important;
            min-height: 0 !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            background: #ffffff !important;
            color: #000000 !important;
            padding: 5mm 6mm !important;
            font-family: Arial, sans-serif;
            font-size: 9px !important;
            line-height: 1.15 !important;
          }
          #modulo-stampa-pdf table { page-break-inside: avoid; }
          #modulo-stampa-pdf p { margin-top: 2px !important; margin-bottom: 2px !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ backgroundColor: '#353b4c', padding: '20px', borderRadius: '10px', border: '1px solid #4a5266', maxWidth: '600px', marginBottom: '25px' }}>
        <h3 style={{ color: '#fcc419', marginTop: 0 }}>📑 Stampa / Scarica Modulo Tesseramento Word</h3>
        <p style={{ fontSize: '13px', color: '#adb5bd' }}>
          Seleziona l'allievo dal database per compilare automaticamente il modulo con i dati dell'inserimento allievo.
        </p>

        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Seleziona Allievo:</label>
        <select 
          value={allievoSelezionatoId} 
          onChange={e => setAllievoSelezionatoId(e.target.value)}
          style={{ width: '100%', padding: '10px', marginTop: '6px', marginBottom: '15px', borderRadius: '6px', backgroundColor: '#2b3140', color: '#fff', border: '1px solid #4a5266', fontWeight: 'bold' }}
        >
          <option value="">-- Seleziona Allievo dall'Anagrafica --</option>
          {allievi.map(a => (
            <option key={a.id} value={a.id}>
              {a.Cognome} {a.Nome} {a.CodiceFiscale ? `(${a.CodiceFiscale})` : ''}
            </option>
          ))}
        </select>

        {allievo && (
          <div style={{ backgroundColor: '#2b3140', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #4a5266' }}>
            <h5 style={{ color: '#4dabf7', margin: '0 0 8px 0' }}>👨‍👩‍👧 Dati Genitore / Responsabile Legale (Fatturazione)</h5>
            <input 
              placeholder="Cognome e Nome Genitore" 
              value={datiResponsabile.nomeResponsabile}
              onChange={e => setDatiResponsabile({ ...datiResponsabile, nomeResponsabile: e.target.value })}
              style={{ width: '100%', padding: '8px', marginBottom: '8px', backgroundColor: '#353b4c', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px' }}
            />
            <input 
              placeholder="Codice Fiscale Genitore" 
              value={datiResponsabile.cfResponsabile}
              onChange={e => setDatiResponsabile({ ...datiResponsabile, cfResponsabile: e.target.value.toUpperCase() })}
              style={{ width: '100%', padding: '8px', backgroundColor: '#353b4c', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px', textTransform: 'uppercase' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={generaEDownloadWord}
            style={{ flex: 1, backgroundColor: '#1c7ed6', color: '#fff', border: 'none', padding: '12px', borderRadius: '6px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}
          >
            📝 Scarica Modulo Word (.docx)
          </button>
          <button 
            onClick={gestisciStampa}
            style={{ flex: 1, backgroundColor: '#fcc419', color: '#000', border: 'none', padding: '12px', borderRadius: '6px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}
          >
            🖨️ Stampa Diretta
          </button>
        </div>
      </div>

      {allievo ? (
        <div id="modulo-stampa-pdf">
          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <h2 style={{ margin: 0, fontSize: '16px' }}>A.S.D ACADEMY OF ARTS</h2>
            <p style={{ margin: '2px 0', fontSize: '10px' }}>C.F.: 92066660637 | https://www.academyofarts.it/ | s.renna@pec.it</p>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '6px' }} border="1" cellPadding="4">
            <tbody>
              <tr>
                <td colSpan="2"><strong>Cognome e Nome:</strong> {allievo.Cognome} {allievo.Nome}</td>
                <td><strong>Categoria:</strong> {getCategoriaDisplay(allievo)}</td>
              </tr>
              <tr>
                <td colSpan="3"><strong>Indirizzo di residenza:</strong> - </td>
              </tr>
              <tr>
                <td><strong>Luogo di nascita:</strong> - </td>
                <td><strong>Data di nascita:</strong> {allievo.DataNascita ? formattaDataIT(allievo.DataNascita) : 'N/D'}</td>
                <td><strong>Sesso:</strong> {allievo.Sesso || 'M'}</td>
              </tr>
              <tr>
                <td colSpan="2"><strong>Codice fiscale:</strong> {allievo.CodiceFiscale || '__________________'}</td>
                <td><strong>Mail:</strong> - </td>
              </tr>
              <tr>
                <td colSpan="2"><strong>Telefono allievo:</strong> {leggiTelefonoAllievo(allievo) || '__________________'}</td>
                <td><strong>Telefono genitore:</strong> {leggiTelefonoGenitore(allievo) || '__________________'}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ fontWeight: 'bold', fontSize: '10px', marginTop: '6px', marginBottom: '2px' }}>
            ADULTO AVENTE LA RESPONSABILITÀ GENITORIALE
          </div>
          <p style={{ margin: '0 0 4px 0', fontSize: '9px' }}>
            indicare l'adulto avente la responsabilità genitoriale a cui sarà intestata la fattura/ricevuta per la quota corsi.
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }} border="1" cellPadding="4">
            <tbody>
              <tr>
                <td><strong>Cognome e Nome:</strong> {datiResponsabile.nomeResponsabile || '_____________________________________'}</td>
                <td><strong>Codice Fiscale Responsabile 1:</strong> {datiResponsabile.cfResponsabile || '_____________________'}</td>
              </tr>
            </tbody>
          </table>

          <p style={{ textAlign: 'justify', fontSize: '9px', margin: '4px 0' }}>
            Il genitore e/o l'esercente la potestà genitoriale del minore o l'atleta maggiorenne con la firma del presente modulo dichiara volontà ad iscrivere l'atleta - ad iscriversi all'attività sportiva organizzata da: <strong>A.S.D ACADEMY OF ARTS</strong>, come tesserato per la stagione sportiva corrente, dichiarando inoltre di aver preso visione ed accettare senza condizioni quanto previsto dallo statuto e dal regolamento interno di A.S.D ACADEMY OF ARTS nonché, parimenti, di essere a conoscenza e di accettare senza condizione tutti i relativi usi e consuetudini, indicazioni programmatiche, disposizioni operative ed indirizzi generali (come disposti dal presidente e dal consiglio direttivo).
          </p>

          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0' }}>
            <div><strong>Data:</strong> {dataOggiIT}</div>
            <div><strong>Firma:</strong> _____________________________________</div>
          </div>

          <div style={{ fontWeight: 'bold', fontSize: '9px', marginTop: '6px' }}>AUTORIZZAZIONE TRATTAMENTO DATI PERSONALI:</div>
          <p style={{ textAlign: 'justify', fontSize: '8.5px', margin: '3px 0' }}>
            L'atleta o i genitori o chi ne esercita potestà genitoriale con la sottoscrizione dichiara di aver preso visione dell'informativa sulla Privacy fornita; di dare il proprio consenso al trattamento dei dati personali da parte di A.S.D ACADEMY OF ARTS, ai sensi dell'art. 13 D.lgs. n. 196/2003 e del GDPR 679/2016 per la realizzazione delle finalità istituzionali di A.S.D ACADEMY OF ARTS e nella misura necessaria all'adempimento degli obblighi fiscali, sanitari, legali ed assicurativi previsti dalle norme.
          </p>

          <div style={{ fontSize: '8.5px', margin: '3px 0' }}>
            Si autorizza la fotografia e/o la ripresa del sottoscritto / del minore [SI] [NO]<br />
            Si acconsente all'uso dei dati personali a fini di comunicazione e marketing [SI] [NO]<br />
            Si acconsente alla pubblicazione di video/foto su sito web/social network [SI] [NO]
          </div>

          <div style={{ fontWeight: 'bold', fontSize: '9px', marginTop: '6px' }}>ESONERO RESPONSABILITÀ:</div>
          <p style={{ textAlign: 'justify', fontSize: '8.5px', margin: '3px 0' }}>
            Il sottoscritto dichiara di essere a conoscenza e di accettare le condizioni relative al tesseramento e alla copertura assicurativa infortuni fornite dal tesseramento. Pertanto esonera A.S.D ACADEMY OF ARTS nella figura del suo Presidente da qualsiasi responsabilità oggettiva o soggettiva.
          </p>

          <div style={{ fontWeight: 'bold', fontSize: '9px', marginTop: '6px' }}>DOCUMENTI NECESSARI - QUOTA:</div>
          <p style={{ textAlign: 'justify', fontSize: '8.5px', margin: '3px 0' }}>
            Ogni tesserato dovrà consegnare all'atto dell'iscrizione la documentazione richiesta. La mancanza di uno solo dei documenti impedirà il perfezionamento dell'iscrizione e la conseguente partecipazione alle attività sportiva.
          </p>

          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0 6px 0' }}>
            <div><strong>Data:</strong> {dataOggiIT}</div>
            <div><strong>Firma:</strong> _____________________________________</div>
          </div>

          <p style={{ textAlign: 'justify', fontSize: '8.5px', margin: '4px 0' }}>
            Ai sensi e per gli effetti dell'art. 1341 e 1342 c.c. ed ulteriori norme in materia, il sottoscritto atleta o i genitori o chi ne esercita potestà genitoriale dichiara espressamente di avere esaminato di approvare tutti i paragrafi della presente scrittura.
          </p>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
            <div><strong>Data:</strong> {dataOggiIT}</div>
            <div><strong>Firma:</strong> _____________________________________</div>
          </div>
        </div>
      ) : (
        <div className="no-print" style={{ color: '#adb5bd' }}>
          Seleziona un allievo per caricare l'anteprima del modulo.
        </div>
      )}
    </div>
  );
}

function HomeDivertente({ utenteCorrente }) {
  const [dataOra, setDataOra] = useState(new Date().toLocaleString('it-IT'));

  useEffect(() => {
    const timer = setInterval(() => {
      setDataOra(new Date().toLocaleString('it-IT'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const keyframesGlow = `
    @keyframes pausaGlow {
      0%, 100% { box-shadow: 0 16px 38px rgba(18, 27, 46, 0.25), 0 0 0 rgba(255, 202, 72, 0); }
      50% { box-shadow: 0 16px 38px rgba(18, 27, 46, 0.32), 0 0 20px rgba(255, 202, 72, 0.18); }
    }
  `;

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      width: '100%',
      maxWidth: '480px',
      margin: '0 auto',
      boxSizing: 'border-box'
    }}>
      <style>{keyframesGlow}</style>

      <div style={{
        background: 'linear-gradient(135deg, #273247, #182131)',
        border: '1px solid rgba(255, 202, 72, 0.75)',
        borderRadius: '22px',
        padding: '24px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 16px 38px rgba(18,27,46,.25)',
        animation: 'pausaGlow 4s ease-in-out infinite',
        boxSizing: 'border-box'
      }}>
        <div style={{ width: '48px', height: '48px', margin: '0 auto 12px', borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, #ffd86b, #e9a520)', fontSize: '23px', boxShadow: '0 8px 18px rgba(233,165,32,.24)' }}>💃</div>
        <div style={{ color: '#ffd86b', fontSize: '11px', fontWeight: '800', letterSpacing: '2px', marginBottom: '7px' }}>
          ACADEMY OF ARTS
        </div>
        <h2 style={{ color: '#ffffff', fontSize: '21px', margin: '0 0 8px', letterSpacing: '.2px' }}>
          Una breve pausa, il ritmo riparte presto
        </h2>
        
        <p style={{ color: '#cbd5e1', fontSize: '14px', margin: '0 0 18px', lineHeight: '1.55' }}>
          La regia è al lavoro dietro le quinte. Torniamo subito in pista: i dati sono al sicuro. ✨
        </p>

        <div style={{ backgroundColor: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.12)', borderRadius: '14px', padding: '12px 14px', marginBottom: '10px' }}>
          <span style={{ fontSize: '10px', color: '#cbd5e1', textTransform: 'uppercase', display: 'block', marginBottom: '5px', letterSpacing: '1.2px' }}>
            🕒 Data e ora
          </span>
          <div style={{ fontSize: '19px', fontWeight: '800', color: '#ffd86b', fontFamily: 'monospace', letterSpacing: '.5px' }}>
            {dataOra}
          </div>
        </div>

        <div style={{ fontSize: '13px', color: '#e2e8f0', backgroundColor: 'rgba(116, 153, 255, .12)', padding: '10px 12px', borderRadius: '12px', border: '1px solid rgba(116,153,255,.27)' }}>
          👤 Gestione affidata a <strong style={{ color: '#93c5fd' }}>{utenteCorrente}</strong>
        </div>
      </div>
    </div>
  );
}

function Login({ onLogin, utenti, sistemaBloccato, utenteInPausa, onAnnullaPausa, messaggiNonLettiLogin = {} }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mostraPassword, setMostraPassword] = useState(false);
  const [errore, setErrore] = useState('');
  const [accessoAdmin, setAccessoAdmin] = useState(false);
  const [profiloScelto, setProfiloScelto] = useState(null);
  // Il conteggio arriva dal server: prima del login il database non è leggibile.
  const messaggiMasterNonLetti = messaggiNonLettiLogin;
  const [recuperoAdmin, setRecuperoAdmin] = useState(false);
  const [emailRecupero, setEmailRecupero] = useState(EMAIL_RECUPERO_ADMIN);
  const [messaggioRecupero, setMessaggioRecupero] = useState('');
  const [invioRecuperoInCorso, setInvioRecuperoInCorso] = useState(false);
  const [tentativiPassword, setTentativiPassword] = useState({});
  const [accessoInCorso, setAccessoInCorso] = useState(false);
  const [richiestaResetAperta, setRichiestaResetAperta] = useState(false);
  const [profiloResetPassword, setProfiloResetPassword] = useState('');
  const [invioResetInCorso, setInvioResetInCorso] = useState(false);
  const [messaggioResetPassword, setMessaggioResetPassword] = useState('');

  const amministratore = utenti.find(u => String(u.ruolo || '').toLowerCase() === 'admin');
  const profiliOperativi = utenti.filter(u => String(u.ruolo || '').toLowerCase() !== 'admin');
  const chiaveAggiornamentoUtente = profilo => `academy_of_arts_aggiornamento_web_visto_${String(profilo?.username || '').trim().toLocaleLowerCase('it-IT')}`;
  const scegliProfilo = profilo => {
    setUsername(profilo.username);
    setPassword('');
    setErrore('');
    setMostraPassword(false);
    setProfiloScelto(profilo);
  };
  const selezioneProfiliVisibile = !utenteInPausa && !accessoAdmin && !profiloScelto;

  const registraTentativoPasswordNonValido = (nomeUtente, messaggio) => {
    const chiave = String(nomeUtente || 'utente').trim().toLocaleLowerCase('it-IT') || 'utente';
    const nuovoTotale = (tentativiPassword[chiave] || 0) + 1;
    setTentativiPassword(tentativi => ({ ...tentativi, [chiave]: nuovoTotale }));
    setErrore(`${messaggio} Tentativo ${Math.min(nuovoTotale, 3)} di 3.`);
    if (nuovoTotale >= 3) {
      setProfiloResetPassword(nomeUtente || username || 'Utente non indicato');
      setMessaggioResetPassword('');
      setRichiestaResetAperta(true);
    }
  };

  const inviaRichiestaResetPassword = async () => {
    const nomeUtente = String(profiloResetPassword || username || 'Utente non indicato').trim() || 'Utente non indicato';
    setInvioResetInCorso(true);
    setMessaggioResetPassword('');
    try {
      await addDoc(collection(db, 'segnalazioni_bug'), {
        mittenteTipo: 'richiesta_reset_password',
        mittenteNome: nomeUtente,
        mittenteRuolo: accessoAdmin ? 'admin' : (profiloScelto?.ruolo || 'operatore'),
        categoria: 'reset_password',
        descrizione: `Richiesta di Reset Password per ${nomeUtente}. L’utente ha inserito una password non valida per 3 volte durante l’accesso alla Web App.`,
        pagina: '/accesso',
        origine: 'login_webapp',
        dataCreazione: new Date().toISOString(),
        stato: 'nuova'
      });
      const chiave = nomeUtente.toLocaleLowerCase('it-IT');
      setTentativiPassword(tentativi => ({ ...tentativi, [chiave]: 0 }));
      setMessaggioResetPassword('✅ Richiesta inviata a DR System Code. Sarai ricontattato per il reset della password.');
    } catch (erroreInvio) {
      console.error('Invio richiesta reset password non riuscito:', erroreInvio);
      setMessaggioResetPassword('Non è stato possibile inviare la richiesta. Riprova tra poco oppure avvisa direttamente la segreteria.');
    } finally {
      setInvioResetInCorso(false);
    }
  };

  useEffect(() => {
    if (utenteInPausa) {
      setUsername(utenteInPausa);
      return;
    }
    // Non sovrascrivere mai il profilo già scelto: ogni aggiornamento di
    // utenti_sistema (presenza online ogni 30s, cache→server all'avvio)
    // rifarebbe questo effetto e riporterebbe il nome al primo profilo,
    // facendo fallire la password corretta.
    if (profiloScelto || accessoAdmin) return;
    if (utenti.length > 0) {
      const primoProfiloOperativo = utenti.find(u => String(u.ruolo || '').toLowerCase() !== 'admin');
      setUsername((primoProfiloOperativo || utenti[0]).username);
    }
  }, [utenteInPausa, utenti, profiloScelto, accessoAdmin]);


  const gestisciLogin = async (e) => {
    e.preventDefault();
    if (accessoInCorso) return;
    const usernameNormalizzato = String(profiloScelto?.username || username || '').trim().toLocaleLowerCase('it-IT');
    const passwordInserita = String(password || '').trim();
    const accessoComeAdmin = accessoAdmin || usernameNormalizzato === String(amministratore?.username || '').trim().toLocaleLowerCase('it-IT');
    let utenteValido = null;
    setErrore('');
    setAccessoInCorso(true);

    try {
    if (accessoComeAdmin && amministratore) {
      try {
        await signInWithEmailAndPassword(auth, EMAIL_RECUPERO_ADMIN, password);
        utenteValido = amministratore;
      } catch (erroreAuth) {
        // Se l'accesso email dell'Admin non è attivo su Firebase, la password
        // viene controllata dal server (mai più nel browser).
        if (!['auth/operation-not-allowed', 'auth/user-not-found'].includes(erroreAuth.code)) {
          registraTentativoPasswordNonValido(amministratore.username, '⚠️ Password amministratore non valida! Riprova.');
          return;
        }
        try {
          utenteValido = await accediStaff({ id: amministratore.id, username: amministratore.username, password: passwordInserita });
        } catch (erroreServer) {
          if (erroreServer?.codice === 'password_errata') {
            registraTentativoPasswordNonValido(amministratore.username, '⚠️ Password amministratore non valida! Riprova.');
          } else {
            setErrore(erroreServer?.message || 'Accesso non riuscito. Riprova tra poco.');
          }
          return;
        }
      }
    } else {
      // La password viene verificata dal server: il browser non riceve più
      // le password degli operatori.
      try {
        utenteValido = await accediStaff({ id: profiloScelto?.id, username: profiloScelto?.username || username, password: passwordInserita });
      } catch (erroreServer) {
        if (erroreServer?.codice === 'password_errata') {
          registraTentativoPasswordNonValido(profiloScelto?.username || username, '⚠️ Password non valida! Riprova.');
        } else {
          setErrore(erroreServer?.message || 'Accesso non riuscito. Riprova tra poco.');
        }
        return;
      }
    }

    if (utenteValido) {
      if (sistemaBloccato && utenteValido.ruolo !== 'admin') {
        esciDaFirebase();
        setErrore('🛠️ MANUTENZIONE IN CORSO — Il gestionale Academy of Arts è temporaneamente non disponibile per un intervento tecnico. Riprova tra poco.');
        return;
      }
      if (utenteValido.ruolo !== 'admin') {
        try {
          localStorage.setItem(chiaveAggiornamentoUtente(utenteValido), VERSIONE_APP);
        } catch (erroreAggiornamentoLocale) {
          console.warn('Impossibile registrare localmente la lettura degli aggiornamenti:', erroreAggiornamentoLocale);
        }
        if (utenteValido.id) updateDoc(doc(db, 'utenti_sistema', utenteValido.id), {
            ultimoAggiornamentoWebVisto: VERSIONE_APP,
            dataUltimoAggiornamentoWebVisto: new Date().toISOString()
          }).catch(erroreAggiornamento => {
          console.warn('Impossibile registrare la lettura degli aggiornamenti:', erroreAggiornamento);
          });
      }
      onLogin(utenteValido);
    } else {
      registraTentativoPasswordNonValido(profiloScelto?.username || username, '⚠️ Password non valida! Riprova.');
    }
    } finally {
      setAccessoInCorso(false);
    }
  };

  const inviaRecuperoPasswordAdmin = async () => {
    const email = emailRecupero.trim().toLocaleLowerCase('it-IT');
    if (!email) return;
    if (email !== EMAIL_RECUPERO_ADMIN.toLocaleLowerCase('it-IT')) {
      setMessaggioRecupero('Per sicurezza il recupero Admin è disponibile solo sull’indirizzo email autorizzato.');
      return;
    }
    setInvioRecuperoInCorso(true);
    setMessaggioRecupero('');
    try {
      await sendPasswordResetEmail(auth, email, { url: `${window.location.origin}/`, handleCodeInApp: false });
      setMessaggioRecupero('Se l’indirizzo è abilitato, riceverai tra poco un’email con il link per scegliere una nuova password.');
    } catch (erroreAuth) {
      console.error('Recupero password Admin non riuscito:', erroreAuth);
      setMessaggioRecupero('Il recupero non è ancora attivo oppure l’indirizzo non è riconosciuto. Contatta l’amministratore del progetto Firebase.');
    } finally {
      setInvioRecuperoInCorso(false);
    }
  };

  return (
    <div className="login-academy-layout" style={{ width: 'min(1100px, 100%)', minHeight: 590, overflow: 'hidden', borderRadius: 28, background: 'rgba(5, 6, 10, .78)', border: '1px solid rgba(255, 255, 255, .13)', boxShadow: '0 30px 85px rgba(0,0,0,.55)' }}>
      <style>{`
        @media (max-width: 720px) {
          .login-academy-layout { grid-template-columns: 1fr !important; min-height: auto !important; }
          .login-academy-visual { display: none !important; }
          .login-academy-form { padding: 30px 14px !important; }
          .login-profile-list { gap: 12px !important; }
          .login-profile-card { width: calc((100vw - 62px) / 3) !important; max-width: 125px; min-width: 82px; }
          .login-profile-photo { width: min(100%, 112px) !important; height: 118px !important; }
          .login-profile-card strong { font-size: 13px !important; }
        }
        @media (max-width: 390px) {
          .login-profile-list { gap: 8px !important; }
          .login-profile-photo { height: 98px !important; }
          .login-profile-card strong { font-size: 12px !important; }
        }
      `}</style>
      <form className="login-academy-form" onSubmit={gestisciLogin} style={{ position: 'relative', zIndex: 1, padding: '43px 42px', background: 'linear-gradient(150deg, rgba(12, 12, 17, .98), rgba(29, 9, 17, .94))', color: '#fff', boxSizing: 'border-box' }}>
        {!utenteInPausa && amministratore && <div style={{ position: 'absolute', top: 18, right: 18, display: 'flex', gap: 7, alignItems: 'center' }}>
          <button type="button" onClick={() => window.location.reload()} title="Ricarica la pagina" style={{ border: '1px solid rgba(151, 218, 255, .52)', borderRadius: 8, padding: '7px 9px', background: 'rgba(64, 154, 203, .16)', color: '#c7eeff', cursor: 'pointer', fontSize: 10, fontWeight: 'bold' }}>↻ Aggiorna</button>
          <button type="button" onClick={() => { setAccessoAdmin(aperto => { const prossimo = !aperto; setUsername(prossimo ? amministratore.username : (profiliOperativi[0]?.username || '')); setPassword(''); setErrore(''); setMessaggioRecupero(''); setRecuperoAdmin(false); setProfiloScelto(null); return prossimo; }); }} style={{ border: '1px solid rgba(255,179,202,.55)', borderRadius: 8, padding: '7px 9px', background: accessoAdmin ? '#9e123f' : 'rgba(255,255,255,.06)', color: '#ffd5e1', cursor: 'pointer', fontSize: 10, fontWeight: 'bold' }}>{accessoAdmin ? '← Operatori' : '🧑‍🔧 Admin'}</button>
        </div>}
        {selezioneProfiliVisibile ? <div style={{ textAlign: 'center', margin: '0 auto 25px' }}>
          <img src="/academy-allievi-icon.png" alt="Logo Academy Of Arts" style={{ width: 180, height: 88, objectFit: 'contain', display: 'block', margin: '0 auto 5px', mixBlendMode: 'screen' }} />
          <h1 style={{ margin: 0, color: '#fff', fontFamily: 'Georgia, serif', fontSize: 35, lineHeight: 1.05, letterSpacing: '.4px' }}>Academy Of Arts</h1>
          <p style={{ margin: '8px 0 0', color: '#ff8eae', fontSize: 10, fontWeight: 'bold', letterSpacing: '2px' }}>TEAM DANCE PROJECT</p>
        </div> : <>
          <img src="/academy-allievi-icon.png" alt="Logo Academy Of Arts" style={{ width: 118, height: 58, objectFit: 'contain', display: 'block', margin: '0 0 16px', mixBlendMode: 'screen' }} />
          <p style={{ margin: '0 0 8px', color: '#ff5a86', fontSize: 11, fontWeight: 'bold', letterSpacing: '2px' }}>ACADEMY OF ARTS</p>
          <h2 style={{ color: '#fff', margin: '0 0 7px', fontFamily: 'Georgia, serif', fontSize: 31, lineHeight: 1.05 }}>
            {recuperoAdmin ? <>Recupera<br />password</> : accessoAdmin ? <>Area<br />Amministratore</> : <>Accesso di<br />{profiloScelto?.username || 'Segreteria'}</>}
          </h2>
          <h4 style={{ margin: '0 0 28px', fontSize: 11, color: '#c9b8bd', textTransform: 'uppercase', letterSpacing: '1.2px', fontWeight: 'normal' }}>
            {utenteInPausa ? `🔓 Sblocca Postazione di ${utenteInPausa}` : recuperoAdmin ? 'Ti invieremo un link sicuro per scegliere una nuova password' : 'Inserisci la tua password per entrare'}
          </h4>
        </>}

        {sistemaBloccato && (
          <div style={{ backgroundColor: 'rgba(185, 28, 28, .25)', color: '#ffb4bd', border: '1px solid rgba(255, 112, 130, .50)', padding: '10px', borderRadius: '9px', fontSize: '12px', marginBottom: '14px', textAlign: 'center', fontWeight: 'bold' }}>
            🛠️ MANUTENZIONE TECNICA IN CORSO · ACCESSO TEMPORANEAMENTE SOSPESO
          </div>
        )}

        {errore && <div style={{ color: '#ffb4bd', background: 'rgba(185, 28, 28, .25)', border: '1px solid rgba(255, 112, 130, .50)', padding: 10, borderRadius: 9, fontSize: '12px', marginBottom: '14px', textAlign: 'center', fontWeight: 'bold' }}>{errore}</div>}

        {richiestaResetAperta && <div role="dialog" aria-modal="true" aria-labelledby="titolo-richiesta-reset" style={{ position: 'fixed', inset: 0, zIndex: 10050, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(7, 4, 8, .76)', backdropFilter: 'blur(3px)' }}>
          <section style={{ width: 'min(100%, 430px)', padding: 25, borderRadius: 18, background: 'linear-gradient(145deg, #fff8fb, #fff)', border: '2px solid #e0527d', boxShadow: '0 24px 64px rgba(0,0,0,.48)', color: '#32101e', textAlign: 'center' }}>
            <div aria-hidden="true" style={{ width: 62, height: 62, margin: '0 auto 12px', display: 'grid', placeItems: 'center', borderRadius: '50%', background: '#ffe0ea', fontSize: 30 }}>🔐</div>
            <h2 id="titolo-richiesta-reset" style={{ margin: 0, color: '#8e1d46', fontFamily: 'Georgia, serif', fontSize: 23 }}>Hai raggiunto 3 tentativi su 3</h2>
            <p style={{ margin: '12px 0 7px', color: '#432332', fontSize: 15, lineHeight: 1.5 }}>L’accesso di <strong>{profiloResetPassword}</strong> è stato sospeso per sicurezza dopo tre tentativi.</p>
            <p style={{ margin: '0 0 18px', color: '#765b66', fontSize: 13, lineHeight: 1.45 }}>Contatta lo sviluppatore: la richiesta indicherà già il nome dell’operatore e il Reset Password richiesto.</p>
            {messaggioResetPassword && <p style={{ margin: '0 0 15px', padding: 11, borderRadius: 9, background: messaggioResetPassword.startsWith('✅') ? '#e8f8ed' : '#fff0f0', border: `1px solid ${messaggioResetPassword.startsWith('✅') ? '#a9dfbb' : '#fecaca'}`, color: messaggioResetPassword.startsWith('✅') ? '#146c3a' : '#b42318', fontSize: 13, fontWeight: 'bold', lineHeight: 1.4 }}>{messaggioResetPassword}</p>}
            {!messaggioResetPassword.startsWith('✅') && <button type="button" disabled={invioResetInCorso} onClick={inviaRichiestaResetPassword} style={{ width: '100%', padding: '12px 15px', border: 0, borderRadius: 10, background: 'linear-gradient(120deg, #a71243, #e0527d)', color: '#fff', fontWeight: 'bold', cursor: invioResetInCorso ? 'wait' : 'pointer', fontSize: 14, opacity: invioResetInCorso ? .7 : 1 }}>{invioResetInCorso ? 'INVIO IN CORSO…' : '📨 INVIA RICHIESTA DI RESET'}</button>}
            <button type="button" onClick={() => setRichiestaResetAperta(false)} style={{ width: '100%', marginTop: 9, padding: 10, borderRadius: 9, border: '1px solid #c9a8b5', background: '#fff', color: '#613a49', fontWeight: 'bold', cursor: 'pointer', fontSize: 12 }}>{messaggioResetPassword.startsWith('✅') ? 'CHIUDI' : 'NON ORA'}</button>
          </section>
        </div>}

        {selezioneProfiliVisibile && (
          <>
            <div className="login-profile-list" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flexWrap: 'wrap', gap: 25, margin: '10px 0 28px' }}>
              {profiliOperativi.map(u => {
                // Solo i due Master mantengono il titolo M°: gli altri
                // collaboratori autorizzati alle lezioni private sono Docenti.
                const ruolo = utenteEMasterPrioritario(u) ? 'M°' : utenteGestisceLezioniPrivate(u) ? 'DOCENTE' : String(u.ruolo || 'operatore').toUpperCase();
                const fotoProfilo = fotoProfiloOperatore(u);
                const iniziali = String(u.username || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(parte => parte[0]).join('').toUpperCase();
                const nomeProfilo = String(u.username || '').trim().toLowerCase();
                const nomeVisualizzato = utenteEMasterPrioritario(u) ? `M° ${u.username}` : u.username;
                const messaggiDaLeggere = String(u.ruolo || '').toLowerCase() === 'master' ? (messaggiMasterNonLetti[nomeProfilo] || 0) : 0;
                const profiloOnline = utenteStaffAttivo(u);
                const simboloProfilo = nomeProfilo === 'lia di martino' || nomeProfilo === 'luisa pagano'
                  ? '👩'
                  : nomeProfilo === 'salvatore renna'
                    ? '👨'
                    : nomeProfilo === 'admin'
                      ? '🧑‍🔧'
                      : iniziali;
                return <button className="login-profile-card" key={u.id || u.username} type="button" onClick={() => scegliProfilo(u)} style={{ width: 150, padding: 0, background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', textAlign: 'center' }}>
                  <span className="login-profile-photo" style={{ position: 'relative', width: 138, height: 150, margin: '0 auto 10px', overflow: 'hidden', display: 'grid', placeItems: 'center', borderRadius: 8, background: fotoProfilo ? '#21131b' : 'linear-gradient(145deg, rgba(199,31,99,.62), rgba(95,12,54,.9))', color: '#ffd5e1', border: fotoProfilo ? '3px solid rgba(255,255,255,.72)' : '3px dashed #ff7a9e', fontWeight: 'bold', fontSize: fotoProfilo ? 16 : 54, boxShadow: '0 10px 26px rgba(0,0,0,.30)' }}>
                    {fotoProfilo ? <img src={fotoProfilo} alt={`Foto di ${u.username}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : simboloProfilo}
                    {messaggiDaLeggere > 0 && <span title={`${messaggiDaLeggere} messaggi da leggere`} style={{ position: 'absolute', top: 6, right: 6, minWidth: 24, height: 24, padding: '0 4px', display: 'inline-grid', placeItems: 'center', borderRadius: 16, background: '#e03131', color: '#fff', fontSize: 12, boxShadow: '0 3px 8px rgba(0,0,0,.45)' }}>🔔{messaggiDaLeggere}</span>}
                  </span>
                  <strong style={{ display: 'block', fontSize: 16, lineHeight: 1.18, color: '#f7e9ed' }}>{nomeVisualizzato}</strong>
                  {!utenteEMasterPrioritario(u) && <small style={{ display: 'block', marginTop: 5, color: '#d8c4cc', fontSize: 10, letterSpacing: .5 }}>{ruolo}</small>}
                  {profiloOnline && <span title={`${u.username} è collegato`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6, padding: '4px 8px', borderRadius: 14, background: 'rgba(62,226,133,.16)', color: '#8ce99a', fontSize: 11, fontWeight: 'bold', lineHeight: 1 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3ee285', boxShadow: '0 0 8px #3ee285' }} />Online</span>}
                </button>;
              })}
            </div>
            <p style={{ margin: '-8px 0 18px', color: '#d8c4cc', fontSize: 12, textAlign: 'center' }}>Scegli il tuo profilo per continuare.</p>
            <div style={{ maxWidth: 620, margin: '0 auto 4px', padding: '17px 20px', borderRadius: 12, background: 'rgba(255,255,255,.055)', borderLeft: '3px solid #e72c62', textAlign: 'left' }}>
              <span style={{ color: '#ff6a96', fontSize: 11, fontWeight: 'bold', letterSpacing: 1.6 }}>01 · ACADEMY OF ARTS</span>
              <h3 style={{ margin: '7px 0 6px', color: '#fff', fontSize: 22, fontFamily: 'Georgia, serif' }}>Danza, passione e organizzazione.</h3>
              <p style={{ margin: 0, color: '#dacbd0', fontSize: 13, lineHeight: 1.5 }}>Un gestionale pensato per accompagnare ogni passo della tua scuola.</p>
              <p style={{ margin: '10px 0 0', color: '#f4a1b9', fontSize: 10, letterSpacing: '.3px' }}>Ideata e realizzata da <strong>DR System Code</strong></p>
            </div>
          </>
        )}

        {!selezioneProfiliVisibile && !utenteInPausa && accessoAdmin && !recuperoAdmin && <div style={{ marginBottom: 20, padding: '14px 13px', borderRadius: 12, background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,179,202,.32)' }}>
          <strong style={{ display: 'block', color: '#ffd5e1', fontSize: 13 }}>🧑‍🔧 Accesso amministratore</strong>
          <span style={{ display: 'block', marginTop: 5, color: '#d8c4cc', fontSize: 11 }}>Utente: {amministratore?.username}. Inserisci la password per gestire le modifiche della web app.</span>
        </div>}

        {!selezioneProfiliVisibile && !utenteInPausa && !accessoAdmin && profiloScelto && <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,179,202,.28)' }}>
          <span style={{ width: 48, height: 52, display: 'grid', placeItems: 'center', overflow: 'hidden', borderRadius: 6, background: 'rgba(255,179,202,.10)', border: '2px dashed #ff7a9e', fontSize: 24 }}>
            {fotoProfiloOperatore(profiloScelto) ? <img src={fotoProfiloOperatore(profiloScelto)} alt={`Foto di ${profiloScelto.username}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (String(profiloScelto.username || '').toLowerCase().includes('lia') || String(profiloScelto.username || '').toLowerCase().includes('luisa') ? '👩' : String(profiloScelto.username || '').toLowerCase().includes('salvatore') ? '👨' : '👤')}
          </span>
          <span><strong style={{ display: 'block', fontSize: 15 }}>{utenteEMasterPrioritario(profiloScelto) ? `M° ${profiloScelto.username}` : profiloScelto.username}</strong><small style={{ color: '#d8c4cc', fontSize: 10 }}>{utenteEMasterPrioritario(profiloScelto) ? 'M°' : utenteGestisceLezioniPrivate(profiloScelto) ? 'DOCENTE' : (profiloScelto.ruolo || 'operatore').toUpperCase()}</small></span>
          <button type="button" onClick={() => { setProfiloScelto(null); setPassword(''); setErrore(''); }} style={{ marginLeft: 'auto', border: '1px solid rgba(255,179,202,.42)', background: 'transparent', color: '#ffd5e1', borderRadius: 7, padding: '6px 8px', cursor: 'pointer', fontSize: 10, fontWeight: 'bold' }}>CAMBIA</button>
        </div>}

        {!selezioneProfiliVisibile && recuperoAdmin && <div style={{ marginTop: 6 }}>
          <div style={{ marginBottom: 18, padding: '14px 13px', borderRadius: 12, background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,179,202,.32)' }}>
            <strong style={{ display: 'block', color: '#ffd5e1', fontSize: 13 }}>🔐 Recupero password Admin</strong>
            <span style={{ display: 'block', marginTop: 5, color: '#d8c4cc', fontSize: 11 }}>Inserisci l’email autorizzata: riceverai un link valido per scegliere una nuova password.</span>
          </div>
          <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#ff7a9e', letterSpacing: 1.1 }}>EMAIL DI RECUPERO</label>
          <input type="email" value={emailRecupero} onChange={e => setEmailRecupero(e.target.value)} required autoFocus style={{ width: '100%', margin: '7px 0 12px', padding: '13px', boxSizing: 'border-box', backgroundColor: 'rgba(255,255,255,.08)', color: '#fff', border: '1px solid rgba(255,255,255,.19)', borderRadius: 8 }} />
          {messaggioRecupero && <div style={{ marginBottom: 12, padding: 10, borderRadius: 9, fontSize: 12, lineHeight: 1.45, color: '#d7f8df', background: 'rgba(34, 197, 94, .16)', border: '1px solid rgba(74, 222, 128, .36)' }}>{messaggioRecupero}</div>}
          <button type="button" disabled={invioRecuperoInCorso} onClick={inviaRecuperoPasswordAdmin} style={{ width: '100%', padding: '14px', background: 'linear-gradient(120deg, #9e123f, #e52b63)', color: 'white', border: '1px solid rgba(255, 159, 184, .55)', borderRadius: 8, cursor: invioRecuperoInCorso ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: 14, letterSpacing: .4, opacity: invioRecuperoInCorso ? .7 : 1 }}>{invioRecuperoInCorso ? 'INVIO IN CORSO…' : 'INVIA LINK DI RECUPERO'}</button>
          <button type="button" onClick={() => { setRecuperoAdmin(false); setMessaggioRecupero(''); }} style={{ width: '100%', marginTop: 9, padding: 10, background: 'transparent', color: '#ffd5e1', border: '1px solid rgba(255,214,225,.36)', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>← TORNA ALL’ACCESSO ADMIN</button>
        </div>}

        {!selezioneProfiliVisibile && !recuperoAdmin && <>
        <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#ff7a9e', letterSpacing: 1.1 }}>{accessoAdmin ? 'PASSWORD AMMINISTRATORE' : 'PASSWORD'}</label>
        <div style={{ position: 'relative', margin: '7px 0 22px' }}>
          {/* Nome utente nascosto: così il browser associa ogni password salvata
              al profilo giusto e non propone quella vecchia o di un altro utente. */}
          <input type="text" name="username" autoComplete="username" value={accessoAdmin ? (amministratore?.username || 'Admin') : (profiloScelto?.username || username || '')} readOnly tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
          <input
            type={mostraPassword ? 'text' : 'password'}
            placeholder="Password"
            name="password"
            autoComplete="current-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoFocus
            style={{ width: '100%', padding: '13px 78px 13px 13px', boxSizing: 'border-box', backgroundColor: 'rgba(255,255,255,.08)', color: '#fff', border: '1px solid rgba(255,255,255,.19)', borderRadius: 8 }}
          />
          <button type="button" onClick={() => setMostraPassword(!mostraPassword)} aria-label={mostraPassword ? 'Nascondi password' : 'Mostra password'} style={{ position: 'absolute', top: '50%', right: 9, transform: 'translateY(-50%)', padding: '5px 7px', border: 'none', borderRadius: 5, cursor: 'pointer', color: '#ffd6e2', background: 'rgba(255,255,255,.10)', fontSize: 11, fontWeight: 'bold' }}>
            {mostraPassword ? 'NASCONDI' : 'MOSTRA'}
          </button>
        </div>

        <button type="submit" disabled={accessoInCorso} style={{ width: '100%', padding: '14px', background: 'linear-gradient(120deg, #9e123f, #e52b63)', color: 'white', border: '1px solid rgba(255, 159, 184, .55)', borderRadius: 8, cursor: accessoInCorso ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: 14, letterSpacing: .6, boxShadow: '0 10px 24px rgba(187, 23, 72, .35)', marginBottom: 10, opacity: accessoInCorso ? .72 : 1 }}>
  {accessoInCorso ? 'VERIFICA IN CORSO…' : 'ACCEDI AL GESTIONALE →'}
</button>

<button type="button" onClick={() => { window.location.href = '/area-allievi'; }} style={{ width: '100%', padding: 11, backgroundColor: 'transparent', color: '#ffd5e1', border: '1px solid rgba(255, 214, 225, .36)', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 13, marginTop: 6 }}>
  📱 Area riservata allievi
</button>
{accessoAdmin && <button type="button" onClick={() => { setRecuperoAdmin(true); setPassword(''); setErrore(''); setMessaggioRecupero(''); }} style={{ display: 'block', margin: '13px auto 0', padding: 2, background: 'transparent', color: '#ffafc6', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: 12, textDecoration: 'underline' }}>Password dimenticata?</button>}
<div style={{ marginTop: 10, textAlign: 'center', color: '#d6b6c1', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '.2px' }}>Ideata e realizzata da <strong style={{ color: '#ffafc6' }}>DR System Code</strong></div>
<div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 9, fontSize: 10 }}><a href="/privacy.html" target="_blank" rel="noreferrer" style={{ color: '#ffd5e1' }}>Privacy</a><a href="/termini.html" target="_blank" rel="noreferrer" style={{ color: '#ffd5e1' }}>Termini</a><a href="/gestione-dati.html" target="_blank" rel="noreferrer" style={{ color: '#ffd5e1' }}>Gestione dati</a><a href="/cookie.html" target="_blank" rel="noreferrer" style={{ color: '#ffd5e1' }}>Cookie</a></div>

        {utenteInPausa && (
          <button type="button" onClick={onAnnullaPausa} style={{ width: '100%', padding: 8, marginTop: 8, backgroundColor: 'rgba(255,255,255,.10)', color: 'white', border: '1px solid rgba(255,255,255,.16)', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
            Annulla / Torna al Login Generale
          </button>
        )}
        </>}
      </form>
    </div>
  );
}

function GestioneDocentiScuola({ utenteLoggato }) {
  const [allievi, setAllievi] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [ricerca, setRicerca] = useState('');
  const [azioneInCorso, setAzioneInCorso] = useState('');
  const [richiesteDocenti, setRichiesteDocenti] = useState([]);
  const ruolo = String(utenteLoggato?.ruolo || '').trim().toLowerCase('it-IT');
  const autorizzato = ruolo === 'admin' || ruolo === 'master';

  useEffect(() => {
    const annullaAllievi = ascoltaRaccolta('allievi', snapshot => {
      setAllievi(ordinaAllieviPerCognomeNome(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))));
    });
    const annullaUtenti = autorizzato ? ascoltaRaccolta('utenti_sistema', snapshot => {
      setUtenti(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })));
    }) : () => {};
    const annullaRichieste = ascoltaRaccolta('richieste_docenti_scuola', snapshot => {
      setRichiesteDocenti(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })));
    });
    return () => { annullaAllievi(); annullaUtenti(); annullaRichieste(); };
  }, [autorizzato]);

  // Include anche i vecchi docenti già segnati con la precedente modalità
  // (ad esempio Roberto Acunzo), così la lista nuova parte già completa.
  const docenti = allievi.filter(allievo => Boolean(allievo.docenteScuola || allievo.esenteMensilita || allievo.isMaestro));
  const testo = ricerca.trim().toLocaleLowerCase('it-IT');
  const candidati = allievi.filter(allievo => {
    if (allievo.docenteScuola || allievo.esenteMensilita || allievo.isMaestro) return false;
    const nome = `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim().toLocaleLowerCase('it-IT');
    return !testo || nome.includes(testo) || String(allievo.CodiceFiscale || '').toLocaleLowerCase('it-IT').includes(testo);
  });
  const accessiEliminabili = utenti.filter(utente => ['operatore', 'docente'].includes(String(utente.ruolo || '').trim().toLocaleLowerCase('it-IT')));
  const richiestePendenti = richiesteDocenti.filter(richiesta => richiesta.stato === 'in_attesa');

  const impostaDocente = async (allievo, docente) => {
    const chiave = `docente-${allievo.id}`;
    setAzioneInCorso(chiave);
    try {
      await updateDoc(doc(db, 'allievi', allievo.id), {
        docenteScuola: docente,
        esenteMensilita: docente,
        // Rimuovendo un docente storico spegniamo anche il vecchio flag,
        // altrimenti continuerebbe a risultare esente.
        ...(docente ? {} : { isMaestro: false, docenteTemporaneo: false }),
        docenteScuolaAggiornatoIl: new Date().toISOString(),
        docenteScuolaAggiornatoDa: utenteLoggato.username
      });
      await registraLogOperazione(utenteLoggato.username, `${docente ? 'Aggiunto' : 'Rimosso'} docente della scuola: ${allievo.Cognome} ${allievo.Nome}`);
    } catch (errore) {
      console.error('Impossibile aggiornare il docente della scuola:', errore);
      alert('Non è stato possibile aggiornare la lista docenti. Riprova.');
    } finally { setAzioneInCorso(''); }
  };

  const inviaRichiestaDocente = async allievo => {
    const giaInviata = richiesteDocenti.some(richiesta => richiesta.stato === 'in_attesa' && richiesta.allievoId === allievo.id && richiesta.richiestaDaId === utenteLoggato.id);
    if (giaInviata) return alert('Hai già inviato una richiesta per questo allievo: è in attesa della decisione del Maestro o dell’Admin.');
    const chiave = `richiesta-${allievo.id}`;
    setAzioneInCorso(chiave);
    try {
      await addDoc(collection(db, 'richieste_docenti_scuola'), {
        allievoId: allievo.id,
        nomeAllievo: `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim(),
        richiestaDaId: utenteLoggato.id || '',
        richiestaDa: utenteLoggato.username || 'Operatore',
        stato: 'in_attesa',
        richiestaIl: new Date().toISOString()
      });
      await registraLogOperazione(utenteLoggato.username, `Richiesta inserimento docente e esenzione mensilità per ${allievo.Cognome} ${allievo.Nome}`);
      alert('✓ Richiesta inviata. Un Maestro o l’Admin dovrà approvarla prima di escludere l’allievo dalle mensilità.');
    } catch (errore) {
      console.error('Impossibile inviare la richiesta docente:', errore);
      alert('Non è stato possibile inviare la richiesta. Riprova.');
    } finally { setAzioneInCorso(''); }
  };

  const decidiRichiestaDocente = async (richiesta, approvata) => {
    const allievo = allievi.find(voce => voce.id === richiesta.allievoId);
    if (approvata && !allievo) return alert('La scheda allievo non è più presente: non posso approvare questa richiesta.');
    if (!window.confirm(`${approvata ? 'Approvare' : 'Rifiutare'} la richiesta per ${richiesta.nomeAllievo}?`)) return;
    const chiave = `decisione-${richiesta.id}`;
    setAzioneInCorso(chiave);
    try {
      if (approvata) {
        await updateDoc(doc(db, 'allievi', richiesta.allievoId), {
          docenteScuola: true,
          esenteMensilita: true,
          docenteScuolaAggiornatoIl: new Date().toISOString(),
          docenteScuolaAggiornatoDa: utenteLoggato.username
        });
      }
      await updateDoc(doc(db, 'richieste_docenti_scuola', richiesta.id), {
        stato: approvata ? 'approvata' : 'rifiutata',
        decisaDa: utenteLoggato.username,
        decisaIl: new Date().toISOString()
      });
      await registraLogOperazione(utenteLoggato.username, `${approvata ? 'Approvata' : 'Rifiutata'} richiesta docente per ${richiesta.nomeAllievo}`);
    } catch (errore) {
      console.error('Impossibile decidere la richiesta docente:', errore);
      alert('Non è stato possibile salvare la decisione. Riprova.');
    } finally { setAzioneInCorso(''); }
  };

  const eliminaAccesso = async utente => {
    if (!window.confirm(`Eliminare l'accesso di ${utente.username}? Verrà tolto solo dalla schermata di login: la sua scheda allievo non verrà modificata.`)) return;
    const chiave = `accesso-${utente.id}`;
    setAzioneInCorso(chiave);
    try {
      await deleteDoc(doc(db, 'utenti_sistema', utente.id));
      await registraLogOperazione(utenteLoggato.username, `Eliminato accesso ${utente.ruolo || 'operatore'}: ${utente.username}`);
    } catch (errore) {
      console.error('Impossibile eliminare l’accesso:', errore);
      alert('Non è stato possibile eliminare questo accesso. Riprova.');
    } finally { setAzioneInCorso(''); }
  };

  const stileScheda = { padding: 18, borderRadius: 14, background: '#fff', border: '1px solid #eadde2', boxShadow: '0 8px 22px rgba(71,22,43,.07)' };
  const pulsante = { border: 0, borderRadius: 8, padding: '8px 11px', color: '#fff', fontWeight: 'bold', cursor: 'pointer' };
  if (!autorizzato) {
    const mieRichieste = richiesteDocenti.filter(richiesta => richiesta.richiestaDaId === utenteLoggato.id || String(richiesta.richiestaDa || '').trim().toLocaleLowerCase('it-IT') === String(utenteLoggato.username || '').trim().toLocaleLowerCase('it-IT'));
    return <div style={{ color: '#2d1721', maxWidth: 850 }}>
      <h2 style={{ margin: 0, color: '#57132c' }}>🧑‍🏫 Richiedi inserimento docente</h2>
      <p style={{ margin: '7px 0 18px', color: '#765b66', lineHeight: 1.5 }}>Puoi segnalare un allievo per entrare nel team insegnanti. La richiesta verrà inviata ai maestri e all’Admin: solo loro potranno approvarla ed escludere la mensilità.</p>
      <section style={stileScheda}>
        <input value={ricerca} onChange={evento => setRicerca(evento.target.value)} placeholder="Cerca l'allievo per cognome, nome o codice fiscale" style={{ width: '100%', boxSizing: 'border-box', padding: 11, borderRadius: 8, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} />
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {candidati.slice(0, 12).map(allievo => <div key={allievo.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: 11, borderRadius: 9, background: '#fbf5f7' }}><div><strong>{allievo.Cognome} {allievo.Nome}</strong><small style={{ display: 'block', marginTop: 2, color: '#765b66' }}>{allievo.CodiceFiscale || 'Codice fiscale non inserito'}</small></div><button type="button" disabled={azioneInCorso === `richiesta-${allievo.id}`} onClick={() => inviaRichiestaDocente(allievo)} style={{ ...pulsante, background: '#6d287f' }}>{azioneInCorso === `richiesta-${allievo.id}` ? 'Invio…' : '📨 Invia richiesta'}</button></div>)}
          {!candidati.length && <p style={{ margin: 0, color: '#765b66' }}>Nessun allievo disponibile con questa ricerca.</p>}
        </div>
      </section>
      <section style={{ ...stileScheda, marginTop: 16 }}>
        <h3 style={{ margin: 0, color: '#6d287f' }}>Le mie richieste</h3>
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>{mieRichieste.map(richiesta => <div key={richiesta.id} style={{ padding: 11, borderRadius: 9, background: richiesta.stato === 'approvata' ? '#eefaf1' : richiesta.stato === 'rifiutata' ? '#fff5f5' : '#fff8e7' }}><strong>{richiesta.nomeAllievo}</strong><small style={{ display: 'block', marginTop: 3, color: '#765b66' }}>{richiesta.stato === 'approvata' ? '✓ Approvata: è ora nella lista docenti.' : richiesta.stato === 'rifiutata' ? '✕ Richiesta rifiutata.' : '⏳ In attesa di approvazione.'}</small></div>)}{!mieRichieste.length && <p style={{ margin: 0, color: '#765b66' }}>Non hai ancora inviato richieste.</p>}</div>
      </section>
    </div>;
  }
  return <div style={{ color: '#2d1721', maxWidth: 1000 }}>
    <h2 style={{ margin: 0, color: '#57132c' }}>🧑‍🏫 Docenti della scuola</h2>
    <p style={{ margin: '7px 0 18px', color: '#765b66', lineHeight: 1.5 }}>Qui Admin e maestri scelgono gli allievi che insegnano in Academy. Ogni docente in questa lista è automaticamente escluso dalle mensilità, ma resta nella sua anagrafica, nei corsi e nelle presenze.</p>

    <section style={{ ...stileScheda, marginBottom: 16 }}>
      <h3 style={{ margin: 0, color: '#b26a00' }}>📨 Richieste degli operatori ({richiestePendenti.length})</h3>
      <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
        {richiestePendenti.map(richiesta => <div key={richiesta.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: 11, borderRadius: 9, background: '#fff8e7', borderLeft: '4px solid #e59f00' }}><div><strong>{richiesta.nomeAllievo}</strong><small style={{ display: 'block', marginTop: 3, color: '#765b66' }}>Richiesta inviata da {richiesta.richiestaDa || 'Operatore'} · {formattaDataIT(richiesta.richiestaIl)}</small></div><div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}><button type="button" disabled={azioneInCorso === `decisione-${richiesta.id}`} onClick={() => decidiRichiestaDocente(richiesta, true)} style={{ ...pulsante, background: '#198754' }}>✓ Approva</button><button type="button" disabled={azioneInCorso === `decisione-${richiesta.id}`} onClick={() => decidiRichiestaDocente(richiesta, false)} style={{ ...pulsante, background: '#c92a2a' }}>Rifiuta</button></div></div>)}
        {!richiestePendenti.length && <p style={{ margin: 0, color: '#176b3a' }}>Non ci sono richieste in attesa.</p>}
      </div>
    </section>

    <section style={stileScheda}>
      <h3 style={{ margin: 0, color: '#6d287f' }}>➕ Aggiungi un allievo come docente</h3>
      <input value={ricerca} onChange={evento => setRicerca(evento.target.value)} placeholder="Cerca per cognome, nome o codice fiscale" style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, padding: 11, borderRadius: 8, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} />
      <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
        {candidati.slice(0, 12).map(allievo => <div key={allievo.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: 11, borderRadius: 9, background: '#fbf5f7' }}><div><strong>{allievo.Cognome} {allievo.Nome}</strong><small style={{ display: 'block', marginTop: 2, color: '#765b66' }}>{allievo.CodiceFiscale || 'Codice fiscale non inserito'}</small></div><button type="button" disabled={azioneInCorso === `docente-${allievo.id}`} onClick={() => impostaDocente(allievo, true)} style={{ ...pulsante, background: '#6d287f' }}>{azioneInCorso === `docente-${allievo.id}` ? 'Salvo…' : '🧑‍🏫 Aggiungi docente'}</button></div>)}
        {candidati.length === 0 && <p style={{ margin: 0, color: '#765b66' }}>{ricerca ? 'Nessun allievo disponibile con questa ricerca.' : 'Tutti gli allievi sono già in lista oppure non ce ne sono ancora.'}</p>}
      </div>
    </section>

    <section style={{ ...stileScheda, marginTop: 16 }}>
      <h3 style={{ margin: 0, color: '#176b3a' }}>✓ Docenti esenti dalle mensilità ({docenti.length})</h3>
      <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
        {docenti.map(allievo => <div key={allievo.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: 11, borderRadius: 9, background: '#eefaf1', borderLeft: '4px solid #198754' }}><div><strong>{allievo.Cognome} {allievo.Nome}</strong><small style={{ display: 'block', marginTop: 2, color: '#176b3a' }}>Escluso automaticamente dalle mensilità</small></div><button type="button" disabled={azioneInCorso === `docente-${allievo.id}`} onClick={() => impostaDocente(allievo, false)} style={{ ...pulsante, background: '#b26a00' }}>{azioneInCorso === `docente-${allievo.id}` ? 'Salvo…' : 'Rimuovi dalla lista'}</button></div>)}
        {!docenti.length && <p style={{ margin: 0, color: '#765b66' }}>Nessun docente aggiunto con la nuova lista.</p>}
      </div>
    </section>

    <section style={{ ...stileScheda, marginTop: 16 }}>
      <h3 style={{ margin: 0, color: '#8f1c1c' }}>🔐 Accessi operatori nella schermata login</h3>
      <p style={{ margin: '7px 0 12px', color: '#765b66', fontSize: 13, lineHeight: 1.45 }}>Se appare un nome inatteso, puoi eliminarne qui il solo accesso. I profili Admin e Master non sono eliminabili da questa schermata.</p>
      <div style={{ display: 'grid', gap: 8 }}>
        {accessiEliminabili.map(utente => <div key={utente.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: 11, borderRadius: 9, background: '#fff5f5', borderLeft: '4px solid #d43d61' }}><div><strong>{utente.username}</strong><small style={{ display: 'block', marginTop: 2, color: '#8f1c1c' }}>{String(utente.ruolo || 'operatore').toLocaleUpperCase('it-IT')}{utente.allievoDocenteId ? ' · collegato a una scheda allievo' : ''}</small></div><button type="button" disabled={azioneInCorso === `accesso-${utente.id}`} onClick={() => eliminaAccesso(utente)} style={{ ...pulsante, background: '#c92a2a' }}>{azioneInCorso === `accesso-${utente.id}` ? 'Elimino…' : '🗑️ Elimina accesso'}</button></div>)}
        {!accessiEliminabili.length && <p style={{ margin: 0, color: '#176b3a' }}>Non ci sono operatori o docenti da controllare.</p>}
      </div>
    </section>
  </div>;
}

function AdminPanel({ utenti, ricaricaUtenti, utenteCorrente, sistemaBloccato, toggleBloccoSistema }) {
  const [nuovePassword, setNuovePassword] = useState({});
  const [messaggiPasswordUtenti, setMessaggiPasswordUtenti] = useState({});
  const [salvataggiPasswordUtenti, setSalvataggiPasswordUtenti] = useState({});
  const [nuovoNome, setNuovoNome] = useState('');
  const [nuovaPass, setNuovaPass] = useState('');
  const [nuovoRuolo, setNuovoRuolo] = useState('operatore');
  const [nuovoDocenteLezioniPrivate, setNuovoDocenteLezioniPrivate] = useState(false);
  const [permessiNuovoUtente, setPermessiNuovoUtente] = useState(['dashboard', 'presenze']);
  const [utentePermessiInModifica, setUtentePermessiInModifica] = useState(null);
  const [permessiInModifica, setPermessiInModifica] = useState([]);
  const [passwordAdminAttuale, setPasswordAdminAttuale] = useState('');
  const [passwordAdminNuova, setPasswordAdminNuova] = useState('');
  const [passwordAdminConferma, setPasswordAdminConferma] = useState('');
  const [mostraPasswordAdmin, setMostraPasswordAdmin] = useState(false);
  const [salvataggioPasswordAdmin, setSalvataggioPasswordAdmin] = useState(false);
  const [messaggioPasswordAdmin, setMessaggioPasswordAdmin] = useState('');
  const [backupInCorso, setBackupInCorso] = useState('');
  const [messaggioBackup, setMessaggioBackup] = useState('');
  const selettoreBackupRef = useRef(null);

  const RACCOLTE_BACKUP = [
    'allievi',
    'lezioni_private',
    'richieste_lezioni_private',
    'pagamenti_rapidi',
    'movimenti_credito_allievi',
    'ricevute_pagamenti',
    'presenze_lezioni'
  ];

  const preparaValoreBackup = valore => {
    if (valore === null || valore === undefined) return valore ?? '';
    if (typeof valore?.toDate === 'function') return valore.toDate().toISOString();
    if (Array.isArray(valore)) return valore.map(preparaValoreBackup);
    if (typeof valore === 'object') return Object.fromEntries(Object.entries(valore).map(([chiave, contenuto]) => [chiave, preparaValoreBackup(contenuto)]));
    return valore;
  };

  const leggiRaccoltaBackup = async nome => {
    const snapshot = await getDocs(collection(db, nome));
    return snapshot.docs.map(documento => ({ id: documento.id, ...preparaValoreBackup(documento.data()) }));
  };

  const scaricaFileBackup = (nome, contenuto, tipo) => {
    const indirizzo = URL.createObjectURL(new Blob([contenuto], { type: tipo }));
    const collegamento = document.createElement('a');
    collegamento.href = indirizzo;
    collegamento.download = nome;
    document.body.appendChild(collegamento);
    collegamento.click();
    collegamento.remove();
    setTimeout(() => URL.revokeObjectURL(indirizzo), 1000);
  };

  const dataNomeBackup = () => new Date().toISOString().replace('T', '_').replaceAll(':', '-').slice(0, 19);

  const valoreCSVBackup = valore => {
    let testo = typeof valore === 'object' && valore !== null ? JSON.stringify(valore) : String(valore ?? '');
    if (/^[=+\-@]/.test(testo)) testo = `'${testo}`;
    return `"${testo.replaceAll('"', '""')}"`;
  };

  const creaCSVBackup = righe => {
    if (!righe.length) return 'id\r\n';
    const colonne = Array.from(new Set(righe.flatMap(riga => Object.keys(riga))));
    return `\uFEFF${colonne.map(valoreCSVBackup).join(';')}\r\n${righe.map(riga => colonne.map(colonna => valoreCSVBackup(riga[colonna])).join(';')).join('\r\n')}`;
  };

  const eseguiEsportazione = async (tipo, operazione) => {
    setBackupInCorso(tipo);
    setMessaggioBackup('');
    try {
      await operazione();
      setMessaggioBackup('✅ File creato e scaricato correttamente. Conservalo in un luogo protetto.');
      registraLogOperazione(utenteCorrente, `Eseguito backup dati Academy: ${tipo}`).catch(() => {});
    } catch (erroreBackup) {
      console.error('Backup Academy non riuscito:', erroreBackup);
      setMessaggioBackup('❌ Non è stato possibile creare il backup. Controlla Internet e riprova.');
    } finally {
      setBackupInCorso('');
    }
  };

  const scaricaBackupCompleto = () => eseguiEsportazione('completo', async () => {
    const risultati = await Promise.all(RACCOLTE_BACKUP.map(async nome => [nome, await leggiRaccoltaBackup(nome)]));
    const backup = {
      formato: 'Academy of Arts Backup',
      versione: 1,
      creatoIl: new Date().toISOString(),
      creatoDa: utenteCorrente?.username || 'Admin',
      dati: Object.fromEntries(risultati)
    };
    scaricaFileBackup(`Backup_Academy_${dataNomeBackup()}.json`, JSON.stringify(backup, null, 2), 'application/json;charset=utf-8');
  });

  const scaricaRaccoltaCSV = (nome, etichetta) => eseguiEsportazione(etichetta, async () => {
    const righe = await leggiRaccoltaBackup(nome);
    scaricaFileBackup(`${etichetta}_Academy_${dataNomeBackup()}.csv`, creaCSVBackup(righe), 'text/csv;charset=utf-8');
  });

  const scaricaPagamentiCSV = () => eseguiEsportazione('pagamenti', async () => {
    const raccolte = ['pagamenti_rapidi', 'movimenti_credito_allievi', 'ricevute_pagamenti'];
    const risultati = await Promise.all(raccolte.map(async nome => (await leggiRaccoltaBackup(nome)).map(riga => ({ origine: nome, ...riga }))));
    scaricaFileBackup(`Pagamenti_Academy_${dataNomeBackup()}.csv`, creaCSVBackup(risultati.flat()), 'text/csv;charset=utf-8');
  });

  const ripristinaDaBackup = async evento => {
    const file = evento.target.files?.[0];
    evento.target.value = '';
    if (!file) return;
    setMessaggioBackup('');

    try {
      if (!file.name.toLocaleLowerCase('it-IT').endsWith('.json')) throw new Error('Seleziona il file JSON del backup completo.');
      const backup = JSON.parse(await file.text());
      if (backup?.formato !== 'Academy of Arts Backup' || !backup?.dati || typeof backup.dati !== 'object') throw new Error('Questo file non è un backup Academy valido.');

      const raccoltePresenti = RACCOLTE_BACKUP.filter(nome => Array.isArray(backup.dati[nome]));
      const totaleDocumenti = raccoltePresenti.reduce((totale, nome) => totale + backup.dati[nome].length, 0);
      if (!raccoltePresenti.length || !totaleDocumenti) throw new Error('Il backup non contiene dati da ripristinare.');

      const riepilogo = raccoltePresenti.map(nome => `${nome}: ${backup.dati[nome].length}`).join('\n');
      const conferma = window.confirm(`Stai per ripristinare ${totaleDocumenti} record dal backup del ${backup.creatoIl || 'giorno non indicato'}.\n\n${riepilogo}\n\nI record con lo stesso ID saranno aggiornati. Gli altri dati presenti oggi NON saranno cancellati. Vuoi continuare?`);
      if (!conferma) return;
      if (window.prompt('Operazione delicata. Scrivi RIPRISTINA in maiuscolo per confermare:') !== 'RIPRISTINA') {
        setMessaggioBackup('Ripristino annullato: conferma non corretta.');
        return;
      }

      setBackupInCorso('ripristino');
      const operazioni = raccoltePresenti.flatMap(nome => backup.dati[nome].map(riga => ({ nome, riga })));
      for (let indice = 0; indice < operazioni.length; indice += 450) {
        const batch = writeBatch(db);
        operazioni.slice(indice, indice + 450).forEach(({ nome, riga }) => {
          if (!riga || typeof riga !== 'object' || !String(riga.id || '').trim()) return;
          const { id, ...dati } = riga;
          batch.set(doc(db, nome, String(id)), dati, { merge: true });
        });
        await batch.commit();
      }
      await registraLogOperazione(utenteCorrente, `Ripristinato backup Academy del ${backup.creatoIl || 'data non indicata'}: ${totaleDocumenti} record`);
      setMessaggioBackup(`✅ Ripristino completato: ${totaleDocumenti} record controllati e reinseriti/aggiornati.`);
    } catch (erroreRipristino) {
      console.error('Ripristino backup non riuscito:', erroreRipristino);
      setMessaggioBackup(`❌ ${erroreRipristino?.message || 'Ripristino non riuscito. Il database non è stato completato.'}`);
    } finally {
      setBackupInCorso('');
    }
  };

  const aggiornaSceltaPermesso = (chiave, selezionato, aggiorna) => aggiorna(correnti => selezionato ? Array.from(new Set([...correnti, chiave])) : correnti.filter(permesso => permesso !== chiave));
  const puoModificarePermessiUtente = utente => !['admin', 'master'].includes(String(utente?.ruolo || '').trim().toLocaleLowerCase('it-IT'));

  const aggiornaPassword = async (idDoc, username) => {
    // Il login elimina gli spazi iniziali/finali: li eliminiamo anche qui,
    // così una password salvata non può poi risultare diversa al momento dell'accesso.
    const pass = String(nuovePassword[idDoc] || '').trim();
    if (!pass) {
      setMessaggiPasswordUtenti(correnti => ({ ...correnti, [idDoc]: { tipo: 'errore', testo: 'Inserisci una password prima di salvare.' } }));
      return;
    }

    setSalvataggiPasswordUtenti(correnti => ({ ...correnti, [idDoc]: true }));
    setMessaggiPasswordUtenti(correnti => ({ ...correnti, [idDoc]: null }));
    try {
      // La password viene salvata cifrata dal server (anche sulle eventuali
      // copie con lo stesso nome) e non è più leggibile da nessuna app.
      await impostaPasswordStaff(idDoc, pass);

      setNuovePassword(correnti => ({ ...correnti, [idDoc]: '' }));
      setMessaggiPasswordUtenti(correnti => ({ ...correnti, [idDoc]: { tipo: 'successo', testo: `Password di ${username} aggiornata correttamente.` } }));
      ricaricaUtenti();

      // Il registro è utile, ma non deve mai trasformare un salvataggio riuscito
      // in un errore per chi sta lavorando.
      registraLogOperazione(utenteCorrente, `Aggiornata password per ${username}`).catch(erroreLog => {
        console.warn('Password aggiornata, ma non è stato possibile registrare il log:', erroreLog);
      });
    } catch (erroreAggiornamento) {
      console.error(`Aggiornamento password non riuscito per ${username}:`, erroreAggiornamento);
      const errorePermessi = erroreAggiornamento?.code === 'permission-denied' || erroreAggiornamento?.stato === 403;
      const erroreRete = erroreAggiornamento?.code === 'unavailable' || erroreAggiornamento?.codice === 'rete' || !navigator.onLine;
      const testo = errorePermessi
        ? 'Modifica non autorizzata. Rientra come Admin e riprova.'
        : erroreRete
          ? 'Connessione assente: la password non è stata modificata. Riprova quando Internet è disponibile.'
          : 'La password non è stata aggiornata. Riprova; se il problema resta, avvisa l’amministratore.';
      setMessaggiPasswordUtenti(correnti => ({ ...correnti, [idDoc]: { tipo: 'errore', testo } }));
    } finally {
      setSalvataggiPasswordUtenti(correnti => ({ ...correnti, [idDoc]: false }));
    }
  };

  const aggiungiUtente = async (e) => {
    e.preventDefault();
    if (!nuovoNome || !nuovaPass) return;

    const nuovoUtente = await addDoc(collection(db, 'utenti_sistema'), {
      username: nuovoNome,
      ruolo: nuovoRuolo,
      docenteLezioniPrivate: nuovoRuolo === 'docente' || nuovoDocenteLezioniPrivate,
      permessiMenu: nuovoRuolo === 'admin' || nuovoRuolo === 'master' ? [] : permessiNuovoUtente,
      fotoUrl: '/logo%20suola.jpeg',
      online: false
    });
    try {
      await impostaPasswordStaff(nuovoUtente.id, nuovaPass);
    } catch (errorePassword) {
      console.error('Password del nuovo utente non salvata:', errorePassword);
      alert(`⚠️ Utente ${nuovoNome} creato, ma la password non è stata salvata (${errorePassword.message}). Impostala di nuovo dalla lista utenti.`);
    }

    await registraLogOperazione(utenteCorrente, `Creato nuovo utente: ${nuovoNome} (${nuovoRuolo}) · accessi: ${permessiNuovoUtente.map(etichettaPermessoOperatore).join(', ')}`);
    setNuovoNome('');
    setNuovaPass('');
    setNuovoDocenteLezioniPrivate(false);
    setPermessiNuovoUtente(['dashboard', 'presenze']);
    alert(`🎉 Nuovo utente ${nuovoNome} (${nuovoRuolo}) creato con successo!`);
    ricaricaUtenti();
  };

  const eliminaUtente = async (idDoc, username, ruolo) => {
    if (String(ruolo || '').toLowerCase() === 'admin') {
      alert('Il profilo Admin non può essere eliminato: senza di esso nessuno potrebbe più gestire la Web App.');
      return;
    }
    if (String(username || '').trim().toLowerCase() === String(utenteCorrente || '').trim().toLowerCase()) {
      alert('Non puoi eliminare il profilo con cui sei collegato.');
      return;
    }
    if (window.confirm(`Eliminare l'utente ${username}?`)) {
      await deleteDoc(doc(db, 'utenti_sistema', idDoc));
      await registraLogOperazione(utenteCorrente, `Eliminato utente ${username}`);
      ricaricaUtenti();
    }
  };

  const aggiornaDocenteLezioniPrivate = async utente => {
    const abilitato = !utenteGestisceLezioniPrivate(utente);
    await updateDoc(doc(db, 'utenti_sistema', utente.id), { docenteLezioniPrivate: abilitato });
    await registraLogOperazione(utenteCorrente, `${abilitato ? 'Abilitato' : 'Disabilitato'} docente lezioni private: ${utente.username}`);
    ricaricaUtenti();
  };

  const apriModificaPermessi = utente => {
    setUtentePermessiInModifica(utente);
    setPermessiInModifica(Array.isArray(utente.permessiMenu) ? utente.permessiMenu : PERMESSI_MENU_OPERATORE.map(([chiave]) => chiave));
  };

  const salvaPermessiUtente = async () => {
    if (!utentePermessiInModifica) return;
    if (!permessiInModifica.length) return alert('Seleziona almeno una voce visibile per questo utente.');
    await updateDoc(doc(db, 'utenti_sistema', utentePermessiInModifica.id), { permessiMenu: permessiInModifica });
    await registraLogOperazione(utenteCorrente, `Aggiornati accessi per ${utentePermessiInModifica.username}: ${permessiInModifica.map(etichettaPermessoOperatore).join(', ')}`);
    setUtentePermessiInModifica(null);
    setPermessiInModifica([]);
    ricaricaUtenti();
  };

  const cambiaPasswordAmministratore = async event => {
    event.preventDefault();
    setMessaggioPasswordAdmin('');
    const nuova = passwordAdminNuova.trim();
    if (!passwordAdminAttuale || !nuova) return setMessaggioPasswordAdmin('Inserisci la password attuale e la nuova password.');
    if (nuova.length < 8) return setMessaggioPasswordAdmin('La nuova password deve contenere almeno 8 caratteri.');
    if (nuova !== passwordAdminConferma) return setMessaggioPasswordAdmin('La conferma della nuova password non corrisponde.');
    if (!auth.currentUser) return setMessaggioPasswordAdmin('Per sicurezza esci e accedi di nuovo come Admin, poi riprova.');
    setSalvataggioPasswordAdmin(true);
    try {
      const credenziale = EmailAuthProvider.credential(EMAIL_RECUPERO_ADMIN, passwordAdminAttuale);
      await reauthenticateWithCredential(auth.currentUser, credenziale);
      await updatePassword(auth.currentUser, nuova);
      const admin = utenti.find(utente => String(utente.ruolo || '').toLowerCase() === 'admin');
      if (admin?.id) await impostaPasswordStaff(admin.id, nuova).catch(errore => console.warn('Password di riserva Admin non aggiornata:', errore));
      await registraLogOperazione(utenteCorrente, 'Password amministratore modificata dal pannello Admin');
      setPasswordAdminAttuale('');
      setPasswordAdminNuova('');
      setPasswordAdminConferma('');
      setMessaggioPasswordAdmin('✅ Password Admin aggiornata. Da ora accedi con la nuova password.');
      ricaricaUtenti();
    } catch (erroreCambio) {
      console.error('Cambio password Admin non riuscito:', erroreCambio);
      if (erroreCambio.code === 'auth/invalid-credential' || erroreCambio.code === 'auth/wrong-password') setMessaggioPasswordAdmin('La password attuale non è corretta.');
      else if (erroreCambio.code === 'auth/requires-recent-login') setMessaggioPasswordAdmin('Per sicurezza esci e accedi di nuovo come Admin, poi riprova.');
      else setMessaggioPasswordAdmin('Non è stato possibile aggiornare la password. Riprova tra poco.');
    } finally { setSalvataggioPasswordAdmin(false); }
  };

  return (
    <div style={{ color: '#f8f9fa' }}>
      <h3 style={{ color: '#fcc419', fontFamily: 'serif' }}>⚙️ Amministrazione & Controllo Direzione</h3>
      <p style={{ fontSize: '14px', color: '#adb5bd' }}>Gestisci lo stato di attivazione del gestionale e le credenziali degli operatori.</p>

      <div style={{ backgroundColor: sistemaBloccato ? '#42242a' : '#223d2b', border: `2px solid ${sistemaBloccato ? '#f03e3e' : '#2b8a3e'}`, borderRadius: '10px', padding: '20px', maxWidth: '550px', marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h4 style={{ margin: '0 0 5px 0', color: sistemaBloccato ? '#ff6b6b' : '#51cf66' }}>
            {sistemaBloccato ? '⏸️ Sistema in Pausa (Bloccato)' : '▶️ Sistema Attivo (Regolare)'}
          </h4>
          <p style={{ margin: 0, fontSize: '13px', color: '#ced4da' }}>
            {sistemaBloccato ? ' Gli operatori non possono effettuare l\'accesso.' : 'Tutti gli utenti autorizzati possono accedere normalmente.'}
          </p>
        </div>
        <button 
          onClick={toggleBloccoSistema}
          style={{ backgroundColor: sistemaBloccato ? '#2b8a3e' : '#c92a2a', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
        >
          {sistemaBloccato ? '🟢 Riattiva' : '⏸️ Metti in Pausa'}
        </button>
      </div>

      <section style={{ backgroundColor: '#353b4c', border: '1px solid #74c0fc', borderRadius: '10px', padding: '20px', maxWidth: '720px', marginBottom: '25px' }}>
        <h4 style={{ margin: '0 0 6px', color: '#74c0fc' }}>💾 Backup Academy</h4>
        <p style={{ margin: '0 0 14px', color: '#ced4da', fontSize: 13, lineHeight: 1.5 }}>Salva una copia degli allievi, delle lezioni private e dei pagamenti. Il backup completo non contiene le password degli operatori.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
          <button type="button" disabled={Boolean(backupInCorso)} onClick={scaricaBackupCompleto} style={{ background: '#1971c2', color: '#fff', border: 0, padding: '10px 13px', borderRadius: 6, cursor: backupInCorso ? 'wait' : 'pointer', fontWeight: 'bold' }}>{backupInCorso === 'completo' ? 'Preparazione…' : '📦 Backup completo (.json)'}</button>
          <button type="button" disabled={Boolean(backupInCorso)} onClick={() => scaricaRaccoltaCSV('allievi', 'Allievi')} style={{ background: '#2b8a3e', color: '#fff', border: 0, padding: '10px 13px', borderRadius: 6, cursor: backupInCorso ? 'wait' : 'pointer', fontWeight: 'bold' }}>👥 Allievi (.csv)</button>
          <button type="button" disabled={Boolean(backupInCorso)} onClick={() => scaricaRaccoltaCSV('lezioni_private', 'Lezioni_private')} style={{ background: '#6741d9', color: '#fff', border: 0, padding: '10px 13px', borderRadius: 6, cursor: backupInCorso ? 'wait' : 'pointer', fontWeight: 'bold' }}>📅 Lezioni private (.csv)</button>
          <button type="button" disabled={Boolean(backupInCorso)} onClick={scaricaPagamentiCSV} style={{ background: '#b26a00', color: '#fff', border: 0, padding: '10px 13px', borderRadius: 6, cursor: backupInCorso ? 'wait' : 'pointer', fontWeight: 'bold' }}>💶 Pagamenti (.csv)</button>
          <button type="button" disabled={Boolean(backupInCorso)} onClick={() => selettoreBackupRef.current?.click()} style={{ background: '#c92a2a', color: '#fff', border: 0, padding: '10px 13px', borderRadius: 6, cursor: backupInCorso ? 'wait' : 'pointer', fontWeight: 'bold' }}>{backupInCorso === 'ripristino' ? 'Ripristino in corso…' : '♻️ Ripristina da backup'}</button>
          <input ref={selettoreBackupRef} type="file" accept="application/json,.json" onChange={ripristinaDaBackup} style={{ display: 'none' }} />
        </div>
        {messaggioBackup && <p style={{ margin: '13px 0 0', color: messaggioBackup.startsWith('✅') ? '#8ce99a' : '#ffb4bd', fontSize: 13, fontWeight: 'bold' }}>{messaggioBackup}</p>}
        <p style={{ margin: '13px 0 0', color: '#ffd8a8', fontSize: 12, lineHeight: 1.45 }}>🔒 I file contengono dati personali: custodiscili su un dispositivo protetto o in un archivio cifrato e non inviarli tramite chat.</p>
      </section>

      <form onSubmit={cambiaPasswordAmministratore} style={{ backgroundColor: '#353b4c', border: '1px solid #fcc419', borderRadius: '10px', padding: '20px', maxWidth: '550px', marginBottom: '25px' }}>
        <h4 style={{ margin: '0 0 6px', color: '#fcc419' }}>🔐 Cambia password Amministratore</h4>
        <p style={{ margin: '0 0 14px', color: '#ced4da', fontSize: 13, lineHeight: 1.4 }}>Inserisci la password attuale e scegli quella nuova. Verrà aggiornata direttamente anche nell’accesso Firebase.</p>
        {[['Password attuale', passwordAdminAttuale, setPasswordAdminAttuale], ['Nuova password', passwordAdminNuova, setPasswordAdminNuova], ['Conferma nuova password', passwordAdminConferma, setPasswordAdminConferma]].map(([etichetta, valore, aggiorna]) => <label key={etichetta} style={{ display: 'block', marginTop: 10, color: '#f8f9fa', fontSize: 12, fontWeight: 'bold' }}>{etichetta}<div style={{ position: 'relative', marginTop: 5 }}><input type={mostraPasswordAdmin ? 'text' : 'password'} value={valore} onChange={event => aggiorna(event.target.value)} required autoComplete="new-password" style={{ width: '100%', boxSizing: 'border-box', padding: '9px 68px 9px 9px', backgroundColor: '#2b3140', color: '#f8f9fa', border: '1px solid #59627a', borderRadius: '4px' }} /> <button type="button" onClick={() => setMostraPasswordAdmin(visibile => !visibile)} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 0, borderRadius: 4, padding: '4px 6px', background: '#4a5266', color: '#fff', fontWeight: 'bold', fontSize: 10, cursor: 'pointer' }}>{mostraPasswordAdmin ? 'NASCONDI' : 'MOSTRA'}</button></div></label>)}
        {messaggioPasswordAdmin && <p style={{ margin: '12px 0 0', color: messaggioPasswordAdmin.startsWith('✅') ? '#8ce99a' : '#ffb4bd', fontSize: 13, fontWeight: 'bold' }}>{messaggioPasswordAdmin}</p>}
        <button type="submit" disabled={salvataggioPasswordAdmin} style={{ marginTop: 14, backgroundColor: '#fcc419', color: '#21151a', border: 'none', padding: '10px 13px', borderRadius: '5px', cursor: salvataggioPasswordAdmin ? 'wait' : 'pointer', fontWeight: 'bold' }}>{salvataggioPasswordAdmin ? 'Aggiornamento in corso...' : 'Salva nuova password Admin'}</button>
      </form>

      <div style={{ backgroundColor: '#353b4c', border: '1px solid #4a5266', borderRadius: '10px', padding: '20px', maxWidth: '450px', marginBottom: '25px' }}>
        <h4 style={{ margin: '0 0 12px 0', color: '#4dabf7' }}>➕ Aggiungi Operatore di Sistema</h4>
        <form onSubmit={aggiungiUtente} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <input placeholder="Nome e Cognome" value={nuovoNome} onChange={e => setNuovoNome(e.target.value)} required style={{ padding: '8px', backgroundColor: '#2b3140', color: '#f8f9fa', border: '1px solid #4a5266', borderRadius: '4px' }} />
          <input type="password" placeholder="Password" value={nuovaPass} onChange={e => setNuovaPass(e.target.value)} required style={{ padding: '8px', backgroundColor: '#2b3140', color: '#f8f9fa', border: '1px solid #4a5266', borderRadius: '4px' }} />
          
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Ruolo:</label>
          <select value={nuovoRuolo} onChange={e => setNuovoRuolo(e.target.value)} style={{ padding: '8px', backgroundColor: '#2b3140', color: '#f8f9fa', border: '1px solid #4a5266', borderRadius: '4px' }}>
            <option value="operatore">Operatore</option>
            <option value="docente">Docente</option>
            <option value="admin">Amministratore</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 9, borderRadius: 6, background: 'rgba(76, 175, 80, .13)', border: '1px solid rgba(81,207,102,.4)', cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}><input type="checkbox" checked={nuovoDocenteLezioniPrivate} onChange={evento => setNuovoDocenteLezioniPrivate(evento.target.checked)} /> 📅 Abilita come docente lezioni private</label>
          {nuovoRuolo !== 'admin' && <fieldset style={{ margin: 0, padding: 11, borderRadius: 8, border: '1px solid #59627a' }}><legend style={{ padding: '0 5px', color: '#fcc419', fontWeight: 'bold', fontSize: 13 }}>Cosa può vedere questo utente</legend><p style={{ margin: '0 0 9px', color: '#ced4da', fontSize: 12, lineHeight: 1.4 }}>Per un nuovo operatore sono già selezionati Dashboard e Registro presenze.</p><div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 7 }}>{PERMESSI_MENU_OPERATORE.map(([chiave, etichetta]) => <label key={chiave} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#f8f9fa', fontSize: 12, cursor: 'pointer' }}><input type="checkbox" checked={permessiNuovoUtente.includes(chiave)} onChange={evento => aggiornaSceltaPermesso(chiave, evento.target.checked, setPermessiNuovoUtente)} />{etichetta}</label>)}</div></fieldset>}

          <button type="submit" style={{ backgroundColor: '#2b8a3e', color: 'white', border: 'none', padding: '10px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
            Crea Utente
          </button>
        </form>
      </div>

      <h4 style={{ color: '#fcc419', marginTop: '20px' }}>Elenco Utenti Registrati</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#353b4c', maxWidth: '800px', borderRadius: '8px', overflow: 'hidden' }}>
        <thead>
          <tr style={{ backgroundColor: '#2b3140', color: '#fcc419', textAlign: 'left' }}>
            <th style={{ padding: '12px' }}>Utente</th>
            <th style={{ padding: '12px' }}>Stato</th>
            <th style={{ padding: '12px' }}>Ruolo</th>
            <th style={{ padding: '12px' }}>Lezioni private</th>
            <th style={{ padding: '12px' }}>Accessi</th>
            <th style={{ padding: '12px' }}>Nuova Password</th>
            <th style={{ padding: '12px' }}>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {utenti.map(u => (
            <tr key={u.id} style={{ borderBottom: '1px solid #4a5266' }}>
              <td style={{ padding: '12px' }}><strong>{u.username}</strong>{puoModificarePermessiUtente(u) && <button type="button" onClick={() => apriModificaPermessi(u)} style={{ display: 'block', marginTop: 7, padding: '5px 7px', border: '1px solid #4dabf7', borderRadius: 5, background: 'transparent', color: '#74c0fc', cursor: 'pointer', fontWeight: 'bold', fontSize: 11 }}>⚙️ Gestisci accessi</button>}</td>
              <td style={{ padding: '12px' }}>
                {utenteStaffAttivo(u) ? (
                  <span style={{ color: '#4dabf7', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <PallinoBluOnline /> Online
                  </span>
                ) : (
                  <span style={{ color: '#adb5bd', fontSize: '13px' }}>Offline</span>
                )}
              </td>
              <td style={{ padding: '12px' }}>
                <span style={{
                  backgroundColor: u.ruolo === 'admin' ? '#c92a2a' : u.ruolo === 'master' ? '#2b8a3e' : '#1c7ed6',
                  color: 'white',
                  padding: '3px 8px',
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  {utenteEMasterPrioritario(u) ? 'M°' : utenteGestisceLezioniPrivate(u) ? 'DOCENTE' : (u.ruolo ? u.ruolo.toUpperCase() : 'OPERATORE')}
                </span>
              </td>
              <td style={{ padding: '12px' }}><label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: utenteGestisceLezioniPrivate(u) ? '#8ce99a' : '#adb5bd', fontSize: 12, fontWeight: 'bold' }}><input type="checkbox" checked={utenteGestisceLezioniPrivate(u)} onChange={() => aggiornaDocenteLezioniPrivate(u)} /> {utenteGestisceLezioniPrivate(u) ? 'Abilitato' : 'Non abilitato'}</label></td>
              <td style={{ padding: '12px', minWidth: 150 }}><small style={{ display: 'block', color: '#ced4da', lineHeight: 1.35 }}>{!puoModificarePermessiUtente(u) ? 'Accesso completo' : Array.isArray(u.permessiMenu) ? u.permessiMenu.map(etichettaPermessoOperatore).join(' · ') : 'Accesso completo (profilo esistente)'}</small>{puoModificarePermessiUtente(u) && <button type="button" onClick={() => apriModificaPermessi(u)} style={{ marginTop: 7, padding: '5px 7px', border: '1px solid #4dabf7', borderRadius: 5, background: 'transparent', color: '#74c0fc', cursor: 'pointer', fontWeight: 'bold', fontSize: 11 }}>⚙️ Modifica accessi</button>}</td>
              <td style={{ padding: '12px' }}>
                <input 
                  type="password" 
                  placeholder="Nuova Pass" 
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={nuovePassword[u.id] || ''} 
                  onChange={e => {
                    setNuovePassword(correnti => ({ ...correnti, [u.id]: e.target.value }));
                    setMessaggiPasswordUtenti(correnti => ({ ...correnti, [u.id]: null }));
                  }}
                  style={{ padding: '6px', width: '120px', backgroundColor: '#2b3140', color: '#f8f9fa', border: '1px solid #4a5266', borderRadius: '4px' }}
                />
                {messaggiPasswordUtenti[u.id] && <small style={{ display: 'block', width: 165, marginTop: 6, color: messaggiPasswordUtenti[u.id].tipo === 'successo' ? '#8ce99a' : '#ffb4bd', lineHeight: 1.3, fontWeight: 'bold' }}>{messaggiPasswordUtenti[u.id].testo}</small>}
              </td>
              <td style={{ padding: '12px' }}>
                <button disabled={salvataggiPasswordUtenti[u.id]} onClick={() => aggiornaPassword(u.id, u.username)} style={{ backgroundColor: '#fcc419', color: '#000', border: 'none', padding: '6px 10px', borderRadius: '4px', cursor: salvataggiPasswordUtenti[u.id] ? 'wait' : 'pointer', marginRight: '6px', fontWeight: 'bold', opacity: salvataggiPasswordUtenti[u.id] ? .7 : 1 }}>{salvataggiPasswordUtenti[u.id] ? 'Salvataggio…' : 'Salva'}</button>
                <button onClick={() => eliminaUtente(u.id, u.username, u.ruolo)} style={{ backgroundColor: '#c92a2a', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Elimina</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {utentePermessiInModifica && <section style={{ marginTop: 18, padding: 16, maxWidth: 760, borderRadius: 10, background: '#2b3140', border: '1px solid #4dabf7' }}><h4 style={{ margin: '0 0 7px', color: '#74c0fc' }}>⚙️ Accessi di {utentePermessiInModifica.username}</h4><p style={{ margin: '0 0 12px', color: '#ced4da', fontSize: 13 }}>Seleziona esclusivamente le schermate che questo utente può vedere e aprire.</p><div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>{PERMESSI_MENU_OPERATORE.map(([chiave, etichetta]) => <label key={chiave} style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#f8f9fa', fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={permessiInModifica.includes(chiave)} onChange={evento => aggiornaSceltaPermesso(chiave, evento.target.checked, setPermessiInModifica)} />{etichetta}</label>)}</div><div style={{ display: 'flex', gap: 9, marginTop: 15 }}><button type="button" onClick={salvaPermessiUtente} style={{ border: 0, borderRadius: 6, padding: '9px 12px', background: '#2b8a3e', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Salva accessi</button><button type="button" onClick={() => { setUtentePermessiInModifica(null); setPermessiInModifica([]); }} style={{ border: '1px solid #adb5bd', borderRadius: 6, padding: '9px 12px', background: 'transparent', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Annulla</button></div></section>}
    </div>
  );
}

function Logs({ utenteLoggato }) {
  const [logs, setLogs] = useState([]);
  const [ricerca, setRicerca] = useState('');

  useEffect(() => {
    if (utenteLoggato.ruolo === 'operatore') return;

    const caricaLogs = async () => {
      const snap = await leggiRaccolta('log_operazioni');
      const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      lista.sort((a, b) => (b.Timestamp || 0) - (a.Timestamp || 0));
      setLogs(lista);
    };
    caricaLogs();
  }, [utenteLoggato]);

  if (utenteLoggato.ruolo === 'operatore') {
    return (
      <div style={{ backgroundColor: '#42242a', color: '#ff6b6b', padding: '20px', borderRadius: '8px', border: '1px solid #f03e3e' }}>
        <h3>🔒 Accesso Riservato</h3>
        <p>Gli Operatori standard non dispongono dei permessi per visualizzare il registro delle operazioni.</p>
      </div>
    );
  }

  const testoRicerca = ricerca.trim().toLocaleLowerCase('it-IT');
  const logsFiltrati = testoRicerca
    ? logs.filter(log => `${log.Utente || ''} ${log.Azione || ''} ${log.DataOra || ''}`.toLocaleLowerCase('it-IT').includes(testoRicerca))
    : logs;

  return (
    <div style={{ color: '#f8f9fa' }}>
      <h3 style={{ color: '#fcc419', fontFamily: 'serif' }}>📜 Storico Operazioni / Audit Log</h3>
      <p style={{ fontSize: '14px', color: '#adb5bd' }}>Elenco cronologico delle azioni eseguite nel sistema.</p>
      <label style={{ display: 'block', maxWidth: 520, marginTop: 16, color: '#f8d7df', fontWeight: 'bold', fontSize: 14 }}>
        🔎 Cerca nello storico
        <input
          type="search"
          value={ricerca}
          onChange={evento => setRicerca(evento.target.value)}
          placeholder="Es. Lia, Salvatore, Luisa o testo dell’azione"
          style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 7, padding: '11px 13px', borderRadius: 8, border: '1px solid #78829b', background: '#252b3a', color: '#fff', fontSize: 15 }}
        />
      </label>
      {testoRicerca && <p style={{ margin: '9px 0 0', color: '#b8e6c9', fontSize: 13 }}>{logsFiltrati.length} {logsFiltrati.length === 1 ? 'operazione trovata' : 'operazioni trovate'}.</p>}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px', backgroundColor: '#353b4c', borderRadius: '8px', overflow: 'hidden' }}>
        <thead>
          <tr style={{ backgroundColor: '#2b3140', color: '#fcc419', textAlign: 'left' }}>
            <th style={{ padding: '12px' }}>Data e Ora</th>
            <th style={{ padding: '12px' }}>Operatore / M°</th>
            <th style={{ padding: '12px' }}>Azione Eseguita</th>
          </tr>
        </thead>
        <tbody>
          {logsFiltrati.map(l => (
            <tr key={l.id} style={{ borderBottom: '1px solid #4a5266' }}>
              <td style={{ padding: '12px', fontSize: '13px', color: '#adb5bd' }}>{l.DataOra}</td>
              <td style={{ padding: '12px' }}><strong style={{ color: '#4dabf7' }}>👤 {l.Utente}</strong></td>
              <td style={{ padding: '12px', fontSize: '14px' }}>{l.Azione}</td>
            </tr>
          ))}
          {!logsFiltrati.length && <tr><td colSpan="3" style={{ padding: '18px 12px', color: '#d6d9e2', textAlign: 'center' }}>Nessuna operazione trovata con questa ricerca.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PresenzaAllieviManuale({ utenteCorrente }) {
  const [allievi, setAllievi] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [presenzeStorico, setPresenzeStorico] = useState([]);
  const [dataSelezionata, setDataSelezionata] = useState(dataOggiISO());
  const [sessioneManuale, setSessioneManuale] = useState("");
  const [mostraDettagliAppello, setMostraDettagliAppello] = useState(false);
  const [adesso, setAdesso] = useState(() => new Date());
  const [salvataggio, setSalvataggio] = useState("");
  const [salvataggioTuttiPresenti, setSalvataggioTuttiPresenti] = useState(false);
  const [errore, setErrore] = useState("");

  useEffect(
    () =>
      ascoltaRaccolta(
        "allievi",
        (snapshot) => {
          setAllievi(
            ordinaAllieviPerCognomeNome(
              snapshot.docs.map((documento) => ({
                id: documento.id,
                ...documento.data(),
              }))
            ).filter((allievo) => !allievo.isMaestro && !allievo.isTirocinante)
          );
        },
        (erroreCaricamento) =>
          setErrore("Non è stato possibile caricare gli allievi.")
      ),
    []
  );

  useEffect(
    () =>
      onSnapshot(
        query(
          collection(db, "presenze_lezioni"),
          where("data", "==", dataSelezionata)
        ),
        (snapshot) => {
          setPresenze(
            snapshot.docs.map((documento) => ({
              id: documento.id,
              ...documento.data(),
            }))
          );
          setErrore((messaggio) =>
            messaggio.startsWith("🔺") ? messaggio : ""
          );
        },
        (erroreCaricamento) => {
          console.error(
            "Impossibile caricare le presenze manuali:",
            erroreCaricamento
          );
          setErrore(
            "Non è stato possibile caricare le presenze di questa data."
          );
        }
      ),
    [dataSelezionata]
  );

  useEffect(
    () =>
      onSnapshot(
        // Solo gli ultimi 12 mesi: bastano per il conteggio delle assenze
        // e non si rileggono ogni volta tutte le presenze dalla nascita della scuola.
        query(
          collection(db, "presenze_lezioni"),
          where("data", ">=", (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); })())
        ),
        (snapshot) => {
          setPresenzeStorico(
            snapshot.docs.map((documento) => ({
              id: documento.id,
              ...documento.data(),
            }))
          );
        },
        (erroreCaricamento) =>
          console.error(
            "Impossibile controllare lo storico delle assenze:",
            erroreCaricamento
          )
      ),
    []
  );

  useEffect(() => {
    const intervallo = window.setInterval(() => setAdesso(new Date()), 30000);
    return () => window.clearInterval(intervallo);
  }, []);

  const giorno = new Date(`${dataSelezionata}T12:00:00`).getDay();
  const sessioniMappa = new Map();
  allievi.forEach((allievo) =>
    appuntamentiPresenzeDelGiorno(allievo, giorno).forEach((appuntamento) => {
      const id = chiaveAppelloPresenze(appuntamento);
      if (!sessioniMappa.has(id))
        sessioniMappa.set(id, { id, ...appuntamento, allievi: [] });
      sessioniMappa.get(id).allievi.push(allievo);
    })
  );
  const sessioni = Array.from(sessioniMappa.values()).sort(
    (prima, dopo) =>
      prima.inizio.localeCompare(dopo.inizio) ||
      prima.corso.localeCompare(dopo.corso, "it")
  );
  const minutiAttuali = adesso.getHours() * 60 + adesso.getMinutes();
  const oggi = dataOggiISO();
  const sessioniAttive =
    dataSelezionata === oggi
      ? sessioni.filter(
          (sessione) =>
            minutiAttuali >= minutiDaOrario(sessione.inizio) &&
            minutiAttuali <= minutiDaOrario(sessione.fine)
        )
      : [];
  const idSessione = sessioni.some(
    (sessione) => sessione.id === sessioneManuale
  )
    ? sessioneManuale
    : sessioniAttive[0]?.id || sessioni[0]?.id || "";
  const sessione =
    sessioni.find((elemento) => elemento.id === idSessione) || null;
  const conteggio = sessione
    ? sessione.allievi.reduce(
        (totali, allievo) => {
          const presenza = presenze.find(
            (elemento) =>
              elemento.allievoId === allievo.id &&
              elemento.sessioneId === sessione.id
          );
          if (presenza?.stato === "presente") totali.presenti += 1;
          if (presenza?.stato === "assente") totali.assenti += 1;
          return totali;
        },
        { presenti: 0, assenti: 0 }
      )
    : { presenti: 0, assenti: 0 };
  const daSegnare = sessione
    ? sessione.allievi.length - conteggio.presenti - conteggio.assenti
    : 0;
  const arriviSegnalatiDallApp = sessione
    ? sessione.allievi.filter(allievo => presenze.some(presenza => presenza.allievoId === allievo.id && presenza.sessioneId === sessione.id && presenza.arrivoSegnalatoApp))
    : [];
  const appelloCompleto = Boolean(sessione && sessione.allievi.length && daSegnare === 0);
  const allieviConAvvisoAssenze = sessione
    ? sessione.allievi.filter(
        (allievo) =>
          contaAssenzeDopoReset(
            presenzeStorico,
            allievo.id,
            resetCicloAssenzePerDisciplina(allievo, sessione.corso),
            sessione.corso
          ) >= 3
      )
    : [];

  const segnaPresenza = async (allievo, stato) => {
    if (!sessione) return;
    const riferimento = doc(
      db,
      "presenze_lezioni",
      `${dataSelezionata}_${sessione.id}_${allievo.id}`
    );
    setSalvataggio(allievo.id);
    try {
      await setDoc(
        riferimento,
        {
          allievoId: allievo.id,
          nomeAllievo: `${allievo.Cognome || ""} ${allievo.Nome || ""}`.trim(),
          data: dataSelezionata,
          sessioneId: sessione.id,
          corso: sessione.corso,
          fascia: sessione.fascia,
          oraInizio: sessione.inizio,
          oraFine: sessione.fine,
          stato,
          origine: "maestro_operatore",
          segnatoDa: utenteCorrente,
          pubblicataIl: new Date().toISOString(),
          aggiornataIl: new Date().toISOString(),
        },
        { merge: true }
      );
      await registraLogOperazione(
        utenteCorrente,
        `${stato === "presente" ? "Segnata presenza" : "Segnata assenza"} di ${
          allievo.Cognome
        } ${allievo.Nome} · ${sessione.corso} ${formattaFasciaDisciplina(
          sessione.fascia
        )} (${dataSelezionata} ${sessione.inizio})`
      );
      const storicoAggiornato = [
        ...presenzeStorico.filter((presenza) => presenza.id !== riferimento.id),
        {
          id: riferimento.id,
          allievoId: allievo.id,
          data: dataSelezionata,
          corso: sessione.corso,
          oraInizio: sessione.inizio,
          stato,
          aggiornataIl: new Date().toISOString(),
        },
      ];
      const assenzeNelCiclo = contaAssenzeDopoReset(
        storicoAggiornato,
        allievo.id,
        resetCicloAssenzePerDisciplina(allievo, sessione.corso),
        sessione.corso
      );
      if (assenzeNelCiclo >= 3) {
        setErrore(
          `🔺 Attenzione: ${allievo.Cognome} ${allievo.Nome} ha ${assenzeNelCiclo} assenze consecutive in ${sessione.corso} negli ultimi 15 giorni. Usa “Reset ciclo” quando vuoi far ripartire il conteggio di questa disciplina.`
        );
      } else {
        setErrore("");
      }
    } catch (erroreSalvataggio) {
      console.error("Impossibile salvare la presenza:", erroreSalvataggio);
      setErrore(erroreSalvataggio?.code === "resource-exhausted"
        ? "Limite giornaliero di Firebase raggiunto: le presenze si potranno salvare di nuovo dopo le 9:00 circa (reset quotidiano). Avvisa l’Admin."
        : `Non è stato possibile salvare. Riprova. (codice: ${erroreSalvataggio?.code || erroreSalvataggio?.message || "sconosciuto"})`);
    } finally {
      setSalvataggio("");
    }
  };

  const segnaTuttiPresenti = async () => {
    if (!sessione || daSegnare <= 0) return;
    const daCompletare = sessione.allievi.filter(allievo => !presenze.some(presenza => presenza.allievoId === allievo.id && presenza.sessioneId === sessione.id && (presenza.stato === 'presente' || presenza.stato === 'assente')));
    if (!daCompletare.length) return;
    if (!window.confirm(`Segnare presenti tutti i ${daCompletare.length} allievi ancora da registrare? Le eventuali assenze già segnate non verranno modificate.`)) return;
    setSalvataggioTuttiPresenti(true);
    try {
      const batch = writeBatch(db);
      const dataOra = new Date().toISOString();
      daCompletare.forEach(allievo => {
        const riferimento = doc(db, "presenze_lezioni", `${dataSelezionata}_${sessione.id}_${allievo.id}`);
        batch.set(riferimento, {
          allievoId: allievo.id,
          nomeAllievo: `${allievo.Cognome || ""} ${allievo.Nome || ""}`.trim(),
          data: dataSelezionata,
          sessioneId: sessione.id,
          corso: sessione.corso,
          fascia: sessione.fascia,
          oraInizio: sessione.inizio,
          oraFine: sessione.fine,
          stato: "presente",
          origine: "maestro_operatore",
          segnatoDa: utenteCorrente,
          pubblicataIl: dataOra,
          aggiornataIl: dataOra,
        }, { merge: true });
      });
      await batch.commit();
      await registraLogOperazione(utenteCorrente, `Segnati tutti presenti (${daCompletare.length}) · ${sessione.corso} ${formattaFasciaDisciplina(sessione.fascia)} (${dataSelezionata} ${sessione.inizio})`);
      setErrore("");
      setMostraDettagliAppello(false);
    } catch (erroreSalvataggio) {
      console.error("Impossibile segnare tutti presenti:", erroreSalvataggio);
      setErrore(`Non è stato possibile segnare tutti presenti. Riprova. (codice: ${erroreSalvataggio?.code || erroreSalvataggio?.message || "sconosciuto"})`);
    } finally {
      setSalvataggioTuttiPresenti(false);
    }
  };

  const resettaCicloAssenze = async (allievo) => {
    if (
      !window.confirm(
        `Far ripartire il conteggio delle assenze di ${sessione?.corso || 'questa disciplina'} per ${allievo.Cognome} ${allievo.Nome}? Il Report annuale rimane invariato.`
      )
    )
      return;
    try {
      await updateDoc(doc(db, "allievi", allievo.id), {
        resetAssenzePerDisciplina: {
          ...(allievo.resetAssenzePerDisciplina || {}),
          [chiaveCicloAssenzeDisciplina(sessione?.corso)]: new Date().toISOString(),
        },
      });
      await registraLogOperazione(
        utenteCorrente,
        `Resettato ciclo assenze di ${sessione?.corso || 'disciplina'} per ${allievo.Cognome} ${allievo.Nome}; report annuale invariato`
      );
      setErrore(
        `✓ Ciclo assenze di ${sessione?.corso || 'questa disciplina'} resettato per ${allievo.Cognome} ${allievo.Nome}. Il Report annuale non è stato modificato.`
      );
    } catch (erroreReset) {
      console.error("Impossibile resettare il ciclo assenze:", erroreReset);
      setErrore(
        "Non è stato possibile resettare il ciclo delle assenze. Riprova."
      );
    }
  };

  return (
    <div style={{ color: "#32101e" }}>
      <section
        style={{
          padding: 22,
          borderRadius: 16,
          background: "linear-gradient(145deg, #fff, #fff4f8)",
          border: "1px solid #e6b2c9",
          boxShadow: "0 10px 25px rgba(88,22,51,.10)",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "#a71243",
            fontSize: 12,
            fontWeight: "bold",
            letterSpacing: 1.2,
          }}
        >
          APPELLO IN CLASSE
        </p>
        <h2 style={{ margin: "6px 0 8px", color: "#57132c", fontSize: 26 }}>
          👥 Presenza allievi
        </h2>
        <p style={{ margin: 0, color: "#694555", lineHeight: 1.5 }}>
          All’apertura viene scelto il corso dell’orario in corso. Puoi cambiare
          manualmente data e corso in qualsiasi momento.
        </p>
        <div
          style={{
            display: "flex",
            gap: 13,
            flexWrap: "wrap",
            marginTop: 18,
            alignItems: "end",
          }}
        >
          <label
            style={{
              display: "grid",
              gap: 6,
              color: "#57132c",
              fontWeight: "bold",
            }}
          >
            Data
            <input
              type="date"
              value={dataSelezionata}
              onChange={(evento) => {
                setDataSelezionata(evento.target.value);
                setSessioneManuale("");
                setMostraDettagliAppello(false);
              }}
              style={{
                padding: "10px 11px",
                borderRadius: 9,
                border: "1px solid #d59ab5",
                color: "#32101e",
                background: "#fff",
                font: "inherit",
              }}
            />
          </label>
          <label
            style={{
              display: "grid",
              gap: 6,
              minWidth: 285,
              flex: 1,
              color: "#57132c",
              fontWeight: "bold",
            }}
          >
            Corso e orario
            <select
              value={idSessione}
              onChange={(evento) => {
                setSessioneManuale(evento.target.value);
                setMostraDettagliAppello(false);
              }}
              style={{
                padding: "10px 11px",
                borderRadius: 9,
                border: "1px solid #d59ab5",
                color: "#32101e",
                background: "#fff",
                font: "inherit",
              }}
            >
              <option value="">Nessun corso disponibile</option>
              {sessioni.map((elemento) => (
                <option key={elemento.id} value={elemento.id}>
                  {elemento.inizio} – {elemento.fine} · {elemento.corso} ·{" "}
                  {formattaFasciaDisciplina(elemento.fascia)} (
                  {elemento.allievi.length})
                </option>
              ))}
            </select>
          </label>
        </div>
        {sessioniAttive.length > 0 && (
          <p
            style={{
              margin: "14px 0 0",
              padding: "9px 11px",
              borderRadius: 9,
              background: "#fff6d8",
              color: "#795900",
              fontWeight: "bold",
            }}
          >
            ⏱️ Appello in corso: il sistema ha selezionato automaticamente
            l’orario attuale.
          </p>
        )}
      </section>
      {errore && (
        <p style={{ color: "#b42318", fontWeight: "bold" }}>{errore}</p>
      )}
      {sessione ? (
        <section
          style={{
            marginTop: 18,
            borderRadius: 16,
            overflow: "hidden",
            border: "1px solid #ead1dc",
            background: "#fff",
            boxShadow: "0 8px 20px rgba(71,22,43,.07)",
          }}
        >
          <header
            style={{
              padding: "17px 19px",
              background: "linear-gradient(110deg, #57132c, #9e2753)",
              color: "#fff",
            }}
          >
            <small
              style={{ color: "#ffd97a", fontWeight: "bold", letterSpacing: 1 }}
            >
              APPELLO DEL CORSO
            </small>
            <h3 style={{ margin: "5px 0 0", fontSize: 21 }}>
              {sessione.corso} · {formattaFasciaDisciplina(sessione.fascia)}
            </h3>
            <span style={{ display: "block", marginTop: 5, color: "#ffe8f0" }}>
              🕒 {sessione.inizio} – {sessione.fine} ·{" "}
              {formattaDataIT(dataSelezionata)}
            </span>
            <div
              style={{
                display: "flex",
                gap: 9,
                flexWrap: "wrap",
                marginTop: 13,
              }}
            >
              <span
                style={{
                  padding: "5px 9px",
                  borderRadius: 13,
                  background: "rgba(81,207,102,.22)",
                  color: "#d3f9d8",
                  fontWeight: "bold",
                }}
              >
                ✓ Presenti: {conteggio.presenti}
              </span>
              <span
                style={{
                  padding: "5px 9px",
                  borderRadius: 13,
                  background: "rgba(255,135,135,.20)",
                  color: "#ffe3e3",
                  fontWeight: "bold",
                }}
              >
                ✕ Assenti: {conteggio.assenti}
              </span>
              <span
                style={{
                  padding: "5px 9px",
                  borderRadius: 13,
                  background: "rgba(255,255,255,.13)",
                  color: "#fff",
                  fontWeight: "bold",
                }}
              >
                Da segnare: {daSegnare}
              </span>
              {arriviSegnalatiDallApp.length > 0 && (
                <span
                  title="Allievi che hanno premuto “Sono arrivato” nell’app. La presenza deve essere comunque confermata dal maestro."
                  style={{
                    padding: "5px 9px",
                    borderRadius: 13,
                    background: "rgba(102,217,255,.20)",
                    color: "#d7f5ff",
                    fontWeight: "bold",
                  }}
                >
                  📱 Arrivi dall’app: {arriviSegnalatiDallApp.length}
                </span>
              )}
              {allieviConAvvisoAssenze.length > 0 && (
                <span
                  style={{
                    padding: "5px 9px",
                    borderRadius: 13,
                    background: "#c92a2a",
                    color: "#fff",
                    fontWeight: "bold",
                  }}
                >
                  🔺 Attenzione: {allieviConAvvisoAssenze.length} con 3+
                  assenze consecutive (ultimi 15 giorni)
                </span>
              )}
              {daSegnare > 0 && (
                <button
                  type="button"
                  disabled={salvataggioTuttiPresenti}
                  onClick={segnaTuttiPresenti}
                  title="Segna presenti solo gli allievi ancora da registrare: le assenze già inserite restano invariate"
                  style={{
                    marginLeft: "auto",
                    padding: "9px 13px",
                    border: "1px solid #96e0ab",
                    borderRadius: 9,
                    background: salvataggioTuttiPresenti ? "#8abf9b" : "#1f8a4c",
                    color: "#fff",
                    fontWeight: "bold",
                    cursor: salvataggioTuttiPresenti ? "wait" : "pointer",
                  }}
                >
                  {salvataggioTuttiPresenti ? "Salvataggio in corso…" : `✓ Tutti presenti (${daSegnare})`}
                </button>
              )}
            </div>
          </header>
          {appelloCompleto && !mostraDettagliAppello ? (
            <section
              style={{
                margin: 18,
                padding: 22,
                borderRadius: 14,
                background: "#ecfdf3",
                border: "2px solid #35a65a",
                color: "#176b3a",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 34, lineHeight: 1 }}>✓</div>
              <h3 style={{ margin: "8px 0 4px", fontSize: 22 }}>
                Presenze complete
              </h3>
              <p style={{ margin: 0, color: "#2d7a45" }}>
                Appello registrato: {conteggio.presenti} presenti e{" "}
                {conteggio.assenti} assenti.
              </p>
              <button
                type="button"
                onClick={() => setMostraDettagliAppello(true)}
                style={{
                  marginTop: 14,
                  padding: "9px 13px",
                  border: "1px solid #238b45",
                  borderRadius: 8,
                  background: "#fff",
                  color: "#176b3a",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                ✎ Modifica presenze
              </button>
            </section>
          ) : (
          <div style={{ display: "grid", gap: 1, background: "#f2e3e9" }}>
            {sessione.allievi.map((allievo) => {
              const presenza = presenze.find(
                (elemento) =>
                  elemento.allievoId === allievo.id &&
                  elemento.sessioneId === sessione.id
              );
              const presente = presenza?.stato === "presente";
              const assente = presenza?.stato === "assente";
              const arrivoSegnalatoDallApp = Boolean(presenza?.arrivoSegnalatoApp);
              const oraArrivoSegnalato = arrivoSegnalatoDallApp && presenza?.arrivoSegnalatoAppIl
                ? new Date(presenza.arrivoSegnalatoAppIl).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
                : '';
              const assenzeNelCiclo = contaAssenzeDopoReset(
                presenzeStorico,
                allievo.id,
                resetCicloAssenzePerDisciplina(allievo, sessione.corso),
                sessione.corso
              );
              const avvisoAssenze = assenzeNelCiclo >= 3;
              return (
                <article
                  key={allievo.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 13,
                    flexWrap: "wrap",
                    padding: "13px 16px",
                    background: avvisoAssenze
                      ? "#fff0f0"
                      : presente
                      ? "#f2fbf4"
                      : assente
                      ? "#fff4f4"
                      : "#fff",
                    borderLeft: avvisoAssenze
                      ? "5px solid #c92a2a"
                      : "5px solid transparent",
                  }}
                >
                  <div>
                    <strong style={{ color: "#32101e", fontSize: 16 }}>
                      {allievo.Cognome} {allievo.Nome}
                    </strong>
                    {arrivoSegnalatoDallApp && (
                      <span
                        title="L’allievo ha segnalato dall’app di essere arrivato. Conferma comunque la presenza con il pulsante verde."
                        style={{
                          display: "inline-block",
                          marginLeft: 8,
                          padding: "3px 7px",
                          borderRadius: 10,
                          background: "#d7f5ff",
                          color: "#07556d",
                          fontSize: 12,
                          fontWeight: "bold",
                        }}
                      >
                        📱 Arrivato dall’app{oraArrivoSegnalato ? ` · ${oraArrivoSegnalato}` : ''}
                      </span>
                    )}
                    {avvisoAssenze && (
                      <span
                        style={{
                          display: "inline-block",
                          marginLeft: 8,
                          padding: "3px 7px",
                          borderRadius: 10,
                          background: "#c92a2a",
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: "bold",
                        }}
                      >
                        🔺 {assenzeNelCiclo} assenze consecutive
                      </span>
                    )}
                    <span
                      style={{
                        display: "block",
                        marginTop: 3,
                        color: presente
                          ? "#176b3a"
                          : assente
                          ? "#b42318"
                          : "#765b66",
                        fontSize: 13,
                        fontWeight: "bold",
                      }}
                    >
                      {presente
                        ? "✓ Presente"
                        : assente
                        ? "✕ Assente"
                        : "Da segnare"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {avvisoAssenze && (
                      <button
                        type="button"
                        disabled={salvataggio === allievo.id}
                        onClick={() => resettaCicloAssenze(allievo)}
                        style={{
                          padding: "8px 11px",
                          border: "1px solid #b42318",
                          borderRadius: 8,
                          background: "#fff",
                          color: "#b42318",
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                      >
                        ↻ Reset ciclo
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={salvataggio === allievo.id}
                      onClick={() => segnaPresenza(allievo, "presente")}
                      style={{
                        padding: "8px 11px",
                        border: 0,
                        borderRadius: 8,
                        background: presente ? "#176b3a" : "#3c9b5d",
                        color: "#fff",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                    >
                      ✓ Presente
                    </button>
                    <button
                      type="button"
                      disabled={salvataggio === allievo.id}
                      onClick={() => segnaPresenza(allievo, "assente")}
                      style={{
                        padding: "8px 11px",
                        border: 0,
                        borderRadius: 8,
                        background: assente ? "#b42318" : "#d95c5c",
                        color: "#fff",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                    >
                      ✕ Assente
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
          )}
        </section>
      ) : (
        <section
          style={{
            marginTop: 18,
            padding: 20,
            borderRadius: 14,
            background: "#fff8db",
            color: "#795900",
            border: "1px solid #f0cf66",
          }}
        >
          <strong>Nessun allievo programmato in questa data.</strong>
          <p style={{ marginBottom: 0 }}>
            Scegli un’altra data o controlla che gli allievi abbiano corso e
            fascia assegnati in Anagrafica.
          </p>
        </section>
      )}
    </div>
  );
}

function RegistroPresenze() {
  const [dataSelezionata, setDataSelezionata] = useState(() => dataOggiISO());
  const [presenze, setPresenze] = useState([]);
  const [errore, setErrore] = useState('');

  useEffect(() => onSnapshot(
    query(collection(db, 'presenze_lezioni'), where('data', '==', dataSelezionata)),
    snapshot => {
      setPresenze(snapshot.docs
        .map(documento => ({ id: documento.id, ...documento.data() }))
        .sort((prima, dopo) => String(prima.nomeAllievo || '').localeCompare(String(dopo.nomeAllievo || ''), 'it')));
      setErrore('');
    },
    erroreCaricamento => {
      console.error('Impossibile caricare il registro presenze:', erroreCaricamento);
      setErrore('Non è stato possibile caricare il registro presenze.');
    }
  ), [dataSelezionata]);

  const presenti = presenze.filter(presenza => presenza.stato === 'presente').length;
  const assenti = presenze.filter(presenza => presenza.stato === 'assente').length;
  const gruppiPresenze = Object.values(presenze.reduce((gruppi, presenza) => {
    const corsi = presenza.corso ? [presenza.corso] : Array.isArray(presenza.corsi) && presenza.corsi.length ? presenza.corsi : ['Gruppo non specificato'];
    corsi.forEach(corso => {
      const fascia = String(presenza.fascia || '').trim();
      const ora = String(presenza.oraInizio || '').trim();
      const chiave = `${corso}__${fascia}__${ora}`;
      if (!gruppi[chiave]) gruppi[chiave] = { chiave, corso, fascia, ora, presenze: [] };
      gruppi[chiave].presenze.push(presenza);
    });
    return gruppi;
  }, {})).map(gruppo => ({
    ...gruppo,
    presenze: [...gruppo.presenze].sort((prima, dopo) => String(prima.nomeAllievo || '').localeCompare(String(dopo.nomeAllievo || ''), 'it')),
    presenti: gruppo.presenze.filter(presenza => presenza.stato === 'presente').length,
    assenti: gruppo.presenze.filter(presenza => presenza.stato === 'assente').length,
    programmate: gruppo.presenze.filter(presenza => presenza.stato === 'assente_programmata').length
  })).sort((prima, dopo) => String(prima.corso).localeCompare(String(dopo.corso), 'it') || String(prima.fascia).localeCompare(String(dopo.fascia), 'it') || String(prima.ora).localeCompare(String(dopo.ora)));

  return <div style={{ color: '#32101e' }}>
    <section style={{ padding: 22, borderRadius: 16, background: 'linear-gradient(145deg, #fff, #fff4f8)', border: '1px solid #e6b2c9', boxShadow: '0 10px 25px rgba(88, 22, 51, .10)' }}>
      <p style={{ margin: 0, color: '#a71243', fontSize: 12, fontWeight: 'bold', letterSpacing: 1.2 }}>ANAGRAFICA ALLIEVI</p>
      <h2 style={{ margin: '6px 0 8px', color: '#57132c', fontSize: 26 }}>📋 Registro presenze giornaliere</h2>
      <p style={{ margin: 0, color: '#694555', lineHeight: 1.5 }}>Qui trovi le presenze e le assenze pubblicate dagli allievi dalla loro App.</p>
      <label style={{ display: 'block', maxWidth: 300, marginTop: 18, color: '#57132c', fontWeight: 'bold' }}>Data da visualizzare<input type="date" value={dataSelezionata} onChange={evento => setDataSelezionata(evento.target.value)} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 7, padding: '11px 12px', border: '1px solid #d59ab5', borderRadius: 9, color: '#32101e', background: '#fff', font: 'inherit' }} /></label>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}><div style={{ padding: '9px 13px', borderRadius: 10, background: '#e9f9ed', color: '#176b3a', fontWeight: 'bold' }}>✓ Presenti: {presenti}</div><div style={{ padding: '9px 13px', borderRadius: 10, background: '#fff0f0', color: '#b42318', fontWeight: 'bold' }}>✕ Assenti: {assenti}</div><div style={{ padding: '9px 13px', borderRadius: 10, background: '#f5eafb', color: '#6d287f', fontWeight: 'bold' }}>Totale comunicazioni: {presenze.length}</div></div>
    </section>
    {errore && <p style={{ color: '#b42318', fontWeight: 'bold' }}>{errore}</p>}
    <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 14, marginTop: 18 }}>
      {gruppiPresenze.map(gruppo => <details key={gruppo.chiave} style={{ borderRadius: 14, border: '1px solid #ead1dc', background: '#fff', overflow: 'hidden', boxShadow: '0 4px 12px rgba(88,22,51,.06)' }}>
        <summary style={{ display: 'block', padding: 15, cursor: 'pointer', listStyle: 'none' }}>
          <strong style={{ display: 'block', color: '#57132c', fontSize: 17 }}>🎓 {gruppo.corso}</strong>
          {(gruppo.fascia || gruppo.ora) && <small style={{ display: 'block', marginTop: 4, color: '#694555' }}>{[gruppo.fascia, gruppo.ora && `ore ${gruppo.ora}`].filter(Boolean).join(' · ')}</small>}
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 11 }}><span style={{ padding: '5px 8px', borderRadius: 13, background: '#e9f9ed', color: '#176b3a', fontWeight: 'bold' }}>✓ {gruppo.presenti} presenti</span><span style={{ padding: '5px 8px', borderRadius: 13, background: '#fff0f0', color: '#b42318', fontWeight: 'bold' }}>✕ {gruppo.assenti} assenti</span>{gruppo.programmate > 0 && <span style={{ padding: '5px 8px', borderRadius: 13, background: '#fff8db', color: '#9c6500', fontWeight: 'bold' }}>📅 {gruppo.programmate} programmate</span>}</div>
          <small style={{ display: 'block', marginTop: 10, color: '#8b6273', fontWeight: 'bold' }}>Tocca per vedere gli allievi del gruppo</small>
        </summary>
        <div style={{ borderTop: '1px solid #f0dfe7', overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 360, borderCollapse: 'collapse' }}><thead><tr style={{ background: '#fff6f9', color: '#57132c', textAlign: 'left' }}><th style={{ padding: 10 }}>Allievo</th><th style={{ padding: 10 }}>Stato</th><th style={{ padding: 10 }}>Ora</th></tr></thead><tbody>{gruppo.presenze.map(presenza => { const programmata = presenza.stato === 'assente_programmata'; const presente = presenza.stato === 'presente'; return <tr key={`${gruppo.chiave}-${presenza.id}`} style={{ borderTop: '1px solid #f4e8ed' }}><td style={{ padding: 10, fontWeight: 'bold' }}>{presenza.nomeAllievo || 'Allievo'}</td><td style={{ padding: 10 }}><span style={{ display: 'inline-block', padding: '4px 7px', borderRadius: 12, background: presente ? '#e9f9ed' : programmata ? '#fff8db' : '#fff0f0', color: presente ? '#176b3a' : programmata ? '#9c6500' : '#b42318', fontWeight: 'bold', fontSize: 12 }}>{presente ? '✓ Presente' : programmata ? '📅 Programmata' : '✕ Assente'}</span></td><td style={{ padding: 10, color: '#694555' }}>{presenza.aggiornataIl ? new Date(presenza.aggiornataIl).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '—'}</td></tr>; })}</tbody></table></div>
      </details>)}
      {!gruppiPresenze.length && <div style={{ gridColumn: '1 / -1', padding: 25, textAlign: 'center', borderRadius: 14, border: '1px solid #ead1dc', background: '#fff', color: '#765b66' }}>Nessun allievo ha ancora comunicato la presenza per questa data.</div>}
    </section>
  </div>;
}

function ReportFrequenzaPresenze() {
  const annoIniziale = String(new Date().getFullYear());
  const [anno, setAnno] = useState(annoIniziale);
  const [ricercaAllievo, setRicercaAllievo] = useState('');
  const [allievi, setAllievi] = useState([]);
  const [presenzeAnno, setPresenzeAnno] = useState([]);
  const [errore, setErrore] = useState('');

  useEffect(() => ascoltaRaccolta('allievi', snapshot => {
    setAllievi(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })));
  }, erroreCaricamento => {
    console.error('Impossibile caricare gli allievi per il report presenze:', erroreCaricamento);
    setErrore('Non è stato possibile caricare gli allievi per il report annuale.');
  }), []);

  useEffect(() => {
    const dataInizio = `${anno}-01-01`;
    const dataFine = `${anno}-12-31`;
    return onSnapshot(query(collection(db, 'presenze_lezioni'), where('data', '>=', dataInizio), where('data', '<=', dataFine)), snapshot => {
      setPresenzeAnno(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })));
      setErrore('');
    }, erroreCaricamento => {
      console.error('Impossibile caricare il report annuale presenze:', erroreCaricamento);
      setErrore('Non è stato possibile caricare le presenze di questo anno.');
    });
  }, [anno]);

  const riepiloghi = new Map();
  const aggiungiAllievo = (id, nome) => {
    const chiave = String(id || nome || '').trim();
    if (!chiave) return null;
    if (!riepiloghi.has(chiave)) riepiloghi.set(chiave, { id: chiave, nome: nome || 'Allievo', presenti: 0, assenti: 0, assenzeProgrammate: 0 });
    return riepiloghi.get(chiave);
  };

  allievi.filter(allievo => !allievo.isMaestro).forEach(allievo => {
    aggiungiAllievo(allievo.id, `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim());
  });
  presenzeAnno.forEach(presenza => {
    const riepilogo = aggiungiAllievo(presenza.allievoId, presenza.nomeAllievo);
    if (!riepilogo) return;
    if (presenza.stato === 'presente') riepilogo.presenti += 1;
    else if (presenza.stato === 'assente_programmata') riepilogo.assenzeProgrammate += 1;
    else if (presenza.stato === 'assente') riepilogo.assenti += 1;
  });
  const ricercaNormalizzata = ricercaAllievo.trim().toLocaleLowerCase('it-IT');
  const righe = Array.from(riepiloghi.values()).map(riepilogo => {
    const totale = riepilogo.presenti + riepilogo.assenti + riepilogo.assenzeProgrammate;
    return { ...riepilogo, totale, frequenza: totale ? Math.round((riepilogo.presenti / totale) * 100) : null };
  }).filter(riepilogo => !ricercaNormalizzata || riepilogo.nome.toLocaleLowerCase('it-IT').includes(ricercaNormalizzata)).sort((prima, dopo) => prima.nome.localeCompare(dopo.nome, 'it'));
  const totalePresenti = righe.reduce((totale, riga) => totale + riga.presenti, 0);
  const totaleAssenti = righe.reduce((totale, riga) => totale + riga.assenti + riga.assenzeProgrammate, 0);

  return <section style={{ marginTop: 18, padding: 22, borderRadius: 16, background: 'linear-gradient(145deg, #fffdf6, #fff4f8)', border: '1px solid #e6b2c9', boxShadow: '0 10px 25px rgba(88, 22, 51, .08)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: 15, flexWrap: 'wrap' }}>
      <div><p style={{ margin: 0, color: '#a71243', fontSize: 12, fontWeight: 'bold', letterSpacing: 1.2 }}>VALUTAZIONE DI FINE ANNO</p><h2 style={{ margin: '6px 0 0', color: '#57132c', fontSize: 24 }}>📊 Report annuale frequenza allievi</h2></div>
      <label style={{ color: '#57132c', fontWeight: 'bold' }}>Cerca allievo<input type="search" value={ricercaAllievo} onChange={evento => setRicercaAllievo(evento.target.value)} placeholder="Nome o cognome" style={{ display: 'block', width: 190, maxWidth: '100%', boxSizing: 'border-box', marginTop: 7, padding: '9px 10px', border: '1px solid #d59ab5', borderRadius: 9, color: '#32101e', background: '#fff', font: 'inherit' }} /></label>
      <label style={{ color: '#57132c', fontWeight: 'bold' }}>Anno<input type="number" min="2020" max="2100" value={anno} onChange={evento => setAnno(evento.target.value.replace(/\D/g, '').slice(0, 4) || annoIniziale)} style={{ display: 'block', width: 115, marginTop: 7, padding: '9px 10px', border: '1px solid #d59ab5', borderRadius: 9, color: '#32101e', background: '#fff', font: 'inherit' }} /></label>
    </div>
    <p style={{ margin: '10px 0 0', color: '#694555', lineHeight: 1.5 }}>Il calcolo usa le comunicazioni inviate dall’App Allievi: presenze, assenze e assenze programmate.</p>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 15 }}><div style={{ padding: '8px 12px', borderRadius: 10, background: '#e9f9ed', color: '#176b3a', fontWeight: 'bold' }}>✓ Presenze: {totalePresenti}</div><div style={{ padding: '8px 12px', borderRadius: 10, background: '#fff0f0', color: '#b42318', fontWeight: 'bold' }}>✕ Assenze: {totaleAssenti}</div><div style={{ padding: '8px 12px', borderRadius: 10, background: '#f5eafb', color: '#6d287f', fontWeight: 'bold' }}>Allievi: {righe.length}</div></div>
    {errore && <p style={{ color: '#b42318', fontWeight: 'bold' }}>{errore}</p>}
    <div style={{ marginTop: 16, overflowX: 'auto', borderRadius: 12, border: '1px solid #ead1dc', background: '#fff' }}><table style={{ width: '100%', minWidth: 710, borderCollapse: 'collapse' }}><thead><tr style={{ background: '#a71243', color: '#fff', textAlign: 'left' }}><th style={{ padding: 12 }}>Allievo</th><th style={{ padding: 12 }}>Presenti</th><th style={{ padding: 12 }}>Assenti</th><th style={{ padding: 12 }}>Programmate</th><th style={{ padding: 12 }}>Totale</th><th style={{ padding: 12 }}>Frequenza</th></tr></thead><tbody>{righe.map(riga => <tr key={riga.id} style={{ borderBottom: '1px solid #f0dfe7' }}><td style={{ padding: 12, fontWeight: 'bold', color: '#32101e' }}>{riga.nome}</td><td style={{ padding: 12, color: '#176b3a', fontWeight: 'bold' }}>{riga.presenti}</td><td style={{ padding: 12, color: '#b42318', fontWeight: 'bold' }}>{riga.assenti}</td><td style={{ padding: 12, color: '#9c6500', fontWeight: 'bold' }}>{riga.assenzeProgrammate}</td><td style={{ padding: 12 }}>{riga.totale}</td><td style={{ padding: 12 }}><span style={{ display: 'inline-block', minWidth: 52, textAlign: 'center', padding: '5px 8px', borderRadius: 14, background: riga.frequenza === null ? '#f1f3f5' : riga.frequenza >= 80 ? '#e9f9ed' : riga.frequenza >= 60 ? '#fff8db' : '#fff0f0', color: riga.frequenza === null ? '#6c757d' : riga.frequenza >= 80 ? '#176b3a' : riga.frequenza >= 60 ? '#9c6500' : '#b42318', fontWeight: 'bold' }}>{riga.frequenza === null ? '—' : `${riga.frequenza}%`}</span></td></tr>)}{!righe.length && <tr><td colSpan="6" style={{ padding: 22, textAlign: 'center', color: '#765b66' }}>Non ci sono ancora dati di presenza per l’anno {anno}.</td></tr>}</tbody></table></div>
  </section>;
}

function Report({ utenteLoggato }) {
  const [allievi, setAllievi] = useState([]);

  useEffect(() => {
    const caricaAllievi = async () => {
      const querySnapshot = await leggiRaccolta('allievi');
      setAllievi(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    caricaAllievi();
  }, []);

  if (utenteLoggato.ruolo === 'operatore') {
    return (
      <div style={{ backgroundColor: '#42242a', color: '#ff6b6b', padding: '20px', borderRadius: '8px', border: '1px solid #f03e3e' }}>
        <h3>🔒 Accesso Riservato</h3>
        <p>Area riservata esclusivamente agli utenti Master e Amministratori.</p>
      </div>
    );
  }

  const calcolaReportAnno = (annoTarget) => {
    const dataRiferimento = new Date(annoTarget, 0, 1);
    let rimasti = [];
    let andatiVia = [];

    allievi.forEach(a => {
      if (a.isMaestro || a.isTirocinante) return;

      const dataIscrizione = a.DataIscrizione ? new Date(a.DataIscrizione) : null;
      if (!dataIscrizione || dataIscrizione > dataRiferimento) return;

      const dataUltimoPag = a.DataUltimoPagamento ? new Date(a.DataUltimoPagamento) : dataIscrizione;
      const diffMesi = (dataRiferimento.getFullYear() - dataUltimoPag.getFullYear()) * 12 + (dataRiferimento.getMonth() - dataUltimoPag.getMonth());

      if (diffMesi >= 5) {
        andatiVia.push({ ...a, mesiInattivita: diffMesi });
      } else {
        rimasti.push(a);
      }
    });

    return { rimasti, andatiVia };
  };

  const annoAttuale = new Date().getFullYear();
  const anniDisponibili = [annoAttuale, annoAttuale - 1, annoAttuale - 2];

  return (
    <div style={{ color: '#2d1721' }}>
      <h3 style={{ color: '#6d1836', fontFamily: 'Georgia, serif' }}>📊 Report Storico Allievi (al 1° Gennaio)</h3>
      <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '20px' }}>
        Un allievo è considerato <strong>andato via</strong> se non ha rinnovato la quota per <strong>5 mesi consecutivi</strong> rispetto al 1° Gennaio (esclusi Maestri e Tirocinanti).
      </p>

      {anniDisponibili.map(anno => {
        const { rimasti, andatiVia } = calcolaReportAnno(anno);
        const totale = rimasti.length + andatiVia.length;
        const percentualeRimasti = totale > 0 ? ((rimasti.length / totale) * 100).toFixed(1) : 0;

        return (
          <div key={anno} style={{ backgroundColor: '#ffffff', border: '1px solid #eadde2', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 8px 20px rgba(71,22,43,.06)' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#a71946' }}>📅 Situazione al 1° Gennaio {anno}</h4>
            
            <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', flexWrap: 'wrap' }}>
              <div style={{ backgroundColor: '#edf8f0', padding: '14px 20px', borderRadius: '8px', borderLeft: '5px solid #2b8a3e' }}>
                <span style={{ fontSize: '12px', color: '#167a43', fontWeight: 'bold' }}>RIMASTI</span>
                <h2 style={{ margin: '5px 0 0 0', color: '#167a43' }}>{rimasti.length}</h2>
              </div>

              <div style={{ backgroundColor: '#fff0f1', padding: '14px 20px', borderRadius: '8px', borderLeft: '5px solid #e03131' }}>
                <span style={{ fontSize: '12px', color: '#c92a2a', fontWeight: 'bold' }}>ABBANDONI (≥ 5 MESI)</span>
                <h2 style={{ margin: '5px 0 0 0', color: '#c92a2a' }}>{andatiVia.length}</h2>
              </div>

              <div style={{ backgroundColor: '#eef6ff', padding: '14px 20px', borderRadius: '8px', borderLeft: '5px solid #1c7ed6' }}>
                <span style={{ fontSize: '12px', color: '#1c7ed6', fontWeight: 'bold' }}>RITENZIONE</span>
                <h2 style={{ margin: '5px 0 0 0', color: '#1c7ed6' }}>{percentualeRimasti}%</h2>
              </div>
            </div>

            {andatiVia.length > 0 && (
              <details style={{ marginTop: '10px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#ff6b6b' }}>
                  🔍 Elenco allievi andati via al 01/01/{anno} ({andatiVia.length})
                </summary>
                <ul style={{ marginTop: '10px', paddingLeft: '20px', color: '#4b5563' }}>
                  {andatiVia.map(a => (
                    <li key={a.id} style={{ marginBottom: '4px', fontSize: '14px' }}>
                      <strong style={{ color: '#111827' }}>{a.Cognome} {a.Nome}</strong> — Tel: {a.Telefono || 'N/D'} (Ultimo saldo: {formattaDataIT(a.DataUltimoPagamento) || 'Nessuno'})
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CompleanniAllievi({ utenteCorrente }) {
  const [allievi, setAllievi] = useState([]);

  useEffect(() => {
    const caricaAllievi = async () => {
      const snap = await leggiRaccolta('allievi');
      setAllievi(ordinaAllieviPerCognomeNome(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    };
    caricaAllievi();
  }, []);

  const oggi = new Date();
  const meseCorrente = oggi.getMonth() + 1;

  const festeggiatiOggi = allievi.filter(a => isCompleannoOggi(a.DataNascita, a.CodiceFiscale));
  
  const altriDelMese = allievi.filter(a => {
    const dataMese = getMeseGiornoCompleanno(a.DataNascita, a.CodiceFiscale);
    if (!dataMese) return false;
    return dataMese.mese === meseCorrente && !isCompleannoOggi(a.DataNascita, a.CodiceFiscale);
  });

  const inviaAuguriWhatsApp = (allievo) => {
    if (!allievo.Telefono) {
      alert("⚠️ Numero di telefono non disponibile per questo allievo!");
      return;
    }
    let numeroPulito = allievo.Telefono.replace(/\D/g, '');
    if ((numeroPulito.length === 9 || numeroPulito.length === 10)) {
      numeroPulito = '39' + numeroPulito;
    }
    const testoAuguri = `*Tanti Auguri di Buon Compleanno, ${allievo.Nome}!* 🎂🎈🎁
Da parte di tutta la famiglia di *Academy Of Arts*! 💃🕺

Ti auguriamo una giornata splendida, piena di gioia e... a ritmo di musica! 🎶✨
Che questo nuovo anno ti porti energia fantastica, sorrisi e grandi traguardi (sia sulla pista da ballo che fuori)! 🚀💫

Ti aspettiamo a lezione per festeggiare insieme! Un abbraccio grande! 🎉🥂`;

    registraLogOperazione(utenteCorrente, `Inviato messaggio di auguri compleanno a ${allievo.Cognome} ${allievo.Nome}`);
    const url = `https://wa.me/${numeroPulito}?text=${encodeURIComponent(testoAuguri)}`;
    window.open(url, '_blank');
    updateDoc(doc(db, 'allievi', allievo.id), { auguriCompleannoInviatiIl: dataOggiISO() })
      .then(() => setAllievi(lista => lista.map(a => a.id === allievo.id ? { ...a, auguriCompleannoInviatiIl: dataOggiISO() } : a)))
      .catch(console.error);
  };

  return (
    <div style={{ color: '#f8f9fa' }}>
      <h3 style={{ color: '#fcc419', fontFamily: 'serif' }}>🎂 Gestione Compleanni Allievi</h3>
      <p style={{ fontSize: '14px', color: '#adb5bd', marginBottom: '25px' }}>
        Controlla chi compie gli anni oggi e invia gli auguri con un semplice click!
      </p>

      <div style={{ backgroundColor: '#453a1a', border: '2px solid #fcc419', borderRadius: '12px', padding: '20px', marginBottom: '30px', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#fcc419', fontSize: '18px' }}>
          🎉 Festeggiati di Oggi ({festeggiatiOggi.length})
        </h4>

        {festeggiatiOggi.length === 0 ? (
          <p style={{ color: '#ced4da', margin: 0 }}>Nessun compleanno in programma per la giornata di oggi.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {festeggiatiOggi.map(a => {
              const eta = calcolaEtaDaCF(a.CodiceFiscale) !== null ? `${calcolaEtaDaCF(a.CodiceFiscale)} anni` : 'Età N/D';
              return (
                <div key={a.id} style={{ backgroundColor: '#2b3140', padding: '14px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', border: '1px solid #fcc419' }}>
                  <div>
                    <strong style={{ color: '#fcc419', fontSize: '16px' }}>{a.Cognome} {a.Nome}</strong> ({a.Sesso || 'M'}) — <span style={{ color: '#51cf66' }}>Compie {eta}! 🎈</span><br />
                    <small style={{ color: '#adb5bd' }}>Tel: {a.Telefono || 'N/D'} | Corsi: {Array.isArray(a.Corsi) ? a.Corsi.join(', ') : 'Nessuno'}</small>
                  </div>
                  <button 
                    onClick={() => inviaAuguriWhatsApp(a)} 
                    style={{ backgroundColor: '#1c7ed6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', boxShadow: '0 2px 8px rgba(28,126,214,0.4)' }}
                  >
                    🎂 Invia Auguri WhatsApp
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ backgroundColor: '#353b4c', border: '1px solid #4a5266', borderRadius: '12px', padding: '20px' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#4dabf7', fontSize: '18px' }}>
          📅 Altri Compleanni nel Mese Corrente ({altriDelMese.length})
        </h4>

        {altriDelMese.length === 0 ? (
          <p style={{ color: '#adb5bd', margin: 0 }}>Nessun altro compleanno registrato per questo mese.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#2b3140', borderRadius: '8px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ backgroundColor: '#4a5266', color: '#fcc419', textAlign: 'left' }}>
                <th style={{ padding: '12px' }}>Allievo (Cognome e Nome)</th>
                <th style={{ padding: '12px' }}>Giorno</th>
                <th style={{ padding: '12px' }}>Telefono</th>
                <th style={{ padding: '12px' }}>Azione Auguri</th>
              </tr>
            </thead>
            <tbody>
              {altriDelMese.map(a => {
                const infoData = getMeseGiornoCompleanno(a.DataNascita, a.CodiceFiscale);
                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid #4a5266' }}>
                    <td style={{ padding: '12px' }}><strong>{a.Cognome} {a.Nome}</strong></td>
                    <td style={{ padding: '12px', color: '#fcc419', fontWeight: 'bold' }}>{infoData ? `${infoData.giorno} del mese` : 'N/D'}</td>
                    <td style={{ padding: '12px', color: '#ced4da' }}>{a.Telefono || 'N/D'}</td>
                    <td style={{ padding: '12px' }}>
                      <button 
                        onClick={() => inviaAuguriWhatsApp(a)} 
                        style={{ backgroundColor: '#1c7ed6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                      >
                        🎂 Auguri WhatsApp
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Ricerca({ utenteCorrente, ruoloUtente = 'operatore' }) {
  const [allievi, setAllievi] = useState([]);
  const [query, setQuery] = useState('');
  const [idInModifica, setIdInModifica] = useState(null);
  
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [sesso, setSesso] = useState('M');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [telefono, setTelefono] = useState('');
  const [telefonoGenitore, setTelefonoGenitore] = useState('');
  const [passwordApp, setPasswordApp] = useState('');
  const [appAbilitata, setAppAbilitata] = useState(true);
  const [corsiMappa, setCorsiMappa] = useState({});
  const [dataNascita, setDataNascita] = useState('');
  const [dataIscrizione, setDataIscrizione] = useState('');
  const [dataUltimoPagamento, setDataUltimoPagamento] = useState('');
  const [isMaestro, setIsMaestro] = useState(false);
  const [isTirocinante, setIsTirocinante] = useState(false);
  const puoGestireRuoliSpeciali = ['master', 'admin'].includes(String(ruoloUtente || '').toLocaleLowerCase('it-IT'));

  const caricaAllievi = async () => {
    const querySnapshot = await leggiRaccolta('allievi');
    setAllievi(ordinaAllieviPerCognomeNome(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }))));
  };

  // Elenco sempre aggiornato dall'ascolto condiviso (nessuna rilettura completa).
  useEffect(() => ascoltaRaccolta('allievi', () => { caricaAllievi(); }), []);

  const allieviFiltrati = allievi.filter(a => {
    const testoCercato = query.toLowerCase().trim();
    const nomeCompleto = `${a.Cognome || ''} ${a.Nome || ''}`.toLowerCase();
    const cf = (a.CodiceFiscale || '').toLowerCase();
    return nomeCompleto.includes(testoCercato) || cf.includes(testoCercato);
  });

  const avviaModifica = (a) => {
    setIdInModifica(a.id);
    setNome(a.Nome || '');
    setCognome(a.Cognome || '');
    setSesso(a.Sesso || 'M');
    setCodiceFiscale(a.CodiceFiscale || '');
    setTelefono(a.Telefono || '');
    setTelefonoGenitore(a.TelefonoGenitore || '');
    setPasswordApp(a.passwordApp || '');
    setAppAbilitata(a.appAbilitata !== false);
    setIsMaestro(!!a.isMaestro);
    setIsTirocinante(!!a.isTirocinante);
    
    const corsiEsistenti = Array.isArray(a.Corsi) ? a.Corsi : (a.Corso ? [a.Corso] : []);
    const nuovaMappa = {};
    corsiEsistenti.forEach(c => {
      const parti = c.split(' - ');
      if (parti.length > 1) {
        nuovaMappa[parti[0]] = gruppoCorsoRiorganizzato(parti[0], parti[1]);
      } else {
        nuovaMappa[c] = DISCIPLINE_DISPONIBILI[0];
      }
    });
    setCorsiMappa(nuovaMappa);

    setDataNascita(a.DataNascita || '');
    setDataIscrizione(a.DataIscrizione || dataOggiISO());
    setDataUltimoPagamento(a.DataUltimoPagamento || '');
  };

  const gestisciToggleCorso = (corso) => {
    const nuovaMappa = { ...corsiMappa };
    if (nuovaMappa[corso]) {
      delete nuovaMappa[corso];
    } else {
      nuovaMappa[corso] = DISCIPLINE_DISPONIBILI[0];
    }
    setCorsiMappa(nuovaMappa);
  };

  const gestisciCambioDisciplina = (corso, disciplina) => {
    setCorsiMappa({ ...corsiMappa, [corso]: disciplina });
  };

  const salvaModifiche = async (e) => {
    e.preventDefault();
    if (!idInModifica) return;

    const erroreNome = verificaNomeECognome(nome, cognome);
    if (erroreNome) {
      alert(`⚠️ ${erroreNome}`);
      return;
    }

    const etaAllievo = calcolaEtaAllievo(dataNascita, codiceFiscale);
    if (etaAllievo !== null && etaAllievo < 18 && !telefonoGenitore.trim()) {
      alert('⚠️ Per un allievo minorenne è obbligatorio inserire il telefono del genitore o tutore.');
      return;
    }

    const listaCorsiFormattata = Object.entries(corsiMappa).map(
      ([corso, disc]) => `${corso} - ${disc}`
    );

    const datiAggiornati = {
      Nome: formattaNomePersona(nome),
      Cognome: formattaNomePersona(cognome),
      Sesso: sesso,
      CodiceFiscale: codiceFiscale.toUpperCase(),
      Telefono: telefono,
      TelefonoGenitore: telefonoGenitore.trim(),
      passwordApp: passwordApp.trim(),
      appAbilitata,
      Corsi: listaCorsiFormattata,
      DataNascita: dataNascita,
      DataIscrizione: dataIscrizione,
      DataUltimoPagamento: dataUltimoPagamento || null,
      isMaestro: isMaestro,
      docenteTemporaneo: isMaestro,
      isTirocinante: isTirocinante
    };

    await updateDoc(doc(db, 'allievi', idInModifica), datiAggiornati);
    await sincronizzaDocenteTemporaneo(idInModifica, datiAggiornati);
    await registraLogOperazione(utenteCorrente, `Aggiornati dati allievo da ricerca: ${cognome} ${nome}`);

    setIdInModifica(null);
    caricaAllievi();
    alert("✅ Dati allievo aggiornati con successo!");
  };

  const getCampiMancanti = (a) => {
    let mancanti = [];
    const etaAllievo = calcolaEtaAllievo(a.DataNascita, a.CodiceFiscale);
    if (!a.CodiceFiscale) mancanti.push("Codice Fiscale");
    if (!a.Telefono) mancanti.push("Telefono");
    if (etaAllievo !== null && etaAllievo < 18 && !a.TelefonoGenitore) mancanti.push("Telefono Genitore/Tutore");
    if (!a.DataNascita && calcolaEtaDaCF(a.CodiceFiscale) === null) mancanti.push("Data di Nascita");
    const corsi = Array.isArray(a.Corsi) ? a.Corsi : (a.Corso ? [a.Corso] : []);
    if (corsi.length === 0) mancanti.push("Nessun Corso");
    return mancanti;
  };

  const etaAllievoInModifica = calcolaEtaAllievo(dataNascita, codiceFiscale);
  const allievoMinorenneInModifica = etaAllievoInModifica !== null && etaAllievoInModifica < 18;

  return (
    <div style={{ color: '#f8f9fa' }}>
      <h3 style={{ color: '#fcc419', fontFamily: 'serif' }}>🔍 Ricerca & Completa Schede Allievi</h3>
      <p style={{ fontSize: '14px', color: '#adb5bd', marginBottom: '20px' }}>
        Cerca un allievo per verificare le informazioni, completare i dati o aggiornare la scheda.
      </p>

      <input 
        placeholder="Cerca per Cognome, Nome o Codice Fiscale..." 
        value={query} 
        onChange={e => setQuery(e.target.value)}
        style={{ width: '100%', maxWidth: '500px', padding: '12px', fontSize: '15px', borderRadius: '8px', border: '2px solid #fcc419', backgroundColor: '#353b4c', color: '#fff', marginBottom: '20px' }}
      />

      {query.trim() === '' ? (
        <p style={{ color: '#adb5bd' }}>Digita un nome, cognome o codice fiscale per effettuare la ricerca in tempo reale.</p>
      ) : allieviFiltrati.length === 0 ? (
        <p style={{ color: '#ff6b6b', fontWeight: 'bold' }}>Nessun allievo trovato per "{query}".</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '20px' }}>
          {allieviFiltrati.map(a => {
            const isEditing = idInModifica === a.id;
            const mancanti = getCampiMancanti(a);
            const listaCorsiAllievo = Array.isArray(a.Corsi) ? a.Corsi : (a.Corso ? [a.Corso] : []);
            const corsiListaText = listaCorsiAllievo.length > 0 ? listaCorsiAllievo.join(', ') : 'Nessuno';
            const eta = calcolaEtaDaCF(a.CodiceFiscale) !== null ? `${calcolaEtaDaCF(a.CodiceFiscale)} anni` : calcolaFasciaEta(a.DataNascita, a.CodiceFiscale);
            const statoPagamento = calcolaStatoPagamentoDinamico(a.DataUltimoPagamento);

            return (
              <div key={a.id} style={{ border: isEditing ? '2px solid #2b8a3e' : '1px solid #4a5266', borderRadius: '12px', padding: '18px', backgroundColor: isEditing ? '#223d2b' : '#353b4c', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
                {!isEditing ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#fcc419', fontSize: '18px' }}>{a.Cognome} {a.Nome}</h4>
                      <button 
                        onClick={() => avviaModifica(a)}
                        style={{ backgroundColor: '#fcc419', color: '#000', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                      >
                        ✏️ Modifica
                      </button>
                    </div>

                    {mancanti.length > 0 && (
                      <div style={{ backgroundColor: '#453a1a', color: '#fcc419', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', margin: '10px 0', border: '1px solid #fcc419' }}>
                        ⚠️ Dati Mancanti: {mancanti.join(', ')}
                      </div>
                    )}

                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#ced4da' }}><strong>Sesso:</strong> {a.Sesso || 'M'}</p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#ced4da' }}><strong>Codice Fiscale:</strong> {a.CodiceFiscale || '❌ Mancante'}</p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#ced4da' }}><strong>Età:</strong> {eta}</p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#ced4da' }}><strong>Telefono:</strong> {a.Telefono || '❌ Mancante'}</p>
                    {calcolaEtaAllievo(a.DataNascita, a.CodiceFiscale) !== null && calcolaEtaAllievo(a.DataNascita, a.CodiceFiscale) < 18 && (
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#ff8787' }}><strong>Genitore/Tutore:</strong> {a.TelefonoGenitore || '❌ Telefono mancante'}</p>
                    )}
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#ced4da' }}><strong>Corsi & Discipline:</strong> {corsiListaText}</p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#ced4da' }}><strong>Data Iscrizione:</strong> {formattaDataIT(a.DataIscrizione)}</p>
                    
                    <hr style={{ margin: '12px 0', borderTop: '1px solid #4a5266' }} />
                    <div style={{ fontSize: '13px' }}>
                      <strong>Stato Pagamento:</strong><br />
                      <BadgeStatoPagamento dataUltimoPagamento={a.DataUltimoPagamento} isMaestro={a.isMaestro} isTirocinante={a.isTirocinante} docenteScuola={a.docenteScuola} esenteMensilita={a.esenteMensilita} /><br />
                      {!allievoEsenteMensilita(a) && (
                        <span style={{ color: '#adb5bd' }}>🗓️ <strong>Ultimo Saldo:</strong> {formattaDataIT(a.DataUltimoPagamento)}</span>
                      )}
                    </div>

                    {!allievoEsenteMensilita(a) && statoPagamento.codice !== 'VERDE' && (
                      <button 
                        onClick={() => inviaMessaggioWhatsApp(a.Telefono, a.Nome, utenteCorrente)}
                        style={{ backgroundColor: '#2b8a3e', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '12px', width: '100%' }}
                      >
                        📲 Invia Sollecito WhatsApp
                      </button>
                    )}
                  </>
                ) : (
                  <form onSubmit={salvaModifiche} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <h4 style={{ margin: '0 0 8px 0', color: '#51cf66' }}>✏️ Modifica Scheda</h4>
                    
                    <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Cognome:</label>
                    <input value={cognome} onChange={e => setCognome(e.target.value)} onBlur={() => setCognome(formattaNomePersona(cognome))} required style={{ padding: '8px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '4px', fontWeight: 'bold' }} />

                    <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Nome:</label>
                    <input value={nome} onChange={e => setNome(e.target.value)} onBlur={() => setNome(formattaNomePersona(nome))} required style={{ padding: '8px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '4px', fontWeight: 'bold' }} />

                    {puoGestireRuoliSpeciali && <div style={{ display: 'flex', gap: '15px', backgroundColor: '#e9ecef', padding: '8px', borderRadius: '4px', border: '2px solid #74c0fc', color: '#212529' }}>
                      <label style={{ fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                        <input 
                          type="checkbox" 
                          checked={isMaestro} 
                          onChange={e => {
                            setIsMaestro(e.target.checked);
                            if (e.target.checked) setIsTirocinante(false);
                          }} 
                          style={{ marginRight: '6px' }} 
                        />
                        👩‍🏫 Docente della scuola · esente mensilità
                      </label>
                      <label style={{ fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                        <input 
                          type="checkbox" 
                          checked={isTirocinante} 
                          onChange={e => {
                            setIsTirocinante(e.target.checked);
                            if (e.target.checked) setIsMaestro(false);
                          }} 
                          style={{ marginRight: '6px' }} 
                        />
                        👨‍🏫 È Tirocinante
                      </label>
                    </div>}

                    <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Sesso:</label>
                    <select value={sesso} onChange={e => setSesso(e.target.value)} style={{ padding: '8px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '4px', fontWeight: 'bold' }}>
                      <option value="M">Maschio (M)</option>
                      <option value="F">Femmina (F)</option>
                    </select>

                    <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Codice Fiscale:</label>
                    <input value={codiceFiscale} onChange={e => setCodiceFiscale(e.target.value)} style={{ textTransform: 'uppercase', padding: '8px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '4px', fontWeight: 'bold' }} placeholder="CF" />

                    <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Telefono:</label>
                    <input value={telefono} onChange={e => setTelefono(e.target.value)} style={{ padding: '8px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '4px', fontWeight: 'bold' }} placeholder="Telefono" />

                    <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Data di Nascita:</label>
                    <input type="date" value={dataNascita} onChange={e => setDataNascita(e.target.value)} style={{ padding: '8px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '4px', fontWeight: 'bold' }} />

                    {allievoMinorenneInModifica && (
                      <>
                        <AvvisoMinorenne telefonoGenitorePresente={Boolean(telefonoGenitore.trim())} />
                        <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#ff8787' }}>Telefono genitore / tutore:</label>
                        <input value={telefonoGenitore} onChange={e => setTelefonoGenitore(e.target.value)} required placeholder="Numero del genitore o tutore" style={{ padding: '8px', backgroundColor: '#fff5f5', color: '#212529', border: '2px solid #e03131', borderRadius: '4px', fontWeight: 'bold' }} />
                      </>
                    )}

                    <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Data Iscrizione:</label>
                    <input type="date" value={dataIscrizione} onChange={e => setDataIscrizione(e.target.value)} style={{ padding: '8px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '4px', fontWeight: 'bold' }} />

                    <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Corsi e Disciplina/Fascia:</label>
                    <div style={{ backgroundColor: '#e9ecef', padding: '10px', borderRadius: '4px', border: '2px solid #74c0fc', color: '#212529' }}>
                      {LISTA_CORSI.map(corso => {
                        const selezionato = !!corsiMappa[corso];
                        return (
                          <div key={corso} style={{ marginBottom: '8px', borderBottom: '1px solid #ced4da', paddingBottom: '6px' }}>
                            <label style={{ fontSize: '13px', display: 'block', cursor: 'pointer', fontWeight: 'bold' }}>
                              <input 
                                type="checkbox" 
                                checked={selezionato} 
                                onChange={() => gestisciToggleCorso(corso)}
                                style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                              />
                              {corso}
                            </label>
                            {selezionato && (
                              <select 
                                value={corsiMappa[corso]} 
                                onChange={e => gestisciCambioDisciplina(corso, e.target.value)}
                                style={{ marginTop: '4px', width: '100%', padding: '4px', borderRadius: '4px', backgroundColor: '#ffffff', color: '#212529', border: '1px solid #74c0fc', fontWeight: 'bold', fontSize: '12px' }}
                              >
                                {DISCIPLINE_DISPONIBILI.map(disc => (
                                  <option key={disc} value={disc}>{disc}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button type="submit" style={{ flex: 1, backgroundColor: '#2b8a3e', color: 'white', border: 'none', padding: '8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                        💾 Salva
                      </button>
                      <button type="button" onClick={() => setIdInModifica(null)} style={{ backgroundColor: '#495057', color: 'white', border: 'none', padding: '8px', borderRadius: '4px', cursor: 'pointer' }}>
                        Annulla
                      </button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RiepilogoIscrizione({ allievo, onChiudi, utenteCorrente }) {
  const corsi = Array.isArray(allievo.Corsi) ? allievo.Corsi : (allievo.Corso ? [allievo.Corso] : []);
  const etaAllievo = calcolaEtaAllievo(allievo.DataNascita, allievo.CodiceFiscale);
  const eMinorenne = etaAllievo !== null && etaAllievo < 18;
  const destinatarioWhatsApp = eMinorenne ? 'genitore o tutore' : 'allievo';
  const numeroWhatsApp = eMinorenne ? allievo.TelefonoGenitore : allievo.Telefono;
  const statoPagamento = allievoEsenteMensilita(allievo)
    ? { stato: 'Esente dal pagamento', colore: '#1c7ed6', dettaglio: 'Nessuna quota mensile prevista' }
    : calcolaStatoPagamentoDinamico(allievo.DataUltimoPagamento);
  const prossimaQuota = prossimaScadenzaQuota(allievo);
  const certificato = allievo.DataCertificatoMedico
    ? `Consegnato il ${formattaDataIT(allievo.DataCertificatoMedico)}`
    : 'Non ancora consegnato';

  const apriWhatsApp = () => {
    if (!numeroWhatsApp) {
      alert(`⚠️ Manca il numero del ${destinatarioWhatsApp}. Inseriscilo nell'anagrafica prima di inviare il riepilogo.`);
      return;
    }
    let numeroPulito = String(numeroWhatsApp).replace(/\D/g, '');
    if (numeroPulito.startsWith('00')) numeroPulito = numeroPulito.slice(2);
    if ((numeroPulito.length === 9 || numeroPulito.length === 10)) numeroPulito = `39${numeroPulito}`;
    const testo = `*Academy of Arts - Riepilogo iscrizione*\n\nCiao, ecco il riepilogo di *${allievo.Nome || ''} ${allievo.Cognome || ''}*.\n\n*Discipline:* ${corsi.length ? corsi.join(', ') : 'Nessuna disciplina assegnata'}\n*Ultimo pagamento:* ${formattaDataIT(allievo.DataUltimoPagamento)}\n*Prossima scadenza:* ${prossimaQuota.testo}\n*Certificato medico:* ${certificato}\n\nPer qualsiasi informazione, contatta la segreteria. 💃`;
    window.open(`https://wa.me/${numeroPulito}?text=${encodeURIComponent(testo)}`, '_blank');
    registraLogOperazione(utenteCorrente, `Aperto riepilogo WhatsApp per ${allievo.Cognome} ${allievo.Nome} (${destinatarioWhatsApp})`);
  };

  const stampaOSalvaPDF = () => {
    const finestra = window.open('', '_blank', 'width=820,height=900');
    if (!finestra) {
      alert('Il browser ha bloccato la finestra di stampa. Consenti i popup e riprova.');
      return;
    }
    const proteggi = valore => String(valore || 'N/D').replace(/[&<>'"]/g, carattere => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[carattere]));
    const righeCorsi = corsi.length ? corsi.map(c => `<li>${proteggi(c)}</li>`).join('') : '<li>Nessuna disciplina assegnata</li>';
    finestra.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Riepilogo iscrizione</title><style>body{font-family:Arial,sans-serif;color:#1d2a44;margin:38px;background:#fff}.intestazione{border-bottom:4px solid #d99a18;padding-bottom:16px;display:flex;justify-content:space-between}.marca{color:#a16207;font-weight:bold;letter-spacing:1px;font-size:12px}h1{margin:7px 0 0;font-size:28px}.scheda{margin-top:22px;border:1px solid #dfe6f1;border-radius:14px;padding:18px}.titolo{font-weight:bold;color:#a16207;font-size:13px;letter-spacing:.6px;margin-bottom:12px}.riga{display:flex;justify-content:space-between;gap:16px;padding:8px 0;border-bottom:1px solid #edf0f5}.riga:last-child{border:0}.etichetta{color:#68738a}.valore{font-weight:bold;text-align:right}.evidenza{background:#fff8e7;border-color:#f3c45b}.nota{margin-top:28px;color:#68738a;font-size:12px;text-align:center}@media print{body{margin:18px}}</style></head><body><div class="intestazione"><div><div class="marca">ACADEMY OF ARTS · SEGRETERIA</div><h1>Riepilogo iscrizione</h1></div><div style="text-align:right;color:#68738a;font-size:12px">Emesso il<br><strong>${proteggi(new Date().toLocaleDateString('it-IT'))}</strong></div></div><section class="scheda"><div class="titolo">DATI ALLIEVO</div><div class="riga"><span class="etichetta">Allievo</span><span class="valore">${proteggi(`${allievo.Cognome || ''} ${allievo.Nome || ''}`)}</span></div><div class="riga"><span class="etichetta">Codice fiscale</span><span class="valore">${proteggi(allievo.CodiceFiscale)}</span></div><div class="riga"><span class="etichetta">Telefono</span><span class="valore">${proteggi(allievo.Telefono)}</span></div><div class="riga"><span class="etichetta">Data iscrizione</span><span class="valore">${proteggi(formattaDataIT(allievo.DataIscrizione))}</span></div></section><section class="scheda"><div class="titolo">CORSI E DISCIPLINE</div><ul style="margin:0;padding-left:20px;line-height:1.7">${righeCorsi}</ul></section><section class="scheda evidenza"><div class="titolo">PAGAMENTI</div><div class="riga"><span class="etichetta">Stato quota</span><span class="valore">${proteggi(statoPagamento.stato)}</span></div><div class="riga"><span class="etichetta">Ultimo pagamento</span><span class="valore">${proteggi(formattaDataIT(allievo.DataUltimoPagamento))}</span></div><div class="riga"><span class="etichetta">Prossima scadenza</span><span class="valore">${proteggi(prossimaQuota.testo)}</span></div></section><section class="scheda"><div class="titolo">DOCUMENTI</div><div class="riga"><span class="etichetta">Certificato medico sportivo</span><span class="valore">${proteggi(certificato)}</span></div></section><p class="nota">Per informazioni o aggiornamenti, contatta la segreteria Academy of Arts.</p></body></html>`);
    finestra.document.close();
    finestra.focus();
    window.setTimeout(() => finestra.print(), 350);
  };

  const sezione = (titolo, contenuto, colore = '#f7f9fc') => (
    <div style={{ background: colore, border: '1px solid #e1e7f0', borderRadius: 14, padding: 15 }}>
      <div style={{ color: '#68738a', fontWeight: 'bold', fontSize: 11, letterSpacing: 1, marginBottom: 9 }}>{titolo}</div>
      {contenuto}
      {titolo === 'DOCUMENTI' && <div style={{ marginTop: 13, paddingTop: 12, borderTop: '1px solid #e1e7f0' }}><small style={{ display: 'block', marginBottom: 8, color: '#68738a' }}>Il riepilogo verrà inviato al {destinatarioWhatsApp}. Il PDF, se necessario, si allega dopo il salvataggio.</small><button onClick={apriWhatsApp} style={{ width: '100%', padding: '10px 12px', border: 0, borderRadius: 9, background: '#25D366', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>💬 Apri WhatsApp</button></div>}
    </div>
  );
  const riga = (etichetta, valore, colore = '#1d2a44') => <div style={{ display: 'flex', justifyContent: 'space-between', gap: 15, padding: '7px 0', borderBottom: '1px solid #e7ecf3', fontSize: 14 }}><span style={{ color: '#68738a' }}>{etichetta}</span><strong style={{ color: colore, textAlign: 'right' }}>{valore}</strong></div>;

  return <div style={{ position: 'fixed', inset: 0, zIndex: 2000, padding: 18, background: 'rgba(14, 23, 41, .66)', display: 'grid', placeItems: 'center', overflowY: 'auto' }}><div style={{ width: 'min(700px, 100%)', maxHeight: 'calc(100vh - 36px)', overflowY: 'auto', background: '#fff', color: '#1d2a44', borderRadius: 22, padding: 24, boxSizing: 'border-box', boxShadow: '0 22px 54px rgba(0,0,0,.35)' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', marginBottom: 18 }}><div><div style={{ color: '#a16207', fontWeight: 'bold', fontSize: 11, letterSpacing: 1.3 }}>ACADEMY OF ARTS · SEGRETERIA</div><h2 style={{ margin: '6px 0 0', fontSize: 24 }}>Riepilogo iscrizione</h2><p style={{ margin: '4px 0 0', color: '#68738a' }}>{allievo.Cognome} {allievo.Nome}</p></div><button onClick={onChiudi} aria-label="Chiudi riepilogo" style={{ border: 0, background: '#f2f5fa', color: '#52627d', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', fontSize: 20 }}>×</button></div><div style={{ display: 'grid', gap: 11 }}>{sezione('DATI ALLIEVO', <>{riga('Codice fiscale', allievo.CodiceFiscale || 'N/D')}{riga('Telefono', allievo.Telefono || 'N/D')}{riga('Data iscrizione', formattaDataIT(allievo.DataIscrizione))}</>)}{sezione('CORSI E DISCIPLINE', corsi.length ? <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>{corsi.map(c => <li key={c}>{c}</li>)}</ul> : <span style={{ color: '#68738a' }}>Nessuna disciplina assegnata</span>)}{sezione('PAGAMENTI', <>{riga('Stato quota', statoPagamento.stato, statoPagamento.colore)}{riga('Ultimo pagamento', formattaDataIT(allievo.DataUltimoPagamento))}{riga('Prossima scadenza', prossimaQuota.testo, prossimaQuota.colore)}</>, '#fff8e7')}{sezione('DOCUMENTI', riga('Certificato medico sportivo', certificato, allievo.DataCertificatoMedico ? '#14804a' : '#c24141'))}</div><div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, flexWrap: 'wrap' }}><button onClick={onChiudi} style={{ padding: '10px 15px', border: '1px solid #d7dee9', borderRadius: 9, background: '#fff', color: '#52627d', cursor: 'pointer', fontWeight: 'bold' }}>Chiudi</button><button onClick={stampaOSalvaPDF} style={{ padding: '10px 15px', border: 0, borderRadius: 9, background: '#1c7ed6', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>🖨️ Stampa / Salva PDF</button></div></div></div>;
}

function StoricoShopAllievo({ allievo, onChiudi, utenteCorrente }) {
  const [consegne, setConsegne] = useState([]);
  const [errore, setErrore] = useState('');
  useEffect(() => onSnapshot(query(collection(db, 'prenotazioni_shop'), where('allievoId', '==', allievo.id)), snapshot => {
    setConsegne(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))
      .filter(prenotazione => prenotazione.stato === 'consegnata')
      .sort((a, b) => String(b.consegnataIl || b.aggiornataIl || b.dataCreazione || '').localeCompare(String(a.consegnataIl || a.aggiornataIl || a.dataCreazione || ''))));
  }, () => setErrore('Impossibile caricare lo storico Shop.')), [allievo.id]);

  const stampaRicevuta = prenotazione => {
    const finestra = window.open('', '_blank', 'width=820,height=900');
    if (!finestra) return alert('Il browser ha bloccato la finestra. Consenti i popup e riprova.');
    const proteggi = valore => String(valore || '—').replace(/[&<>'"]/g, carattere => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[carattere]));
    const dataConsegna = formattaDataIT(prenotazione.consegnataIl || prenotazione.aggiornataIl || prenotazione.dataCreazione);
    const dettagli = [prenotazione.taglia ? `Taglia ${prenotazione.taglia}` : '', prenotazione.colore || ''].filter(Boolean).join(' · ') || '—';
    const numeroRicevuta = prenotazione.numeroRicevuta || `AOA-${String(prenotazione.consegnataIl || prenotazione.dataCreazione || '').slice(0, 10).replaceAll('-', '')}-${prenotazione.id.slice(0, 6).toUpperCase()}`;
    const logo = `${window.location.origin}/academy-allievi-icon.png`;
    finestra.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Ricevuta ${proteggi(numeroRicevuta)}</title><style>@page{size:A4;margin:14mm}body{font-family:Arial,sans-serif;color:#201721;margin:0}.testata{display:flex;align-items:center;gap:16px;border-bottom:3px solid #57132c;padding-bottom:18px}.testata img{width:96px;height:52px;object-fit:contain}.brand{font-family:Georgia,serif;font-size:29px;color:#57132c;font-weight:bold}.team{margin-top:4px;color:#8b4261;font-size:11px;font-weight:bold;letter-spacing:1.3px}.titolo{margin-top:24px;padding:14px 16px;background:#f7eff8;border-left:5px solid #8e3fa7}.titolo h1{margin:0;color:#57132c;font-size:22px}.titolo p{margin:6px 0 0;color:#614854}.righe{margin-top:24px;font-size:16px;line-height:1.7}.riga{padding:8px 0;border-bottom:1px solid #eadde2}.etichetta{font-weight:bold}.footer{margin-top:42px;padding-top:18px;border-top:1px solid #c9b8bd;color:#614854;font-size:13px}@media print{body{margin:0}}</style></head><body><header class="testata"><img src="${proteggi(logo)}" alt="Logo Academy Of Arts"><div><div class="brand">Academy Of Arts</div><div class="team">TEAM DANCE PROJECT</div></div></header><section class="titolo"><h1>Ricevuta materiale consegnato</h1><p>Documento di consegna del materiale Academy Of Arts</p></section><section class="righe"><div class="riga"><span class="etichetta">N. ricevuta:</span> ${proteggi(numeroRicevuta)}</div><div class="riga"><span class="etichetta">Allievo:</span> ${proteggi(prenotazione.nomeAllievo || `${allievo.Cognome || ''} ${allievo.Nome || ''}`)}</div><div class="riga"><span class="etichetta">Articolo consegnato:</span> ${proteggi(prenotazione.articoloNome)}</div><div class="riga"><span class="etichetta">Dettagli:</span> ${proteggi(dettagli)}</div><div class="riga"><span class="etichetta">Quantità:</span> ${proteggi(prenotazione.quantita)} pezzo/i</div><div class="riga"><span class="etichetta">Data consegna:</span> ${proteggi(dataConsegna)}</div><div class="riga"><span class="etichetta">Modalità di pagamento:</span> Alla consegna</div></section><footer class="footer"><p>Grazie per aver scelto Academy Of Arts.</p><p>Firma per ricevuta: ________________________________________</p></footer></body></html>`);
    finestra.document.close();
    finestra.focus();
    window.setTimeout(() => finestra.print(), 350);
    registraLogOperazione(utenteCorrente, `Ristampata ricevuta Shop ${numeroRicevuta} per ${prenotazione.nomeAllievo || allievo.Cognome}`);
  };

  return <div style={{ position: 'fixed', inset: 0, zIndex: 2000, padding: 18, background: 'rgba(14,23,41,.66)', display: 'grid', placeItems: 'center', overflowY: 'auto' }}><div style={{ width: 'min(720px, 100%)', maxHeight: 'calc(100vh - 36px)', overflowY: 'auto', background: '#fff', color: '#2d1721', borderRadius: 22, padding: 24, boxSizing: 'border-box', boxShadow: '0 22px 54px rgba(0,0,0,.35)' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start' }}><div><p style={{ margin: 0, color: '#8e3fa7', fontSize: 11, fontWeight: 'bold', letterSpacing: 1.2 }}>ACADEMY OF ARTS · ANAGRAFICA ALLIEVI</p><h2 style={{ margin: '7px 0 3px', color: '#57132c' }}>🛍️ Storico Shop</h2><p style={{ margin: 0, color: '#765b66' }}>{allievo.Cognome} {allievo.Nome}</p></div><button type="button" onClick={onChiudi} aria-label="Chiudi storico Shop" style={{ border: 0, background: '#f2e9f5', color: '#57132c', width: 35, height: 35, borderRadius: '50%', cursor: 'pointer', fontSize: 20 }}>×</button></div><p style={{ margin: '18px 0 12px', color: '#765b66', lineHeight: 1.45 }}>Qui restano tutte le consegne effettuate. Puoi ristampare o salvare in PDF la ricevuta in qualsiasi momento.</p>{errore && <p style={{ color: '#b42318', fontWeight: 'bold' }}>{errore}</p>}{consegne.length ? <div style={{ display: 'grid', gap: 10 }}>{consegne.map(prenotazione => <article key={prenotazione.id} style={{ padding: 14, borderRadius: 12, background: '#f7f1fb', borderLeft: '5px solid #6d28d9' }}><strong style={{ color: '#32101e' }}>{prenotazione.articoloNome}</strong><span style={{ float: 'right', color: '#6d28d9', fontWeight: 'bold' }}>✓ Consegnato</span><p style={{ margin: '8px 0 0', color: '#553744' }}>{prenotazione.quantita} pezzo/i{prenotazione.taglia ? ` · Taglia ${prenotazione.taglia}` : ''}{prenotazione.colore ? ` · ${prenotazione.colore}` : ''}</p><small style={{ display: 'block', marginTop: 5, color: '#765b66' }}>Consegnato il {formattaDataIT(prenotazione.consegnataIl || prenotazione.aggiornataIl || prenotazione.dataCreazione)}{prenotazione.gestitaDa ? ` · Operatore: ${prenotazione.gestitaDa}` : ''}</small><button type="button" onClick={() => stampaRicevuta(prenotazione)} style={{ marginTop: 11, border: 0, borderRadius: 8, padding: '9px 11px', background: '#57132c', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>🧾 Ristampa / salva ricevuta</button></article>)}</div> : <div style={{ marginTop: 16, padding: 18, borderRadius: 12, background: '#f7f3f5', color: '#765b66' }}>Non risultano ancora articoli consegnati a questo allievo.</div>}</div></div>;
}

function Allievi({ utenteCorrente, ruoloUtente = 'operatore', modalita = 'anagrafica' }) {
  const mostraModulo = modalita === 'iscrizione';
  const ruolo = String(ruoloUtente || '').toLowerCase();
  const puoEliminareDirettamente = ruolo === 'master' || ruolo === 'admin';
  const puoGestireRuoliSpeciali = ruolo === 'master' || ruolo === 'admin';
  const location = useLocation();
  const navigate = useNavigate();
  const [allievi, setAllievi] = useState([]);
  const [queryAnagrafica, setQueryAnagrafica] = useState('');
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [sesso, setSesso] = useState('M');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [comuneNascita, setComuneNascita] = useState('');
  const [codiceComuneNascita, setCodiceComuneNascita] = useState('');
  const [comuniItaliani, setComuniItaliani] = useState([]);
  const [caricamentoComuni, setCaricamentoComuni] = useState(false);
  const [telefono, setTelefono] = useState('');
  const [telefonoGenitore, setTelefonoGenitore] = useState('');
  const [passwordApp, setPasswordApp] = useState('');
  const [appAbilitata, setAppAbilitata] = useState(true);
  const [corsiMappa, setCorsiMappa] = useState({});
  const [dataNascita, setDataNascita] = useState('');
  const [dataIscrizione, setDataIscrizione] = useState(dataOggiISO());
  const [dataUltimoPagamento, setDataUltimoPagamento] = useState('');
  const [dataCertificatoMedico, setDataCertificatoMedico] = useState('');
  const [isMaestro, setIsMaestro] = useState(false);
  const [isTirocinante, setIsTirocinante] = useState(false);
  const [idInModifica, setIdInModifica] = useState(null);
  const [importando, setImportando] = useState(false);
  const [riepilogoAllievo, setRiepilogoAllievo] = useState(null);
  const [storicoShopAllievo, setStoricoShopAllievo] = useState(null);
  const [richiesteCancellazione, setRichiesteCancellazione] = useState([]);
  const moduloAllievoRef = useRef(null);

  const caricaAllievi = async () => {
    const querySnapshot = await leggiRaccolta('allievi');
    setAllievi(ordinaAllieviPerCognomeNome(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }))));
  };

  // Elenco sempre aggiornato dall'ascolto condiviso (nessuna rilettura completa).
  useEffect(() => ascoltaRaccolta('allievi', () => { caricaAllievi(); }), []);

  useEffect(() => ascoltaRaccolta('richieste_cancellazione_allievi', snapshot => {
    const richiesteVisibili = snapshot.docs
      .map(documento => ({ id: documento.id, ...documento.data() }))
      .filter(richiesta => ruolo !== 'operatore' || richiesta.operatoreId === utenteCorrente)
      .sort((a, b) => String(b.dataRichiesta || '').localeCompare(String(a.dataRichiesta || '')));
    setRichiesteCancellazione(richiesteVisibili);
  }, errore => console.error('Impossibile caricare le richieste di cancellazione:', errore)), [ruolo, utenteCorrente]);

  const festeggiatiOggi = allievi.filter(a => isCompleannoOggi(a.DataNascita, a.CodiceFiscale));

  const allieviFiltratiAnagrafica = allievi.filter(a => {
    const testoCercato = queryAnagrafica.toLowerCase().trim();
    const nomeCompleto = `${a.Cognome || ''} ${a.Nome || ''}`.toLowerCase();
    const cf = (a.CodiceFiscale || '').toLowerCase();
    return nomeCompleto.includes(testoCercato) || cf.includes(testoCercato);
  });

  const gestisciImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportando(true);
    const reader = new FileReader();

    reader.onload = async (evt) => {
      const testo = evt.target.result;
      const righe = testo.split('\n').map(r => r.trim()).filter(r => r.length > 0);
      
      if (righe.length < 2) {
        alert("⚠️ Il file sembra vuoto!");
        setImportando(false);
        return;
      }

      const separatore = righe[0].includes(';') ? ';' : ',';
      const intestazioni = righe[0].split(separatore).map(h => h.trim().toLowerCase());

      const idxNome = intestazioni.findIndex(h => h.includes('nome') && !h.includes('cognome'));
      const idxCognome = intestazioni.findIndex(h => h.includes('cognome'));
      const idxSesso = intestazioni.findIndex(h => h.includes('sesso'));
      const idxCF = intestazioni.findIndex(h => h.includes('codice') || h.includes('fiscale') || h === 'cf');
      const idxTel = intestazioni.findIndex(h => h.includes('tel') || h.includes('cell') || h.includes('mobile'));
      const idxCorsi = intestazioni.findIndex(h => h.includes('corso') || h.includes('corsi'));

      let contatore = 0;
      let righeScartate = 0;

      for (let i = 1; i < righe.length; i++) {
        const colonne = righe[i].split(separatore).map(c => c.trim());
        const nomeVal = idxNome !== -1 && colonne[idxNome] ? colonne[idxNome] : '';
        const cognomeVal = idxCognome !== -1 && colonne[idxCognome] ? colonne[idxCognome] : '';
        const sessoVal = idxSesso !== -1 && colonne[idxSesso] ? colonne[idxSesso].toUpperCase() : 'M';
        const cfVal = idxCF !== -1 && colonne[idxCF] ? colonne[idxCF].toUpperCase() : '';
        const telVal = idxTel !== -1 && colonne[idxTel] ? colonne[idxTel] : '';
        const corsiStr = idxCorsi !== -1 && colonne[idxCorsi] ? colonne[idxCorsi] : '';
        const corsiArray = corsiStr ? corsiStr.split(/[-/|;]/).map(c => c.trim()).filter(c => c) : [];

        if (nomeVal && cognomeVal && !verificaNomeECognome(nomeVal, cognomeVal)) {
          await addDoc(collection(db, 'allievi'), {
            Nome: formattaNomePersona(nomeVal),
            Cognome: formattaNomePersona(cognomeVal),
            Sesso: sessoVal || 'M',
            CodiceFiscale: cfVal,
            Telefono: telVal,
            Corsi: corsiArray,
            DataNascita: '',
            DataIscrizione: dataOggiISO(),
            DataUltimoPagamento: null,
            isMaestro: false,
            isTirocinante: false
          });
          contatore++;
        } else if (nomeVal || cognomeVal) {
          righeScartate++;
        }
      }

      await registraLogOperazione(utenteCorrente, `Importati ${contatore} allievi da file CSV`);
      setImportando(false);
      alert(`🎉 Importazione completata! Aggiunti ${contatore} allievi.${righeScartate ? ` Scartate ${righeScartate} righe con Nome o Cognome non validi.` : ''}`);
      caricaAllievi();
    };

    reader.readAsText(file);
  };

  const gestisciToggleCorso = (corso) => {
    const nuovaMappa = { ...corsiMappa };
    if (nuovaMappa[corso]) {
      delete nuovaMappa[corso];
    } else {
      nuovaMappa[corso] = DISCIPLINE_DISPONIBILI[0];
    }
    setCorsiMappa(nuovaMappa);
  };

  const gestisciCambioDisciplina = (corso, disciplina) => {
    setCorsiMappa({ ...corsiMappa, [corso]: disciplina });
  };

  const trovaComune = (testo, elenco = comuniItaliani) => {
    const cercato = normalizzaRicercaComune(testo);
    if (!cercato) return null;
    return elenco.find(comune => normalizzaRicercaComune(comune.nome) === cercato
      || normalizzaRicercaComune(`${comune.nome} ${comune.sigla || ''}`) === cercato) || null;
  };

  const caricaComuniItaliani = async () => {
    if (comuniItaliani.length) return comuniItaliani;
    setCaricamentoComuni(true);
    try {
      const chiaveCache = 'academy_comuni_catastali_v1';
      const salvati = JSON.parse(localStorage.getItem(chiaveCache) || '[]');
      if (Array.isArray(salvati) && salvati.length > 7000) {
        setComuniItaliani(salvati);
        return salvati;
      }
      const risposta = await fetch(URL_ELENCO_COMUNI_ITALIANI, { cache: 'force-cache' });
      if (!risposta.ok) throw new Error('Elenco dei Comuni non raggiungibile.');
      const dati = await risposta.json();
      const elenco = Array.isArray(dati) ? dati
        .filter(comune => comune?.nome && /^[A-Z]\d{3}$/.test(String(comune.codiceCatastale || '')))
        .map(comune => ({ nome: comune.nome, sigla: comune.sigla || '', codiceCatastale: comune.codiceCatastale })) : [];
      if (!elenco.length) throw new Error('Elenco dei Comuni non valido.');
      setComuniItaliani(elenco);
      try { localStorage.setItem(chiaveCache, JSON.stringify(elenco)); } catch (erroreCache) { console.warn('Impossibile salvare la cache dei Comuni:', erroreCache); }
      return elenco;
    } catch (errore) {
      console.error('Impossibile caricare i Comuni italiani:', errore);
      alert('Non riesco a cercare automaticamente il Comune in questo momento. Verifica la connessione e riprova.');
      return [];
    } finally {
      setCaricamentoComuni(false);
    }
  };

  const aggiornaComuneNascita = valore => {
    setComuneNascita(valore);
    const comuneTrovato = trovaComune(valore);
    setCodiceComuneNascita(comuneTrovato?.codiceCatastale || '');
  };

  const gestisciSalvataggio = async (e) => {
    e.preventDefault();
    if (!nome || !cognome) return;

    const erroreNome = verificaNomeECognome(nome, cognome);
    if (erroreNome) {
      alert(`⚠️ ${erroreNome}`);
      return;
    }

    const etaAllievo = calcolaEtaAllievo(dataNascita, codiceFiscale);
    if (etaAllievo !== null && etaAllievo < 18 && !telefonoGenitore.trim()) {
      alert('⚠️ Per un allievo minorenne è obbligatorio inserire il telefono del genitore o tutore.');
      return;
    }

    const listaCorsiFormattata = Object.entries(corsiMappa).map(
      ([corso, disc]) => `${corso} - ${disc}`
    );

    const datiAllievo = {
      Nome: formattaNomePersona(nome),
      Cognome: formattaNomePersona(cognome),
      Sesso: sesso,
      CodiceFiscale: codiceFiscale.toUpperCase(),
      ComuneNascita: formattaNomePersona(comuneNascita),
      CodiceComuneNascita: codiceComuneNascita.trim().toUpperCase(),
      Telefono: telefono,
      TelefonoGenitore: telefonoGenitore.trim(),
      passwordApp: passwordApp.trim(),
      appAbilitata,
      Corsi: listaCorsiFormattata,
      DataNascita: dataNascita,
      DataIscrizione: dataIscrizione,
      DataUltimoPagamento: dataUltimoPagamento || null,
      DataCertificatoMedico: dataCertificatoMedico || null,
      isMaestro: isMaestro,
      docenteTemporaneo: isMaestro,
      isTirocinante: isTirocinante
    };

    const eraUnaModifica = Boolean(idInModifica);
    if (eraUnaModifica) {
      await updateDoc(doc(db, 'allievi', idInModifica), datiAllievo);
      await sincronizzaDocenteTemporaneo(idInModifica, datiAllievo);
      await registraLogOperazione(utenteCorrente, `Modificato allievo: ${cognome} ${nome}`);
      setIdInModifica(null);
    } else {
      const nuovoAllievo = await addDoc(collection(db, 'allievi'), datiAllievo);
      await sincronizzaDocenteTemporaneo(nuovoAllievo.id, datiAllievo);
      await registraLogOperazione(utenteCorrente, `Inserito nuovo allievo: ${cognome} ${nome}`);
      setRiepilogoAllievo({ id: nuovoAllievo.id, ...datiAllievo });
    }

    resetForm();
    caricaAllievi();
    if (eraUnaModifica) navigate('/allievi#lista-allievi');
  };

  const avviaModifica = (allievo) => {
    if (!mostraModulo) {
      navigate(`/iscrizione-allievo?modifica=${encodeURIComponent(allievo.id)}`);
      return;
    }
    setIdInModifica(allievo.id);
    setNome(allievo.Nome || '');
    setCognome(allievo.Cognome || '');
    setSesso(allievo.Sesso || 'M');
    setCodiceFiscale(allievo.CodiceFiscale || '');
    setComuneNascita(allievo.ComuneNascita || '');
    setCodiceComuneNascita(allievo.CodiceComuneNascita || '');
    setTelefono(allievo.Telefono || '');
    setTelefonoGenitore(allievo.TelefonoGenitore || '');
    setPasswordApp(allievo.passwordApp || '');
    setAppAbilitata(allievo.appAbilitata !== false);
    setIsMaestro(!!allievo.isMaestro);
    setIsTirocinante(!!allievo.isTirocinante);

    const corsiEsistenti = Array.isArray(allievo.Corsi) ? allievo.Corsi : (allievo.Corso ? [allievo.Corso] : []);
    const nuovaMappa = {};
    corsiEsistenti.forEach(c => {
      const parti = c.split(' - ');
      if (parti.length > 1) {
        nuovaMappa[parti[0]] = gruppoCorsoRiorganizzato(parti[0], parti[1]);
      } else {
        nuovaMappa[c] = DISCIPLINE_DISPONIBILI[0];
      }
    });
    setCorsiMappa(nuovaMappa);

    setDataNascita(allievo.DataNascita || '');
    setDataIscrizione(allievo.DataIscrizione || dataOggiISO());
    setDataUltimoPagamento(allievo.DataUltimoPagamento || '');
    setDataCertificatoMedico(allievo.DataCertificatoMedico || '');
    window.setTimeout(() => moduloAllievoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  const resetForm = () => {
    setIdInModifica(null);
    setNome('');
    setCognome('');
    setSesso('M');
    setCodiceFiscale('');
    setComuneNascita('');
    setCodiceComuneNascita('');
    setTelefono('');
    setTelefonoGenitore('');
    setPasswordApp('');
    setAppAbilitata(true);
    setCorsiMappa({});
    setDataNascita('');
    setDataIscrizione(dataOggiISO());
    setDataUltimoPagamento('');
    setDataCertificatoMedico('');
    setIsMaestro(false);
    setIsTirocinante(false);
  };

  useEffect(() => {
    if (!mostraModulo || idInModifica) return;
    const idDaModificare = new URLSearchParams(location.search).get('modifica');
    const allievoDaModificare = idDaModificare ? allievi.find(a => a.id === idDaModificare) : null;
    if (allievoDaModificare) avviaModifica(allievoDaModificare);
  }, [allievi, idInModifica, location.search, mostraModulo]);

  const eliminaAllievo = async (id, nomeCompleto) => {
    if (!puoEliminareDirettamente) {
      const richiestaGiaInAttesa = richiesteCancellazione.some(richiesta => richiesta.allievoId === id && richiesta.stato === 'in_attesa');
      if (richiestaGiaInAttesa) {
        alert('La richiesta di cancellazione per questo allievo è già in attesa del Master.');
        return;
      }
      if (!window.confirm(`Inviare al Master la richiesta di cancellazione per ${nomeCompleto}? L’allievo non verrà eliminato finché il Master non approva.`)) return;
      await addDoc(collection(db, 'richieste_cancellazione_allievi'), {
        allievoId: id,
        nomeAllievo: nomeCompleto,
        operatoreId: utenteCorrente,
        operatoreNome: utenteCorrente,
        stato: 'in_attesa',
        dataRichiesta: new Date().toISOString()
      });
      await registraLogOperazione(utenteCorrente, `Richiesta cancellazione allievo inviata al Master: ${nomeCompleto}`);
      alert('Richiesta inviata al Master. L’allievo rimane presente fino all’approvazione.');
      return;
    }
    if (window.confirm(`Eliminare definitivamente l'allievo ${nomeCompleto}?`)) {
      await deleteDoc(doc(db, 'allievi', id));
      await registraLogOperazione(utenteCorrente, `Eliminato allievo: ${nomeCompleto}`);
      caricaAllievi();
    }
  };

  const cambiaStatoApp = async (allievo) => {
    const nuovaAbilitazione = allievo.appAbilitata === false;
    if (!nuovaAbilitazione && !window.confirm(`Disabilitare l’app per ${allievo.Cognome} ${allievo.Nome}? Non potrà più accedere né ricevere notifiche.`)) return;
    await updateDoc(doc(db, 'allievi', allievo.id), { appAbilitata: nuovaAbilitazione });
    await registraLogOperazione(utenteCorrente, `${nuovaAbilitazione ? 'Abilitata' : 'Disabilitata'} app allievo per ${allievo.Cognome} ${allievo.Nome}`);
    caricaAllievi();
  };

  const etaCalcolataCF = calcolaEtaDaCF(codiceFiscale);
  const etaAllievoForm = calcolaEtaAllievo(dataNascita, codiceFiscale);
  const allievoMinorenne = etaAllievoForm !== null && etaAllievoForm < 18;
  const scadenzaCertificatoForm = calcolaScadenzaCertificatoMedico(dataCertificatoMedico);
  const suggerimentiComuni = comuneNascita.trim().length >= 2
    ? comuniItaliani.filter(comune => normalizzaRicercaComune(comune.nome).includes(normalizzaRicercaComune(comuneNascita))).slice(0, 10)
    : [];
  const generaCodiceFiscale = async () => {
    try {
      const elenco = comuniItaliani.length ? comuniItaliani : await caricaComuniItaliani();
      const comuneTrovato = trovaComune(comuneNascita, elenco);
      if (!comuneTrovato) throw new Error('Scrivi e seleziona un Comune di nascita valido dall’elenco proposto.');
      setComuneNascita(comuneTrovato.nome);
      setCodiceComuneNascita(comuneTrovato.codiceCatastale);
      setCodiceFiscale(generaCodiceFiscaleRapido({ nome, cognome, sesso, dataNascita, codiceComune: comuneTrovato.codiceCatastale }));
    } catch (errore) {
      alert(`⚠️ ${errore.message}`);
    }
  };

  return (
    <div style={{ color: '#f8f9fa' }}>
      {!mostraModulo && festeggiatiOggi.length > 0 && (
        <div style={{ backgroundColor: '#453a1a', border: '2px solid #fcc419', borderRadius: '10px', padding: '15px', marginBottom: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#fcc419', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🎂 COMPLEANNO OGGI! Tanti Auguri! 🎉
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {festeggiatiOggi.map(f => {
              const eta = calcolaEtaDaCF(f.CodiceFiscale) !== null ? calcolaEtaDaCF(f.CodiceFiscale) : 'N/D';
              return (
                <li key={f.id} style={{ fontSize: '15px', color: '#fcc419', fontWeight: 'bold' }}>
                  {f.Cognome} {f.Nome} — Compie {eta} anni! 🎈 {f.Telefono && `(Tel: ${f.Telefono})`}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h3 style={{ margin: 0, color: '#fcc419', fontFamily: 'serif' }}>{mostraModulo ? '📝 Iscrizione Allievo' : 'Anagrafica Allievi'}</h3>
        {!mostraModulo && <div style={{ backgroundColor: '#353b4c', border: '1px solid #fcc419', color: '#fcc419', padding: '10px 20px', borderRadius: '25px', fontWeight: 'bold', fontSize: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
          👥 Totale Allievi: {allievi.length}
        </div>}
      </div>

      {mostraModulo && <div style={{ backgroundColor: '#353b4c', border: '1px solid #4a5266', borderRadius: '10px', padding: '15px', marginBottom: '20px', maxWidth: '450px' }}>
        <strong style={{ color: '#4dabf7' }}>📥 Importa Elenco Allievi (CSV UTF-8)</strong>
        <p style={{ margin: '6px 0 10px 0', fontSize: '12px', color: '#adb5bd' }}>
          Carica il file CSV per registrare tutti gli allievi automaticamente.
        </p>
        <input 
          type="file" 
          accept=".csv" 
          onChange={gestisciImportCSV} 
          disabled={importando}
          style={{ fontSize: '13px', color: '#ced4da' }}
        />
        {importando && <p style={{ color: '#fcc419', fontWeight: 'bold', margin: '8px 0 0 0' }}>⏳ Caricamento in corso...</p>}
      </div>}

      {mostraModulo && <form className="modulo-allievo-academy" ref={moduloAllievoRef} onSubmit={gestisciSalvataggio} style={{ scrollMarginTop: '18px', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '450px', backgroundColor: '#fffafd', padding: '20px', borderRadius: '10px', border: '1px solid #d9b8c5', marginBottom: '25px' }}>
        <h4 style={{ margin: 0, color: idInModifica ? '#51cf66' : '#fcc419' }}>{idInModifica ? 'Modifica Allievo' : 'Iscrizione Nuovo Allievo'}</h4>
        
        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Cognome:</label>
        <input placeholder="Inserisci Cognome" value={cognome} onChange={e => setCognome(e.target.value)} onBlur={() => setCognome(formattaNomePersona(cognome))} required style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />

        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Nome:</label>
        <input placeholder="Inserisci Nome" value={nome} onChange={e => setNome(e.target.value)} onBlur={() => setNome(formattaNomePersona(nome))} required style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />

        {puoGestireRuoliSpeciali && <div className="ruoli-allievo-academy" style={{ display: 'flex', gap: '15px', backgroundColor: '#f7eff8', padding: '10px', borderRadius: '6px', border: '1px solid #d9b8c5' }}>
          <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#74c0fc', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={isMaestro} 
              onChange={e => {
                setIsMaestro(e.target.checked);
                if (e.target.checked) setIsTirocinante(false);
              }} 
              style={{ marginRight: '8px', transform: 'scale(1.2)' }} 
            />
            👩‍🏫 Docente della scuola · esente mensilità
          </label>
          <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffa94d', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={isTirocinante} 
              onChange={e => {
                setIsTirocinante(e.target.checked);
                if (e.target.checked) setIsMaestro(false);
              }} 
              style={{ marginRight: '8px', transform: 'scale(1.2)' }} 
            />
            👨‍🏫 È Tirocinante
          </label>
        </div>}

        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Sesso:</label>
        <select value={sesso} onChange={e => setSesso(e.target.value)} style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }}>
          <option value="M">Maschio (M)</option>
          <option value="F">Femmina (F)</option>
        </select>

        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Password per App Allievo:</label>
        <input
          type="password"
          placeholder="Password da consegnare all'allievo"
          value={passwordApp}
          onChange={e => setPasswordApp(e.target.value)}
          required={appAbilitata}
          style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }}
        />

        <label className="stato-app-allievo-academy" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 12px', borderRadius: 8, background: '#f7eff8', border: '1px solid #a71243', color: '#57132c', cursor: 'pointer' }}>
          <input type="checkbox" checked={appAbilitata} onChange={e => setAppAbilitata(e.target.checked)} style={{ marginTop: 2, transform: 'scale(1.2)' }} />
          <span><strong style={{ display: 'block', fontSize: 13 }}>📱 App allievo {appAbilitata ? 'abilitata' : 'disabilitata'}</strong><small style={{ display: 'block', marginTop: 4, fontSize: 11 }}>{appAbilitata ? 'Può accedere all’app e ricevere avvisi.' : 'Non può accedere all’app né ricevere avvisi push.'}</small></span>
        </label>

        {etaCalcolataCF !== null && (
          <div className="eta-allievo-academy" style={{ fontSize: '13px', color: '#57132c', fontWeight: 'bold', backgroundColor: '#f0e4f4', padding: '8px', borderRadius: '6px', border: '1px solid #c5a4df' }}>
            🎂 Età calcolata dal Codice Fiscale: {etaCalcolataCF} anni
          </div>
        )}

        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Telefono Allievo:</label>
        <input placeholder="Numero di telefono" value={telefono} onChange={e => setTelefono(e.target.value)} style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />

        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Data di Nascita:</label>
        <input type="date" value={dataNascita} onChange={e => setDataNascita(e.target.value)} style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />

        <section style={{ padding: 12, borderRadius: 8, background: '#f7eff8', border: '1px solid #c5a4df' }}>
          <strong style={{ display: 'block', color: '#57132c', fontSize: 14 }}>⚡ Codice fiscale rapido</strong>
          <small style={{ display: 'block', margin: '4px 0 10px', color: '#553744', lineHeight: 1.35 }}>Scrivi solo il Comune di nascita: il codice catastale viene cercato automaticamente. Il risultato va sempre verificato con tessera sanitaria o documento.</small>
          <input list="comuni-nascita-academy" placeholder="Comune di nascita" value={comuneNascita} onFocus={caricaComuniItaliani} onChange={e => aggiornaComuneNascita(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', backgroundColor: '#fff', color: '#212529', border: '1px solid #b58ac3', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />
          <datalist id="comuni-nascita-academy">{suggerimentiComuni.map(comune => <option key={comune.codiceCatastale} value={comune.nome} label={`${comune.sigla || 'Italia'} · ${comune.codiceCatastale}`} />)}</datalist>
          <div style={{ marginTop: 8, minHeight: 18, color: codiceComuneNascita ? '#176b3a' : '#765b66', fontSize: 12, fontWeight: 'bold' }}>{caricamentoComuni ? '🔎 Ricerca dei Comuni in corso…' : codiceComuneNascita ? `✓ Codice del Comune trovato automaticamente: ${codiceComuneNascita}` : 'Inizia a scrivere il Comune e scegli il suggerimento.'}</div>
          <button type="button" onClick={generaCodiceFiscale} disabled={caricamentoComuni} style={{ width: '100%', marginTop: 9, padding: '10px', border: 0, borderRadius: 7, background: '#7e2e99', color: '#fff', fontWeight: 'bold', cursor: caricamentoComuni ? 'wait' : 'pointer', opacity: caricamentoComuni ? .7 : 1 }}>⚡ {caricamentoComuni ? 'Ricerca Comune…' : 'Genera codice fiscale'}</button>
          <label style={{ display: 'block', marginTop: 11, fontSize: '12px', fontWeight: 'bold', color: '#57132c' }}>Codice Fiscale:</label>
          <input placeholder="Codice Fiscale (16 caratteri)" value={codiceFiscale} onChange={e => setCodiceFiscale(e.target.value.toUpperCase())} maxLength={16} style={{ width: '100%', boxSizing: 'border-box', marginTop: 5, textTransform: 'uppercase', padding: '10px', backgroundColor: '#fff', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />
        </section>

        {allievoMinorenne && (
          <>
            <AvvisoMinorenne telefonoGenitorePresente={Boolean(telefonoGenitore.trim())} />
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#ff8787' }}>Telefono genitore / tutore:</label>
            <input
              placeholder="Numero del genitore o tutore"
              value={telefonoGenitore}
              onChange={e => setTelefonoGenitore(e.target.value)}
              required
              style={{ padding: '10px', backgroundColor: '#fff5f5', color: '#212529', border: '2px solid #e03131', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }}
            />
          </>
        )}

        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Data Iscrizione:</label>
        <input type="date" value={dataIscrizione} onChange={e => setDataIscrizione(e.target.value)} required style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />

        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Data Ultimo Pagamento:</label>
        <input type="date" value={dataUltimoPagamento} onChange={e => setDataUltimoPagamento(e.target.value)} style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />

        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#fcc419' }}>Data Certificato Medico:</label>
        <input type="date" value={dataCertificatoMedico} onChange={e => setDataCertificatoMedico(e.target.value)} style={{ padding: '10px', backgroundColor: '#e9ecef', color: '#212529', border: '2px solid #74c0fc', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }} />
        {scadenzaCertificatoForm && <small style={{ marginTop: '-7px', color: certificatoMedicoScaduto(dataCertificatoMedico) ? '#ff6b6b' : '#63e6be', fontWeight: 'bold' }}>Scadenza certificato: {formattaDataIT(scadenzaCertificatoForm.toISOString().split('T')[0])}</small>}
        
        <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#fcc419' }}>Seleziona Corsi e Disciplina/Fascia:</label>
        <div className="elenco-corsi-allievo-academy" style={{ backgroundColor: '#f7eff8', padding: '12px', borderRadius: '6px', border: '1px solid #d9b8c5', color: '#2d1721' }}>
          {LISTA_CORSI.map(corso => {
            const selezionato = !!corsiMappa[corso];
            return (
              <div key={corso} style={{ marginBottom: '10px', borderBottom: '1px solid #ced4da', paddingBottom: '8px' }}>
                <label style={{ fontSize: '14px', display: 'block', cursor: 'pointer', fontWeight: 'bold' }}>
                  <input 
                    type="checkbox" 
                    checked={selezionato} 
                    onChange={() => gestisciToggleCorso(corso)}
                    style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                  />
                  {corso}
                </label>
                {selezionato && (
                  <div style={{ marginTop: '6px', paddingLeft: '24px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#495057' }}>Disciplina/Fascia d'età:</label>
                    <select 
                      value={corsiMappa[corso]} 
                      onChange={e => gestisciCambioDisciplina(corso, e.target.value)}
                      style={{ width: '100%', padding: '6px', marginTop: '2px', borderRadius: '4px', border: '1px solid #74c0fc', fontWeight: 'bold', backgroundColor: '#fff' }}
                    >
                      {DISCIPLINE_DISPONIBILI.map(disc => (
                        <option key={disc} value={disc}>{formattaFasciaDisciplina(disc)}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button className="bottone-salva-allievo-academy" type="submit" style={{ padding: '12px', cursor: 'pointer', flex: 1, backgroundColor: '#a71243', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '15px' }}>
            {idInModifica ? 'Aggiorna Allievo' : 'Salva Allievo'}
          </button>
          {idInModifica && (
            <button type="button" onClick={() => { resetForm(); navigate('/allievi#lista-allievi'); }} style={{ padding: '12px', cursor: 'pointer', backgroundColor: '#495057', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}>
              Annulla
            </button>
          )}
        </div>
      </form>}

      {!mostraModulo && <>
      <div style={{ margin: '30px 0 15px 0' }}>
        <h4 style={{ color: '#fcc419', marginBottom: '8px' }}>🔎 Cerca Allievo per Modifica / Eliminazione Rapida</h4>
        <input 
          placeholder="Cerca per nome oppure spara il Codice Fiscale..." 
          value={queryAnagrafica} 
          onChange={e => setQueryAnagrafica(e.target.value)}
          style={{ width: '100%', maxWidth: '450px', padding: '10px', fontSize: '14px', borderRadius: '8px', border: '2px solid #fcc419', backgroundColor: '#353b4c', color: '#fff' }}
        />
      </div>

      <ul id="lista-allievi" style={{ listStyle: 'none', padding: 0, scrollMarginTop: '20px' }}>
        {allieviFiltratiAnagrafica.map(a => {
          const corsiLista = Array.isArray(a.Corsi) ? a.Corsi.join(', ') : (a.Corso || 'Nessuno');
          const etaRilevata = calcolaEtaAllievo(a.DataNascita, a.CodiceFiscale);
          const etaVisualizzata = etaRilevata !== null ? `${etaRilevata} anni` : calcolaFasciaEta(a.DataNascita, a.CodiceFiscale);
          const eFesteggiato = isCompleannoOggi(a.DataNascita, a.CodiceFiscale);
          const datiTutoreMancanti = etaRilevata !== null && etaRilevata < 18 && !a.TelefonoGenitore;
          const richiestaCancellazione = richiesteCancellazione.find(richiesta => richiesta.allievoId === a.id);

          return (
            <li key={a.id} style={{ position: 'relative', padding: datiTutoreMancanti ? '14px 150px 14px 14px' : '14px', borderBottom: '1px solid #eadde2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: eFesteggiato ? '#fff8e5' : '#ffffff', marginBottom: '10px', borderRadius: '10px', flexWrap: 'wrap', gap: '10px', boxShadow: '0 7px 18px rgba(71,22,43,.05)' }}>
              {datiTutoreMancanti && <span className="richiamo-dati-mancanti" title="Telefono genitore/tutore mancante" style={{ position: 'absolute', top: 13, right: 14, padding: '5px 8px', borderRadius: 12, background: '#fff1f1', border: '1px solid #ff8787', color: '#c92a2a', fontSize: 11, fontWeight: 'bold', letterSpacing: '.4px' }}>⚠ MANCANO DATI</span>}
              <div>
                <strong style={{ color: '#111827', fontSize: '16px' }}>
                  {a.Cognome} {a.Nome}
                </strong> <span style={{ color: '#4b5563' }}>({a.Sesso || 'M'})</span> {eFesteggiato && <span style={{ color: '#a16207', fontWeight: 'bold' }}> 🎉 COMPLEANNO OGGI!</span>} {a.CodiceFiscale && <small style={{ color: '#4b5563' }}>— CF: {a.CodiceFiscale}</small>}<br />
                <small style={{ color: '#4b5563' }}>Corsi e Discipline: <em>{corsiLista}</em></small><br />
                <small style={{ color: '#4b5563' }}>
                  Età: <strong>{etaVisualizzata}</strong> | Tel: {a.Telefono || 'N/D'} | Data Iscrizione: <strong>{formattaDataIT(a.DataIscrizione)}</strong>
                </small><br />
                <BadgeStatoPagamento dataUltimoPagamento={a.DataUltimoPagamento} isMaestro={a.isMaestro} isTirocinante={a.isTirocinante} docenteScuola={a.docenteScuola} esenteMensilita={a.esenteMensilita} />
                <BadgeCertificatoMedico dataCertificato={a.DataCertificatoMedico} />
                <span style={{ display: 'inline-block', marginLeft: 7, padding: '4px 8px', borderRadius: 12, background: a.appAbilitata === false ? '#ffe3e3' : '#dcfce7', color: a.appAbilitata === false ? '#b42318' : '#167043', fontWeight: 'bold', fontSize: 11 }}>📱 App {a.appAbilitata === false ? 'disabilitata' : 'abilitata'}</span>
                {ruolo === 'operatore' && richiestaCancellazione && <span style={{ display: 'inline-block', marginLeft: 7, padding: '4px 8px', borderRadius: 12, background: richiestaCancellazione.stato === 'in_attesa' ? '#fff3cd' : richiestaCancellazione.stato === 'approvata' ? '#dcfce7' : '#ffe3e3', color: richiestaCancellazione.stato === 'in_attesa' ? '#7a4b00' : richiestaCancellazione.stato === 'approvata' ? '#167043' : '#b42318', fontWeight: 'bold', fontSize: 11 }}>🗑️ {richiestaCancellazione.stato === 'in_attesa' ? 'Cancellazione in attesa del Master' : richiestaCancellazione.stato === 'approvata' ? 'Cancellazione approvata' : 'Cancellazione non approvata'}</span>}
              </div>
              <div style={{ display: 'flex', width: '100%', gap: '8px', paddingTop: '10px', borderTop: '1px solid #eadde2', flexWrap: 'wrap' }}>
                <button onClick={() => setRiepilogoAllievo(a)} style={{ backgroundColor: '#4dabf7', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>📄 Riepilogo</button>
                <button onClick={() => setStoricoShopAllievo(a)} style={{ backgroundColor: '#6d28d9', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>🛍️ Storico Shop</button>
                <button onClick={() => cambiaStatoApp(a)} style={{ backgroundColor: a.appAbilitata === false ? '#7b2cbf' : '#9e123f', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>{a.appAbilitata === false ? '📱 Abilita app' : '🚫 Disabilita app'}</button>
                <button onClick={() => avviaModifica(a)} style={{ backgroundColor: '#fcc419', color: '#000', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Modifica</button>
                <button disabled={ruolo === 'operatore' && richiestaCancellazione?.stato === 'in_attesa'} onClick={() => eliminaAllievo(a.id, `${a.Cognome} ${a.Nome}`)} style={{ backgroundColor: '#c92a2a', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: ruolo === 'operatore' && richiestaCancellazione?.stato === 'in_attesa' ? 'not-allowed' : 'pointer', opacity: ruolo === 'operatore' && richiestaCancellazione?.stato === 'in_attesa' ? .6 : 1, fontWeight: 'bold' }}>{puoEliminareDirettamente ? 'Elimina' : richiestaCancellazione?.stato === 'in_attesa' ? 'In attesa Master' : 'Richiedi cancellazione'}</button>
              </div>
            </li>
          );
        })}
      </ul>
      </>}
      {riepilogoAllievo && <RiepilogoIscrizione allievo={riepilogoAllievo} onChiudi={() => setRiepilogoAllievo(null)} utenteCorrente={utenteCorrente} />}
      {storicoShopAllievo && <StoricoShopAllievo allievo={storicoShopAllievo} onChiudi={() => setStoricoShopAllievo(null)} utenteCorrente={utenteCorrente} />}
    </div>
  );
}

function Corsi() {
  const [allievi, setAllievi] = useState([]);
  const [reportStampa, setReportStampa] = useState(null);

  const coloriCorsi = {
    "Danza Moderna": "#ff6b6b",         // Rosso corallo
    "Danze Latino Americane": "#fcc419", // Giallo oro
    "Solo Latin": "#51cf66",             // Verde brillante
    "Hip-hop": "#cc5de8",                // Viola / Lilla
    "Ballo di Coppia": "#e03131"         // Rosso
  };

  useEffect(() => {
    const caricaAllievi = async () => {
      const querySnapshot = await leggiRaccolta('allievi');
      setAllievi(ordinaAllieviPerCognomeNome(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }))));
    };
    caricaAllievi();
  }, []);

  const stampaFasciaDisciplina = (nomeCorso, disciplina, listaAllievi) => {
    setReportStampa({ corso: nomeCorso, disciplina, allievi: listaAllievi });
    setTimeout(() => {
      window.print();
    }, 300);
  };

  return (
    <div style={{ color: '#f8f9fa' }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #stampa-report-fascia, #stampa-report-fascia * { visibility: visible; }
          #stampa-report-fascia { position: absolute; left: 0; top: 0; width: 100%; color: #000; background: #fff; padding: 20px; }
          .no-print { display: none !important; }
        }
      `}</style>

      {reportStampa && (
        <div id="stampa-report-fascia">
          <h2 style={{ margin: 0 }}>ACADEMY OF ARTS - REPORT CORSI</h2>
                  <h3 style={{ margin: '5px 0 15px 0' }}>Corso: {reportStampa.corso} — Disciplina/Fascia: {formattaFasciaDisciplina(reportStampa.disciplina)}</h3>
          <p><strong>Totale Allievi Iscritti:</strong> {reportStampa.allievi.length}</p>
          <hr />
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }} border="1" cellPadding="6">
            <thead>
              <tr style={{ backgroundColor: '#f1f3f5' }}>
                <th>#</th>
                <th>Cognome e Nome</th>
                <th>Ruolo</th>
                <th>Sesso</th>
                <th>Telefono</th>
              </tr>
            </thead>
            <tbody>
              {reportStampa.allievi.map((a, idx) => (
                <tr key={a.id}>
                  <td>{idx + 1}</td>
                  <td><strong>{a.Cognome} {a.Nome}</strong></td>
                  <td>{a.isMaestro ? 'Maestro' : a.isTirocinante ? 'Tirocinante' : 'Allievo'}</td>
                  <td>{a.Sesso || 'M'}</td>
                  <td>{a.Telefono || 'N/D'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginTop: '30px', fontSize: '12px' }}>Stampato il: {new Date().toLocaleString('it-IT')}</p>
        </div>
      )}

      <div className="no-print">
        <h3 style={{ color: '#fcc419', fontFamily: 'serif', marginBottom: '20px' }}>🎓 Corsi & Categorizzazione Allievi per Fascia</h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
          {CORSI_INFO.map(corsoObj => {
            const coloreTitolo = coloriCorsi[corsoObj.nome] || '#fcc419';

            const iscrittiCorso = allievi.filter(a => {
              const corsiArr = Array.isArray(a.Corsi) ? a.Corsi : (a.Corso ? [a.Corso] : []);
              return corsiArr.some(c => c.startsWith(corsoObj.nome));
            });

            // Le fasce orarie restano visibili anche quando un gruppo non ha
            // ancora iscritti: così la segreteria vede subito il programma.
            const gruppiDiscipline = Object.fromEntries(Object.keys(corsoObj.orari || {}).map(disciplina => [disciplina, []]));
            iscrittiCorso.forEach(a => {
              const corsiArr = Array.isArray(a.Corsi) ? a.Corsi : [a.Corso];
              const corsoTrovato = corsiArr.find(c => c.startsWith(corsoObj.nome));
              
              let disciplina = 'Non specificata';
              if (corsoTrovato && corsoTrovato.includes(' - ')) {
                disciplina = gruppoCorsoRiorganizzato(corsoObj.nome, corsoTrovato.split(' - ')[1]);
              }

              if (!gruppiDiscipline[disciplina]) {
                gruppiDiscipline[disciplina] = [];
              }
              gruppiDiscipline[disciplina].push(a);
            });

            const disciplineVisualizzate = [
              ...Object.keys(corsoObj.orari || {}),
              ...Object.keys(gruppiDiscipline).filter(disciplina => !Object.prototype.hasOwnProperty.call(corsoObj.orari || {}, disciplina))
            ];

            return (
              <div key={corsoObj.nome} style={{ border: `2px solid ${coloreTitolo}`, borderRadius: '12px', padding: '20px', backgroundColor: '#353b4c', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
                <h4 style={{ margin: '0 0 4px 0', color: coloreTitolo, fontSize: '22px', fontWeight: 'bold' }}>
                  {corsoObj.nome}
                </h4>
                <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#4dabf7', fontWeight: 'bold' }}>
                  📅 Giorni: {corsoObj.testoGiorni}
                </p>
                <div style={{ backgroundColor: '#2b3140', padding: '6px 12px', borderRadius: '6px', fontWeight: 'bold', color: '#51cf66', display: 'inline-block', marginBottom: '15px' }}>
                  Totale Complessivo Iscritti: {iscrittiCorso.length}
                </div>

                <hr style={{ borderColor: '#4a5266', margin: '10px 0 15px 0' }} />

                {disciplineVisualizzate.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#adb5bd' }}>Nessun allievo iscritto a questo corso.</p>
                ) : (
                  disciplineVisualizzate.map(disc => {
                    const lista = gruppiDiscipline[disc] || [];
                    const fasciaUnder = /^under\b/i.test(disc);
                    const fasciaRossa = /^(over|adult latin|adulti latin|adulti|ballo di coppia)\b/i.test(disc);
                    const fasciaRiorganizzata = ['beginners', 'syllabus', 'competition', 'kid', 'teen', 'advanced', 'advance', 'lady group'].includes(String(disc).trim().toLocaleLowerCase('it-IT'));
                    const orariDisciplina = corsoObj.orari?.[disc] || [];
                    return (
                    <div key={disc} style={{ backgroundColor: '#2b3140', border: `1px solid ${fasciaRiorganizzata ? '#e03131' : '#4a5266'}`, borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ color: fasciaUnder || fasciaRossa || fasciaRiorganizzata ? '#e03131' : coloreTitolo, fontWeight: 'bold', fontSize: '16px' }}>
                          🏷️ {formattaFasciaDisciplina(disc)} ({lista.length})
                        </span>
                        <button 
                          onClick={() => stampaFasciaDisciplina(corsoObj.nome, disc, lista)}
                          style={{ backgroundColor: '#1c7ed6', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}
                        >
                          🖨️ Stampa Report
                        </button>
                      </div>

                      {orariDisciplina.length > 0 && <div style={{ margin: '0 0 10px', padding: '8px 10px', borderRadius: 7, background: '#e5f4e9', borderLeft: '3px solid #8e3fa7', color: '#57132c', fontSize: 12, fontWeight: 'bold', lineHeight: 1.45 }}>🕒 {orariDisciplina.join(' · ')}</div>}

                      {lista.length ? <ul style={{ paddingLeft: '18px', margin: 0, color: '#4b5563' }}>
                        {lista.map(a => (
                          <li key={a.id} style={{ fontSize: '13px', marginBottom: '3px' }}>
                            <strong style={{ color: '#111827' }}>{a.Cognome} {a.Nome}</strong> ({a.Sesso || 'M'}) 
                            {a.isMaestro && <span style={{ color: '#74c0fc', fontWeight: 'bold' }}> [Maestro]</span>}
                            {a.isTirocinante && <span style={{ color: '#ffa94d', fontWeight: 'bold' }}> [Tirocinante]</span>}
                            {a.Telefono && <small style={{ color: '#6b7280' }}> - Tel: {a.Telefono}</small>}
                          </li>
                        ))}
                      </ul> : <small style={{ display: 'block', color: '#adb5bd' }}>Nessun allievo assegnato a questa fascia.</small>}
                    </div>
                  );
                  })
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function stampaRicevutaPagamento(ricevuta, finestra) {
  const proteggi = valore => String(valore ?? '—').replace(/[&<>'"]/g, carattere => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[carattere]));
  const importo = Number(ricevuta.importo || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const numeroVisualizzato = ricevuta.prova ? 'PROVA' : ricevuta.numeroProgressivo;
  const titolo = ricevuta.prova ? 'PROVA DI STAMPA — NON REGISTRATA' : 'RICEVUTA DI PAGAMENTO';
  finestra.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Ricevuta ${proteggi(numeroVisualizzato)}</title><style>@page{size:A4;margin:14mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#171717;margin:0;font-size:13px}.testata{display:grid;grid-template-columns:42% 58%;border-top:2px solid #1f6b45;border-bottom:1px solid #222;min-height:120px}.logo{padding:10px;border-right:1px solid #777;display:grid;place-items:center;text-align:center}.logo img{max-width:205px;max-height:70px;object-fit:contain}.nome-scuola{margin-top:4px;font-family:Georgia,serif;font-size:22px;font-weight:bold;letter-spacing:1.4px;color:#16452e}.dati{padding:10px 14px;line-height:1.42}.dati strong{font-size:16px}.direzione{font-size:10px;margin-bottom:5px}.riepilogo{display:flex;justify-content:space-between;gap:16px;margin-top:23px;align-items:stretch}.numero{width:210px;border:1px solid #222;padding:11px}.intestata{flex:1;border:1px solid #222;padding:11px;line-height:1.55}.titolo{margin:24px 0 10px;padding:10px 12px;border-top:2px solid #1f6b45;border-bottom:1px solid #222;font-size:17px;font-weight:bold;color:#1f6b45;letter-spacing:.3px}.prova{color:#a61b3d;border-color:#a61b3d}.tabella{width:100%;border-collapse:collapse;min-height:220px}.tabella th,.tabella td{border:1px solid #222;padding:10px;vertical-align:top}.tabella th{background:#e8e8e8;text-transform:uppercase;font-size:11px;letter-spacing:.4px}.tabella th:nth-child(1){width:62%}.tabella th:nth-child(2){width:14%}.tabella th:nth-child(3){width:24%}.des{height:175px}.totale{margin-left:auto;width:38%;border-collapse:collapse}.totale td{border:1px solid #222;padding:8px}.totale td:last-child{text-align:right;font-weight:bold}.grande{font-size:16px;background:#e9f3ed}.note{margin-top:26px;border-top:1px solid #777;padding-top:11px;font-size:12px}@media print{body{margin:0}}</style></head><body><header class="testata"><div class="logo"><img src="${window.location.origin}/logo%20suola.jpeg" alt="Logo Academy Of Arts"><div class="nome-scuola">ACADEMY OF ARTS</div></div><div class="dati"><div class="direzione">M° Salvatore Renna &amp; M° Lia Di Martino</div><strong>ACADEMY OF ARTS A.S.D.</strong><br>Via C. Alberto, 22<br><strong>80040 Terzigno (NA)</strong><br><strong>C.F. 92066370637</strong><br>infoartsitaly@gmail.com</div></header><section class="riepilogo"><div class="numero"><strong>Ricevuta n. ${proteggi(numeroVisualizzato)}</strong><br><br>Data: <strong>${proteggi(formattaDataIT(ricevuta.dataPagamento))}</strong></div><div class="intestata"><strong>INTESTATA A</strong><br>${proteggi(ricevuta.nomeAllievo)}<br>${ricevuta.codiceFiscale ? `C.F. ${proteggi(ricevuta.codiceFiscale)}` : ''}</div></section><h1 class="titolo ${ricevuta.prova ? 'prova' : ''}">${proteggi(titolo)}</h1><table class="tabella"><thead><tr><th>Descrizione</th><th>Quantità</th><th>Importo</th></tr></thead><tbody><tr><td class="des">${proteggi(ricevuta.descrizione)}</td><td>1</td><td style="text-align:right">${proteggi(importo)}</td></tr></tbody></table><table class="totale"><tr><td>Totale</td><td>${proteggi(importo)}</td></tr><tr class="grande"><td>Totale ricevuta</td><td>${proteggi(importo)}</td></tr></table><section class="note"><strong>Modalità di pagamento:</strong> ${proteggi(ricevuta.modalitaPagamento)}<br><strong>${ricevuta.prova ? 'Prova preparata da' : 'Registrata da'}:</strong> ${proteggi(ricevuta.emessaDa)}</section></body></html>`);
  finestra.document.close();
  finestra.focus();
  window.setTimeout(() => finestra.print(), 350);
}

function inviaRicevutaSuWhatsApp(allievo, ricevuta) {
  const eta = calcolaEtaAllievo(allievo.DataNascita, allievo.CodiceFiscale);
  const destinatarioGenitore = eta !== null && eta < 18 && allievo.TelefonoGenitore;
  const telefono = String(destinatarioGenitore ? allievo.TelefonoGenitore : allievo.Telefono || '').replace(/\D/g, '');
  if (!telefono) {
    alert(`Non è presente un numero WhatsApp per ${allievo.Cognome} ${allievo.Nome}.`);
    return;
  }
  const numeroInternazionale = (telefono.length === 9 || telefono.length === 10) ? `39${telefono}` : telefono.replace(/^00/, '');
  const importo = Number(ricevuta.importo || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const testo = `*Academy Of Arts* 🧾\n\nCiao, ti confermiamo la registrazione del pagamento.\n\n*Ricevuta n. ${ricevuta.numeroProgressivo}*\n${ricevuta.descrizione}\n*Importo: ${importo}*\n*Data: ${formattaDataIT(ricevuta.dataPagamento)}*\n*Modalità: ${ricevuta.modalitaPagamento}*\n\nGrazie!`;
  window.open(`https://wa.me/${numeroInternazionale}?text=${encodeURIComponent(testo)}`, '_blank', 'noopener,noreferrer');
}

function ModalRicevutaPagamento({ allievo, inEmissione, onChiudi, onEmetti, onStampaProva }) {
  const [importo, setImporto] = useState('');
  const [modalitaPagamento, setModalitaPagamento] = useState('Contanti');
  const [errore, setErrore] = useState('');
  const [ricevutaEmessa, setRicevutaEmessa] = useState(null);
  const oggi = dataOggiISO();
  const [dataPagamento, setDataPagamento] = useState(oggi);
  const mese = new Date(`${dataPagamento}T12:00:00`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

  const datiValidi = () => {
    const valore = Number(String(importo).replace(',', '.'));
    if (!Number.isFinite(valore) || valore <= 0) {
      setErrore('Inserisci un importo valido maggiore di zero.');
      return null;
    }
    setErrore('');
    return { importo: valore, dataPagamento, modalitaPagamento, descrizione: `Quota associativa mese di ${mese}` };
  };

  const invia = async event => {
    event.preventDefault();
    const dati = datiValidi();
    if (!dati) return;
    const ricevuta = await onEmetti(dati);
    if (ricevuta) setRicevutaEmessa(ricevuta);
  };

  const stampaProva = () => {
    const dati = datiValidi();
    if (dati) onStampaProva(dati);
  };

  return <div role="dialog" aria-modal="true" aria-labelledby="titolo-ricevuta-pagamento" style={{ position: 'fixed', inset: 0, zIndex: 11000, display: 'grid', placeItems: 'center', padding: 18, background: 'rgba(28, 10, 20, .65)' }}><section style={{ width: 'min(510px, 100%)', maxHeight: 'calc(100vh - 36px)', overflowY: 'auto', padding: 24, borderRadius: 18, background: '#fff', color: '#2d1721', boxShadow: '0 24px 56px rgba(0,0,0,.35)' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 15, alignItems: 'flex-start' }}><div><p style={{ margin: 0, color: '#8e3fa7', fontWeight: 'bold', fontSize: 11, letterSpacing: 1.1 }}>ACADEMY OF ARTS A.S.D.</p><h2 id="titolo-ricevuta-pagamento" style={{ margin: '6px 0 3px', color: '#57132c' }}>🧾 Emetti ricevuta di pagamento</h2><p style={{ margin: 0, color: '#765b66' }}>{allievo.Cognome} {allievo.Nome}</p></div><button type="button" onClick={onChiudi} disabled={inEmissione} aria-label="Chiudi" style={{ width: 34, height: 34, borderRadius: '50%', border: 0, background: '#f2e9f5', color: '#57132c', fontSize: 20, cursor: 'pointer' }}>×</button></div>{ricevutaEmessa ? <div style={{ marginTop: 20, textAlign: 'center' }}><div style={{ fontSize: 38 }}>✅</div><h3 style={{ margin: '9px 0 5px', color: '#16844a' }}>Ricevuta n. {ricevutaEmessa.numeroProgressivo} registrata</h3><p style={{ margin: 0, color: '#553744', lineHeight: 1.5 }}>La ricevuta è stata aperta per la stampa. Puoi inviare subito il riepilogo all’allievo su WhatsApp.</p><button type="button" onClick={() => inviaRicevutaSuWhatsApp(allievo, ricevutaEmessa)} style={{ width: '100%', marginTop: 18, padding: '12px 15px', border: 0, borderRadius: 10, background: '#16844a', color: '#fff', cursor: 'pointer', fontWeight: 'bold', fontSize: 16 }}>📲 Invia ricevuta su WhatsApp</button><p style={{ margin: '10px 0 0', color: '#765b66', fontSize: 12, lineHeight: 1.45 }}>WhatsApp riceve numero, importo e dati della ricevuta. Per allegare il PDF, salvalo dalla stampa e aggiungilo alla chat.</p><button type="button" onClick={onChiudi} style={{ marginTop: 14, padding: '10px 18px', border: '1px solid #d9b8c5', borderRadius: 9, background: '#fff', color: '#57132c', cursor: 'pointer', fontWeight: 'bold' }}>Chiudi</button></div> : <><div style={{ marginTop: 18, padding: 12, borderRadius: 10, background: '#f7eff8', borderLeft: '4px solid #8e3fa7', color: '#553744', lineHeight: 1.45 }}><strong>Numerazione protetta</strong><br />Per una prova usa il tasto dedicato: non registra nulla. Solo il tasto <strong>“Assegna numero e stampa”</strong> fa avanzare la numerazione dal n. <strong>103</strong>.</div><form onSubmit={invia} style={{ display: 'grid', gap: 14, marginTop: 19 }}><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Importo ricevuto (€)<input autoFocus required inputMode="decimal" type="number" min="0.01" step="0.01" value={importo} onChange={event => setImporto(event.target.value)} placeholder="Es. 65,00" style={{ padding: 12, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Data pagamento<input type="date" value={dataPagamento} onChange={event => setDataPagamento(event.target.value)} style={{ padding: 11, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Modalità di pagamento<select value={modalitaPagamento} onChange={event => setModalitaPagamento(event.target.value)} style={{ padding: 11, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit', background: '#fff' }}><option>Contanti</option><option>Bonifico</option><option>POS</option></select></label></div><div style={{ padding: 11, borderRadius: 9, background: '#fbf6f8', color: '#553744', fontSize: 13 }}><strong>Descrizione automatica:</strong> Quota associativa mese di {mese}</div>{errore && <p role="alert" style={{ margin: 0, color: '#b42318', fontWeight: 'bold' }}>{errore}</p>}<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', marginTop: 4 }}><button type="button" disabled={inEmissione} onClick={onChiudi} style={{ padding: '10px 14px', borderRadius: 9, border: '1px solid #d9b8c5', background: '#fff', color: '#57132c', cursor: 'pointer', fontWeight: 'bold' }}>Annulla</button><button type="button" disabled={inEmissione} onClick={stampaProva} style={{ padding: '10px 14px', borderRadius: 9, border: '1px solid #d9b8c5', background: '#fff4f6', color: '#9b1640', cursor: 'pointer', fontWeight: 'bold' }}>🖨️ Stampa prova</button><button type="submit" disabled={inEmissione} style={{ padding: '10px 14px', borderRadius: 9, border: 0, background: '#57132c', color: '#fff', cursor: inEmissione ? 'wait' : 'pointer', fontWeight: 'bold' }}>{inEmissione ? 'Registrazione…' : '🔢 Assegna numero e stampa'}</button></div></form></>}</section></div>;
}

function stampaRicevutaAccredito(movimento, finestra) {
  const proteggi = valore => String(valore ?? '—').replace(/[&<>'"]/g, carattere => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[carattere]));
  const euro = valore => Number(valore || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const utilizzo = movimento.tipo === 'utilizzo';
  const titolo = utilizzo ? 'Ricevuta di utilizzo accredito' : 'Ricevuta di pagamento con accredito';
  const descrizione = utilizzo
    ? `Accredito utilizzato per il pagamento: ${euro(movimento.importoCreditoUtilizzato)}`
    : `Ricevuto oggi: ${euro(movimento.importoVersato)} · Importo dovuto oggi: ${euro(movimento.quotaTrattenuta)} · Accredito creato: ${euro(movimento.importoAccredito)}`;
  finestra.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>${proteggi(titolo)}</title><style>@page{size:A4;margin:18mm}body{font-family:Arial,sans-serif;color:#241522;margin:0}.head{border-bottom:3px solid #8e3fa7;padding-bottom:17px}.brand{font-family:Georgia,serif;font-size:30px;font-weight:bold;color:#57132c}.team{margin-top:4px;color:#8e3fa7;font-size:11px;font-weight:bold;letter-spacing:1.4px}.box{margin-top:24px;border:1px solid #d9b8c5;border-radius:12px;padding:18px}.titolo{margin:0;color:#57132c;font-size:23px}.riga{padding:11px 0;border-bottom:1px solid #eddde5;line-height:1.45}.riga:last-child{border:0}.etichetta{display:block;color:#765b66;font-size:12px;font-weight:bold;text-transform:uppercase;letter-spacing:.5px}.evidenza{margin-top:18px;background:#f6eff9;border-left:5px solid #8e3fa7;font-size:18px;font-weight:bold;color:#57132c}.nota{margin-top:28px;color:#765b66;font-size:12px;line-height:1.5}@media print{body{margin:0}}</style></head><body><header class="head"><div class="brand">ACADEMY OF ARTS</div><div class="team">TEAM DANCE PROJECT · SEGRETERIA</div></header><section class="box"><h1 class="titolo">${proteggi(titolo)}</h1><div class="riga"><span class="etichetta">Allievo</span><strong>${proteggi(movimento.nomeAllievo)}</strong></div><div class="riga"><span class="etichetta">Data</span><strong>${proteggi(formattaDataIT(movimento.data))}</strong></div><div class="riga"><span class="etichetta">Dettaglio</span>${proteggi(descrizione)}</div><div class="riga evidenza"><span class="etichetta">Credito residuo disponibile</span>${proteggi(euro(movimento.creditoResiduo))}</div></section><p class="nota">Documento interno di accredito Academy Of Arts. Conservare come promemoria del credito disponibile per i prossimi pagamenti.<br>Registrato da: ${proteggi(movimento.registratoDa)}</p></body></html>`);
  finestra.document.close();
  finestra.focus();
  window.setTimeout(() => finestra.print(), 350);
}

function ModalCreditoAllievo({ allievo, tipo, onChiudi, onRegistra }) {
  const oggi = dataOggiISO();
  const creditoAttuale = Math.max(0, Number(allievo.CreditoResiduo || 0));
  const [importoVersato, setImportoVersato] = useState('');
  const [quotaTrattenuta, setQuotaTrattenuta] = useState('');
  const [creditoDaUsare, setCreditoDaUsare] = useState('');
  const [data, setData] = useState(oggi);
  const [mensilitaSaldata, setMensilitaSaldata] = useState(true);
  const [errore, setErrore] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const eAccredito = tipo === 'accredito';
  const valore = testo => Number(String(testo).replace(',', '.'));
  const nuovoCreditoStimato = eAccredito
    ? creditoAttuale + Math.max(0, valore(importoVersato) - valore(quotaTrattenuta))
    : Math.max(0, creditoAttuale - valore(creditoDaUsare));

  const salva = async evento => {
    evento.preventDefault();
    const versato = valore(importoVersato);
    const quota = valore(quotaTrattenuta);
    const utilizzo = valore(creditoDaUsare);
    if (eAccredito && (!Number.isFinite(versato) || !Number.isFinite(quota) || versato <= quota || quota < 0)) {
      setErrore('Inserisci il ricevuto di oggi e l’importo dovuto di oggi: il ricevuto deve essere maggiore dell’importo dovuto.');
      return;
    }
    if (!eAccredito && (!Number.isFinite(utilizzo) || utilizzo <= 0 || utilizzo > creditoAttuale)) {
      setErrore('Inserisci un accredito da usare compreso nell’accredito disponibile.');
      return;
    }
    setErrore('');
    setSalvataggio(true);
    const esito = await onRegistra(eAccredito
      ? { tipo: 'accredito', data, importoVersato: versato, quotaTrattenuta: quota, importoAccredito: versato - quota }
      : { tipo: 'utilizzo', data, importoCreditoUtilizzato: utilizzo, mensilitaSaldata });
    setSalvataggio(false);
    if (esito) onChiudi();
  };

  if (window.__academyInterfacciaCreditoSemplificata !== false)

  return <div role="dialog" aria-modal="true" aria-labelledby="titolo-credito-allievo" style={{ position: 'fixed', inset: 0, zIndex: 11200, display: 'grid', placeItems: 'center', padding: 18, background: 'rgba(28, 10, 20, .65)' }}><section style={{ width: 'min(510px, 100%)', maxHeight: 'calc(100vh - 36px)', overflowY: 'auto', padding: 24, borderRadius: 18, background: '#fff', color: '#2d1721', boxShadow: '0 24px 56px rgba(0,0,0,.35)' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start' }}><div><p style={{ margin: 0, color: '#8e3fa7', fontWeight: 'bold', fontSize: 11, letterSpacing: 1.1 }}>PAGAMENTI · ACADEMY OF ARTS</p><h2 id="titolo-credito-allievo" style={{ margin: '6px 0 3px', color: '#57132c' }}>{eAccredito ? '💶 Pagamento con resto' : '↘ Usa accredito'}</h2><p style={{ margin: 0, color: '#765b66' }}>{allievo.Cognome} {allievo.Nome}</p></div><button type="button" onClick={onChiudi} aria-label="Chiudi" style={{ width: 34, height: 34, borderRadius: '50%', border: 0, background: '#f2e9f5', color: '#57132c', fontSize: 20, cursor: 'pointer' }}>×</button></div><div style={{ marginTop: 17, padding: '11px 13px', borderRadius: 10, background: '#f5eafb', borderLeft: '4px solid #8e3fa7', color: '#57132c' }}><strong>Accredito disponibile: {creditoAttuale.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</strong><br /><small>{eAccredito ? 'Inserisci il ricevuto e l’importo dovuto oggi: la differenza diventa accredito.' : 'Usa tutto o una parte dell’accredito, in base all’importo dovuto oggi.'}</small></div><form onSubmit={salva} style={{ display: 'grid', gap: 13, marginTop: 18 }}>{eAccredito ? <><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Ricevuto oggi (€)<input autoFocus required type="number" min="0.01" step="0.01" value={importoVersato} onChange={evento => setImportoVersato(evento.target.value)} placeholder="Es. 100,00" style={{ padding: 12, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Importo dovuto oggi (€)<input required type="number" min="0" step="0.01" value={quotaTrattenuta} onChange={evento => setQuotaTrattenuta(evento.target.value)} placeholder="Es. 78,00" style={{ padding: 12, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label></> : <><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Accredito da usare oggi (€)<input autoFocus required type="number" min="0.01" max={creditoAttuale} step="0.01" value={creditoDaUsare} onChange={evento => setCreditoDaUsare(evento.target.value)} placeholder="Es. 35,00" style={{ padding: 12, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label><label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#553744' }}><input type="checkbox" checked={mensilitaSaldata} onChange={evento => setMensilitaSaldata(evento.target.checked)} /> Con questo accredito il pagamento di oggi è completo</label></>}<label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Data operazione<input type="date" value={data} onChange={evento => setData(evento.target.value)} style={{ padding: 11, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label><div style={{ padding: 11, borderRadius: 9, background: '#fbf6f8', color: '#553744' }}><strong>Accredito residuo dopo l’operazione:</strong> {nuovoCreditoStimato.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</div>{errore && <p role="alert" style={{ margin: 0, color: '#b42318', fontWeight: 'bold' }}>{errore}</p>}<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}><button type="button" onClick={onChiudi} disabled={salvataggio} style={{ padding: '10px 14px', borderRadius: 9, border: '1px solid #d9b8c5', background: '#fff', color: '#57132c', cursor: 'pointer' }}>Annulla</button><button type="submit" disabled={salvataggio} style={{ padding: '10px 14px', borderRadius: 9, border: 0, background: '#8e3fa7', color: '#fff', cursor: salvataggio ? 'wait' : 'pointer', fontWeight: 'bold' }}>{salvataggio ? 'Salvataggio…' : eAccredito ? '💾 Salva pagamento e accredito' : '💾 Usa accredito e stampa'}</button></div></form></section></div>;

  return <div role="dialog" aria-modal="true" aria-labelledby="titolo-credito-allievo" style={{ position: 'fixed', inset: 0, zIndex: 11200, display: 'grid', placeItems: 'center', padding: 18, background: 'rgba(28, 10, 20, .65)' }}><section style={{ width: 'min(510px, 100%)', maxHeight: 'calc(100vh - 36px)', overflowY: 'auto', padding: 24, borderRadius: 18, background: '#fff', color: '#2d1721', boxShadow: '0 24px 56px rgba(0,0,0,.35)' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start' }}><div><p style={{ margin: 0, color: '#8e3fa7', fontWeight: 'bold', fontSize: 11, letterSpacing: 1.1 }}>PAGAMENTI · ACADEMY OF ARTS</p><h2 id="titolo-credito-allievo" style={{ margin: '6px 0 3px', color: '#57132c' }}>{eAccredito ? '➕ Registra accredito' : '↘ Scala credito'}</h2><p style={{ margin: 0, color: '#765b66' }}>{allievo.Cognome} {allievo.Nome}</p></div><button type="button" onClick={onChiudi} aria-label="Chiudi" style={{ width: 34, height: 34, borderRadius: '50%', border: 0, background: '#f2e9f5', color: '#57132c', fontSize: 20, cursor: 'pointer' }}>×</button></div><div style={{ marginTop: 17, padding: '11px 13px', borderRadius: 10, background: '#f5eafb', borderLeft: '4px solid #8e3fa7', color: '#57132c' }}><strong>Credito attuale: {creditoAttuale.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</strong><br /><small>{eAccredito ? 'Registra il resto lasciato dall’allievo per una prossima mensilità.' : 'Usa tutto o una parte del credito disponibile.'}</small></div><form onSubmit={salva} style={{ display: 'grid', gap: 13, marginTop: 18 }}>{eAccredito ? <><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Importo versato (€)<input autoFocus required type="number" min="0.01" step="0.01" value={importoVersato} onChange={evento => setImportoVersato(evento.target.value)} placeholder="Es. 100,00" style={{ padding: 12, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Quota trattenuta per la mensilità (€)<input required type="number" min="0" step="0.01" value={quotaTrattenuta} onChange={evento => setQuotaTrattenuta(evento.target.value)} placeholder="Es. 65,00" style={{ padding: 12, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label></> : <><label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Importo da scalare dal credito (€)<input autoFocus required type="number" min="0.01" max={creditoAttuale} step="0.01" value={creditoDaUsare} onChange={evento => setCreditoDaUsare(evento.target.value)} placeholder="Es. 35,00" style={{ padding: 12, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label><label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#553744' }}><input type="checkbox" checked={mensilitaSaldata} onChange={evento => setMensilitaSaldata(evento.target.checked)} /> Con questo credito la mensilità è saldata</label></>}<label style={{ display: 'grid', gap: 6, fontWeight: 'bold' }}>Data operazione<input type="date" value={data} onChange={evento => setData(evento.target.value)} style={{ padding: 11, borderRadius: 9, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit' }} /></label><div style={{ padding: 11, borderRadius: 9, background: '#fbf6f8', color: '#553744' }}><strong>Credito residuo dopo l’operazione:</strong> {nuovoCreditoStimato.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</div>{errore && <p role="alert" style={{ margin: 0, color: '#b42318', fontWeight: 'bold' }}>{errore}</p>}<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}><button type="button" onClick={onChiudi} disabled={salvataggio} style={{ padding: '10px 14px', borderRadius: 9, border: '1px solid #d9b8c5', background: '#fff', color: '#57132c', cursor: 'pointer', fontWeight: 'bold' }}>Annulla</button><button type="submit" disabled={salvataggio} style={{ padding: '10px 14px', borderRadius: 9, border: 0, background: '#8e3fa7', color: '#fff', cursor: salvataggio ? 'wait' : 'pointer', fontWeight: 'bold' }}>{salvataggio ? 'Salvataggio…' : eAccredito ? '💾 Salva e stampa accredito' : '💾 Scala credito e stampa'}</button></div></form></section></div>;
}

function ModalStornoSaldo({ allievo, onChiudi, onConferma }) {
  const [inCorso, setInCorso] = useState(false);
  const ultimoSaldo = allievo.ultimoSaldoRapido || {};
  const confermaStorno = async () => {
    setInCorso(true);
    const esito = await onConferma(allievo);
    setInCorso(false);
    if (esito) onChiudi();
  };

  return <div role="dialog" aria-modal="true" aria-labelledby="titolo-storno-saldo" style={{ position: 'fixed', inset: 0, zIndex: 11200, display: 'grid', placeItems: 'center', padding: 18, background: 'rgba(28, 10, 20, .65)' }}><section style={{ width: 'min(500px, 100%)', padding: 24, borderRadius: 18, background: '#fff', color: '#2d1721', boxShadow: '0 24px 56px rgba(0,0,0,.35)' }}><p style={{ margin: 0, color: '#b42318', fontWeight: 'bold', fontSize: 11, letterSpacing: 1.1 }}>OPERAZIONE DA CONFERMARE</p><h2 id="titolo-storno-saldo" style={{ margin: '6px 0 7px', color: '#57132c' }}>↩ Storna ultimo saldo</h2><p style={{ margin: 0, color: '#553744', lineHeight: 1.5 }}>Stai annullando il saldo registrato per <strong>{allievo.Cognome} {allievo.Nome}</strong> in data <strong>{formattaDataIT(ultimoSaldo.dataPagamento || allievo.DataUltimoPagamento)}</strong>.</p><div style={{ marginTop: 17, padding: '12px 14px', borderRadius: 10, background: '#fff4f4', borderLeft: '4px solid #c92a2a', color: '#8f1c1c', lineHeight: 1.5 }}><strong>Attenzione:</strong> lo stato del pagamento tornerà a quello precedente{ultimoSaldo.dataPrecedente ? ` (${formattaDataIT(ultimoSaldo.dataPrecedente)})` : ' (nessun saldo registrato)'}. L’operazione resterà comunque nello storico come storno.</div><div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', marginTop: 20 }}><button type="button" disabled={inCorso} onClick={onChiudi} style={{ padding: '10px 14px', borderRadius: 9, border: '1px solid #d9b8c5', background: '#fff', color: '#57132c', cursor: 'pointer', fontWeight: 'bold' }}>Non annullare</button><button type="button" disabled={inCorso} onClick={confermaStorno} style={{ padding: '10px 14px', borderRadius: 9, border: 0, background: '#c92a2a', color: '#fff', cursor: inCorso ? 'wait' : 'pointer', fontWeight: 'bold' }}>{inCorso ? 'Storno in corso…' : 'Conferma storno'}</button></div></section></div>;
}

function ModalCorreggiSaldoPrecedente({ allievo, onChiudi, onConferma }) {
  const [modalita, setModalita] = useState('nessun_saldo');
  const [dataCorretta, setDataCorretta] = useState(allievo.DataUltimoPagamento || '');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState('');
  const salvaCorrezione = async evento => {
    evento.preventDefault();
    if (modalita === 'data_corretta' && !dataCorretta) {
      setErrore('Scegli la data del saldo corretto.');
      return;
    }
    setInCorso(true);
    const esito = await onConferma(allievo, modalita === 'data_corretta' ? dataCorretta : '');
    setInCorso(false);
    if (esito) onChiudi();
  };

  return <div role="dialog" aria-modal="true" aria-labelledby="titolo-correggi-saldo" style={{ position: 'fixed', inset: 0, zIndex: 11200, display: 'grid', placeItems: 'center', padding: 18, background: 'rgba(28, 10, 20, .65)' }}><section style={{ width: 'min(520px, 100%)', padding: 24, borderRadius: 18, background: '#fff', color: '#2d1721', boxShadow: '0 24px 56px rgba(0,0,0,.35)' }}><p style={{ margin: 0, color: '#b26a00', fontWeight: 'bold', fontSize: 11, letterSpacing: 1.1 }}>CORREZIONE MANUALE</p><h2 id="titolo-correggi-saldo" style={{ margin: '6px 0 7px', color: '#57132c' }}>✎ Correggi saldo precedente</h2><p style={{ margin: 0, color: '#553744', lineHeight: 1.5 }}>Per <strong>{allievo.Cognome} {allievo.Nome}</strong> risulta un saldo del <strong>{formattaDataIT(allievo.DataUltimoPagamento)}</strong>. Essendo stato salvato prima dello storno automatico, scegli tu come correggerlo.</p><form onSubmit={salvaCorrezione} style={{ display: 'grid', gap: 11, marginTop: 18 }}><label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: 12, borderRadius: 10, border: modalita === 'nessun_saldo' ? '2px solid #c92a2a' : '1px solid #ead1dc', cursor: 'pointer' }}><input type="radio" checked={modalita === 'nessun_saldo'} onChange={() => setModalita('nessun_saldo')} /> <span><strong>Non era stato pagato</strong><br /><small>Rimuove il saldo errato e riporta l’allievo da saldare.</small></span></label><label style={{ display: 'grid', gap: 8, padding: 12, borderRadius: 10, border: modalita === 'data_corretta' ? '2px solid #8e3fa7' : '1px solid #ead1dc', cursor: 'pointer' }}><span style={{ display: 'flex', alignItems: 'center', gap: 9 }}><input type="radio" checked={modalita === 'data_corretta'} onChange={() => setModalita('data_corretta')} /> <strong>Il saldo era in un’altra data</strong></span><input type="date" disabled={modalita !== 'data_corretta'} value={dataCorretta} onChange={evento => setDataCorretta(evento.target.value)} style={{ padding: 10, borderRadius: 8, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit', background: modalita === 'data_corretta' ? '#fff' : '#f1f1f1' }} /></label><p style={{ margin: 0, padding: '10px 12px', borderRadius: 9, background: '#fff8db', color: '#795900', fontSize: 13 }}>La correzione sarà salvata nello storico, con il nome dell’operatore che l’ha eseguita.</p>{errore && <p style={{ margin: 0, color: '#b42318', fontWeight: 'bold' }}>{errore}</p>}<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', marginTop: 5 }}><button type="button" disabled={inCorso} onClick={onChiudi} style={{ padding: '10px 14px', borderRadius: 9, border: '1px solid #d9b8c5', background: '#fff', color: '#57132c', cursor: 'pointer', fontWeight: 'bold' }}>Annulla</button><button type="submit" disabled={inCorso} style={{ padding: '10px 14px', borderRadius: 9, border: 0, background: '#b26a00', color: '#fff', cursor: inCorso ? 'wait' : 'pointer', fontWeight: 'bold' }}>{inCorso ? 'Correzione in corso…' : 'Salva correzione'}</button></div></form></section></div>;
}

function Pagamenti({ utenteCorrente }) {
  const [allievi, setAllievi] = useState([]);
  const [dateSelezionate, setDateSelezionate] = useState({});
  const [queryPagamenti, setQueryPagamenti] = useState('');
  const [allievoPerRicevuta, setAllievoPerRicevuta] = useState(null);
  const [operazioneCredito, setOperazioneCredito] = useState(null);
  const [allievoPerStornoSaldo, setAllievoPerStornoSaldo] = useState(null);
  const [allievoPerCorrezioneSaldo, setAllievoPerCorrezioneSaldo] = useState(null);
  const [salvataggioSaldoId, setSalvataggioSaldoId] = useState('');
  const [emissioneRicevutaInCorso, setEmissioneRicevutaInCorso] = useState(false);

  const caricaAllievi = async () => {
    const querySnapshot = await leggiRaccolta('allievi');
    const listaCompleta = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    
    const allieviSoggettiAPagamento = listaCompleta.filter(a => !allievoEsenteMensilita(a));
    setAllievi(ordinaAllieviPerCognomeNome(allieviSoggettiAPagamento));
  };

  // Elenco sempre aggiornato dall'ascolto condiviso (nessuna rilettura completa).
  useEffect(() => ascoltaRaccolta('allievi', () => { caricaAllievi(); }), []);

  const registraPagamentoRapido = async (allievo) => {
    const dataPagamento = dateSelezionate[allievo.id] || dataOggiISO();
    const refDoc = doc(db, 'allievi', allievo.id);
    const riferimentoOperazione = doc(collection(db, 'pagamenti_rapidi'));
    setSalvataggioSaldoId(allievo.id);
    try {
      await runTransaction(db, async transazione => {
        const snapshotAllievo = await transazione.get(refDoc);
        if (!snapshotAllievo.exists()) throw new Error('Allievo non trovato.');
        const dataPrecedente = snapshotAllievo.data().DataUltimoPagamento || '';
        const registratoIl = new Date().toISOString();
        transazione.update(refDoc, { DataUltimoPagamento: dataPagamento, ultimoSaldoRapido: { operazioneId: riferimentoOperazione.id, dataPagamento, dataPrecedente, registratoDa: utenteCorrente, registratoIl } });
        transazione.set(riferimentoOperazione, { allievoId: allievo.id, nomeAllievo: `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim(), dataPagamento, dataPrecedente, registratoDa: utenteCorrente, registratoIl, stornato: false });
      });
      await registraLogOperazione(utenteCorrente, `Registrato saldo mensile per ${allievo.Cognome} ${allievo.Nome} (Data: ${formattaDataIT(dataPagamento)})`);
      caricaAllievi();
    } catch (errore) {
      console.error('Impossibile registrare il saldo:', errore);
      alert('Non è stato possibile salvare il saldo. Riprova.');
    } finally {
      setSalvataggioSaldoId('');
    }
  };

  const stornaPagamentoRapido = async allievo => {
    const ultimoSaldo = allievo.ultimoSaldoRapido;
    if (!ultimoSaldo?.operazioneId) {
      alert('Lo storno è disponibile per l’ultimo saldo registrato con questa funzione.');
      return false;
    }
    try {
      const riferimentoAllievo = doc(db, 'allievi', allievo.id);
      const riferimentoOperazione = doc(db, 'pagamenti_rapidi', ultimoSaldo.operazioneId);
      await runTransaction(db, async transazione => {
        const snapshotAllievo = await transazione.get(riferimentoAllievo);
        if (!snapshotAllievo.exists()) throw new Error('Allievo non trovato.');
        const saldoCorrente = snapshotAllievo.data().ultimoSaldoRapido;
        if (!saldoCorrente?.operazioneId || saldoCorrente.operazioneId !== ultimoSaldo.operazioneId) throw new Error('Questo saldo è già stato modificato. Aggiorna la pagina e riprova.');
        if ((snapshotAllievo.data().DataUltimoPagamento || '') !== (saldoCorrente.dataPagamento || '')) throw new Error('Dopo questo saldo è stato registrato un altro pagamento (ricevuta o credito): lo storno non è più possibile da qui.');
        transazione.update(riferimentoAllievo, { DataUltimoPagamento: saldoCorrente.dataPrecedente || null, ultimoSaldoRapido: null });
        transazione.set(riferimentoOperazione, { stornato: true, stornatoIl: new Date().toISOString(), stornatoDa: utenteCorrente }, { merge: true });
      });
      await registraLogOperazione(utenteCorrente, `Stornato saldo mensile di ${allievo.Cognome} ${allievo.Nome}; ripristinato stato precedente${ultimoSaldo.dataPrecedente ? ` (${formattaDataIT(ultimoSaldo.dataPrecedente)})` : ''}`);
      caricaAllievi();
      return true;
    } catch (errore) {
      console.error('Impossibile stornare il saldo:', errore);
      alert(errore.message || 'Non è stato possibile stornare il saldo. Riprova.');
      return false;
    }
  };

  const correggiSaldoPrecedente = async (allievo, dataCorretta) => {
    try {
      const riferimentoAllievo = doc(db, 'allievi', allievo.id);
      const riferimentoCorrezione = doc(collection(db, 'correzioni_saldi'));
      const correttoIl = new Date().toISOString();
      await runTransaction(db, async transazione => {
        const snapshotAllievo = await transazione.get(riferimentoAllievo);
        if (!snapshotAllievo.exists()) throw new Error('Allievo non trovato.');
        const dataErrata = snapshotAllievo.data().DataUltimoPagamento || '';
        transazione.update(riferimentoAllievo, { DataUltimoPagamento: dataCorretta || null });
        transazione.set(riferimentoCorrezione, { allievoId: allievo.id, nomeAllievo: `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim(), dataErrata, dataCorretta: dataCorretta || '', correttoDa: utenteCorrente, correttoIl, motivo: dataCorretta ? 'data_saldo_corretta' : 'saldo_errato_rimosso' });
      });
      await registraLogOperazione(utenteCorrente, `Corretto saldo precedente di ${allievo.Cognome} ${allievo.Nome}: ${dataCorretta ? `nuova data ${formattaDataIT(dataCorretta)}` : 'saldo errato rimosso'}`);
      caricaAllievi();
      return true;
    } catch (errore) {
      console.error('Impossibile correggere il saldo:', errore);
      alert(errore.message || 'Non è stato possibile correggere il saldo. Riprova.');
      return false;
    }
  };

  const registraOperazioneCredito = async dati => {
    if (!operazioneCredito) return null;
    const { allievo, tipo } = operazioneCredito;
    const finestra = window.open('', '_blank', 'width=820,height=900');
    if (!finestra) {
      alert('Il browser ha bloccato la stampa. Consenti i popup e riprova.');
      return null;
    }
    try {
      const riferimentoAllievo = doc(db, 'allievi', allievo.id);
      const riferimentoMovimento = doc(collection(db, 'movimenti_credito_allievi'));
      const movimento = await runTransaction(db, async transazione => {
        const snapshotAllievo = await transazione.get(riferimentoAllievo);
        if (!snapshotAllievo.exists()) throw new Error('Allievo non trovato.');
        const datiAllievo = snapshotAllievo.data();
        const creditoPrecedente = Math.max(0, Number(datiAllievo.CreditoResiduo || 0));
        const importoAccredito = tipo === 'accredito' ? Number(dati.importoAccredito) : 0;
        const importoCreditoUtilizzato = tipo === 'utilizzo' ? Number(dati.importoCreditoUtilizzato) : 0;
        if (tipo === 'accredito' && (!Number.isFinite(importoAccredito) || importoAccredito <= 0)) throw new Error('L’accredito deve essere maggiore di zero.');
        if (tipo === 'utilizzo' && (!Number.isFinite(importoCreditoUtilizzato) || importoCreditoUtilizzato <= 0 || importoCreditoUtilizzato > creditoPrecedente)) throw new Error('Il credito da scalare non è disponibile.');
        const creditoResiduo = tipo === 'accredito' ? creditoPrecedente + importoAccredito : creditoPrecedente - importoCreditoUtilizzato;
        const aggiornamentoAllievo = { CreditoResiduo: Math.round(creditoResiduo * 100) / 100, CreditoAggiornatoIl: new Date().toISOString() };
        if (tipo === 'accredito' || dati.mensilitaSaldata) aggiornamentoAllievo.DataUltimoPagamento = dataPagamentoPiuRecente(datiAllievo.DataUltimoPagamento, dati.data);
        const datiMovimento = {
          allievoId: allievo.id,
          nomeAllievo: `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim(),
          tipo,
          data: dati.data,
          importoVersato: Number(dati.importoVersato || 0),
          quotaTrattenuta: Number(dati.quotaTrattenuta || 0),
          importoAccredito,
          importoCreditoUtilizzato,
          creditoPrecedente,
          creditoResiduo: aggiornamentoAllievo.CreditoResiduo,
          mensilitaSaldata: Boolean(dati.mensilitaSaldata || tipo === 'accredito'),
          registratoDa: utenteCorrente,
          registratoIl: new Date().toISOString()
        };
        transazione.update(riferimentoAllievo, aggiornamentoAllievo);
        transazione.set(riferimentoMovimento, datiMovimento);
        return datiMovimento;
      });
      stampaRicevutaAccredito(movimento, finestra);
      alert(`✅ Operazione salvata. Si è aperta la ricevuta ${movimento.tipo === 'accredito' ? 'del pagamento con accredito' : 'di utilizzo dell’accredito'}: puoi stamparla o salvarla in PDF.`);
      await registraLogOperazione(utenteCorrente, `${movimento.tipo === 'accredito' ? 'Registrato accredito' : 'Scalato credito'} per ${movimento.nomeAllievo}: credito residuo ${movimento.creditoResiduo.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}`);
      caricaAllievi();
      return movimento;
    } catch (errore) {
      console.error('Impossibile registrare il credito:', errore);
      finestra.close();
      alert(errore.message || 'Non è stato possibile registrare il credito. Riprova.');
      return null;
    }
  };

  const emettiRicevutaPagamento = async dati => {
    if (!allievoPerRicevuta || emissioneRicevutaInCorso) return;
    const finestra = window.open('', '_blank', 'width=900,height=900');
    if (!finestra) {
      alert('Il browser ha bloccato la stampa. Consenti i popup e riprova.');
      return;
    }
    setEmissioneRicevutaInCorso(true);
    try {
      const ricevutaRef = doc(collection(db, 'ricevute_pagamenti'));
      const contatoreRef = doc(db, 'impostazioni', 'progressivo_ricevute_pagamenti');
      const allievoRef = doc(db, 'allievi', allievoPerRicevuta.id);
      const ricevuta = await runTransaction(db, async transazione => {
        const contatore = await transazione.get(contatoreRef);
        const snapshotAllievoRicevuta = await transazione.get(allievoRef);
        const ultimoNumero = Number(contatore.exists() ? contatore.data().ultimoNumero : 102);
        const numeroProgressivo = Math.max(103, Number.isFinite(ultimoNumero) ? ultimoNumero + 1 : 103);
        const datiRicevuta = {
          numeroProgressivo,
          allievoId: allievoPerRicevuta.id,
          nomeAllievo: `${allievoPerRicevuta.Cognome || ''} ${allievoPerRicevuta.Nome || ''}`.trim(),
          codiceFiscale: allievoPerRicevuta.CodiceFiscale || '',
          importo: dati.importo,
          dataPagamento: dati.dataPagamento,
          modalitaPagamento: dati.modalitaPagamento,
          descrizione: dati.descrizione,
          emessaDa: utenteCorrente,
          emessaIl: new Date().toISOString()
        };
        transazione.set(contatoreRef, { ultimoNumero: numeroProgressivo, aggiornatoIl: new Date().toISOString(), aggiornatoDa: utenteCorrente }, { merge: true });
        transazione.set(ricevutaRef, datiRicevuta);
        transazione.update(allievoRef, { DataUltimoPagamento: dataPagamentoPiuRecente(snapshotAllievoRicevuta.exists() ? snapshotAllievoRicevuta.data().DataUltimoPagamento : '', dati.dataPagamento) });
        return datiRicevuta;
      });
      stampaRicevutaPagamento(ricevuta, finestra);
      await registraLogOperazione(utenteCorrente, `Emessa ricevuta n. ${ricevuta.numeroProgressivo} per ${ricevuta.nomeAllievo} (${Number(ricevuta.importo).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })})`);
      caricaAllievi();
      return ricevuta;
    } catch (errore) {
      console.error('Impossibile emettere la ricevuta:', errore);
      finestra.close();
      alert('Non è stato possibile emettere la ricevuta. Per garantire il numero progressivo controlla la connessione Internet e riprova.');
    } finally {
      setEmissioneRicevutaInCorso(false);
    }
  };

  const stampaProvaRicevuta = dati => {
    if (!allievoPerRicevuta) return;
    const finestra = window.open('', '_blank', 'width=900,height=900');
    if (!finestra) {
      alert('Il browser ha bloccato la stampa. Consenti i popup e riprova.');
      return;
    }
    stampaRicevutaPagamento({
      ...dati,
      prova: true,
      allievoId: allievoPerRicevuta.id,
      nomeAllievo: `${allievoPerRicevuta.Cognome || ''} ${allievoPerRicevuta.Nome || ''}`.trim(),
      codiceFiscale: allievoPerRicevuta.CodiceFiscale || '',
      emessaDa: utenteCorrente
    }, finestra);
  };

  const allieviFiltrati = allievi.filter(a => {
    const testoCercato = queryPagamenti.toLowerCase().trim();
    const nomeCompleto = `${a.Cognome || ''} ${a.Nome || ''}`.toLowerCase();
    const cf = (a.CodiceFiscale || '').toLowerCase();
    return nomeCompleto.includes(testoCercato) || cf.includes(testoCercato);
  });

  const allieviDaSaldareCount = allievi.filter(a => {
    const st = calcolaStatoPagamentoDinamico(a.DataUltimoPagamento);
    return st.codice !== 'VERDE';
  }).length;

  return (
    <div style={{ color: '#f8f9fa' }}>
      <h3 style={{ color: '#fcc419', fontFamily: 'serif' }}>💳 Pagamenti e Verifica Stato Allievi</h3>

      <div style={{ backgroundColor: '#353b4c', border: '1px solid #4a5266', borderRadius: '12px', padding: '20px', marginTop: '15px' }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#4dabf7' }}>🔍 Cerca Allievo e Verifica Saldo</h4>
        <p style={{ fontSize: '14px', color: '#adb5bd', marginTop: 0 }}>
          Digita il cognome o il Codice Fiscale per verificare lo stato saldo. Puoi anche cliccare nel campo e leggere il Codice Fiscale con la pistola. (Nota: <em>Maestri e Tirocinanti sono esentati e non compaiono in questo elenco</em>).
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input 
            placeholder="🔎 Cerca per nome o spara il Codice Fiscale..." 
            value={queryPagamenti} 
            onChange={e => setQueryPagamenti(e.target.value)}
            style={{ flex: 1, minWidth: '280px', maxWidth: '500px', padding: '12px', fontSize: '15px', borderRadius: '8px', border: '2px solid #fcc419', backgroundColor: '#2b3140', color: '#fff' }}
          />

          <div style={{
            backgroundColor: allieviDaSaldareCount > 0 ? '#c92a2a' : '#2b8a3e',
            color: 'white',
            padding: '10px 16px',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <span>⚠️ Non Paganti / Da Saldare:</span>
            <span style={{
              backgroundColor: '#ffffff',
              color: allieviDaSaldareCount > 0 ? '#c92a2a' : '#2b8a3e',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 'bold'
            }}>
              {allieviDaSaldareCount}
            </span>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', backgroundColor: '#2b3140', borderRadius: '8px', overflow: 'hidden' }}>
          <thead>
            <tr style={{ backgroundColor: '#4a5266', color: '#fcc419', textAlign: 'left' }}>
              <th style={{ padding: '12px' }}>Allievo (Cognome e Nome)</th>
              <th style={{ padding: '12px' }}>Stato Attuale</th>
              <th style={{ padding: '12px' }}>Ultimo Pagamento</th>
              <th style={{ padding: '12px' }}>Credito</th>
              <th style={{ padding: '12px' }}>Azione Saldo</th>
              <th style={{ padding: '12px' }}>Sollecito</th>
            </tr>
          </thead>
          <tbody>
            {allieviFiltrati.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: '#ff6b6b', fontWeight: 'bold' }}>
                  Nessun allievo trovato.
                </td>
              </tr>
            ) : (
              allieviFiltrati.map(a => {
                const stato = calcolaStatoPagamentoDinamico(a.DataUltimoPagamento);
                const creditoDisponibile = Math.max(0, Number(a.CreditoResiduo || 0));

                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid #4a5266' }}>
                    <td style={{ padding: '12px' }}>
                      <strong>{a.Cognome} {a.Nome}</strong> ({a.Sesso || 'M'})<br />
                      <small style={{ color: '#adb5bd' }}>Tel: {a.Telefono || 'N/D'}</small>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <BadgeStatoPagamento dataUltimoPagamento={a.DataUltimoPagamento} isMaestro={a.isMaestro} isTirocinante={a.isTirocinante} docenteScuola={a.docenteScuola} esenteMensilita={a.esenteMensilita} />
                    </td>
                    <td style={{ padding: '12px', color: '#ced4da' }}>
                      {a.DataUltimoPagamento ? `🗓️ ${formattaDataIT(a.DataUltimoPagamento)}` : 'Nessuno'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <strong style={{ color: creditoDisponibile > 0 ? '#e599f7' : '#adb5bd' }}>{creditoDisponibile.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</strong>
                      {creditoDisponibile > 0 && <small style={{ display: 'block', marginTop: 3, color: '#d9b8e4' }}>Accredito disponibile</small>}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input 
                          type="date" 
                          value={dateSelezionate[a.id] || dataOggiISO()} 
                          onChange={e => setDateSelezionate({ ...dateSelezionate, [a.id]: e.target.value })}
                          style={{ padding: '6px', backgroundColor: '#353b4c', color: '#fff', border: '1px solid #495057', borderRadius: '4px' }}
                        />
                        <button 
                          onClick={() => registraPagamentoRapido(a)}
                          disabled={salvataggioSaldoId === a.id}
                          style={{ backgroundColor: '#2b8a3e', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          {salvataggioSaldoId === a.id ? 'Salvataggio…' : '✅ Segna come pagato'}
                        </button>
                        {a.ultimoSaldoRapido?.operazioneId && <button type="button" onClick={() => setAllievoPerStornoSaldo(a)} style={{ backgroundColor: '#c92a2a', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>↩ Storna pagamento</button>}
                        {!a.ultimoSaldoRapido?.operazioneId && a.DataUltimoPagamento && <button type="button" onClick={() => setAllievoPerCorrezioneSaldo(a)} style={{ backgroundColor: '#b26a00', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>✎ Correggi saldo</button>}
                        <button
                          type="button"
                          onClick={() => setAllievoPerRicevuta(a)}
                          style={{ backgroundColor: '#8e3fa7', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          🧾 Ricevuta
                        </button>
                        <button
                          type="button"
                          onClick={() => setOperazioneCredito({ allievo: a, tipo: 'accredito' })}
                          style={{ backgroundColor: '#6d287f', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          💶 Pagamento con resto
                        </button>
                        {creditoDisponibile > 0 && <button
                          type="button"
                          onClick={() => setOperazioneCredito({ allievo: a, tipo: 'utilizzo' })}
                          style={{ backgroundColor: '#e67700', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          ↘ Usa accredito
                        </button>}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {stato.codice !== 'VERDE' ? (
                        <button 
                          onClick={() => inviaMessaggioWhatsApp(a.Telefono, a.Nome, utenteCorrente)}
                          style={{ backgroundColor: '#2b8a3e', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
                        >
                          📲 WhatsApp
                        </button>
                      ) : (
                        <span style={{ color: '#51cf66', fontSize: '12px', fontWeight: 'bold' }}>✅ In Regola</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {allievoPerRicevuta && <ModalRicevutaPagamento key={allievoPerRicevuta.id} allievo={allievoPerRicevuta} inEmissione={emissioneRicevutaInCorso} onChiudi={() => setAllievoPerRicevuta(null)} onEmetti={emettiRicevutaPagamento} onStampaProva={stampaProvaRicevuta} />}
      {operazioneCredito && <ModalCreditoAllievo key={`${operazioneCredito.allievo.id}-${operazioneCredito.tipo}`} allievo={operazioneCredito.allievo} tipo={operazioneCredito.tipo} onChiudi={() => setOperazioneCredito(null)} onRegistra={registraOperazioneCredito} />}
      {allievoPerStornoSaldo && <ModalStornoSaldo allievo={allievoPerStornoSaldo} onChiudi={() => setAllievoPerStornoSaldo(null)} onConferma={stornaPagamentoRapido} />}
      {allievoPerCorrezioneSaldo && <ModalCorreggiSaldoPrecedente allievo={allievoPerCorrezioneSaldo} onChiudi={() => setAllievoPerCorrezioneSaldo(null)} onConferma={correggiSaldoPrecedente} />}
    </div>
  );
}

function LezioniPrivate({ utenteCorrente, utenteLoggato }) {
  const location = useLocation();
  const [lezioni, setLezioni] = useState([]);
  const [allievi, setAllievi] = useState([]);
  const [richiesteApp, setRichiesteApp] = useState([]);
  const [docentiPrivati, setDocentiPrivati] = useState(() => MAESTRI.map(username => ({ username })));
  const [maestro, setMaestro] = useState(MAESTRI[0]);
  
  const [queryAllievo, setQueryAllievo] = useState('');
  const [allievoSelezionatoObj, setAllievoSelezionatoObj] = useState(null);
  const [mostraTendinaAllievi, setMostraTendinaAllievi] = useState(false);
  const containerAllieviRef = useRef(null);

  const [dataLezione, setDataLezione] = useState(dataOggiISO());
  const [oraInizio, setOraInizio] = useState('15:00');
  const [ripetiOgniMese, setRipetiOgniMese] = useState(false);
  const [numeroMesiRipetizione, setNumeroMesiRipetizione] = useState(6);
  const [frequenzaRipetizione, setFrequenzaRipetizione] = useState('settimanale');
  const [lezioneInModifica, setLezioneInModifica] = useState(null);
  const [bozzaLezione, setBozzaLezione] = useState({ data: '', ora: '', maestro: '' });
  const [fasceDaModificare, setFasceDaModificare] = useState(() => Object.fromEntries(Object.entries(DISPONIBILITA_LEZIONI_PREDEFINITA).map(([giorno, orari]) => [giorno, orari.join(', ')])));
  const [indisponibilitaDocenti, setIndisponibilitaDocenti] = useState({ periodi: [] });
  const [fascePerData, setFascePerData] = useState({});
  const [giorniOrariSelezionati, setGiorniOrariSelezionati] = useState([]);
  const [orariDataSpecifica, setOrariDataSpecifica] = useState([]);
  const [meseOrariSpeciali, setMeseOrariSpeciali] = useState(() => dataOggiISO().slice(0, 7));
  const [meseCalendarioDocenti, setMeseCalendarioDocenti] = useState(() => dataOggiISO().slice(0, 7));
  const [giornoCalendarioSelezionato, setGiornoCalendarioSelezionato] = useState(null);
  const [impegnoDaInserire, setImpegnoDaInserire] = useState({ dataInizio: dataOggiISO(), dataFine: dataOggiISO(), maestri: [], gestioneManuale: false });
  const [maestroSostituzioni, setMaestroSostituzioni] = useState(MAESTRI[0]);
  const [periodoSostituzioni, setPeriodoSostituzioni] = useState({ dataInizio: dataOggiISO(), dataFine: dataOggiISO() });
  const [sostituzioniManuali, setSostituzioniManuali] = useState({});
  const [ricercaElencoLezioni, setRicercaElencoLezioni] = useState('');
  const [mostraPassateElenco, setMostraPassateElenco] = useState(false);
  const ruolo = String(utenteLoggato?.ruolo || '').toLowerCase();
  const docentiPrivatiUnici = docentiLezioniPrivateSenzaDuplicati(docentiPrivati);
  const nomiDocentiPrivati = docentiPrivatiUnici.map(docente => String(docente.username || '').trim()).filter(Boolean);
  const chiaveDocentiPrivati = nomiDocentiPrivati.join('|');
  const docenteCorrentePrivato = docentiPrivatiUnici.some(docente => docente.id === utenteLoggato?.id || String(docente.username || '').trim().toLocaleLowerCase('it-IT') === String(utenteLoggato?.username || '').trim().toLocaleLowerCase('it-IT'));
  const puoGestireRichiesteApp = ruolo === 'admin' || docenteCorrentePrivato;
  const docentePrivatoDaNome = nome => docentiPrivatiUnici.find(docente => String(docente.username || '').trim().toLocaleLowerCase('it-IT') === String(nome || '').trim().toLocaleLowerCase('it-IT'));
  const avvisaAssegnazioneLezione = lezioneId => {
    if (!lezioneId) return;
    intestazioniAutenticate({ 'Content-Type': 'application/json' }).then(intestazioni => fetch(URL_SERVIZIO_ASSEGNAZIONE_LEZIONE, {
      method: 'POST', headers: intestazioni, body: JSON.stringify({ lezioneId })
    })).then(async risposta => {
      const esito = await risposta.json().catch(() => ({}));
      if (!risposta.ok || !esito.inviato) console.warn('Notifica assegnazione lezione non inviata:', esito.errore || esito.motivo || 'nessun telefono registrato');
    }).catch(errore => console.warn('Servizio notifiche assegnazioni non raggiungibile:', errore));
  };

  const caricaDati = async () => {
    const snapLezioni = await leggiRaccolta('lezioni_private');
    const snapAllievi = await leggiRaccolta('allievi');
    setLezioni(snapLezioni.docs.map(d => ({ id: d.id, ...d.data() })));
    setAllievi(ordinaAllieviPerCognomeNome(snapAllievi.docs.map(d => ({ id: d.id, ...d.data() }))));
  };

  useEffect(() => {
    // Le prenotazioni e l'elenco allievi restano sincronizzati fra tutte le
    // postazioni della segreteria senza dover aggiornare la pagina.
    const annullaAscoltoLezioni = ascoltaRaccolta('lezioni_private', snap => {
      setLezioni(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const annullaAscoltoAllievi = ascoltaRaccolta('allievi', snap => {
      setAllievi(ordinaAllieviPerCognomeNome(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    });
    const annullaAscoltoRichieste = ascoltaRaccolta('richieste_lezioni_private', snap => {
      const richiesteVisibili = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(richiesta => ['in_attesa', 'richiesta_disponibilita', 'orario_proposto'].includes(richiesta.stato))
        .filter(richiesta => ruolo === 'admin' || richiesta.maestroId === utenteLoggato?.id || String(richiesta.maestroNome || '').toLowerCase() === String(utenteLoggato?.username || '').toLowerCase())
        .sort((a, b) => `${a.dataDesiderata || ''} ${a.oraDesiderata || ''}`.localeCompare(`${b.dataDesiderata || ''} ${b.oraDesiderata || ''}`));
      setRichiesteApp(richiesteVisibili);
    }, errore => console.error('Impossibile sincronizzare le richieste dall’App Allievi:', errore));
    const annullaAscoltoDisponibilita = onSnapshot(doc(db, 'impostazioni', 'lezioni_private'), snapshot => {
      if (snapshot.exists() && snapshot.data().fasce) {
        const impostazioni = snapshot.data();
        const fasce = completaDisponibilitaLezioniPrivate(impostazioni.fasce);
        setFasceDaModificare(Object.fromEntries([1, 2, 3, 4, 5, 6].map(giorno => [giorno, Array.isArray(fasce[giorno]) ? fasce[giorno].join(', ') : ''])));
        setIndisponibilitaDocenti(impostazioni.indisponibilitaDocenti || { periodi: [] });
        setFascePerData(impostazioni.fascePerData && typeof impostazioni.fascePerData === 'object' ? impostazioni.fascePerData : {});
        // Pubblica una sola volta i nuovi orari del sabato anche per le
        // impostazioni create prima dell'aggiunta del sabato.
        if (!Array.isArray(impostazioni.fasce?.[6])) {
          setDoc(doc(db, 'impostazioni', 'lezioni_private'), { fasce, aggiornataIl: new Date().toISOString(), aggiornataDa: utenteCorrente }, { merge: true }).catch(errore => console.error('Impossibile aggiungere le fasce del sabato:', errore));
        }
      } else {
        setDoc(doc(db, 'impostazioni', 'lezioni_private'), { fasce: DISPONIBILITA_LEZIONI_PREDEFINITA, aggiornataIl: new Date().toISOString(), aggiornataDa: utenteCorrente }).catch(errore => console.error('Impossibile inizializzare le disponibilità:', errore));
      }
    }, errore => console.error('Impossibile leggere le disponibilità:', errore));

    const handleClickOutside = (event) => {
      if (containerAllieviRef.current && !containerAllieviRef.current.contains(event.target)) {
        setMostraTendinaAllievi(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      annullaAscoltoLezioni();
      annullaAscoltoAllievi();
      annullaAscoltoRichieste();
      annullaAscoltoDisponibilita();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ruolo, utenteLoggato?.id, utenteLoggato?.username, utenteCorrente]);

  useEffect(() => ascoltaRaccolta('utenti_sistema', snapshot => {
    const elenco = docentiLezioniPrivateSenzaDuplicati(snapshot.docs
      .map(documento => ({ id: documento.id, ...documento.data() }))
      .filter(utenteGestisceLezioniPrivate));
    setDocentiPrivati(elenco.length ? elenco : MAESTRI.map(username => ({ username })));
  }, errore => console.error('Impossibile leggere i docenti delle lezioni private:', errore)), []);

  useEffect(() => {
    const elencoDocenti = chiaveDocentiPrivati.split('|').filter(Boolean);
    const primoDocente = elencoDocenti[0] || MAESTRI[0];
    setMaestro(corrente => elencoDocenti.includes(corrente) ? corrente : primoDocente);
    setMaestroSostituzioni(corrente => elencoDocenti.includes(corrente) ? corrente : primoDocente);
  }, [chiaveDocentiPrivati]);

  useEffect(() => {
    const destinazione = String(location.hash || '').replace('#', '');
    if (!destinazione) return undefined;
    const timer = setTimeout(() => document.getElementById(destinazione)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    return () => clearTimeout(timer);
  }, [location.hash, lezioni.length]);

  const allieviFiltratiLezioni = allievi.filter(a => {
    const q = queryAllievo.toLowerCase().trim();
    const nomeCompleto = `${a.Cognome || ''} ${a.Nome || ''}`.toLowerCase();
    const cf = (a.CodiceFiscale || '').toLowerCase();
    return nomeCompleto.includes(q) || cf.includes(q);
  });

  const calcolaOraFine = (ora) => {
    if (!ora) return '';
    const [h, m] = ora.split(':').map(Number);
    const dataObj = new Date(2000, 0, 1, h, m);
    dataObj.setMinutes(dataObj.getMinutes() + 45);
    const fineH = dataObj.getHours().toString().padStart(2, '0');
    const fineM = dataObj.getMinutes().toString().padStart(2, '0');
    return `${fineH}:${fineM}`;
  };

  // Calendario mensile: include anche eventuali docenti storici, così nessuna
  // lezione già registrata sparisce dal riepilogo se un docente viene disabilitato.
  const nomiCalendarioDocenti = Array.from(new Set([
    ...nomiDocentiPrivati,
    ...lezioni.map(lezione => String(lezione.Maestro || '').trim()).filter(Boolean)
  ])).sort((prima, dopo) => prima.localeCompare(dopo, 'it'));
  const lezioniMeseCalendario = lezioni.filter(lezione => String(lezione.Data || '').startsWith(`${meseCalendarioDocenti}-`));
  const datiCalendarioDocenti = nomiCalendarioDocenti.map(nome => {
    const appuntamentiStorici = lezioni
      .filter(lezione => String(lezione.Maestro || '').trim() === nome)
      .sort((prima, dopo) => `${prima.Data || ''} ${prima.OraInizio || ''}`.localeCompare(`${dopo.Data || ''} ${dopo.OraInizio || ''}`));
    const appuntamenti = appuntamentiStorici.filter(lezione => String(lezione.Data || '').startsWith(`${meseCalendarioDocenti}-`));
    const haGiornoSelezionato = giornoCalendarioSelezionato?.docente === nome;
    const dataOggiNelMese = dataOggiISO().startsWith(`${meseCalendarioDocenti}-`) ? dataOggiISO() : '';
    const dataGiornoSelezionato = haGiornoSelezionato ? giornoCalendarioSelezionato.data : dataOggiNelMese;
    const appuntamentiGiornoSelezionato = dataGiornoSelezionato ? appuntamenti.filter(lezione => lezione.Data === dataGiornoSelezionato) : [];
    return {
      nome,
      appuntamenti,
      appuntamentiStorici,
      perGiorno: appuntamenti.reduce((giorni, lezione) => ({ ...giorni, [lezione.Data]: [...(giorni[lezione.Data] || []), lezione] }), {}),
      dataGiornoSelezionato,
      haGiornoSelezionato,
      appuntamentiGiornoSelezionato
    };
  });
  const [annoCalendarioDocenti, meseCalendarioDocentiNumero] = String(meseCalendarioDocenti || '').split('-').map(Number);
  const giorniDelMeseCalendario = Number.isFinite(annoCalendarioDocenti) && Number.isFinite(meseCalendarioDocentiNumero)
    ? new Date(annoCalendarioDocenti, meseCalendarioDocentiNumero, 0).getDate() : 0;
  // Lunedì è la prima colonna (Date.getDay() usa domenica = 0).
  const spaziPrimaDelMese = giorniDelMeseCalendario ? (new Date(annoCalendarioDocenti, meseCalendarioDocentiNumero - 1, 1).getDay() + 6) % 7 : 0;
  const etichettaMeseCalendario = Number.isFinite(annoCalendarioDocenti) && Number.isFinite(meseCalendarioDocentiNumero)
    ? new Intl.DateTimeFormat('it-IT', { month: 'long', year: 'numeric' }).format(new Date(annoCalendarioDocenti, meseCalendarioDocentiNumero - 1, 1)) : '';

  const dateRicorrentiMensili = (dataBase, numeroMesi) => {
    const [anno, mese, giorno] = dataBase.split('-').map(Number);
    return Array.from({ length: numeroMesi }, (_, indice) => {
      const meseAssoluto = mese - 1 + indice;
      const annoDestinazione = anno + Math.floor(meseAssoluto / 12);
      const meseDestinazione = ((meseAssoluto % 12) + 12) % 12;
      const ultimoGiorno = new Date(annoDestinazione, meseDestinazione + 1, 0).getDate();
      const data = new Date(annoDestinazione, meseDestinazione, Math.min(giorno, ultimoGiorno));
      return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
    });
  };

  const dateRicorrentiSettimanali = (dataBase, numeroSettimane) => {
    const base = new Date(`${dataBase}T12:00:00`);
    return Array.from({ length: numeroSettimane }, (_, indice) => {
      const data = new Date(base);
      data.setDate(base.getDate() + indice * 7);
      return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
    });
  };

  const dateRicorrentiQuindicinali = (dataBase, numeroLezioni) => {
    const base = new Date(`${dataBase}T12:00:00`);
    return Array.from({ length: numeroLezioni }, (_, indice) => {
      const data = new Date(base);
      data.setDate(base.getDate() + indice * 14);
      return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
    });
  };

  const fineRicorrenzaLezione = lezione => {
    if (!(lezione?.ricorrenzaMensile || lezione?.ricorrenzaSettimanale || lezione?.ricorrenza)) return '';
    if (lezione.dataFineRicorrenza) return lezione.dataFineRicorrenza;
    const totale = Number(lezione.totaleRicorrenze || 1);
    const numero = Number(lezione.numeroProgressivoRicorrenza || 1);
    if (lezione.frequenzaRicorrenza === 'quindicinale' || lezione.ricorrenzaQuindicinale) return dateRicorrentiQuindicinali(lezione.Data, Math.max(1, totale - numero + 1)).at(-1);
    return lezione.frequenzaRicorrenza === 'settimanale' || lezione.ricorrenzaSettimanale ? dateRicorrentiSettimanali(lezione.Data, Math.max(1, totale - numero + 1)).at(-1) : dateRicorrentiMensili(lezione.Data, Math.max(1, totale - numero + 1)).at(-1);
  };
  const etichettaRicorrenza = lezione => lezione.frequenzaRicorrenza === 'quindicinale' || lezione.ricorrenzaQuindicinale ? 'Ogni 2 settimane' : lezione.frequenzaRicorrenza === 'settimanale' || lezione.ricorrenzaSettimanale ? 'Settimanale' : 'Mensile';

  const salvaDisponibilitaApp = async () => {
    const fasce = Object.fromEntries([1, 2, 3, 4, 5, 6].map(giorno => [giorno, String(fasceDaModificare[giorno] || '').split(',').map(orario => orario.trim()).filter(orario => /^([01]\d|2[0-3]):[0-5]\d$/.test(orario)).sort()]));
    const numeroOrariPubblicati = Object.values(fasce).reduce((totale, orari) => totale + orari.length, 0);
    try {
      await setDoc(doc(db, 'impostazioni', 'lezioni_private'), { fasce, aggiornataIl: new Date().toISOString(), aggiornataDa: utenteCorrente }, { merge: true });
      await registraLogOperazione(utenteCorrente, 'Aggiornate le disponibilità delle lezioni private per l’App Allievi');
      alert(numeroOrariPubblicati ? 'Disponibilità pubblicata nell’App Allievi.' : 'Nessun orario è disponibile: le nuove richieste di lezioni private sono temporaneamente sospese nell’App Allievi.');
    } catch (errore) { console.error(errore); alert('Non è stato possibile salvare le disponibilità.'); }
  };

  const orariSelezionabiliPerData = data => {
    if (!data) return [];
    const giorno = new Date(`${data}T12:00:00`).getDay();
    return [...(giorno === 6 ? ORARI_SABATO_LEZIONI_PRIVATE : ORARI_SELEZIONABILI_LEZIONI)].sort();
  };
  const orariSettimanaliPerData = data => {
    const giorno = new Date(`${data}T12:00:00`).getDay();
    return String(fasceDaModificare[giorno] || '').split(',').map(valore => valore.trim()).filter(Boolean).sort();
  };
  const orariEffettiviPerData = data => Array.isArray(fascePerData[data]) ? fascePerData[data] : orariSettimanaliPerData(data);
  const selezionaGiornoOrari = data => {
    if (!data || data < dataOggiISO()) return;
    setGiorniOrariSelezionati(correnti => {
      const aggiornati = correnti.includes(data) ? correnti.filter(valore => valore !== data) : [...correnti, data].sort();
      // Con un solo giorno selezionato mostriamo gli orari già validi per quel giorno.
      if (aggiornati.length === 1) setOrariDataSpecifica([...orariEffettiviPerData(aggiornati[0])]);
      if (aggiornati.length === 0) setOrariDataSpecifica([]);
      return aggiornati;
    });
  };
  const cambiaOrarioDataSpecifica = orario => setOrariDataSpecifica(correnti => correnti.includes(orario) ? correnti.filter(valore => valore !== orario) : [...correnti, orario].sort());
  const elencoGiorniIT = giorni => giorni.map(formattaDataIT).join(', ');
  const salvaOrariGiorniSelezionati = async (chiudi = false) => {
    if (!giorniOrariSelezionati.length) return alert('Seleziona prima uno o più giorni nel calendario.');
    const perData = Object.fromEntries(giorniOrariSelezionati.map(data => [data, chiudi ? [] : orariDataSpecifica.filter(orario => orariSelezionabiliPerData(data).includes(orario)).sort()]));
    try {
      await setDoc(doc(db, 'impostazioni', 'lezioni_private'), { fascePerData: perData, aggiornataIl: new Date().toISOString(), aggiornataDa: utenteCorrente }, { merge: true });
      await registraLogOperazione(utenteCorrente, `Orari lezioni private ${chiudi ? 'chiusi' : 'pubblicati'} per: ${elencoGiorniIT(giorniOrariSelezionati)}`);
      alert(chiudi ? `Giorni chiusi: ${elencoGiorniIT(giorniOrariSelezionati)}.` : `Orari pubblicati nell’App Allievi per: ${elencoGiorniIT(giorniOrariSelezionati)}.`);
      setGiorniOrariSelezionati([]);
      setOrariDataSpecifica([]);
    } catch (errore) { console.error(errore); alert('Non è stato possibile salvare gli orari.'); }
  };
  const ripristinaSettimanaliGiorniSelezionati = async () => {
    if (!giorniOrariSelezionati.length) return;
    if (!window.confirm(`Nei giorni selezionati torneranno a valere gli orari settimanali. Continuare?`)) return;
    try {
      await updateDoc(doc(db, 'impostazioni', 'lezioni_private'), { ...Object.fromEntries(giorniOrariSelezionati.map(data => [`fascePerData.${data}`, deleteField()])), aggiornataIl: new Date().toISOString(), aggiornataDa: utenteCorrente });
      setGiorniOrariSelezionati([]);
      setOrariDataSpecifica([]);
    } catch (errore) { console.error(errore); alert('Non è stato possibile ripristinare gli orari settimanali.'); }
  };
  const [annoOrariSpeciali, numeroMeseOrariSpeciali] = String(meseOrariSpeciali).split('-').map(Number);
  const celleCalendarioOrari = (() => {
    const giorniNelMese = new Date(annoOrariSpeciali, numeroMeseOrariSpeciali, 0).getDate();
    const celle = [];
    for (let giorno = 1; giorno <= giorniNelMese; giorno++) {
      const data = `${meseOrariSpeciali}-${String(giorno).padStart(2, '0')}`;
      const giornoSettimana = new Date(`${data}T12:00:00`).getDay();
      if (giornoSettimana === 0) continue; // domenica esclusa
      if (!celle.length) for (let vuoto = 1; vuoto < giornoSettimana; vuoto++) celle.push(null);
      celle.push(data);
    }
    while (celle.length % 6) celle.push(null);
    return celle;
  })();
  const cambiaMeseOrariSpeciali = direzione => {
    const nuovo = new Date(annoOrariSpeciali, numeroMeseOrariSpeciali - 1 + direzione, 1, 12);
    const valore = `${nuovo.getFullYear()}-${String(nuovo.getMonth() + 1).padStart(2, '0')}`;
    if (valore < dataOggiISO().slice(0, 7)) return;
    setMeseOrariSpeciali(valore);
  };
  const orariMostratiNelCalendario = [...new Set(giorniOrariSelezionati.flatMap(orariSelezionabiliPerData))].sort();

  const cambiaFasciaDisponibile = (giorno, orario) => {
    setFasceDaModificare(correnti => {
      const selezionati = String(correnti[giorno] || '').split(',').map(valore => valore.trim()).filter(Boolean);
      const aggiornati = selezionati.includes(orario) ? selezionati.filter(valore => valore !== orario) : [...selezionati, orario];
      const orariDelGiorno = giorno === 6 ? ORARI_SABATO_LEZIONI_PRIVATE : ORARI_SELEZIONABILI_LEZIONI;
      return { ...correnti, [giorno]: orariDelGiorno.filter(valore => aggiornati.includes(valore)).join(', ') };
    });
  };

  const cambiaImpegnoDaInserire = nomeMaestro => setImpegnoDaInserire(corrente => {
    const maestri = Array.isArray(corrente?.maestri) ? corrente.maestri : [];
    return { ...corrente, maestri: maestri.includes(nomeMaestro) ? maestri.filter(nome => nome !== nomeMaestro) : [...maestri, nomeMaestro] };
  });
  const salvaIndisponibilitaOggi = async () => {
    const dataInizio = String(impegnoDaInserire?.dataInizio || dataOggiISO());
    const dataFine = String(impegnoDaInserire?.dataFine || dataInizio);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dataInizio) || !/^\d{4}-\d{2}-\d{2}$/.test(dataFine) || dataFine < dataInizio) return alert('Inserisci un periodo valido: la data finale non può essere precedente a quella iniziale.');
    const maestri = Array.isArray(impegnoDaInserire?.maestri) ? impegnoDaInserire.maestri : [];
    if (!maestri.length) return alert('Spunta almeno un Maestro impegnato prima di salvare.');
    const periodiEsistenti = periodiImpegniMaestri(indisponibilitaDocenti);
    const periodoUguale = periodiEsistenti.findIndex(periodo => periodo.dataInizio === dataInizio && periodo.dataFine === dataFine);
    const nuovoPeriodo = { id: periodoUguale >= 0 ? periodiEsistenti[periodoUguale].id : `impegno-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, dataInizio, dataFine, maestri: periodoUguale >= 0 ? Array.from(new Set([...(periodiEsistenti[periodoUguale].maestri || []), ...maestri])) : maestri, gestioneManuale: Boolean(impegnoDaInserire.gestioneManuale) };
    const periodi = (periodoUguale >= 0 ? periodiEsistenti.map((periodo, indice) => indice === periodoUguale ? nuovoPeriodo : periodo) : [...periodiEsistenti, nuovoPeriodo]).sort((prima, dopo) => String(prima.dataInizio || '').localeCompare(String(dopo.dataInizio || '')) || String(prima.dataFine || '').localeCompare(String(dopo.dataFine || '')));
    const dati = { periodi };
    const agendaProvvisoria = lezioni.map(lezione => ({ ...lezione }));
    const spostamenti = [];
    const lezioniNonSmistabili = [];
    const maestroSegnatoImpegnato = nome => maestri.some(maestroImpegnato => String(maestroImpegnato).trim().toLocaleLowerCase('it-IT') === String(nome || '').trim().toLocaleLowerCase('it-IT'));
    for (const lezione of impegnoDaInserire.gestioneManuale ? [] : agendaProvvisoria.filter(lezione => maestroSegnatoImpegnato(lezione.Maestro) && lezione.Data >= dataInizio && lezione.Data <= dataFine)) {
      const anagrafica = allievi.find(allievo => allievo.id === lezione.allievoId) || allievi.find(allievo => `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim().toLocaleLowerCase('it-IT') === String(lezione.Allievo || '').trim().toLocaleLowerCase('it-IT'));
      const eta = lezione.etaAllievo ?? calcolaEtaAllievo(anagrafica?.DataNascita, anagrafica?.CodiceFiscale);
      const sesso = lezione.sessoAllievo || anagrafica?.Sesso || '';
      const ragazzaFinoA13Anni = eta !== null && Number(eta) <= 13 && /^(f|femmina)/i.test(String(sesso).trim());
      const candidati = ragazzaFinoA13Anni ? nomiDocentiPrivati : nomiDocentiPrivati.filter(nome => String(nome).trim().toLocaleLowerCase('it-IT') !== 'luisa pagano');
      const maestroAlternativo = candidati.find(candidato => !maestroImpegnatoNelPeriodo(dati, candidato, lezione.Data)
        && !docenteLezionePrivataSovrapposto(agendaProvvisoria, { docente: candidato, data: lezione.Data, oraInizio: lezione.OraInizio, oraFine: lezione.OraFine || oraFineLezionePrivata(lezione.OraInizio), escludiId: lezione.id })
        && numeroLezioniPrivateSovrapposte(agendaProvvisoria, { data: lezione.Data, oraInizio: lezione.OraInizio, oraFine: lezione.OraFine || oraFineLezionePrivata(lezione.OraInizio), escludiId: lezione.id }) < MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE);
      if (!maestroAlternativo) {
        lezioniNonSmistabili.push(`${lezione.Allievo} · ${formattaDataIT(lezione.Data)} ore ${lezione.OraInizio}`);
        continue;
      }
      const maestroPrecedente = lezione.Maestro;
      lezione.Maestro = maestroAlternativo;
      spostamenti.push({ id: lezione.id, da: maestroPrecedente, a: maestroAlternativo, allievo: lezione.Allievo });
    }
    if (lezioniNonSmistabili.length) return alert(`Non posso salvare questo impegno perché non c’è un Maestro libero per: ${lezioniNonSmistabili.join('; ')}. Sposta prima queste lezioni dall’agenda.`);
    try {
      const batch = writeBatch(db);
      batch.set(doc(db, 'impostazioni', 'lezioni_private'), { indisponibilitaDocenti: dati, aggiornataIl: new Date().toISOString(), aggiornataDa: utenteCorrente }, { merge: true });
      spostamenti.forEach(spostamento => {
        const docenteAssegnato = docentePrivatoDaNome(spostamento.a);
        batch.update(doc(db, 'lezioni_private', spostamento.id), { Maestro: spostamento.a, maestroId: docenteAssegnato?.id || '', notificaAssegnazioneInviata: false, riorganizzataDaAssenza: true, ultimoSpostamento: `Da ${spostamento.da} a ${spostamento.a}`, ultimaModifica: new Date().toISOString(), modificataDa: utenteCorrente });
      });
      await batch.commit();
      spostamenti.forEach(spostamento => avvisaAssegnazioneLezione(spostamento.id));
      const periodo = dataInizio === dataFine ? formattaDataIT(dataInizio) : `dal ${formattaDataIT(dataInizio)} al ${formattaDataIT(dataFine)}`;
      setIndisponibilitaDocenti(dati);
      setImpegnoDaInserire({ dataInizio: dataOggiISO(), dataFine: dataOggiISO(), maestri: [], gestioneManuale: false });
      if (impegnoDaInserire.gestioneManuale) {
        setMaestroSostituzioni(maestri[0]);
        setPeriodoSostituzioni({ dataInizio, dataFine });
      }
      await registraLogOperazione(utenteCorrente, `Segnati impegnati per lezioni private ${periodo}: ${maestri.join(', ')}${spostamenti.length ? `. Smistate automaticamente ${spostamenti.length} lezioni` : ''}`);
      alert(`Impegno salvato ${periodo}. Le nuove richieste verranno assegnate agli altri Maestri liberi.${impegnoDaInserire.gestioneManuale ? ' Le lezioni già in agenda restano da smistare nella sezione Sostituzioni manuali.' : spostamenti.length ? ` Ho smistato automaticamente ${spostamenti.length} lezione/i già in agenda.` : ''}`);
    } catch (errore) { console.error(errore); alert('Non è stato possibile salvare gli impegni dei Maestri.'); }
  };
  const annullaImpegnoMaestri = async id => {
    const periodi = periodiImpegniMaestri(indisponibilitaDocenti).filter(periodo => periodo.id !== id);
    try {
      await setDoc(doc(db, 'impostazioni', 'lezioni_private'), { indisponibilitaDocenti: { periodi }, aggiornataIl: new Date().toISOString(), aggiornataDa: utenteCorrente }, { merge: true });
      setIndisponibilitaDocenti({ periodi });
      await registraLogOperazione(utenteCorrente, 'Annullato un impegno dei Maestri per le lezioni private');
    } catch (errore) { console.error(errore); alert('Non è stato possibile annullare l’impegno del Maestro.'); }
  };
  const annullaImpegniMaestri = async () => {
    if (!window.confirm('Vuoi annullare tutti gli impegni dei Maestri attualmente registrati?')) return;
    const datiVuoti = { periodi: [] };
    try {
      await setDoc(doc(db, 'impostazioni', 'lezioni_private'), { indisponibilitaDocenti: datiVuoti, aggiornataIl: new Date().toISOString(), aggiornataDa: utenteCorrente }, { merge: true });
      setIndisponibilitaDocenti(datiVuoti);
      await registraLogOperazione(utenteCorrente, 'Annullati gli impegni dei Maestri per le lezioni private');
      alert('Gli impegni dei Maestri sono stati annullati.');
    } catch (errore) { console.error(errore); alert('Non è stato possibile annullare gli impegni dei Maestri.'); }
  };
  const lezioniDaSostituire = lezioni.filter(lezione => lezione.Maestro === maestroSostituzioni && lezione.Data >= periodoSostituzioni.dataInizio && lezione.Data <= periodoSostituzioni.dataFine);
  const salvaSostituzioniManuali = async () => {
    const piani = lezioniDaSostituire.map(lezione => ({ lezione, maestro: sostituzioniManuali[lezione.id] })).filter(piano => piano.maestro && piano.maestro !== maestroSostituzioni);
    if (!piani.length) return alert('Scegli almeno una lezione e il relativo Maestro sostituto.');
    const agendaProvvisoria = lezioni.filter(lezione => !piani.some(piano => piano.lezione.id === lezione.id));
    for (const piano of piani) {
      if (maestroImpegnatoNelPeriodo(indisponibilitaDocenti, piano.maestro, piano.lezione.Data)) return alert(`${piano.maestro} è segnato/a come impegnato/a il ${formattaDataIT(piano.lezione.Data)}.`);
      const conflittoDocente = docenteLezionePrivataSovrapposto(agendaProvvisoria, { docente: piano.maestro, data: piano.lezione.Data, oraInizio: piano.lezione.OraInizio, oraFine: piano.lezione.OraFine || oraFineLezionePrivata(piano.lezione.OraInizio) });
      if (conflittoDocente) return alert(`${piano.maestro} ha già una lezione privata il ${formattaDataIT(piano.lezione.Data)} dalle ${conflittoDocente.OraInizio} alle ${conflittoDocente.OraFine || oraFineLezionePrivata(conflittoDocente.OraInizio)}.`);
      if (numeroLezioniPrivateSovrapposte(agendaProvvisoria, { data: piano.lezione.Data, oraInizio: piano.lezione.OraInizio, oraFine: piano.lezione.OraFine || oraFineLezionePrivata(piano.lezione.OraInizio) }) >= MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE) return alert(`In questa fascia ci sono già ${MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE} lezioni private. Non è possibile inserirne una terza.`);
      agendaProvvisoria.push({ ...piano.lezione, Maestro: piano.maestro });
    }
    try {
      const batch = writeBatch(db);
      piani.forEach(piano => {
        const docenteAssegnato = docentePrivatoDaNome(piano.maestro);
        batch.update(doc(db, 'lezioni_private', piano.lezione.id), { Maestro: piano.maestro, maestroId: docenteAssegnato?.id || '', notificaAssegnazioneInviata: false, sostituzioneManuale: true, ultimoSpostamento: `Sostituzione manuale: ${maestroSostituzioni} → ${piano.maestro}`, ultimaModifica: new Date().toISOString(), modificataDa: utenteCorrente });
      });
      await batch.commit();
      piani.forEach(piano => avvisaAssegnazioneLezione(piano.lezione.id));
      setSostituzioniManuali({});
      await registraLogOperazione(utenteCorrente, `Gestite manualmente ${piani.length} sostituzioni di ${maestroSostituzioni}`);
      alert(`${piani.length} lezione/i assegnata/e ai Maestri sostituti.`);
    } catch (errore) { console.error(errore); alert('Non è stato possibile salvare le sostituzioni.'); }
  };

  const prossimoOrarioLezione = (orario) => {
    const [ore, minuti] = String(orario || '').split(':').map(Number);
    const totale = ore * 60 + minuti + 45;
    return `${String(Math.floor(totale / 60)).padStart(2, '0')}:${String(totale % 60).padStart(2, '0')}`;
  };

  const prenotalezione = async (e) => {
    e.preventDefault();
    if (!nomiDocentiPrivati.length) return alert('Prima abilita almeno un docente per le lezioni private in Amministrazione.');
    if (!allievoSelezionatoObj || !dataLezione || !oraInizio) return;
    const massimoRipetizioni = frequenzaRipetizione === 'settimanale' ? 52 : frequenzaRipetizione === 'quindicinale' ? 26 : 12;
    const numeroRipetizioni = Math.min(massimoRipetizioni, Math.max(2, Number(numeroMesiRipetizione || 2)));
    const dateDaPrenotare = ripetiOgniMese ? (frequenzaRipetizione === 'settimanale' ? dateRicorrentiSettimanali(dataLezione, numeroRipetizioni) : frequenzaRipetizione === 'quindicinale' ? dateRicorrentiQuindicinali(dataLezione, numeroRipetizioni) : dateRicorrentiMensili(dataLezione, numeroRipetizioni)) : [dataLezione];
    const oraFineCalcolata = calcolaOraFine(oraInizio);
    const dateDocenteOccupato = dateDaPrenotare.filter(data => docenteLezionePrivataSovrapposto(lezioni, { docente: maestro, data, oraInizio, oraFine: oraFineCalcolata }));
    if (dateDocenteOccupato.length) {
      alert(`${maestro} ha già una lezione privata in queste date e in questa fascia oraria: ${dateDocenteOccupato.map(formattaDataIT).join(', ')}. Scegli un altro docente oppure un altro orario.`);
      return;
    }
    const dateFasciaCompleta = dateDaPrenotare.filter(data => numeroLezioniPrivateSovrapposte(lezioni, { data, oraInizio, oraFine: oraFineCalcolata }) >= MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE);
    if (dateFasciaCompleta.length) {
      alert(`In queste date ci sono già ${MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE} lezioni private nello stesso orario: ${dateFasciaCompleta.map(formattaDataIT).join(', ')}. Scegli un altro orario.`);
      return;
    }

    const nomeAllievoCompleto = `${allievoSelezionatoObj.Cognome} ${allievoSelezionatoObj.Nome}`;
    const dateAllievoOccupato = dateDaPrenotare.filter(data => lezioneSovrappostaPerAllievo(lezioni, {
      allievoId: allievoSelezionatoObj.id,
      allievoNome: nomeAllievoCompleto,
      data,
      oraInizio,
      oraFine: oraFineCalcolata
    }));
    if (dateAllievoOccupato.length) {
      alert(`${nomeAllievoCompleto} ha già una lezione privata sovrapposta in queste date: ${dateAllievoOccupato.map(formattaDataIT).join(', ')}. Scegli un altro orario.`);
      return;
    }
    const gruppoRicorrenzaId = ripetiOgniMese ? `ricorrenza-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` : null;
    const dataFineRicorrenza = ripetiOgniMese ? dateDaPrenotare.at(-1) : null;
    const docenteAssegnato = docentePrivatoDaNome(maestro);
    const dataNotificaMaestro = new Date().toISOString();
    const batch = writeBatch(db);
    const riferimentoAvvisoAllievo = doc(collection(db, 'avvisi_allievi'));
    const nuoveLezioni = [];
    dateDaPrenotare.forEach((data, indice) => {
      const riferimentoLezione = doc(collection(db, 'lezioni_private'));
      nuoveLezioni.push(riferimentoLezione.id);
      batch.set(riferimentoLezione, {
      Maestro: maestro,
      maestroId: docenteAssegnato?.id || '',
      Allievo: nomeAllievoCompleto,
      allievoId: allievoSelezionatoObj.id,
      etaAllievo: calcolaEtaAllievo(allievoSelezionatoObj.DataNascita, allievoSelezionatoObj.CodiceFiscale),
      sessoAllievo: allievoSelezionatoObj.Sesso || '',
      Telefono: allievoSelezionatoObj.Telefono || '',
      Data: data,
      OraInizio: oraInizio,
      OraFine: oraFineCalcolata,
      Durata: '45 minuti',
      ricorrenza: ripetiOgniMese,
      frequenzaRicorrenza: ripetiOgniMese ? frequenzaRipetizione : null,
      ricorrenzaMensile: ripetiOgniMese && frequenzaRipetizione === 'mensile',
      ricorrenzaSettimanale: ripetiOgniMese && frequenzaRipetizione === 'settimanale',
      ricorrenzaQuindicinale: ripetiOgniMese && frequenzaRipetizione === 'quindicinale',
      numeroProgressivoRicorrenza: ripetiOgniMese ? indice + 1 : null,
      totaleRicorrenze: ripetiOgniMese ? dateDaPrenotare.length : null,
      gruppoRicorrenzaId,
      dataInizioRicorrenza: ripetiOgniMese ? dataLezione : null,
      dataFineRicorrenza,
      notificaAssegnazioneInviata: false,
      // Una sola notifica anche per una serie ricorrente: il maestro vede
      // subito la prima data e il numero complessivo delle lezioni inserite.
      notificaMaestroCreataIl: indice === 0 ? dataNotificaMaestro : '',
      notificaMaestroQuantita: indice === 0 ? dateDaPrenotare.length : 0,
      lettoDalMaestro: indice !== 0
    });
    });
    // La prenotazione manuale della segreteria è già confermata: l'allievo la
    // riceve subito nell'app, senza dover approvare o inviare altro.
    batch.set(riferimentoAvvisoAllievo, {
      allievoId: allievoSelezionatoObj.id,
      titolo: '📅 Lezione privata confermata',
      messaggio: dateDaPrenotare.length > 1
        ? `La segreteria ha confermato ${dateDaPrenotare.length} lezioni private con ${maestro}. La prima è il ${formattaDataIT(dataLezione)} alle ${oraInizio}.`
        : `La segreteria ha confermato la tua lezione privata con ${maestro} il ${formattaDataIT(dataLezione)} alle ${oraInizio}.`,
      importante: true,
      letto: false,
      tipo: 'lezione_privata_confermata',
      origine: 'prenotazione_manuale_webapp',
      dataCreazione: dataNotificaMaestro
    });
    await batch.commit();
    // Per una serie ricorrente basta un avviso: tutte le date sono già in agenda.
    avvisaAssegnazioneLezione(nuoveLezioni[0]);

    const descrizioneRicorrenza = frequenzaRipetizione === 'quindicinale' ? 'ogni 2 settimane' : frequenzaRipetizione === 'settimanale' ? 'settimanali' : 'mensili';
    await registraLogOperazione(utenteCorrente, `${ripetiOgniMese ? `Prenotate ${dateDaPrenotare.length} lezioni private ${descrizioneRicorrenza}` : 'Prenotata lezione privata'} (45 min): M° ${maestro} con ${nomeAllievoCompleto} dal ${formattaDataIT(dataLezione)} ore ${oraInizio}`);

    setOraInizio('15:00');
    setRipetiOgniMese(false);
    setNumeroMesiRipetizione(6);
    setFrequenzaRipetizione('settimanale');
    setQueryAllievo('');
    setAllievoSelezionatoObj(null);
    caricaDati();
  };

  const avviaModificaLezione = lezione => {
    setLezioneInModifica(lezione.id);
    setBozzaLezione({ data: lezione.Data || '', ora: lezione.OraInizio || '', maestro: lezione.Maestro || nomiDocentiPrivati[0] || MAESTRI[0] });
  };

  const salvaModificaLezione = async lezione => {
    const { data, ora, maestro: nuovoMaestro } = bozzaLezione;
    if (!data || !ora || !nomiDocentiPrivati.includes(nuovoMaestro)) return alert('Scegli data, ora e docente prima di salvare.');
    const oraFine = calcolaOraFine(ora);
    const conflittoDocente = docenteLezionePrivataSovrapposto(lezioni, { docente: nuovoMaestro, data, oraInizio: ora, oraFine, escludiId: lezione.id });
    if (conflittoDocente) return alert(`${nuovoMaestro} ha già una lezione privata il ${formattaDataIT(data)} dalle ${conflittoDocente.OraInizio} alle ${conflittoDocente.OraFine || calcolaOraFine(conflittoDocente.OraInizio)}.`);
    const lezioniNellaFascia = numeroLezioniPrivateSovrapposte(lezioni, { data, oraInizio: ora, oraFine, escludiId: lezione.id });
    if (lezioniNellaFascia >= MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE) return alert(`In questa fascia ci sono già ${MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE} lezioni private. Non è possibile inserirne una terza.`);
    const conflittoAllievo = lezioneSovrappostaPerAllievo(lezioni, { allievoId: lezione.allievoId, allievoNome: lezione.Allievo, data, oraInizio: ora, oraFine, escludiId: lezione.id });
    if (conflittoAllievo) return alert(`${lezione.Allievo} ha già una lezione privata sovrapposta in questa data.`);
    try {
      const docenteAssegnato = docentePrivatoDaNome(nuovoMaestro);
      const assegnazioneCambiata = nuovoMaestro !== lezione.Maestro;
      const pianificazioneCambiata = assegnazioneCambiata || data !== lezione.Data || ora !== lezione.OraInizio;
      const dataModifica = new Date().toISOString();
      const batch = writeBatch(db);
      batch.update(doc(db, 'lezioni_private', lezione.id), {
        Maestro: nuovoMaestro,
        maestroId: docenteAssegnato?.id || '',
        Data: data,
        OraInizio: ora,
        OraFine: oraFine,
        ...(assegnazioneCambiata ? { notificaAssegnazioneInviata: false } : {}),
        ultimaModifica: dataModifica,
        modificataDa: utenteCorrente
      });
      // L'operatore può correggere un inserimento: l'allievo vede subito
      // la nuova data/orario e riceve anche un avviso dedicato nell'app.
      if (pianificazioneCambiata && lezione.allievoId) {
        batch.set(doc(collection(db, 'avvisi_allievi')), {
          allievoId: lezione.allievoId,
          titolo: '📝 Lezione privata modificata',
          messaggio: `La tua lezione privata è stata aggiornata: ${formattaDataIT(data)} alle ${ora} con ${nuovoMaestro}.`,
          importante: true,
          letto: false,
          tipo: 'lezione_privata_modificata',
          origine: 'modifica_operatore_webapp',
          dataCreazione: dataModifica
        });
      }
      await batch.commit();
      if (assegnazioneCambiata) avvisaAssegnazioneLezione(lezione.id);
      await registraLogOperazione(utenteCorrente, `Modificata lezione privata di ${lezione.Allievo}: ${formattaDataIT(data)} alle ${ora}, docente ${nuovoMaestro}`);
      setLezioneInModifica(null);
      setBozzaLezione({ data: '', ora: '', maestro: '' });
      alert('Lezione aggiornata correttamente.');
    } catch (errore) {
      console.error(errore);
      alert('Non è stato possibile modificare la lezione. Riprova.');
    }
  };

  const lezionePrivataConclusa = lezione => {
    const data = String(lezione?.Data || '');
    const oraFine = String(lezione?.OraFine || calcolaOraFine(lezione?.OraInizio) || '');
    const fine = new Date(`${data}T${oraFine}:00`).getTime();
    return Number.isFinite(fine) && fine <= Date.now();
  };

  const eliminaLezionePrivata = async lezione => {
    const giaConclusa = lezionePrivataConclusa(lezione);
    const conferma = window.confirm(`Eliminare ${giaConclusa ? 'definitivamente ' : ''}la lezione privata di ${lezione.Allievo} del ${formattaDataIT(lezione.Data)} alle ${lezione.OraInizio}?\n\n${giaConclusa ? 'Sarà rimossa anche dal conteggio storico.' : "L'allievo riceverà subito l'avviso di annullamento nell'app."}`);
    if (!conferma) return;
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'lezioni_private', lezione.id));
      // Per una lezione futura cancellata per errore, l'allievo non deve
      // trovare l'appuntamento sparito senza spiegazione.
      if (!giaConclusa && lezione.allievoId) {
        batch.set(doc(collection(db, 'avvisi_allievi')), {
          allievoId: lezione.allievoId,
          titolo: '❌ Lezione privata annullata',
          messaggio: `La lezione privata del ${formattaDataIT(lezione.Data)} alle ${lezione.OraInizio} con ${lezione.Maestro} è stata annullata dalla segreteria.`,
          importante: true,
          letto: false,
          tipo: 'lezione_privata_annullata',
          origine: 'annullamento_operatore_webapp',
          dataCreazione: new Date().toISOString()
        });
      }
      await batch.commit();
      await registraLogOperazione(utenteCorrente, `${giaConclusa ? 'Eliminata lezione privata conclusa' : 'Annullata lezione privata inserita per errore'}: ${lezione.Allievo} · ${formattaDataIT(lezione.Data)} ore ${lezione.OraInizio} · ${lezione.Maestro}`);
      alert(giaConclusa ? 'Lezione privata conclusa eliminata.' : 'Lezione privata annullata e avviso inviato all’allievo.');
    } catch (errore) {
      console.error(errore);
      alert('Non è stato possibile eliminare la lezione. Riprova.');
    }
  };

  // Elenco completo delle lezioni private, raggruppato per allievo: serve alla
  // segreteria per trovare subito una lezione senza sfogliare il calendario.
  const testoRicercaElenco = ricercaElencoLezioni.trim().toLocaleLowerCase('it-IT');
  const lezioniElenco = lezioni
    .filter(lezione => mostraPassateElenco || !lezionePrivataConclusa(lezione))
    .filter(lezione => !testoRicercaElenco || `${lezione.Allievo || ''} ${lezione.Maestro || ''}`.toLocaleLowerCase('it-IT').includes(testoRicercaElenco))
    .sort((prima, dopo) => `${prima.Data || ''} ${prima.OraInizio || ''}`.localeCompare(`${dopo.Data || ''} ${dopo.OraInizio || ''}`));
  const gruppiElencoLezioni = Object.entries(lezioniElenco.reduce((gruppi, lezione) => {
    const nome = String(lezione.Allievo || 'Allievo senza nome').trim();
    return { ...gruppi, [nome]: [...(gruppi[nome] || []), lezione] };
  }, {})).sort(([primo], [secondo]) => primo.localeCompare(secondo, 'it'));
  // Stesse lezioni raggruppate prima per anno e poi per allievo.
  const annoCorrenteElenco = dataOggiISO().slice(0, 4);
  const anniElencoLezioni = Object.entries(lezioniElenco.reduce((anni, lezione) => {
    const anno = String(lezione.Data || '').slice(0, 4) || 'Senza data';
    const nome = String(lezione.Allievo || 'Allievo senza nome').trim();
    const allieviAnno = anni[anno] || {};
    return { ...anni, [anno]: { ...allieviAnno, [nome]: [...(allieviAnno[nome] || []), lezione] } };
  }, {}))
    .sort(([primo], [secondo]) => primo.localeCompare(secondo))
    .map(([anno, perAllievo]) => ({ anno, allievi: Object.entries(perAllievo).sort(([primo], [secondo]) => primo.localeCompare(secondo, 'it')), totale: Object.values(perAllievo).reduce((somma, elenco) => somma + elenco.length, 0) }));
  // Promemoria WhatsApp all'allievo (come nella vecchia agenda).
  const inviaPromemoriaWhatsApp = lezione => {
    const anagrafica = allievi.find(allievo => allievo.id === lezione.allievoId);
    let numeroPulito = String(lezione.Telefono || anagrafica?.Telefono || '').replace(/\D/g, '').replace(/^00/, '');
    if (!numeroPulito) {
      alert('⚠️ Numero di telefono non presente per questo allievo!');
      return;
    }
    if (!numeroPulito.startsWith('39') && (numeroPulito.length === 9 || numeroPulito.length === 10)) numeroPulito = `39${numeroPulito}`;
    const [anno, mese, giorno] = String(lezione.Data || '').split('-').map(Number);
    const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
    const giornoLezione = new Date(anno, mese - 1, giorno); giornoLezione.setHours(0, 0, 0, 0);
    const differenzaGiorni = Math.round((giornoLezione.getTime() - oggi.getTime()) / 86400000);
    const indicazioneData = differenzaGiorni === 0 ? 'di oggi' : differenzaGiorni === 1 ? 'di domani' : `del giorno ${formattaDataIT(lezione.Data)}`;
    const messaggio = `Ciao ${lezione.Allievo}, ti ricordiamo la lezione privata ${indicazioneData} alle ore ${lezione.OraInizio} con il Maestro ${lezione.Maestro}. Academy Of Arts`;
    window.open(`https://wa.me/${numeroPulito}?text=${encodeURIComponent(messaggio)}`, '_blank', 'noopener');
  };
  const apriLezioneNelCalendario = (lezione, modifica = false) => {
    if (!lezione?.Data) return;
    setMeseCalendarioDocenti(String(lezione.Data).slice(0, 7));
    setGiornoCalendarioSelezionato({ docente: String(lezione.Maestro || '').trim(), data: lezione.Data });
    if (modifica) avviaModificaLezione(lezione);
    window.setTimeout(() => document.getElementById('calendario-docenti-lezioni-private')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const dataInizioImpegno = String(impegnoDaInserire?.dataInizio || dataOggiISO());
  const dataFineImpegno = String(impegnoDaInserire?.dataFine || dataInizioImpegno);
  const maestriImpegnatiSelezionati = Array.isArray(impegnoDaInserire?.maestri) ? impegnoDaInserire.maestri : [];
  const periodiImpegniRegistrati = periodiImpegniMaestri(indisponibilitaDocenti);

  return (
    <div style={{ color: '#2d1721' }}>
      <h3 style={{ color: '#57132c', fontFamily: 'serif' }}>Prenotazione Lezioni Private (45 minuti)</h3>

      <section id="docenti-lezioni-private" style={{ maxWidth: 760, marginBottom: 20, padding: 14, borderRadius: 10, background: '#f7f1fb', border: '1px solid #cba8df', color: '#2d1721', scrollMarginTop: 18 }}>
        <strong style={{ color: '#6d287f' }}>👩‍🏫 Docenti abilitati per le lezioni private</strong>
        <p style={{ margin: '6px 0 10px', color: '#553744', fontSize: 13, lineHeight: 1.45 }}>Questa è la scaletta usata nell’agenda, nelle sostituzioni e nell’App Allievi. Per aggiungere un aiuto temporaneo, crea il suo account in Amministrazione e spunta “Abilita come docente lezioni private”.</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{docentiPrivatiUnici.map(docente => <span key={docente.id || docente.username} style={{ padding: '7px 10px', borderRadius: 16, background: '#fff', border: '1px solid #d6b9e2', color: '#4b2059', fontWeight: 'bold', fontSize: 13 }}>{qualificaDocenteLezioniPrivate(docente)} {docente.username}</span>)}</div>
        <Link to="/admin" style={{ display: 'inline-block', marginTop: 11, color: '#6d287f', fontWeight: 'bold', fontSize: 13 }}>⚙️ Gestisci docenti in Amministrazione</Link>
      </section>

      <section style={{ maxWidth: 620, marginBottom: 20, padding: 15, borderRadius: 10, background: '#fff', border: '1px solid #b8c5d9' }}>
        <strong style={{ color: '#24437f' }}>📅 Orari prenotabili dagli allievi</strong>
        <p style={{ margin: '7px 0 10px', color: '#232f43', fontSize: 13 }}><strong>1.</strong> Tocca uno o più giorni &nbsp;·&nbsp; <strong>2.</strong> spunta le fasce orarie &nbsp;·&nbsp; <strong>3.</strong> premi <strong>Pubblica</strong>. Gli allievi vedranno nell’App solo questi giorni e questi orari.</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <button type="button" onClick={() => cambiaMeseOrariSpeciali(-1)} style={{ padding: '6px 11px', border: 0, borderRadius: 6, background: '#e7ecf7', cursor: 'pointer', fontWeight: 'bold' }}>◀</button>
          <strong style={{ color: '#24437f', textTransform: 'capitalize' }}>{new Date(annoOrariSpeciali, numeroMeseOrariSpeciali - 1, 1, 12).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</strong>
          <button type="button" onClick={() => cambiaMeseOrariSpeciali(1)} style={{ padding: '6px 11px', border: 0, borderRadius: 6, background: '#e7ecf7', cursor: 'pointer', fontWeight: 'bold' }}>▶</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 5 }}>
          {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(nome => <div key={nome} style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold', color: '#5a6b8c' }}>{nome}</div>)}
          {celleCalendarioOrari.map((data, indice) => {
            if (!data) return <div key={`vuoto-${indice}`} />;
            const passato = data < dataOggiISO();
            const selezionato = giorniOrariSelezionati.includes(data);
            const speciale = Array.isArray(fascePerData[data]);
            const orari = orariEffettiviPerData(data);
            const testoOrari = orari.length <= 2 ? orari.join(' ') : `${orari.length} orari`;
            const etichetta = speciale ? (orari.length ? testoOrari : 'Chiuso') : (orari.length ? `↻ ${testoOrari}` : '');
            return <button key={data} type="button" disabled={passato} onClick={() => selezionaGiornoOrari(data)} title={orari.length ? orari.join(', ') : 'Nessun orario'} style={{ minHeight: 52, padding: 4, borderRadius: 7, cursor: passato ? 'default' : 'pointer', opacity: passato ? 0.35 : 1, border: selezionato ? '2px solid #3659b5' : '1px solid #d4dbe8', background: selezionato ? '#d6e4ff' : speciale ? (orari.length ? '#c9efd3' : '#ffe3e3') : (orari.length ? '#efe3ff' : '#fff'), color: '#17212d' }}>
              <strong style={{ display: 'block', fontSize: 15 }}>{Number(data.slice(8, 10))}</strong>
              <small style={{ display: 'block', fontSize: 10, lineHeight: 1.2, color: speciale ? (orari.length ? '#1f6b34' : '#a61e1e') : '#5b3c8c' }}>{etichetta}</small>
            </button>;
          })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '9px 0 0', fontSize: 11, color: '#3b4760' }}><span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#c9efd3', marginRight: 4 }} />Prenotabile</span><span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#efe3ff', marginRight: 4 }} />Prenotabile (ogni settimana ↻)</span><span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#ffe3e3', marginRight: 4 }} />Chiuso</span><span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#fff', border: '1px solid #d4dbe8', marginRight: 4 }} />Nessun orario</span></div>
        {giorniOrariSelezionati.length > 0 && <div style={{ marginTop: 12, padding: 11, borderRadius: 8, background: '#f3f6fc', border: '1px solid #d4dbe8' }}>
          <strong style={{ display: 'block', marginBottom: 4, color: '#24437f', fontSize: 14 }}>Giorni scelti: {elencoGiorniIT(giorniOrariSelezionati)}</strong><span style={{ display: 'block', marginBottom: 8, color: '#3b4760', fontSize: 12 }}>Spunta gli orari che gli allievi potranno prenotare in {giorniOrariSelezionati.length === 1 ? 'questo giorno' : 'questi giorni'}.</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>{orariMostratiNelCalendario.map(orario => { const selezionato = orariDataSpecifica.includes(orario); return <label key={orario} style={{ display: 'flex', alignItems: 'center', gap: 5, minHeight: 37, padding: '6px 5px', borderRadius: 5, border: selezionato ? '1px solid #7da5ec' : '1px solid transparent', background: selezionato ? '#d6e4ff' : '#fff', color: '#17212d', fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={selezionato} onChange={() => cambiaOrarioDataSpecifica(orario)} /><span><strong>{orario}</strong>{selezionato && <small style={{ display: 'inline-block', marginLeft: 4, padding: '2px 5px', borderRadius: 4, background: '#e7ddff', color: '#6342b8', fontWeight: 'bold' }}>Fine {prossimoOrarioLezione(orario)}</small>}</span></label>; })}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
            <button type="button" onClick={() => salvaOrariGiorniSelezionati(false)} disabled={!orariDataSpecifica.length} style={{ padding: '9px 12px', border: 0, borderRadius: 6, background: orariDataSpecifica.length ? '#3659b5' : '#9aa8c7', color: '#fff', cursor: orariDataSpecifica.length ? 'pointer' : 'default', fontWeight: 'bold' }}>Pubblica</button>
            <button type="button" onClick={() => salvaOrariGiorniSelezionati(true)} style={{ padding: '9px 12px', border: 0, borderRadius: 6, background: '#ffe0e0', color: '#8f1c1c', cursor: 'pointer', fontWeight: 'bold' }}>Chiudi questi giorni</button>
            {giorniOrariSelezionati.some(data => Array.isArray(fascePerData[data])) && <button type="button" onClick={ripristinaSettimanaliGiorniSelezionati} style={{ padding: '9px 12px', border: 0, borderRadius: 6, background: '#e7ecf7', color: '#24437f', cursor: 'pointer' }}>Usa orari settimanali</button>}
            <button type="button" onClick={() => { setGiorniOrariSelezionati([]); setOrariDataSpecifica([]); }} style={{ padding: '9px 12px', border: 0, borderRadius: 6, background: 'transparent', color: '#5a6b8c', cursor: 'pointer' }}>Annulla selezione</button>
          </div>
        </div>}
      </section>

      <details style={{ maxWidth: 620, marginBottom: 20, padding: 15, borderRadius: 10, background: '#fff', border: '1px solid #d9b8c5' }}>
        <summary style={{ cursor: 'pointer', color: '#7f2443', fontWeight: 'bold' }}>↻ Orari che si ripetono ogni settimana (facoltativo)</summary>
        <p style={{ margin: '8px 0 0', color: '#432332', fontSize: 13 }}>Usalo solo se un orario vale <strong>tutte</strong> le settimane (es. ogni lunedì alle 15:15). Nei giorni del calendario che hai impostato a mano valgono invece gli orari del calendario.</p>
        <p style={{ margin: '7px 0 8px', color: '#432332', fontSize: 13 }}>Non sono gli orari di un solo Maestro: sono le fasce comuni in cui l’App può accettare richieste. Spunta gli orari disponibili: non devi scrivere nulla a mano. Puoi anche lasciare tutte le caselle vuote per sospendere temporaneamente le richieste.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>{[1, 2, 3, 4, 5, 6].map(giorno => { const selezionati = String(fasceDaModificare[giorno] || '').split(',').map(valore => valore.trim()); const orariDelGiorno = giorno === 6 ? ORARI_SABATO_LEZIONI_PRIVATE : ORARI_SELEZIONABILI_LEZIONI; return <fieldset key={giorno} style={{ margin: 0, padding: 10, borderRadius: 8, border: '1px solid #d9b8c5', minWidth: 0 }}><legend style={{ padding: '0 4px', color: '#351522', fontSize: 13, fontWeight: 'bold' }}>{NOMI_GIORNI_LEZIONI[giorno]}</legend><div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>{orariDelGiorno.map(orario => { const selezionato = selezionati.includes(orario); const fine = prossimoOrarioLezione(orario); return <label key={orario} style={{ display: 'flex', alignItems: 'center', gap: 5, minHeight: 37, padding: '6px 5px', borderRadius: 5, border: selezionato ? '1px solid #ec7da5' : '1px solid transparent', background: selezionato ? '#ffd6e7' : '#fff', color: '#2d1721', fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={selezionato} onChange={() => cambiaFasciaDisponibile(giorno, orario)} /><span><strong>{orario}</strong>{selezionato && <small style={{ display: 'inline-block', marginTop: 3, padding: '2px 5px', borderRadius: 4, background: '#e7ddff', color: '#6342b8', fontWeight: 'bold' }}>Fine {fine}</small>}</span></label>; })}</div></fieldset>; })}</div>
        <button type="button" onClick={salvaDisponibilitaApp} style={{ marginTop: 13, padding: '9px 12px', border: 0, borderRadius: 6, background: '#9c36b5', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>Salva orari settimanali</button>
      </details>

      <section style={{ maxWidth: 620, marginBottom: 20, padding: 15, borderRadius: 10, background: '#fff8fb', border: '1px solid #e8a3bd' }}>
        <strong style={{ color: '#8e1d46' }}>📌 Aggiungi impegno docente · giorno o periodo</strong>
        <p style={{ margin: '7px 0 11px', color: '#553744', fontSize: 13, lineHeight: 1.45 }}>Puoi registrare più impegni separati: ad esempio Salvatore dal 7 al 9 e Lia solo l’8. In tutte quelle date l’App Allievi non assegnerà nuove richieste al docente segnato. Puoi scegliere lo smistamento automatico oppure gestire manualmente le lezioni già in agenda.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <label style={{ display: 'grid', gap: 5, color: '#432332', fontWeight: 'bold', fontSize: 13 }}>Dal giorno<input type="date" value={dataInizioImpegno} onChange={evento => setImpegnoDaInserire(corrente => ({ ...corrente, dataInizio: evento.target.value }))} style={{ padding: 8, borderRadius: 7, border: '1px solid #e8a3bd', color: '#2d1721', background: '#fff', font: 'inherit' }} /></label>
          <label style={{ display: 'grid', gap: 5, color: '#432332', fontWeight: 'bold', fontSize: 13 }}>Fino al giorno<input type="date" min={dataInizioImpegno} value={dataFineImpegno} onChange={evento => setImpegnoDaInserire(corrente => ({ ...corrente, dataFine: evento.target.value }))} style={{ padding: 8, borderRadius: 7, border: '1px solid #e8a3bd', color: '#2d1721', background: '#fff', font: 'inherit' }} /></label>
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>{nomiDocentiPrivati.map(nomeMaestro => <label key={nomeMaestro} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 8, background: maestriImpegnatiSelezionati.includes(nomeMaestro) ? '#ffe0ea' : '#fff', border: `1px solid ${maestriImpegnatiSelezionati.includes(nomeMaestro) ? '#e0527d' : '#e8c5d1'}`, color: '#432332', fontWeight: 'bold', cursor: 'pointer' }}><input type="checkbox" checked={maestriImpegnatiSelezionati.includes(nomeMaestro)} onChange={() => cambiaImpegnoDaInserire(nomeMaestro)} />{nomeMaestro}</label>)}</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, padding: 10, borderRadius: 8, background: '#fff', border: '1px solid #e8c5d1', color: '#432332', fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={Boolean(impegnoDaInserire.gestioneManuale)} onChange={evento => setImpegnoDaInserire(corrente => ({ ...corrente, gestioneManuale: evento.target.checked }))} /><span><strong>Gestisco manualmente le lezioni già in agenda</strong><small style={{ display: 'block', marginTop: 3, color: '#6c3b4e' }}>L’assenza viene salvata, ma scegli tu Maestro per Maestro nella sezione “Sostituzioni manuali”.</small></span></label>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginTop: 13 }}><button type="button" onClick={salvaIndisponibilitaOggi} style={{ padding: '9px 12px', border: 0, borderRadius: 6, background: '#b4235b', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>Aggiungi impegno</button></div>
        <div style={{ marginTop: 16, display: 'grid', gap: 8 }}><strong style={{ color: '#5d1531', fontSize: 14 }}>Impegni già registrati</strong>{periodiImpegniRegistrati.length ? periodiImpegniRegistrati.map(periodo => <div key={periodo.id} style={{ display: 'flex', gap: 9, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', padding: 10, borderRadius: 8, background: '#ffe8f0', border: '1px solid #e8a3bd', color: '#5d1531', fontSize: 13 }}><span><strong>{(periodo.maestri || []).join(', ')}</strong> · {periodo.dataInizio === periodo.dataFine ? formattaDataIT(periodo.dataInizio) : `dal ${formattaDataIT(periodo.dataInizio)} al ${formattaDataIT(periodo.dataFine)}`}</span><button type="button" onClick={() => annullaImpegnoMaestri(periodo.id)} style={{ padding: '7px 9px', border: '1px solid #b4235b', borderRadius: 6, background: '#fff', color: '#8e1d46', cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>Annulla questo impegno</button></div>) : <small style={{ color: '#6c3b4e' }}>Nessun impegno registrato.</small>}</div>
        {periodiImpegniRegistrati.length > 0 && <button type="button" onClick={annullaImpegniMaestri} style={{ width: 'fit-content', marginTop: 11, padding: '8px 10px', border: '1px solid #b4235b', borderRadius: 6, background: '#fff', color: '#8e1d46', cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>Annulla tutti gli impegni</button>}
      </section>

      <section style={{ maxWidth: 760, marginBottom: 20, padding: 15, borderRadius: 10, background: '#f7f5ff', border: '1px solid #b9a7ef', color: '#2d1721' }}>
        <strong style={{ color: '#5736a5' }}>🔁 Sostituzioni manuali per assenza</strong>
        <p style={{ margin: '7px 0 12px', color: '#432332', fontSize: 13, lineHeight: 1.45 }}>Scegli il Maestro assente e il periodo: vedrai solo le sue lezioni. Per ogni riga decidi a chi assegnarla; la serie mensile rimane invariata nelle altre date.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}><label style={{ display: 'grid', gap: 5, fontSize: 13, fontWeight: 'bold' }}>Docente assente<select value={maestroSostituzioni} onChange={evento => { setMaestroSostituzioni(evento.target.value); setSostituzioniManuali({}); }} style={{ padding: 8, borderRadius: 7, border: '1px solid #b9a7ef', background: '#fff', color: '#2d1721' }}>{docentiPrivatiUnici.map(docente => <option key={docente.id || docente.username} value={docente.username}>{qualificaDocenteLezioniPrivate(docente)} {docente.username}</option>)}</select></label><label style={{ display: 'grid', gap: 5, fontSize: 13, fontWeight: 'bold' }}>Dal giorno<input type="date" value={periodoSostituzioni.dataInizio} onChange={evento => setPeriodoSostituzioni(corrente => ({ ...corrente, dataInizio: evento.target.value }))} style={{ padding: 8, borderRadius: 7, border: '1px solid #b9a7ef', background: '#fff', color: '#2d1721' }} /></label><label style={{ display: 'grid', gap: 5, fontSize: 13, fontWeight: 'bold' }}>Fino al giorno<input type="date" min={periodoSostituzioni.dataInizio} value={periodoSostituzioni.dataFine} onChange={evento => setPeriodoSostituzioni(corrente => ({ ...corrente, dataFine: evento.target.value }))} style={{ padding: 8, borderRadius: 7, border: '1px solid #b9a7ef', background: '#fff', color: '#2d1721' }} /></label></div>
        {lezioniDaSostituire.length ? <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>{lezioniDaSostituire.map(lezione => <div key={lezione.id} style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', padding: 10, borderRadius: 8, background: '#fff', border: '1px solid #d8cff5' }}><span><strong>{lezione.Allievo}</strong><small style={{ display: 'block', marginTop: 3, color: '#5d4a86' }}>{formattaDataIT(lezione.Data)} · {lezione.OraInizio}–{lezione.OraFine}{(lezione.ricorrenza || lezione.ricorrenzaMensile || lezione.ricorrenzaSettimanale) ? ` · ${etichettaRicorrenza(lezione)} fino al ${formattaDataIT(fineRicorrenzaLezione(lezione))}` : ''}</small></span><select value={sostituzioniManuali[lezione.id] || ''} onChange={evento => setSostituzioniManuali(corrente => ({ ...corrente, [lezione.id]: evento.target.value }))} style={{ minWidth: 180, padding: 8, borderRadius: 7, border: '1px solid #8f79d5', background: '#fff', color: '#2d1721' }}><option value="">Scegli Maestro sostituto</option>{nomiDocentiPrivati.filter(nome => nome !== maestroSostituzioni && !maestroImpegnatoNelPeriodo(indisponibilitaDocenti, nome, lezione.Data)).map(nome => <option key={nome} value={nome}>{nome}</option>)}</select></div>)}<button type="button" onClick={salvaSostituzioniManuali} style={{ justifySelf: 'start', padding: '9px 12px', border: 0, borderRadius: 6, background: '#6546bc', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>Salva sostituzioni scelte</button></div> : <p style={{ margin: '14px 0 0', color: '#5d4a86', fontSize: 13 }}>Nessuna lezione del Maestro selezionato in questo periodo.</p>}
      </section>

      <section style={{ maxWidth: 440, marginTop: 24, marginBottom: 25 }}>
        <h4 style={{ margin: '0 0 9px', color: '#57132c', fontSize: 19 }}>📅 Prenotazione Lezioni Private</h4>
        <p style={{ margin: '0 0 10px', color: '#553744', fontSize: 13 }}>Inserisci qui una nuova lezione privata e assegna docente, allievo, data e orario.</p>
      <form onSubmit={prenotalezione} style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px', backgroundColor: '#353b4c', padding: '20px', borderRadius: '10px', border: '1px solid #4a5266' }}>
        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Seleziona docente:</label>
        <select value={maestro} onChange={e => setMaestro(e.target.value)} style={{ padding: '8px', backgroundColor: '#2b3140', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px' }}>
          {nomiDocentiPrivati.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Cerca Allievo (Digita Cognome o Nome):</label>
        <div ref={containerAllieviRef} style={{ position: 'relative' }}>
          <input 
            type="text"
            placeholder="Inizia a digitare il cognome..."
            value={allievoSelezionatoObj ? `${allievoSelezionatoObj.Cognome} ${allievoSelezionatoObj.Nome}` : queryAllievo}
            onChange={e => {
              setQueryAllievo(e.target.value);
              setAllievoSelezionatoObj(null);
              setMostraTendinaAllievi(true);
            }}
            onFocus={() => setMostraTendinaAllievi(true)}
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: '#2b3140', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px', fontWeight: 'bold' }}
          />
          {mostraTendinaAllievi && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              maxHeight: '180px',
              overflowY: 'auto',
              backgroundColor: '#2b3140',
              border: '1px solid #74c0fc',
              borderRadius: '0 0 4px 4px',
              zIndex: 100,
              boxShadow: '0 4px 10px rgba(0,0,0,0.5)'
            }}>
              {allieviFiltratiLezioni.length === 0 ? (
                <div style={{ padding: '8px', fontSize: '12px', color: '#adb5bd' }}>Nessun allievo trovato</div>
              ) : (
                allieviFiltratiLezioni.map(a => (
                  <div 
                    key={a.id}
                    onClick={() => {
                      setAllievoSelezionatoObj(a);
                      setQueryAllievo('');
                      setMostraTendinaAllievi(false);
                    }}
                    style={{ padding: '8px 10px', fontSize: '13px', borderBottom: '1px solid #eadde2', cursor: 'pointer', color: '#111827', backgroundColor: '#fff', fontWeight: 600 }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5e8ed'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                  >
                    <strong>{a.Cognome}</strong> {a.Nome} {a.CodiceFiscale ? `(${a.CodiceFiscale})` : ''}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Data Lezione:</label>
        <input type="date" value={dataLezione} onChange={e => setDataLezione(e.target.value)} required style={{ padding: '8px', backgroundColor: '#2b3140', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px' }} />

        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Ora Inizio (45 min):</label>
        <input type="time" value={oraInizio} onChange={e => setOraInizio(e.target.value)} required style={{ padding: '8px', backgroundColor: '#2b3140', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px' }} />

        <div style={{ fontSize: '12px', color: '#4dabf7', fontWeight: 'bold' }}>
          ⏱️ Orario: <strong>{oraInizio} - {calcolaOraFine(oraInizio)}</strong>
        </div>

        <div style={{ padding: '11px', borderRadius: 8, border: '1px solid #9775fa', background: 'rgba(151,117,250,.12)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}><input type="checkbox" checked={ripetiOgniMese} onChange={event => setRipetiOgniMese(event.target.checked)} /> 🔁 Crea lezioni ripetitive</label>
          {ripetiOgniMese && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 9, marginTop: 10 }}><label style={{ fontSize: '12px', fontWeight: 'bold' }}>Frequenza<select value={frequenzaRipetizione} onChange={event => setFrequenzaRipetizione(event.target.value)} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 5, padding: 8, backgroundColor: '#2b3140', color: '#fff', border: '1px solid #4a5266', borderRadius: 4 }}><option value="settimanale">Ogni settimana</option><option value="quindicinale">Ogni 2 settimane</option><option value="mensile">Ogni mese</option></select></label><label style={{ fontSize: '12px', fontWeight: 'bold' }}>{frequenzaRipetizione === 'settimanale' ? 'Per quante settimane?' : frequenzaRipetizione === 'quindicinale' ? 'Per quante lezioni?' : 'Per quanti mesi?'}<input type="number" min="2" max={frequenzaRipetizione === 'settimanale' ? '52' : frequenzaRipetizione === 'quindicinale' ? '26' : '12'} value={numeroMesiRipetizione} onChange={event => setNumeroMesiRipetizione(event.target.value)} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 5, padding: 8, backgroundColor: '#2b3140', color: '#fff', border: '1px solid #4a5266', borderRadius: 4 }} /></label></div>}
          <small style={{ display: 'block', marginTop: 8, color: '#ced4da', lineHeight: 1.35 }}>Solo Web App: la Segreteria può scegliere liberamente date e orari, ma il sistema blocca sempre le sovrapposizioni con lezioni già presenti.</small>
        </div>

        <button type="submit" style={{ padding: '10px', cursor: 'pointer', backgroundColor: '#fcc419', color: '#000', border: 'none', borderRadius: '6px', fontWeight: 'bold', marginTop: '5px' }}>
          Prenota Lezione
        </button>
      </form>
      </section>

      {puoGestireRichiesteApp && <section style={{ margin: '0 0 25px', padding: 16, borderRadius: 12, border: '1px solid #f59f00', background: 'rgba(245, 159, 0, .12)' }}>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div><strong style={{ color: '#ffd43b', fontSize: 17 }}>🔔 Richieste dall’App Allievi — aggiornamento in tempo reale</strong><div style={{ marginTop: 5, color: '#f8f9fa', fontSize: 13 }}>Ogni nuova richiesta compare qui immediatamente e resta in attesa della decisione del Maestro.</div></div>
          <Link to="/richieste-lezioni-private" style={{ padding: '9px 12px', borderRadius: 8, background: '#f59f00', color: '#24130a', textDecoration: 'none', fontWeight: 'bold', fontSize: 13 }}>Apri richieste {richiesteApp.length > 0 ? `(${richiesteApp.length})` : ''}</Link>
        </div>
        {richiesteApp.length > 0 ? <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>{richiesteApp.map(richiesta => <div key={richiesta.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(0,0,0,.2)', borderLeft: '4px solid #ffd43b', color: '#fff' }}><strong>{richiesta.nomeAllievo}</strong> · M° {richiesta.maestroNome} · {formattaDataIT(richiesta.dataDesiderata)} alle {richiesta.oraDesiderata}<span style={{ float: 'right', color: '#ffd43b', fontSize: 12, fontWeight: 'bold' }}>{richiesta.stato === 'richiesta_disponibilita' ? 'Disponibilità richiesta' : richiesta.stato === 'orario_proposto' ? 'Orario proposto' : 'In attesa'}</span></div>)}</div> : <div style={{ marginTop: 12, color: '#dee2e6', fontSize: 13 }}>Nessuna nuova richiesta: l’agenda è aggiornata.</div>}
      </section>}

      <h4 id="agenda-lezioni-private" tabIndex="-1" style={{ color: '#57132c', marginTop: '25px', scrollMarginTop: 18 }}>Agenda Lezioni Private</h4>
      <p style={{ margin: '6px 0 10px', color: '#553744', fontSize: 13 }}>Prima trovi il calendario di ogni docente con gli allievi; sotto rimane il dettaglio completo raggruppato per allievo.</p>
      <section id="calendario-docenti-lezioni-private" style={{ marginTop: 12, padding: 16, borderRadius: 13, background: 'linear-gradient(135deg, #fff8e8, #f6efff)', border: '1px solid #d9b5e7', color: '#2d1721', scrollMarginTop: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h4 style={{ margin: 0, color: '#57132c', fontSize: 19 }}>🗓️ Calendario di ogni docente</h4>
            <p style={{ margin: '5px 0 0', color: '#553744', fontSize: 13 }}>Ogni docente vede con quale allievo ha lezione, data, ora e totale degli appuntamenti.</p>
          </div>
          <label style={{ display: 'grid', gap: 4, color: '#4b2059', fontSize: 12, fontWeight: 'bold' }}>Mese da visualizzare
            <input type="month" value={meseCalendarioDocenti} onChange={evento => setMeseCalendarioDocenti(evento.target.value)} style={{ minWidth: 155, padding: '8px 10px', borderRadius: 7, border: '1px solid #a870c0', background: '#fff', color: '#2d1721', fontWeight: 'bold' }} />
          </label>
        </div>
        <div style={{ marginTop: 13, padding: '8px 11px', borderRadius: 8, background: 'rgba(109,40,127,.09)', color: '#572066', fontSize: 13, fontWeight: 'bold' }}>📆 {etichettaMeseCalendario ? etichettaMeseCalendario.charAt(0).toUpperCase() + etichettaMeseCalendario.slice(1) : 'Scegli un mese'} · {lezioniMeseCalendario.length} {lezioniMeseCalendario.length === 1 ? 'lezione privata nel mese' : 'lezioni private nel mese'} <span style={{ color: '#2d1721' }}>· Archivio completo: {lezioni.length} {lezioni.length === 1 ? 'lezione' : 'lezioni'}</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 13, marginTop: 13 }}>
          {datiCalendarioDocenti.map(docente => (
            <article key={docente.nome} style={{ padding: 12, borderRadius: 11, background: '#fff', border: `1px solid ${docente.appuntamenti.length ? '#9b6fc0' : '#dfcfd6'}`, boxShadow: '0 3px 9px rgba(71,22,43,.06)' }}>
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <div><strong style={{ display: 'block', color: '#32101e', fontSize: 16 }}>👩‍🏫 {docente.nome}</strong><small style={{ display: 'block', marginTop: 3, color: '#654b57' }}>{docente.appuntamenti.length ? 'Giorni viola = appuntamenti prenotati' : 'Nessuna lezione privata in questo mese'}</small></div>
                <span style={{ display: 'grid', gap: 4, textAlign: 'right' }}><b style={{ color: '#8a4a08', fontSize: 11 }}>{docente.appuntamenti.length} nel mese</b></span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 3, fontSize: 10, textAlign: 'center', color: '#775b66', fontWeight: 'bold', marginBottom: 3 }}>{['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((giorno, indice) => <span key={`${giorno}-${indice}`}>{giorno}</span>)}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 3 }}>
                {Array.from({ length: spaziPrimaDelMese }).map((_, indice) => <span key={`spazio-${indice}`} aria-hidden="true" />)}
                {Array.from({ length: giorniDelMeseCalendario }, (_, indice) => {
                  const giorno = indice + 1;
                  const data = `${meseCalendarioDocenti}-${String(giorno).padStart(2, '0')}`;
                  const appuntamenti = docente.perGiorno[data] || [];
                  const prenotato = appuntamenti.length > 0;
                  const nomeBreve = String(appuntamenti[0]?.Allievo || '').split(/\s+/).filter(Boolean).slice(0, 1).join('');
                  const oggi = data === dataOggiISO();
                  const selezionato = giornoCalendarioSelezionato?.docente === docente.nome && giornoCalendarioSelezionato?.data === data;
                  return <button type="button" key={data} title={prenotato ? appuntamenti.map(lezione => `${lezione.OraInizio} ${lezione.Allievo}`).join(' · ') : `${giorno} ${etichettaMeseCalendario}`} onClick={() => setGiornoCalendarioSelezionato({ docente: docente.nome, data })} style={{ display: 'grid', placeItems: 'center', minHeight: 42, padding: 2, borderRadius: 6, border: prenotato ? '1px solid #6d287f' : oggi ? '2px solid #e89b00' : '1px solid #eee2e8', outline: selezionato ? '3px solid #35a7ff' : 'none', outlineOffset: 1, boxShadow: oggi ? '0 0 0 2px rgba(255,201,60,.35)' : 'none', background: prenotato ? 'linear-gradient(135deg, #6d287f, #9c36b5)' : oggi ? 'linear-gradient(135deg, #fff8cf, #ffe08a)' : '#fff', color: prenotato ? '#fff' : oggi ? '#724900' : '#755d67', cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>{giorno}{prenotato && <small style={{ display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 8, lineHeight: 1.15 }}>{appuntamenti.length > 1 ? `${appuntamenti.length} all.` : nomeBreve}</small>}{oggi && !prenotato && <small style={{ display: 'block', fontSize: 7, lineHeight: 1 }}>OGGI</small>}</button>;
                })}
              </div>
              {(docente.haGiornoSelezionato || docente.appuntamentiGiornoSelezionato.length > 0) && <div style={{ display: 'grid', gap: 5, marginTop: 10, padding: 10, borderRadius: 8, background: '#edf7ff', border: '1px solid #74c0fc', color: '#173b60', fontSize: 12 }}><strong>📅 {docente.haGiornoSelezionato ? `Lezioni del ${formattaDataIT(docente.dataGiornoSelezionato)}` : 'Lezioni di oggi'}</strong>{docente.appuntamentiGiornoSelezionato.length ? docente.appuntamentiGiornoSelezionato.map(lezione => lezioneInModifica === lezione.id ? <div key={lezione.id} style={{ display: 'grid', gap: 7, padding: 9, borderRadius: 7, background: '#f1e7ff', border: '1px solid #9b6fc0', color: '#4b2059' }}><strong>✏️ Modifica lezione di {lezione.Allievo}</strong><div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}><label>Data<input type="date" value={bozzaLezione.data} onChange={evento => setBozzaLezione(bozza => ({ ...bozza, data: evento.target.value }))} style={{ width: '100%', boxSizing: 'border-box', marginTop: 2, padding: 6, borderRadius: 5, border: '1px solid #9b6fc0', color: '#2d1721' }} /></label><label>Ora<input type="time" value={bozzaLezione.ora} onChange={evento => setBozzaLezione(bozza => ({ ...bozza, ora: evento.target.value }))} style={{ width: '100%', boxSizing: 'border-box', marginTop: 2, padding: 6, borderRadius: 5, border: '1px solid #9b6fc0', color: '#2d1721' }} /></label></div><label>👩‍🏫 Cambia insegnante<select value={bozzaLezione.maestro} onChange={evento => setBozzaLezione(bozza => ({ ...bozza, maestro: evento.target.value }))} style={{ width: '100%', boxSizing: 'border-box', marginTop: 2, padding: 6, borderRadius: 5, border: '1px solid #9b6fc0', color: '#2d1721' }}>{nomiDocentiPrivati.map(nomeDocente => <option key={nomeDocente} value={nomeDocente}>{nomeDocente}</option>)}</select></label><small style={{ color: '#604372' }}>Puoi modificare data, ora e insegnante. Sono consentite due lezioni contemporanee solo con docenti e allievi diversi.</small><div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}><button type="button" onClick={() => salvaModificaLezione(lezione)} style={{ padding: '6px 8px', border: 0, borderRadius: 5, background: '#6d287f', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>Salva</button><button type="button" onClick={() => { setLezioneInModifica(null); setBozzaLezione({ data: '', ora: '', maestro: '' }); }} style={{ padding: '6px 8px', border: '1px solid #8d789b', borderRadius: 5, background: '#fff', color: '#4b2059', cursor: 'pointer', fontWeight: 'bold' }}>Annulla</button></div></div> : <div key={lezione.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '5px 7px', borderRadius: 5, background: '#fff' }}><span>🕒 {lezione.OraInizio}–{lezione.OraFine || calcolaOraFine(lezione.OraInizio)} · <strong>{lezione.Allievo}</strong></span><span style={{ display: 'flex', gap: 5, flex: '0 0 auto' }}><button type="button" onClick={() => avviaModificaLezione(lezione)} style={{ padding: '5px 7px', border: 0, borderRadius: 5, background: '#1971c2', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>Modifica</button><button type="button" onClick={() => eliminaLezionePrivata(lezione)} style={{ padding: '5px 7px', border: 0, borderRadius: 5, background: '#c92a2a', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>{lezionePrivataConclusa(lezione) ? 'Elimina' : 'Annulla'}</button></span></div>) : <span>Nessuna lezione privata per questo docente in questo giorno.</span>}</div>}
              {docente.appuntamentiStorici.length > 0 && <details style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #eee2e8', color: '#4b2059', fontSize: 12 }}><summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>📚 Vedi tutto lo storico ({docente.appuntamentiStorici.length} lezioni)</summary><div style={{ display: 'grid', gap: 4, marginTop: 8, maxHeight: 180, overflowY: 'auto', paddingRight: 4 }}>{docente.appuntamentiStorici.map(lezione => <span key={lezione.id} style={{ padding: '5px 7px', borderRadius: 5, background: '#faf6fc' }}>{formattaDataIT(lezione.Data)} · {lezione.OraInizio}–{lezione.OraFine || calcolaOraFine(lezione.OraInizio)} · <strong>{lezione.Allievo}</strong></span>)}</div></details>}
            </article>
          ))}
        </div>
        {!datiCalendarioDocenti.length && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: '#fff', color: '#654b57' }}>Non ci sono docenti abilitati alle lezioni private.</div>}
      </section>

      <section id="elenco-lezioni-private" style={{ marginTop: 18, padding: 16, borderRadius: 13, background: '#fff', border: '1px solid #d9b5e7', color: '#2d1721', scrollMarginTop: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h4 style={{ margin: 0, color: '#57132c', fontSize: 19 }}>📋 Elenco lezioni assegnate</h4>
            <p style={{ margin: '5px 0 0', color: '#553744', fontSize: 13 }}>Lezioni raggruppate per anno e per allievo: tocca un nome per vedere tutte le sue lezioni. Cerca per allievo o docente.</p>
          </div>
          <span style={{ padding: '6px 10px', borderRadius: 20, background: '#f3e8fa', color: '#572066', fontWeight: 'bold', fontSize: 13 }}>{lezioniElenco.length} {lezioniElenco.length === 1 ? 'lezione' : 'lezioni'} · {gruppiElencoLezioni.length} {gruppiElencoLezioni.length === 1 ? 'allievo' : 'allievi'}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
          <input type="search" value={ricercaElencoLezioni} onChange={evento => setRicercaElencoLezioni(evento.target.value)} placeholder="🔍 Cerca allievo o docente…" style={{ flex: '1 1 240px', minWidth: 0, padding: '10px 12px', borderRadius: 8, border: '1px solid #a870c0', background: '#fff', color: '#2d1721', fontSize: 15 }} />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: '#4b2059', fontSize: 13, fontWeight: 'bold', cursor: 'pointer' }}><input type="checkbox" checked={mostraPassateElenco} onChange={evento => setMostraPassateElenco(evento.target.checked)} /> Mostra anche le lezioni già fatte</label>
        </div>
        {gruppiElencoLezioni.length ? <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
          {anniElencoLezioni.map(({ anno, allievi: allieviAnno, totale }) => (
            <details key={`${anno}-${testoRicercaElenco ? 'ricerca' : 'tutti'}-${mostraPassateElenco ? 'storico' : 'futuro'}`} open={Boolean(testoRicercaElenco) || anno === annoCorrenteElenco || anniElencoLezioni.length === 1} style={{ borderRadius: 11, border: '1px solid #d9b5e7', background: '#fdfbfe' }}>
              <summary style={{ cursor: 'pointer', padding: '11px 13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', color: '#57132c', fontWeight: 'bold', fontSize: 17 }}>
                <span>📅 {anno}</span>
                <small style={{ color: '#654b57', fontSize: 13 }}>{totale} {totale === 1 ? 'lezione' : 'lezioni'} · {allieviAnno.length} {allieviAnno.length === 1 ? 'allievo' : 'allievi'}</small>
              </summary>
              <div style={{ display: 'grid', gap: 8, padding: '0 10px 10px' }}>
                {allieviAnno.map(([nomeAllievo, lezioniAllievo]) => {
                  const prossima = lezioniAllievo.find(lezione => !lezionePrivataConclusa(lezione)) || lezioniAllievo[0];
                  return (
                    <details key={`${anno}-${nomeAllievo}-${testoRicercaElenco ? 'ricerca' : 'tutti'}`} open={Boolean(testoRicercaElenco)} style={{ borderRadius: 10, background: '#faf6fc', border: '1px solid #e3d1ec' }}>
                      <summary style={{ cursor: 'pointer', padding: '9px 11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span><strong style={{ color: '#32101e', fontSize: 15 }}>👤 {nomeAllievo}</strong>{prossima ? <small style={{ display: 'block', color: '#654b57', marginTop: 2 }}>{lezionePrivataConclusa(prossima) ? 'Ultima' : 'Prossima'}: {formattaDataIT(prossima.Data)} · {prossima.OraInizio} · {prossima.Maestro || 'Docente non indicato'}</small> : null}</span>
                        <small style={{ color: '#572066', fontWeight: 'bold', background: '#f3e8fa', padding: '3px 8px', borderRadius: 12 }}>{lezioniAllievo.length} {lezioniAllievo.length === 1 ? 'lezione' : 'lezioni'}</small>
                      </summary>
                      <div style={{ display: 'grid', gap: 6, padding: '0 9px 9px' }}>
                        {lezioniAllievo.map(lezione => {
                          const conclusa = lezionePrivataConclusa(lezione);
                          return <div key={lezione.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 10px', borderRadius: 8, background: '#fff', borderLeft: `4px solid ${conclusa ? '#adb5bd' : '#9b6fc0'}`, opacity: conclusa ? 0.75 : 1 }}>
                            <span style={{ color: '#2d1721', fontSize: 14, lineHeight: 1.4 }}><strong>{formattaDataIT(lezione.Data)}</strong> · {lezione.OraInizio}–{lezione.OraFine || calcolaOraFine(lezione.OraInizio)} · 👩‍🏫 {lezione.Maestro || 'Docente non indicato'}{lezione.gruppoRicorrenzaId || lezione.frequenzaRicorrenza ? <small style={{ display: 'block', color: '#7a5b8a' }}>↻ {etichettaRicorrenza(lezione)}</small> : null}{conclusa ? <small style={{ display: 'block', color: '#868e96' }}>Già svolta</small> : null}</span>
                            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => apriLezioneNelCalendario(lezione)} style={{ padding: '6px 9px', borderRadius: 6, border: '1px solid #9b6fc0', background: '#fff', color: '#4b2059', fontWeight: 'bold', fontSize: 12, cursor: 'pointer' }}>🗓️ Calendario</button>
                              {!conclusa && <button type="button" onClick={() => inviaPromemoriaWhatsApp(lezione)} style={{ padding: '6px 9px', borderRadius: 6, border: 0, background: '#fcc419', color: '#24130a', fontWeight: 'bold', fontSize: 12, cursor: 'pointer' }}>🟡 Promemoria WhatsApp</button>}
                              {!conclusa && <button type="button" onClick={() => apriLezioneNelCalendario(lezione, true)} style={{ padding: '6px 9px', borderRadius: 6, border: 0, background: '#8e3fa7', color: '#fff', fontWeight: 'bold', fontSize: 12, cursor: 'pointer' }}>✏️ Modifica</button>}
                              <button type="button" onClick={() => eliminaLezionePrivata(lezione)} style={{ padding: '6px 9px', borderRadius: 6, border: 0, background: '#c92a2a', color: '#fff', fontWeight: 'bold', fontSize: 12, cursor: 'pointer' }}>🗑️ Elimina</button>
                            </span>
                          </div>;
                        })}
                      </div>
                    </details>
                  );
                })}
              </div>
            </details>
          ))}
        </div> : <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: '#faf6fc', color: '#654b57' }}>{testoRicercaElenco ? 'Nessuna lezione trovata con questa ricerca.' : mostraPassateElenco ? 'Non ci sono lezioni private registrate.' : 'Non ci sono lezioni private in programma. Spunta “Mostra anche le lezioni già fatte” per vedere lo storico.'}</div>}
      </section>

    </div>
  );
}

function NotificheLezioniPrivate({ utenteLoggato }) {
  const [richieste, setRichieste] = useState([]);
  const [lezioni, setLezioni] = useState([]);
  const ruolo = String(utenteLoggato?.ruolo || '').toLowerCase();
  const amministratore = ruolo === 'admin';

  useEffect(() => {
    const appartieneAlDocente = elemento => {
      if (amministratore) return true;
      const idDocente = String(elemento.maestroId || '').trim();
      if (idDocente) return idDocente === String(utenteLoggato?.id || '').trim();
      const nomeDocente = elemento.maestroNome || elemento.Maestro || '';
      return String(nomeDocente).trim().toLocaleLowerCase('it-IT') === String(utenteLoggato?.username || '').trim().toLocaleLowerCase('it-IT');
    };
    const interrompiRichieste = ascoltaRaccolta('richieste_lezioni_private', snapshot => {
      setRichieste(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })).filter(appartieneAlDocente));
    }, errore => console.error('Impossibile leggere le notifiche delle richieste:', errore));
    const interrompiLezioni = ascoltaRaccolta('lezioni_private', snapshot => {
      setLezioni(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })).filter(lezione => lezione.notificaMaestroCreataIl && appartieneAlDocente(lezione)));
    }, errore => console.error('Impossibile leggere le notifiche delle lezioni:', errore));
    return () => { interrompiRichieste(); interrompiLezioni(); };
  }, [amministratore, utenteLoggato?.id, utenteLoggato?.username]);

  const notifiche = [
    ...richieste.map(richiesta => ({
      id: richiesta.id,
      raccolta: 'richieste_lezioni_private',
      letto: Boolean(richiesta.lettoDalMaestro),
      data: richiesta.dataRichiesta || '',
      titolo: 'Nuova richiesta dall’App Allievi',
      testo: `${richiesta.nomeAllievo || 'Un allievo'} chiede una lezione privata il ${formattaDataIT(richiesta.dataDesiderata)} alle ${richiesta.oraDesiderata || ''}.`,
      docente: richiesta.maestroNome || ''
    })),
    ...lezioni.map(lezione => {
      const quantita = Math.max(1, Number(lezione.notificaMaestroQuantita || 1));
      return {
        id: lezione.id,
        raccolta: 'lezioni_private',
        letto: Boolean(lezione.lettoDalMaestro),
        data: lezione.notificaMaestroCreataIl || '',
        titolo: quantita > 1 ? `${quantita} lezioni private inserite dalla segreteria` : 'Nuova lezione privata inserita dalla segreteria',
        testo: `${lezione.Allievo || 'Allievo'} · ${formattaDataIT(lezione.Data)} · ${lezione.OraInizio || ''}${lezione.OraFine ? `–${lezione.OraFine}` : ''}`,
        docente: lezione.Maestro || ''
      };
    })
  ].sort((prima, dopo) => String(dopo.data || '').localeCompare(String(prima.data || '')));
  const nonLette = notifiche.filter(notifica => !notifica.letto);
  const segnaComeLetta = async notifica => {
    if (notifica.letto) return;
    await updateDoc(doc(db, notifica.raccolta, notifica.id), { lettoDalMaestro: true, notificaLettaIl: new Date().toISOString() });
  };
  const segnaTutteComeLette = async () => {
    await Promise.all(nonLette.map(segnaComeLetta));
  };

  return <div style={{ maxWidth: 850, color: '#2d1721' }}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <div><h2 style={{ margin: 0, color: '#57132c' }}>🔔 Notifiche lezioni private</h2><p style={{ margin: '8px 0 16px', color: '#553744', lineHeight: 1.5 }}>Qui trovi le nuove richieste dall’App Allievi e le lezioni inserite dalla segreteria per te.</p></div>
      {nonLette.length > 0 && <button type="button" onClick={segnaTutteComeLette} style={{ padding: '9px 12px', border: 0, borderRadius: 8, background: '#6d43c1', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>✓ Segna tutte come lette</button>}
    </div>
    {notifiche.length ? <div style={{ display: 'grid', gap: 10 }}>{notifiche.slice(0, 60).map(notifica => <button key={`${notifica.raccolta}-${notifica.id}`} type="button" onClick={() => segnaComeLetta(notifica)} style={{ width: '100%', padding: 15, borderRadius: 12, border: `1px solid ${notifica.letto ? '#eadde2' : '#e2a0bd'}`, borderLeft: `5px solid ${notifica.letto ? '#b8a8af' : '#d82d62'}`, background: notifica.letto ? '#fff' : '#fff3f7', color: '#2d1721', textAlign: 'left', cursor: notifica.letto ? 'default' : 'pointer', boxShadow: notifica.letto ? 'none' : '0 7px 18px rgba(216,45,98,.08)' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}><strong style={{ fontSize: 16 }}>{notifica.letto ? '✓ ' : '🔔 '}{notifica.titolo}</strong>{!notifica.letto && <span style={{ padding: '4px 7px', borderRadius: 12, background: '#d82d62', color: '#fff', fontSize: 10, fontWeight: 'bold' }}>NUOVA</span>}</div><span style={{ display: 'block', marginTop: 7, color: '#553744', lineHeight: 1.4 }}>{notifica.testo}</span>{notifica.docente && amministratore && <small style={{ display: 'block', marginTop: 7, color: '#7f2443' }}>Docente: {notifica.docente}</small>}<small style={{ display: 'block', marginTop: 8, color: '#80616e' }}>{formattaDataIT(notifica.data)}</small></button>)}</div> : <div style={{ padding: 20, borderRadius: 12, background: '#fff', border: '1px solid #eadde2', color: '#6f6268' }}>Non sono ancora arrivate notifiche per le tue lezioni private.</div>}
  </div>;
}

function RichiesteLezioniPrivate({ utenteLoggato }) {
  const [richieste, setRichieste] = useState([]);
  const [lezioni, setLezioni] = useState([]);
  const [allievi, setAllievi] = useState([]);
  const [disponibilitaApp, setDisponibilitaApp] = useState(DISPONIBILITA_LEZIONI_PREDEFINITA);
  const [indisponibilitaDocenti, setIndisponibilitaDocenti] = useState({ data: '', maestri: [] });
  const [bozze, setBozze] = useState({});
  const [azioneInCorso, setAzioneInCorso] = useState('');
  const ruolo = String(utenteLoggato.ruolo || '').toLowerCase();
  const amministratore = ruolo === 'admin';

  useEffect(() => {
    const stopRichieste = ascoltaRaccolta('richieste_lezioni_private', snapshot => setRichieste(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })).filter(r => amministratore || r.maestroId === utenteLoggato.id || String(r.maestroNome || '').toLowerCase() === String(utenteLoggato.username || '').toLowerCase()).sort((a, b) => String(b.dataRichiesta || '').localeCompare(String(a.dataRichiesta || '')))), errore => console.error('Impossibile caricare le richieste:', errore));
    const stopLezioni = ascoltaRaccolta('lezioni_private', snapshot => setLezioni(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))), errore => console.error('Impossibile caricare le lezioni:', errore));
    const stopAllievi = ascoltaRaccolta('allievi', snapshot => setAllievi(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))), errore => console.error('Impossibile caricare gli allievi:', errore));
    const stopDisponibilita = onSnapshot(doc(db, 'impostazioni', 'lezioni_private'), snapshot => { if (snapshot.exists()) { const impostazioni = snapshot.data(); if (impostazioni.fasce) setDisponibilitaApp(completaDisponibilitaLezioniPrivate(impostazioni.fasce, impostazioni.fascePerData || {})); setIndisponibilitaDocenti(impostazioni.indisponibilitaDocenti || { data: '', maestri: [] }); } }, errore => console.error('Impossibile leggere le disponibilità:', errore));
    return () => { stopRichieste(); stopLezioni(); stopAllievi(); stopDisponibilita(); };
  }, [amministratore, utenteLoggato.id, utenteLoggato.username]);

  const aggiornaBozza = (richiesta, modifica) => setBozze(correnti => ({ ...correnti, [richiesta.id]: { data: richiesta.dataProposta || richiesta.dataDesiderata || '', ora: richiesta.oraProposta || richiesta.oraDesiderata || '', nota: richiesta.notaRisposta || '', ...correnti[richiesta.id], ...modifica } }));
  const orarioOccupato = (richiesta, data, ora) => lezioni.some(lezione => String(lezione.Maestro || '').trim().toLowerCase() === String(richiesta.maestroNome || '').trim().toLowerCase() && lezione.Data === data && orariLezioneSiSovrappongono(ora, oraFineLezionePrivata(ora), lezione.OraInizio, lezione.OraFine));
  const slotCompleto = (data, ora) => lezioni.filter(lezione => lezione.Data === data && orariLezioneSiSovrappongono(ora, oraFineLezionePrivata(ora), lezione.OraInizio, lezione.OraFine)).length >= MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE;
  const allievoGiaOccupato = (richiesta, data, ora) => lezioneSovrappostaPerAllievo(lezioni, {
    allievoId: richiesta.allievoId,
    allievoNome: richiesta.nomeAllievo,
    data,
    oraInizio: ora,
    oraFine: ''
  });
  const maestroNonDisponibile = (nomeMaestro, data) => maestroImpegnatoNelPeriodo(indisponibilitaDocenti, nomeMaestro, data);
  const eRagazzaFinoA13Anni = (lezione) => {
    const anagrafica = allievi.find(allievo => allievo.id === lezione.allievoId) || allievi.find(allievo => `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim().toLowerCase() === String(lezione.Allievo || '').trim().toLowerCase());
    const eta = lezione.etaAllievo ?? calcolaEtaAllievo(anagrafica?.DataNascita, anagrafica?.CodiceFiscale);
    const sesso = lezione.sessoAllievo || anagrafica?.Sesso || '';
    return eta !== null && Number(eta) <= 13 && /^(f|femmina)/i.test(String(sesso).trim());
  };

  const scambioConLuisaDisponibile = (richiesta, data, ora) => {
    const maestro = String(richiesta.maestroNome || '').trim().toLowerCase();
    if (!['lia di martino', 'salvatore renna'].includes(maestro) || Number(richiesta.etaAllievo) < 14) return null;
    const lezioneDaSpostare = lezioni.find(lezione => String(lezione.Maestro || '').trim().toLowerCase() === maestro && lezione.Data === data && lezione.OraInizio === ora && eRagazzaFinoA13Anni(lezione));
    const luisaOccupata = lezioni.some(lezione => String(lezione.Maestro || '').trim().toLowerCase() === 'luisa pagano' && lezione.Data === data && lezione.OraInizio === ora);
    return lezioneDaSpostare && !luisaOccupata ? lezioneDaSpostare : null;
  };

  const liberaRiservaRichiesta = async richiesta => {
    const data = richiesta.riservaFasciaId || richiesta.dataDesiderata;
    if (!data) return;
    const riferimentoRiserva = doc(db, 'riserve_lezioni_private', data);
    await runTransaction(db, async transazione => {
      const riserva = await transazione.get(riferimentoRiserva);
      if (!riserva.exists()) return;
      const occupazioni = Array.isArray(riserva.data().occupazioni) ? riserva.data().occupazioni : [];
      transazione.update(riferimentoRiserva, {
        occupazioni: occupazioni.filter(occupazione => occupazione.chiave !== `richiesta-${richiesta.id}`),
        aggiornataIl: new Date().toISOString()
      });
    });
  };

  const approva = async richiesta => {
    const bozza = bozze[richiesta.id] || { data: richiesta.dataProposta || richiesta.dataDesiderata, ora: richiesta.oraProposta || richiesta.oraDesiderata, nota: '' };
    if (!bozza.data || !bozza.ora) return alert('Indica data e orario prima di approvare.');
    if (!orarioConsentitoLezioniPrivate(bozza.data, bozza.ora, disponibilitaApp)) return alert('L’orario scelto non è tra le disponibilità pubblicate dalla Web App.');
    if (maestroNonDisponibile(richiesta.maestroNome, bozza.data)) return alert(`Il M° ${richiesta.maestroNome} risulta impegnato/a in questa data. Proponi un altro orario o assegna la lezione a un altro Maestro dall’agenda.`);
    if (slotCompleto(bozza.data, bozza.ora)) return alert(`In questo orario sono già presenti ${MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE} lezioni private.`);
    const conflittoAllievo = allievoGiaOccupato(richiesta, bozza.data, bozza.ora);
    if (conflittoAllievo) return alert(`${richiesta.nomeAllievo} ha già una lezione privata dalle ${conflittoAllievo.OraInizio} alle ${conflittoAllievo.OraFine || oraFineLezionePrivata(conflittoAllievo.OraInizio)} in questa data. Non puoi approvarne un'altra sovrapposta.`);
    const lezioneDaSpostare = orarioOccupato(richiesta, bozza.data, bozza.ora) ? scambioConLuisaDisponibile(richiesta, bozza.data, bozza.ora) : null;
    if (orarioOccupato(richiesta, bozza.data, bozza.ora) && !lezioneDaSpostare) return alert('Questo orario è già occupato da una lezione privata. Proponi un altro orario.');
    setAzioneInCorso(richiesta.id);
    try {
      const [h, m] = bozza.ora.split(':').map(Number); const fine = new Date(2000, 0, 1, h, m); fine.setMinutes(fine.getMinutes() + 45);
      const oraFine = `${String(fine.getHours()).padStart(2, '0')}:${String(fine.getMinutes()).padStart(2, '0')}`;
      const batch = writeBatch(db);
      if (lezioneDaSpostare) batch.update(doc(db, 'lezioni_private', lezioneDaSpostare.id), { Maestro: 'Luisa Pagano', riorganizzataAutomaticamente: true, motivoRiorganizzazione: 'Spazio liberato per un allievo dai 14 anni in su.' });
      batch.set(doc(collection(db, 'lezioni_private')), { Maestro: richiesta.maestroNome, Allievo: richiesta.nomeAllievo, allievoId: richiesta.allievoId || '', etaAllievo: richiesta.etaAllievo ?? null, sessoAllievo: richiesta.sessoAllievo || '', Telefono: richiesta.telefonoAllievo || '', Data: bozza.data, OraInizio: bozza.ora, OraFine: oraFine, Durata: '45 minuti', richiestaLezioneId: richiesta.id });
      batch.update(doc(db, 'richieste_lezioni_private', richiesta.id), { stato: 'approvata', dataProposta: bozza.data, oraProposta: bozza.ora, notaRisposta: bozza.nota.trim() || (lezioneDaSpostare ? 'La tua lezione privata è stata confermata. L’organizzazione ha aggiornato la disponibilità dei docenti.' : 'La tua lezione privata è stata confermata.'), dataDecisione: new Date().toISOString(), maestroRisposta: utenteLoggato.username, lettoDalMaestro: true, lettoDallAllievo: false });
      await batch.commit();
      await registraLogOperazione(utenteLoggato.username, `${lezioneDaSpostare ? 'Riorganizzata lezione con Luisa e ' : ''}approvata lezione privata per ${richiesta.nomeAllievo}: ${formattaDataIT(bozza.data)} ${bozza.ora}`);
    } catch (errore) { console.error(errore); alert('Non è stato possibile approvare la richiesta.'); }
    finally { setAzioneInCorso(''); }
  };

  const proponi = async richiesta => {
    const bozza = bozze[richiesta.id] || { data: richiesta.dataProposta || richiesta.dataDesiderata || '', ora: richiesta.oraProposta || richiesta.oraDesiderata || '', nota: richiesta.notaRisposta || '' };
    if (!bozza.data || !bozza.ora) return alert('Scegli una data e un orario da proporre.');
    const dataAttuale = richiesta.dataProposta || richiesta.dataDesiderata || '';
    const oraAttuale = richiesta.oraProposta || richiesta.oraDesiderata || '';
    if (bozza.data === dataAttuale && bozza.ora === oraAttuale) return alert(`Per proporre un'alternativa puoi mantenere la data, ma scegli un orario diverso da ${oraAttuale}.`);
    if (!orarioConsentitoLezioniPrivate(bozza.data, bozza.ora, disponibilitaApp)) return alert('L’orario scelto non è tra le disponibilità pubblicate dalla Web App.');
    if (maestroNonDisponibile(richiesta.maestroNome, bozza.data)) return alert(`Il M° ${richiesta.maestroNome} risulta impegnato/a in questa data. Scegli un altro orario o un altro Maestro.`);
    if (slotCompleto(bozza.data, bozza.ora)) return alert(`In questo orario sono già presenti ${MASSIMO_LEZIONI_PRIVATE_CONTEMPORANEE} lezioni private.`);
    const conflittoAllievo = allievoGiaOccupato(richiesta, bozza.data, bozza.ora);
    if (conflittoAllievo) return alert(`${richiesta.nomeAllievo} ha già una lezione privata sovrapposta in questa data. Proponi un altro orario.`);
    if (orarioOccupato(richiesta, bozza.data, bozza.ora)) return alert('Anche questo orario è già occupato. Scegline un altro libero.');
    setAzioneInCorso(richiesta.id);
    try { await updateDoc(doc(db, 'richieste_lezioni_private', richiesta.id), { stato: 'orario_proposto', dataProposta: bozza.data, oraProposta: bozza.ora, notaRisposta: bozza.nota.trim() || 'Ti propongo questo orario alternativo. Confermeremo la lezione appena possibile.', dataDecisione: new Date().toISOString(), maestroRisposta: utenteLoggato.username, lettoDalMaestro: true, lettoDallAllievo: false }); await registraLogOperazione(utenteLoggato.username, `Proposto nuovo orario lezione privata a ${richiesta.nomeAllievo}`); }
    catch (errore) { console.error(errore); alert('Non è stato possibile proporre il nuovo orario.'); }
    finally { setAzioneInCorso(''); }
  };

  const rifiuta = async richiesta => {
    const bozza = bozze[richiesta.id] || {};
    setAzioneInCorso(richiesta.id);
    try { await updateDoc(doc(db, 'richieste_lezioni_private', richiesta.id), { stato: 'non_disponibile', notaRisposta: bozza.nota?.trim() || 'Al momento non abbiamo disponibilità in questa fascia oraria. Contatta il Maestro per un’alternativa.', dataDecisione: new Date().toISOString(), maestroRisposta: utenteLoggato.username, lettoDalMaestro: true, lettoDallAllievo: false }); await liberaRiservaRichiesta(richiesta); await registraLogOperazione(utenteLoggato.username, `Non disponibile richiesta lezione privata di ${richiesta.nomeAllievo}`); }
    catch (errore) { console.error(errore); alert('Non è stato possibile aggiornare la richiesta.'); }
    finally { setAzioneInCorso(''); }
  };

  const pendenti = richieste.filter(r => ['in_attesa', 'richiesta_disponibilita', 'orario_proposto'].includes(r.stato));
  const CampoPropostaOrario = ({ richiesta, bozza }) => {
    const giorno = bozza.data ? new Date(`${bozza.data}T12:00:00`).getDay() : null;
    const orari = giorno !== null ? orariLezioniPrivatePerData(bozza.data, disponibilitaApp) : [];
    const valoreSelezionato = orari.includes(bozza.ora) ? bozza.ora : '';
    const cambiaData = (data) => {
      const nuovoGiorno = data ? new Date(`${data}T12:00:00`).getDay() : null;
      const nuoviOrari = nuovoGiorno !== null ? orariLezioniPrivatePerData(data, disponibilitaApp) : [];
      aggiornaBozza(richiesta, { data, ora: nuoviOrari.includes(bozza.ora) ? bozza.ora : (nuoviOrari[0] || '') });
    };
    return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
      <label>Data proposta
        <input type="date" value={bozza.data} onChange={e => cambiaData(e.target.value)} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 5, padding: 10, borderRadius: 8, border: '1px solid #d9b8c5' }} />
      </label>
      <label>Orario proposto
        <select value={valoreSelezionato} disabled={!orari.length} onChange={e => aggiornaBozza(richiesta, { ora: e.target.value })} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 5, padding: 10, borderRadius: 8, border: '1px solid #d9b8c5', background: orari.length ? '#fff' : '#f3f3f3' }}>
          <option value="">{orari.length ? 'Scegli un orario' : 'Nessun orario pubblicato'}</option>
          {orari.map(ora => <option key={ora} value={ora}>{ora} – {oraFineLezionePrivata(ora)}</option>)}
        </select>
        <small style={{ display: 'block', marginTop: 4, color: '#6f6268' }}>{orari.length ? 'Sono visibili solo gli orari stabiliti.' : 'Per questa data non ci sono orari pubblicati.'}</small>
      </label>
    </div>;
  };
  return <div style={{ color: '#2d1721', maxWidth: 930 }}><h2 style={{ marginTop: 0, color: '#57132c' }}>🔔 Richieste lezioni private</h2><p style={{ color: '#553744', lineHeight: 1.5 }}>Prima di confermare, l’orario viene sempre confrontato con le lezioni private già registrate. Puoi approvare, proporre un’alternativa oppure indicare che non sei disponibile.</p>{pendenti.length ? pendenti.map(richiesta => { const bozza = bozze[richiesta.id] || { data: richiesta.dataProposta || richiesta.dataDesiderata || '', ora: richiesta.oraProposta || richiesta.oraDesiderata || '', nota: richiesta.notaRisposta || '' }; const impegni = lezioni.filter(l => String(l.Maestro || '').trim().toLowerCase() === String(richiesta.maestroNome || '').trim().toLowerCase() && l.Data === bozza.data); const occupato = bozza.data && bozza.ora && orarioOccupato(richiesta, bozza.data, bozza.ora); return <article key={richiesta.id} style={{ marginTop: 14, padding: 18, borderRadius: 14, background: '#fff', border: '1px solid #eadde2', borderLeft: '5px solid #f59f00', boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}><strong style={{ fontSize: 18, color: '#32101e' }}>{richiesta.nomeAllievo}</strong><span style={{ float: 'right', color: '#a35d00', fontWeight: 'bold', fontSize: 12 }}>{richiesta.stato === 'richiesta_disponibilita' ? 'Disponibilità richiesta' : 'In attesa'}</span><p style={{ margin: '7px 0', color: '#5c4650' }}>Maestro: <strong>{richiesta.maestroNome}</strong> · Richiesta: {formattaDataIT(richiesta.dataDesiderata)} alle {richiesta.oraDesiderata}</p>{richiesta.notaAllievo && <p style={{ margin: '8px 0', color: '#5c4650' }}>Nota allievo: {richiesta.notaAllievo}</p>}<CampoPropostaOrario richiesta={richiesta} bozza={bozza} /><div style={{ marginTop: 10, padding: 10, borderRadius: 9, background: occupato ? '#fff0f0' : '#f0fff4', color: occupato ? '#b42318' : '#176b3a', fontSize: 13 }}>{occupato ? '⚠️ Orario occupato: non può essere approvato.' : impegni.length ? `Agenda ${richiesta.maestroNome}: ${impegni.map(l => `${l.OraInizio}-${l.OraFine}`).join(', ')}.` : '✓ Nessuna lezione registrata in questa data per il Maestro.'}</div><label style={{ display: 'block', marginTop: 10 }}>Messaggio per l’allievo<textarea value={bozza.nota} onChange={e => aggiornaBozza(richiesta, { nota: e.target.value })} rows={2} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 5, padding: 10, borderRadius: 8, border: '1px solid #d9b8c5', resize: 'vertical' }} /></label><div style={{ display: 'flex', gap: 9, marginTop: 13, flexWrap: 'wrap' }}><button type="button" disabled={azioneInCorso === richiesta.id || occupato} onClick={() => approva(richiesta)} style={{ padding: '10px 12px', border: 0, borderRadius: 8, background: '#198754', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Approva lezione</button><button type="button" disabled={azioneInCorso === richiesta.id || occupato} onClick={() => proponi(richiesta)} style={{ padding: '10px 12px', border: 0, borderRadius: 8, background: '#1971c2', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>🕒 Proponi orario</button><button type="button" disabled={azioneInCorso === richiesta.id} onClick={() => rifiuta(richiesta)} style={{ padding: '10px 12px', border: 0, borderRadius: 8, background: '#c92a2a', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Non disponibile</button></div></article>; }) : <div style={{ padding: 20, borderRadius: 12, background: '#fff', border: '1px solid #eadde2', color: '#6f6268' }}>Non ci sono richieste di lezioni private da gestire.</div>}</div>;
}

function Divise({ utenteCorrente }) {
  const [allievi, setAllievi] = useState([]);
  const [divise, setDivise] = useState([]);
  const [allievoSelezionato, setAllievoSelezionato] = useState('');
  
  const [prendemaglia, setPrendeMaglia] = useState(false);
  const [tagliaMaglia, setTagliaMaglia] = useState(TAGLIE_ABBIGLIAMENTO[3]);

  const [prendetuta, setPrendeTuta] = useState(false);
  const [tagliaTuta, setTagliaTuta] = useState(TAGLIE_ABBIGLIAMENTO[3]);

  const [prendescarpe, setPrendeScarpe] = useState(false);
  const [tagliaScarpe, setTagliaScarpe] = useState(TAGLIE_SCARPE_EUR[12]);

  const [prendeborsone, setPrendeBorsone] = useState(false);

  const caricaDati = async () => {
    const snapAllievi = await leggiRaccolta('allievi');
    const snapDivise = await leggiRaccolta('divise');
    setAllievi(ordinaAllieviPerCognomeNome(snapAllievi.docs.map(d => ({ id: d.id, ...d.data() }))));
    setDivise(snapDivise.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    // Ogni consegna o cancellazione appare subito sulle altre postazioni.
    const annullaAscoltoAllievi = ascoltaRaccolta('allievi', snap => {
      setAllievi(ordinaAllieviPerCognomeNome(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    });
    const annullaAscoltoDivise = ascoltaRaccolta('divise', snap => {
      setDivise(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => {
      annullaAscoltoAllievi();
      annullaAscoltoDivise();
    };
  }, []);

  const salvaDivisa = async (e) => {
    e.preventDefault();
    if (!allievoSelezionato) return;

    await addDoc(collection(db, 'divise'), {
      Allievo: allievoSelezionato,
      Maglia: prendemaglia ? tagliaMaglia : 'No',
      Tuta: prendetuta ? tagliaTuta : 'No',
      Scarpe: prendescarpe ? tagliaScarpe : 'No',
      Borsone: prendeborsone ? 'Sì' : 'No'
    });

    await registraLogOperazione(utenteCorrente, `Assegnata divisa/materiale a ${allievoSelezionato}`);

    setPrendeMaglia(false);
    setPrendeTuta(false);
    setPrendeScarpe(false);
    setPrendeBorsone(false);
    setAllievoSelezionato('');
    caricaDati();
  };

  const eliminaDivisa = async (id, allievo) => {
    if (window.confirm("Cancellare questa registrazione divisa?")) {
      await deleteDoc(doc(db, 'divise', id));
      await registraLogOperazione(utenteCorrente, `Cancellata registrazione divisa di ${allievo}`);
      caricaDati();
    }
  };

  const stampaReport = () => window.print();

  const inviaMailReport = () => {
    let corpoTesto = "RIEPILOGO ORDINI DIVISE E MATERIALE - ACADEMY OF ARTS\n\n";
    divise.forEach((d, index) => {
      corpoTesto += `${index + 1}. Allievo: ${d.Allievo}\n`;
      corpoTesto += `   - Maglia: ${d.Maglia !== 'No' ? d.Maglia : 'No'}\n`;
      corpoTesto += `   - Tuta: ${d.Tuta !== 'No' ? d.Tuta : 'No'}\n`;
      corpoTesto += `   - Scarpe: ${d.Scarpe !== 'No' ? d.Scarpe : 'No'}\n`;
      corpoTesto += `   - Borsone: ${d.Borsone}\n\n`;
    });
    const mailtoUrl = `mailto:?subject=${encodeURIComponent("Ordine Materiale e Divise - Academy Of Arts")}&body=${encodeURIComponent(corpoTesto)}`;
    window.location.href = mailtoUrl;
  };

  return (
    <div style={{ color: '#f8f9fa' }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #sezione-report-stampa, #sezione-report-stampa * { visibility: visible; }
          #sezione-report-stampa { position: absolute; left: 0; top: 0; width: 100%; background: #fff; color: #000; padding: 20px; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print">
        <h3 style={{ color: '#fcc419', fontFamily: 'serif' }}>Gestione Divise, Scarpe e Materiale</h3>

        <form onSubmit={salvaDivisa} style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '450px', backgroundColor: '#353b4c', padding: '20px', borderRadius: '10px', border: '1px solid #4a5266', marginBottom: '25px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Seleziona Allievo (Digita cognome):</label>
          <select value={allievoSelezionato} onChange={e => setAllievoSelezionato(e.target.value)} required style={{ padding: '8px', backgroundColor: '#2b3140', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px' }}>
            <option value="">-- Scegli Allievo --</option>
            {allievi.map(a => <option key={a.id} value={`${a.Cognome} ${a.Nome}`}>{a.Cognome} {a.Nome}</option>)}
          </select>

          <div style={{ backgroundColor: '#2b3140', padding: '10px', borderRadius: '6px', border: '1px solid #4a5266' }}>
            <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
              <input type="checkbox" checked={prendemaglia} onChange={e => setPrendeMaglia(e.target.checked)} style={{ marginRight: '8px' }} />
              Maglia
            </label>
            {prendemaglia && (
              <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '12px', marginRight: '8px' }}>Taglia:</label>
                <select value={tagliaMaglia} onChange={e => setTagliaMaglia(e.target.value)} style={{ padding: '4px', backgroundColor: '#353b4c', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px' }}>
                  {TAGLIE_ABBIGLIAMENTO.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
          </div>

          <div style={{ backgroundColor: '#2b3140', padding: '10px', borderRadius: '6px', border: '1px solid #4a5266' }}>
            <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
              <input type="checkbox" checked={prendetuta} onChange={e => setPrendeTuta(e.target.checked)} style={{ marginRight: '8px' }} />
              Tuta
            </label>
            {prendetuta && (
              <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '12px', marginRight: '8px' }}>Taglia:</label>
                <select value={tagliaTuta} onChange={e => setTagliaTuta(e.target.value)} style={{ padding: '4px', backgroundColor: '#353b4c', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px' }}>
                  {TAGLIE_ABBIGLIAMENTO.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
          </div>

          <div style={{ backgroundColor: '#2b3140', padding: '10px', borderRadius: '6px', border: '1px solid #4a5266' }}>
            <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
              <input type="checkbox" checked={prendescarpe} onChange={e => setPrendeScarpe(e.target.checked)} style={{ marginRight: '8px' }} />
              Scarpe
            </label>
            {prendescarpe && (
              <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '12px', marginRight: '8px' }}>Numero (EUR):</label>
                <select value={tagliaScarpe} onChange={e => setTagliaScarpe(e.target.value)} style={{ padding: '4px', backgroundColor: '#353b4c', color: '#fff', border: '1px solid #4a5266', borderRadius: '4px' }}>
                  {TAGLIE_SCARPE_EUR.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
          </div>

          <div style={{ backgroundColor: '#2b3140', padding: '10px', borderRadius: '6px', border: '1px solid #4a5266' }}>
            <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
              <input type="checkbox" checked={prendeborsone} onChange={e => setPrendeBorsone(e.target.checked)} style={{ marginRight: '8px' }} />
              Borsone
            </label>
          </div>

          <button type="submit" style={{ padding: '10px', cursor: 'pointer', backgroundColor: '#fcc419', color: '#000', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}>
            Registra Divisa / Materiale
          </button>
        </form>
      </div>

      <div id="sezione-report-stampa">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
          <h4 style={{ margin: 0, color: '#fcc419', fontFamily: 'serif' }}>📦 Riepilogo Consegne & Report Ordini</h4>
          <div className="no-print" style={{ display: 'flex', gap: '8px' }}>
            <button onClick={stampaReport} style={{ backgroundColor: '#1c7ed6', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
              🖨️ Stampa
            </button>
            <button onClick={inviaMailReport} style={{ backgroundColor: '#2b8a3e', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
              📧 Email
            </button>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', backgroundColor: '#353b4c', borderRadius: '8px', overflow: 'hidden' }}>
          <thead>
            <tr style={{ backgroundColor: '#2b3140', color: '#fcc419', textAlign: 'left' }}>
              <th style={{ padding: '12px' }}>Allievo</th>
              <th style={{ padding: '12px' }}>Maglia</th>
              <th style={{ padding: '12px' }}>Tuta</th>
              <th style={{ padding: '12px' }}>Scarpe</th>
              <th style={{ padding: '12px' }}>Borsone</th>
              <th className="no-print" style={{ padding: '12px' }}>Azione</th>
            </tr>
          </thead>
          <tbody>
            {divise.map(d => (
              <tr key={d.id} style={{ borderBottom: '1px solid #4a5266' }}>
                <td style={{ padding: '12px' }}><strong style={{ color: '#fff' }}>{d.Allievo}</strong></td>
                <td style={{ padding: '12px', color: '#ced4da' }}>{d.Maglia !== 'No' ? `Sì (${d.Maglia})` : 'No'}</td>
                <td style={{ padding: '12px', color: '#ced4da' }}>{d.Tuta !== 'No' ? `Sì (${d.Tuta})` : 'No'}</td>
                <td style={{ padding: '12px', color: '#ced4da' }}>{d.Scarpe !== 'No' ? `Sì (${d.Scarpe})` : 'No'}</td>
                <td style={{ padding: '12px', color: '#ced4da' }}>{d.Borsone}</td>
                <td className="no-print" style={{ padding: '12px' }}>
                  <button onClick={() => eliminaDivisa(d.id, d.Allievo)} style={{ backgroundColor: '#c92a2a', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                    Elimina
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function allievoAttivoNellApp(allievo) {
  if (!allievo.appAllievoOnline || !allievo.appAllievoUltimaAttivita) return false;
  const ultimaAttivita = allievo.appAllievoUltimaAttivita?.toDate
    ? allievo.appAllievoUltimaAttivita.toDate()
    : new Date(allievo.appAllievoUltimaAttivita);
  const differenza = Date.now() - ultimaAttivita.getTime();
  // Un orario del telefono impostato nel futuro non deve lasciare un allievo
  // online per errore: è valido solo l'ultimo aggiornamento reale di 90 secondi.
  return !Number.isNaN(ultimaAttivita.getTime()) && differenza >= -60 * 1000 && differenza < 5 * 60 * 1000;
}

// Lo stato `online` non basta: se il browser viene chiuso all'improvviso,
// Firestore può non ricevere il logout. La presenza resta valida soltanto
// per 90 secondi dall'ultimo segnale ricevuto.
function utenteStaffAttivo(utente) {
  if (!utente?.online || !utente?.lastSeen) return false;
  const ultimoAccesso = utente.lastSeen?.toDate
    ? utente.lastSeen.toDate()
    : new Date(utente.lastSeen);
  const differenza = Date.now() - ultimoAccesso.getTime();
  return !Number.isNaN(ultimoAccesso.getTime()) && differenza >= -60 * 1000 && differenza < 5 * 60 * 1000;
}

function dataUltimaPresenza(utente) {
  const valore = utente?.lastSeen?.toDate ? utente.lastSeen.toDate() : new Date(utente?.lastSeen || 0);
  const data = valore.getTime();
  return Number.isNaN(data) ? 0 : data;
}

function presentazioneMeteoTerzigno(codice) {
  const valore = Number(codice);
  if (valore === 0) return { icona: '☀️', testo: 'Sereno' };
  if ([1, 2, 3].includes(valore)) return { icona: '⛅', testo: 'Parzialmente nuvoloso' };
  if ([45, 48].includes(valore)) return { icona: '🌫️', testo: 'Nebbia' };
  if ([51, 53, 55, 56, 57].includes(valore)) return { icona: '🌦️', testo: 'Pioviggine' };
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(valore)) return { icona: '🌧️', testo: 'Pioggia' };
  if ([71, 73, 75, 77, 85, 86].includes(valore)) return { icona: '❄️', testo: 'Neve' };
  if ([95, 96, 99].includes(valore)) return { icona: '⛈️', testo: 'Temporale' };
  return { icona: '🌤️', testo: 'Meteo' };
}

function DashboardSegreteria({ utenteLoggato }) {
  const utenteCorrente = utenteLoggato.username;
  const eOperatoreAcademy = String(utenteCorrente || '').trim().toLocaleLowerCase('it-IT') === 'academyofarts';
  const nascondiPagamentiInRegola = eOperatoreAcademy;
  const chiaveTimerLezioni = `academy-of-arts-timer-lezioni-${String(utenteCorrente || 'staff').toLocaleLowerCase('it-IT')}`;
  const [allievi, setAllievi] = useState([]);
  const [accessiAppAllievi, setAccessiAppAllievi] = useState([]);
  const [mostraTuttiAccessiApp, setMostraTuttiAccessiApp] = useState(false);
  const [lezioniPrivate, setLezioniPrivate] = useState([]);
  const [docentiPrivati, setDocentiPrivati] = useState(() => MAESTRI.map(username => ({ username })));
  const [messaggiNuovi, setMessaggiNuovi] = useState(0);
  const [oraTimer, setOraTimer] = useState(() => Date.now());
  const [meteoTerzigno, setMeteoTerzigno] = useState(null);
  const audioTimerRef = useRef(null);
  const suonoTimerAttivoRef = useRef(false);
  const [timerLezioni, setTimerLezioni] = useState(() => {
    const predefiniti = [
      { id: 'sessanta', titolo: 'Timer 1 Academy', durataSecondi: 60 * 60, rimanenti: 60 * 60, inCorso: false, scadenza: null },
      { id: 'quarantacinque', titolo: 'Timer 2 Academy', durataSecondi: 45 * 60, rimanenti: 45 * 60, inCorso: false, scadenza: null }
    ];
    try {
      const salvati = JSON.parse(localStorage.getItem(`academy-of-arts-timer-lezioni-${String(utenteCorrente || 'staff').toLocaleLowerCase('it-IT')}`) || 'null');
      if (!Array.isArray(salvati) || salvati.length !== 2) return predefiniti;
      return predefiniti.map(timer => {
        const salvato = salvati.find(elemento => elemento.id === timer.id);
        return salvato ? { ...timer, ...salvato, titolo: timer.titolo } : timer;
      });
    } catch {
      return predefiniti;
    }
  });
  const ruolo = String(utenteLoggato.ruolo || '').toLowerCase();
  const gestisceMessaggi = ruolo === 'master' || ruolo === 'operatore' || ruolo === 'admin';

  const abilitaAudioTimer = () => {
    try {
      const ContestoAudio = window.AudioContext || window.webkitAudioContext;
      if (!ContestoAudio) return;
      if (!audioTimerRef.current) audioTimerRef.current = new ContestoAudio();
      if (audioTimerRef.current.state === 'suspended') audioTimerRef.current.resume();
    } catch (erroreAudio) {
      console.warn('Audio del timer non disponibile:', erroreAudio);
    }
  };

  const suonaScadenzaTimer = () => {
    const contesto = audioTimerRef.current;
    if (!contesto || contesto.state !== 'running' || suonoTimerAttivoRef.current) return;
    suonoTimerAttivoRef.current = true;
    Array.from({ length: 20 }, (_, indice) => indice).forEach(indice => {
      const ritardo = indice * 0.5;
      const oscillatore = contesto.createOscillator();
      const volume = contesto.createGain();
      oscillatore.type = 'sine';
      oscillatore.frequency.setValueAtTime(indice % 2 === 0 ? 784 : 1046.5, contesto.currentTime + ritardo);
      volume.gain.setValueAtTime(0.0001, contesto.currentTime + ritardo);
      volume.gain.exponentialRampToValueAtTime(0.18, contesto.currentTime + ritardo + 0.02);
      volume.gain.exponentialRampToValueAtTime(0.0001, contesto.currentTime + ritardo + 0.29);
      oscillatore.connect(volume);
      volume.connect(contesto.destination);
      oscillatore.start(contesto.currentTime + ritardo);
      oscillatore.stop(contesto.currentTime + ritardo + 0.3);
    });
    window.setTimeout(() => { suonoTimerAttivoRef.current = false; }, 10050);
  };

  useEffect(() => {
    const intervallo = window.setInterval(() => {
      const adesso = Date.now();
      setOraTimer(adesso);
      setTimerLezioni(timerAttuali => {
        let cambiato = false;
        const aggiornati = timerAttuali.map(timer => {
          if (!timer.inCorso || !timer.scadenza || timer.scadenza > adesso) return timer;
          cambiato = true;
          return { ...timer, rimanenti: 0, inCorso: false, scadenza: null };
        });
        if (cambiato) window.setTimeout(suonaScadenzaTimer, 0);
        return cambiato ? aggiornati : timerAttuali;
      });
    }, 1000);
    return () => window.clearInterval(intervallo);
  }, []);

  useEffect(() => {
    let annullato = false;
    const caricaMeteoTerzigno = async () => {
      try {
        const rispostaLuogo = await fetch('https://geocoding-api.open-meteo.com/v1/search?name=Terzigno&count=1&language=it&format=json', { cache: 'no-store' });
        const luogo = (await rispostaLuogo.json())?.results?.[0];
        if (!luogo) throw new Error('Terzigno non trovato');
        const urlMeteo = `https://api.open-meteo.com/v1/forecast?latitude=${luogo.latitude}&longitude=${luogo.longitude}&current=temperature_2m,weather_code&timezone=Europe%2FRome`;
        const rispostaMeteo = await fetch(urlMeteo, { cache: 'no-store' });
        const corrente = (await rispostaMeteo.json())?.current;
        if (!corrente || annullato) return;
        setMeteoTerzigno({ temperatura: Math.round(corrente.temperature_2m), ...presentazioneMeteoTerzigno(corrente.weather_code) });
      } catch (erroreMeteo) {
        if (!annullato) setMeteoTerzigno({ errore: true });
        console.warn('Impossibile aggiornare il meteo di Terzigno:', erroreMeteo);
      }
    };
    caricaMeteoTerzigno();
    const intervallo = window.setInterval(caricaMeteoTerzigno, 30 * 60 * 1000);
    return () => { annullato = true; window.clearInterval(intervallo); };
  }, []);

  useEffect(() => {
    localStorage.setItem(chiaveTimerLezioni, JSON.stringify(timerLezioni));
  }, [chiaveTimerLezioni, timerLezioni]);

  useEffect(() => {
    const carica = async () => {
      try {
        const snap = await leggiRaccolta('allievi');
        setAllievi(ordinaAllieviPerCognomeNome(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
      } catch (errore) {
        console.warn('Aggiornamento elenco allievi non riuscito:', errore);
      }
    };
    carica();
    // Prima rileggeva tutti gli allievi ogni 5 minuti: ora l'elenco arriva
    // dall'ascolto condiviso e si aggiorna da solo quando qualcosa cambia.
    return ascoltaRaccolta('allievi', () => { carica(); });
  }, []);

  useEffect(() => ascoltaRaccolta('accessi_app_allievi', snapshot => {
    setAccessiAppAllievi(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })));
  }, errore => console.error('Impossibile leggere gli accessi dell’App Allievi:', errore)), []);

  useEffect(() => ascoltaRaccolta('lezioni_private', snapshot => {
    setLezioniPrivate(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })));
  }, errore => console.error('Impossibile aggiornare il riepilogo delle lezioni private:', errore)), []);

  useEffect(() => ascoltaRaccolta('utenti_sistema', snapshot => {
    const elenco = docentiLezioniPrivateSenzaDuplicati(snapshot.docs
      .map(documento => ({ id: documento.id, ...documento.data() }))
      .filter(utenteGestisceLezioniPrivate));
    setDocentiPrivati(elenco.length ? elenco : MAESTRI.map(username => ({ username })));
  }, errore => console.error('Impossibile leggere i docenti per il riepilogo lezioni:', errore)), []);

  useEffect(() => {
    if (!gestisceMessaggi) return undefined;
    return ascoltaRaccolta('messaggi_allievi', snapshot => {
      const numero = snapshot.docs.map(documento => documento.data()).filter(messaggio => {
        if (messaggio.lettoDaStaff) return false;
        if (ruolo === 'admin') return true;
        if (ruolo === 'master') return messaggio.canale === 'segreteria' || (messaggio.canale === 'maestro' && (messaggio.maestroId === utenteLoggato.id || String(messaggio.maestroNome || '').trim().toLocaleLowerCase('it-IT') === String(utenteLoggato.username || '').trim().toLocaleLowerCase('it-IT')));
        return messaggio.canale === 'segreteria';
      }).length;
      setMessaggiNuovi(numero);
    }, errore => console.error('Impossibile aggiornare le notifiche dei messaggi:', errore));
  }, [gestisceMessaggi, ruolo, utenteLoggato.id, utenteLoggato.username]);

  const compleanni = allievi.filter(a => isCompleannoOggi(a.DataNascita, a.CodiceFiscale)).length;
  const auguriDaInviare = allievi.filter(a => isCompleannoOggi(a.DataNascita, a.CodiceFiscale) && a.auguriCompleannoInviatiIl !== dataOggiISO()).length;
  const quoteDaVerificare = allievi.filter(a => !allievoEsenteMensilita(a) && calcolaStatoPagamentoDinamico(a.DataUltimoPagamento).codice !== 'VERDE').length;
  const pagamentiInRegola = allievi.filter(a => !allievoEsenteMensilita(a) && calcolaStatoPagamentoDinamico(a.DataUltimoPagamento).codice === 'VERDE').length;
  const allieviOnlineApp = allievi.filter(allievoAttivoNellApp).length;
  const accessiOggiApp = accessiAppAllievi.filter(accesso => accesso.giornoUltimoAccesso === dataOggiISO());
  const ultimiAccessiApp = [...accessiAppAllievi].sort((prima, dopo) => String(dopo.ultimoAccesso || '').localeCompare(String(prima.ultimoAccesso || '')));
  const accessiDaMostrare = mostraTuttiAccessiApp ? ultimiAccessiApp : ultimiAccessiApp.slice(0, 7);
  const formattaDataOraAccesso = valore => {
    const data = valore?.toDate ? valore.toDate() : new Date(valore);
    return Number.isNaN(data.getTime()) ? 'Data non disponibile' : data.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const riepilogoLezioniPrivate = docentiPrivati.map(docente => ({
    maestro: docente.username
  }));
  const dataOraDashboard = new Date(oraTimer);
  const oraDelGiornoDashboard = dataOraDashboard.getHours();
  const salutoDashboard = oraDelGiornoDashboard < 12 ? 'Buongiorno' : oraDelGiornoDashboard < 18 ? 'Buon pomeriggio' : 'Buonasera';
  const dataBreveDashboard = dataOraDashboard.toLocaleDateString('it-IT');
  const giornoSettimanaDashboard = dataOraDashboard.toLocaleDateString('it-IT', { weekday: 'long' });
  const oraEstesaDashboard = dataOraDashboard.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const giornoDashboard = dataOraDashboard.getDay();
  const minutiDashboard = dataOraDashboard.getHours() * 60 + dataOraDashboard.getMinutes();
  const corsiOggiMappa = new Map();
  allievi.filter(allievo => !allievo.isMaestro && !allievo.isTirocinante).forEach(allievo => {
    appuntamentiPresenzeDelGiorno(allievo, giornoDashboard).forEach(appuntamento => {
      const id = chiaveAppelloPresenze(appuntamento);
      if (!corsiOggiMappa.has(id)) corsiOggiMappa.set(id, { id, ...appuntamento });
    });
  });
  const corsiOggi = Array.from(corsiOggiMappa.values()).sort((prima, dopo) => minutiDaOrario(prima.inizio) - minutiDaOrario(dopo.inizio) || prima.corso.localeCompare(dopo.corso, 'it'));
  const corsoInCorso = corsiOggi.find(corso => minutiDashboard >= minutiDaOrario(corso.inizio) && minutiDashboard < minutiDaOrario(corso.fine));
  const corsiSuccessivi = corsiOggi.filter(corso => minutiDaOrario(corso.inizio) > minutiDashboard);
  const lezioniPrivateOggi = lezioniPrivate
    .filter(lezione => String(lezione.Data || '') === dataOggiISO())
    .sort((prima, dopo) => minutiDaOrario(prima.OraInizio) - minutiDaOrario(dopo.OraInizio));
  const lezioniPrivateInCorso = lezioniPrivateOggi.filter(lezione => minutiDashboard >= minutiDaOrario(lezione.OraInizio) && minutiDashboard < minutiDaOrario(lezione.OraFine || oraFineLezionePrivata(lezione.OraInizio)));
  const lezioniPrivateSuccessive = lezioniPrivateOggi.filter(lezione => minutiDaOrario(lezione.OraInizio) > minutiDashboard);
  const impegniSuccessiviOggi = [
    ...corsiSuccessivi.map(corso => ({ tipo: 'corso', id: corso.id, inizio: corso.inizio, fine: corso.fine, corso })),
    ...lezioniPrivateSuccessive.map(lezione => ({ tipo: 'privata', id: lezione.id, inizio: lezione.OraInizio, fine: lezione.OraFine || oraFineLezionePrivata(lezione.OraInizio), lezione }))
  ].sort((prima, dopo) => minutiDaOrario(prima.inizio) - minutiDaOrario(dopo.inizio));
  const mostraTimerLezioni = String(utenteCorrente || '').trim().toLocaleLowerCase('it-IT') === 'academyofarts';
  const box = { background: 'rgba(255,255,255,.94)', border: '1px solid #e7dce1', borderRadius: 16, boxShadow: '0 10px 26px rgba(71,22,43,.07)' };
  const secondiRimanentiTimer = timer => timer.inCorso && timer.scadenza ? Math.max(0, Math.ceil((timer.scadenza - oraTimer) / 1000)) : Math.max(0, Number(timer.rimanenti || 0));
  const formatoTimer = secondi => `${String(Math.floor(secondi / 60)).padStart(2, '0')}:${String(secondi % 60).padStart(2, '0')}`;
  const avviaOPausaTimer = timer => {
    abilitaAudioTimer();
    const rimanenti = secondiRimanentiTimer(timer);
    setTimerLezioni(timerAttuali => timerAttuali.map(elemento => {
      if (elemento.id !== timer.id) return elemento;
      if (elemento.inCorso) return { ...elemento, rimanenti, inCorso: false, scadenza: null };
      if (rimanenti <= 0) return { ...elemento, rimanenti: elemento.durataSecondi, inCorso: true, scadenza: Date.now() + elemento.durataSecondi * 1000 };
      return { ...elemento, rimanenti, inCorso: true, scadenza: Date.now() + rimanenti * 1000 };
    }));
  };
  const reimpostaTimer = timer => setTimerLezioni(timerAttuali => timerAttuali.map(elemento => elemento.id === timer.id ? { ...elemento, rimanenti: elemento.durataSecondi, inCorso: false, scadenza: null } : elemento));
  const aggiungiMinutiTimer = (timer, minutiDaAggiungere) => setTimerLezioni(timerAttuali => timerAttuali.map(elemento => {
    if (elemento.id !== timer.id) return elemento;
    const rimanenti = elemento.inCorso && elemento.scadenza ? Math.max(0, Math.ceil((elemento.scadenza - Date.now()) / 1000)) : Math.max(0, Number(elemento.rimanenti || 0));
    const nuovoRimanente = rimanenti + minutiDaAggiungere * 60;
    return { ...elemento, rimanenti: nuovoRimanente, scadenza: elemento.inCorso ? Date.now() + nuovoRimanente * 1000 : null };
  }));
  const impostaDurataTimer = (timer, minuti) => {
    const durataSecondi = Math.min(240 * 60, Math.max(1 * 60, Number(minuti || 0) * 60));
    if (!Number.isFinite(durataSecondi)) return;
    setTimerLezioni(timerAttuali => timerAttuali.map(elemento => elemento.id === timer.id ? { ...elemento, durataSecondi, rimanenti: durataSecondi, inCorso: false, scadenza: null } : elemento));
  };

  return (
    <div style={{ color: '#253047', maxWidth: 1180 }}>
      <style>{`
        @keyframes ballerinoCompleanno {
          0%, 100% { transform: translateY(0) scale(1.04, .94); }
          12% { transform: translateY(0) scale(1.08, .88); }
          45% { transform: translateY(-13px) scale(.96, 1.05); }
          68% { transform: translateY(-5px) scale(1, 1.01); }
          82% { transform: translateY(0) scale(1.04, .94); }
        }
        .ballerino-compleanno { animation: ballerinoCompleanno 1.35s cubic-bezier(.25, .7, .35, 1) infinite; }
        @keyframes timerScadutoLampeggio {
          0%, 100% { background: #fff4f4; box-shadow: 0 0 0 0 rgba(201,42,42,0), 0 7px 16px rgba(201,42,42,.08); }
          50% { background: #ffd6d6; box-shadow: 0 0 0 7px rgba(201,42,42,.20), 0 10px 24px rgba(201,42,42,.28); }
        }
        .timer-scaduto { animation: timerScadutoLampeggio .9s ease-in-out infinite; }
        @media (max-width: 1100px) {
          .dashboard-header { padding: 166px 18px 20px !important; }
          .dashboard-meta { top: 14px !important; left: 18px !important; right: 18px !important; width: auto !important; }
        }
        @media (max-width: 600px) {
          .dashboard-header { padding: 184px 18px 20px !important; }
          .dashboard-meta { top: 12px !important; left: 12px !important; right: 12px !important; gap: 5px !important; }
          .dashboard-iscritti { font-size: 10px !important; padding: 6px 8px !important; }
        }
      `}</style>
      <div className="dashboard-header" style={{ ...box, position: 'relative', padding: '26px 590px 26px 28px', marginBottom: 18, overflow: 'hidden', border: '1px solid #471225', backgroundImage: "linear-gradient(100deg, rgba(20,5,13,.98) 0%, rgba(55,10,29,.93) 59%, rgba(109,10,48,.68) 100%), url('/sfondo-login-segreteria.webp')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <p style={{ margin: 0, color: '#ff8aaa', fontWeight: 'bold', letterSpacing: 1.5, fontSize: 11 }}>ACADEMY OF ARTS · SEGRETERIA</p>
        <h1 style={{ margin: '8px 0 5px', fontSize: 30, color: '#ffffff', fontFamily: 'Georgia, serif' }}>{salutoDashboard}, {utenteCorrente}! 👋</h1>
        <p style={{ margin: 0, color: '#f1d9e2', textTransform: 'capitalize' }}>📅 {giornoSettimanaDashboard} · {dataBreveDashboard} · Qui trovi una panoramica veloce della scuola.</p>
        <div className="dashboard-meta" style={{ position: 'absolute', top: 18, right: 24, width: 540, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 7 }}>
          <div className="dashboard-iscritti" title="Totale allievi iscritti" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px 10px', borderRadius: 16, color: '#ffe4ed', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.19)', fontSize: 12, fontWeight: 'bold' }}>👥 <span>Allievi iscritti&nbsp;{allievi.length}</span></div>
          <div title="Allievi attivi nell’app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 10px', borderRadius: 16, color: '#d8ffe7', background: 'rgba(19,110,65,.58)', border: '1px solid #74dca2', boxShadow: '0 5px 14px rgba(0,0,0,.20)', fontWeight: 'bold' }}><span aria-hidden="true" style={{ fontSize: 16 }}>📱</span><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#58e893', boxShadow: '0 0 8px #58e893' }} />{allieviOnlineApp}<small style={{ fontSize: 9, letterSpacing: .8 }}>ONLINE</small></div>
          <div aria-label={`Ora attuale ${oraEstesaDashboard}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minWidth: 0, padding: '8px 10px', borderRadius: 13, background: 'linear-gradient(135deg, rgba(252,207,80,.34), rgba(255,255,255,.12))', border: '1px solid rgba(255,211,107,.78)', boxShadow: '0 7px 18px rgba(0,0,0,.22)' }}><span aria-hidden="true" style={{ fontSize: 18 }}>🕰️</span><strong style={{ color: '#ffe29a', fontSize: 26, lineHeight: 1, letterSpacing: 1.2, fontVariantNumeric: 'tabular-nums' }}>{oraEstesaDashboard}</strong></div>
          <div title="Meteo attuale di Terzigno" style={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 9px', borderRadius: 13, color: '#e5f7ff', background: 'linear-gradient(135deg, rgba(28,120,170,.52), rgba(55,191,214,.22))', border: '1px solid rgba(125,222,255,.70)', boxShadow: '0 7px 18px rgba(0,0,0,.22)' }}>{meteoTerzigno?.errore ? <small style={{ fontWeight: 'bold', fontSize: 10 }}>🌦️ Meteo non disponibile</small> : meteoTerzigno ? <><span aria-hidden="true" style={{ fontSize: 21 }}>{meteoTerzigno.icona}</span><span style={{ minWidth: 0 }}><strong style={{ display: 'block', fontSize: 17, lineHeight: 1 }}>{meteoTerzigno.temperatura}°</strong><small style={{ display: 'block', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 9, fontWeight: 'bold' }}>TERZIGNO · {meteoTerzigno.testo}</small></span></> : <small style={{ fontWeight: 'bold', fontSize: 10 }}>🌦️ Meteo Terzigno…</small>}</div>
        </div>
      </div>

      <section style={{ ...box, marginBottom: 18, padding: '13px 15px', background: 'linear-gradient(115deg, #f1fbff, #fcf9ff)', border: '1px solid #b9d8e8' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 168 }}><span aria-hidden="true" style={{ display: 'grid', placeItems: 'center', width: 35, height: 35, borderRadius: 11, background: 'linear-gradient(135deg, #277f9b, #4d9fc0)', fontSize: 18 }}>📱</span><div><strong style={{ display: 'block', color: '#16485b', fontSize: 16 }}>Accessi App Allievi</strong><small style={{ color: '#5e7782', fontSize: 11 }}>Aggiornamento in tempo reale</small></div></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 11px', borderRadius: 11, background: '#fff', border: '1px solid #d4e7ee' }}><span aria-hidden="true">👥</span><strong style={{ color: '#176078', fontSize: 18 }}>{accessiAppAllievi.length}</strong><small style={{ color: '#54717d', fontWeight: 'bold' }}>hanno aperto l’app</small></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 11px', borderRadius: 11, background: '#f1fff5', border: '1px solid #c9ead3' }}><span aria-hidden="true">●</span><strong style={{ color: '#1f7a38', fontSize: 18 }}>{accessiOggiApp.length}</strong><small style={{ color: '#38734a', fontWeight: 'bold' }}>oggi</small></div>
          <div style={{ flex: '1 1 215px', minWidth: 0, paddingLeft: 11, borderLeft: '1px solid #cfe2eb' }}>{ultimiAccessiApp[0] ? <><small style={{ display: 'block', color: '#6d7280', fontSize: 10, fontWeight: 'bold', letterSpacing: .5 }}>ULTIMO ACCESSO</small><strong style={{ display: 'inline-block', maxWidth: '100%', marginTop: 2, color: '#624083', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ultimiAccessiApp[0].nomeAllievo}</strong><small style={{ marginLeft: 6, color: '#6e6575', fontSize: 11 }}>{formattaDataOraAccesso(ultimiAccessiApp[0].ultimoAccesso)}</small></> : <small style={{ color: '#617884' }}>In attesa del primo accesso.</small>}</div>
          <button type="button" onClick={() => setMostraTuttiAccessiApp(visibile => !visibile)} style={{ border: 0, borderRadius: 9, padding: '8px 11px', background: mostraTuttiAccessiApp ? '#e0f3f8' : '#176f89', color: mostraTuttiAccessiApp ? '#15566a' : '#fff', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }}>{mostraTuttiAccessiApp ? 'Chiudi elenco ▲' : `Vedi accessi (${ultimiAccessiApp.length}) ▾`}</button>
        </div>
        {mostraTuttiAccessiApp && <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #cfe2eb' }}>{accessiDaMostrare.length ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 7 }}>{accessiDaMostrare.map(accesso => <div key={accesso.id} style={{ padding: '8px 10px', borderRadius: 8, background: '#fff', border: '1px solid #d8e8ee' }}><strong style={{ color: '#294e5d', fontSize: 13 }}>👤 {accesso.nomeAllievo || 'Allievo'}</strong><small style={{ display: 'block', marginTop: 3, color: '#617884', fontSize: 11 }}>Ultimo accesso: {formattaDataOraAccesso(accesso.ultimoAccesso)}</small></div>)}</div> : <p style={{ margin: 0, color: '#617884', fontSize: 13 }}>Non è stato ancora registrato alcun accesso.</p>}</div>}
      </section>

      {messaggiNuovi > 0 && <Link to="/messaggi-allievi" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, padding: '14px 17px', borderRadius: 14, border: '1px solid #e03131', background: 'linear-gradient(100deg, #fff0f1, #fff8f8)', color: '#8e1d2f', textDecoration: 'none', boxShadow: '0 7px 18px rgba(177, 35, 60, .11)' }}><span><strong style={{ fontSize: 16 }}>🔔 Hai {messaggiNuovi} {messaggiNuovi === 1 ? 'messaggio da leggere' : 'messaggi da leggere'}</strong><small style={{ display: 'block', marginTop: 3, color: '#a33a4b' }}>Apri qui per leggere e rispondere agli allievi.</small></span><strong style={{ whiteSpace: 'nowrap' }}>Apri messaggi →</strong></Link>}

      {mostraTimerLezioni && <section style={{ ...box, marginBottom: 18, padding: 18, background: 'linear-gradient(135deg, #fffdf5, #f9f2ff)', border: '1px solid #dfca8a' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}><div><p style={{ margin: 0, color: '#9a6b00', fontWeight: 'bold', fontSize: 11, letterSpacing: 1.1 }}>GESTIONE LEZIONI PRIVATE</p><h2 style={{ margin: '5px 0 0', color: '#57132c', fontSize: 21 }}>⏱️ Timer delle lezioni</h2></div><small style={{ maxWidth: 340, color: '#6f5c3d', lineHeight: 1.4 }}>I due timer sono indipendenti, restano memorizzati su questo dispositivo e puoi modificarne la durata quando vuoi.</small></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(255px, 1fr))', gap: 13, marginTop: 15 }}>
          {timerLezioni.map(timer => {
            const rimanenti = secondiRimanentiTimer(timer);
            const scaduto = rimanenti === 0;
            const timerUno = timer.id === 'sessanta';
            const coloreBase = timerUno ? '#315f9e' : '#8a5a08';
            const coloreScuro = timerUno ? '#24456d' : '#5c3900';
            const sfondo = timerUno ? '#edf6ff' : '#fff6e5';
            const bordo = timerUno ? '#9fc1e8' : '#e5bf70';
            return <article key={timer.id} className={scaduto ? 'timer-scaduto' : ''} style={{ padding: 15, borderRadius: 13, background: scaduto ? '#fff4f4' : sfondo, border: `1px solid ${scaduto ? '#e8abab' : bordo}`, borderTop: `5px solid ${scaduto ? '#c92a2a' : coloreBase}` }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}><strong style={{ color: scaduto ? '#8f1c1c' : coloreScuro }}>{timer.titolo}</strong><span style={{ padding: '4px 8px', borderRadius: 12, background: timer.inCorso ? '#e9f9ed' : scaduto ? '#ffe4e4' : timerUno ? '#dcecff' : '#fff0c8', color: timer.inCorso ? '#176b3a' : scaduto ? '#b42318' : coloreScuro, fontSize: 11, fontWeight: 'bold' }}>{timer.inCorso ? '● IN CORSO' : scaduto ? '⚠ SCADUTO' : 'IN ATTESA'}</span></div><div aria-label={`Tempo rimanente ${formatoTimer(rimanenti)}`} style={{ margin: '12px 0', color: scaduto ? '#c92a2a' : coloreScuro, fontSize: 40, lineHeight: 1, letterSpacing: 1.5, fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>{formatoTimer(rimanenti)}</div><label style={{ display: 'flex', alignItems: 'center', gap: 7, color: coloreScuro, fontSize: 12, fontWeight: 'bold' }}>Durata (minuti)<input type="number" min="1" max="240" defaultValue={Math.round(timer.durataSecondi / 60)} onBlur={evento => impostaDurataTimer(timer, evento.target.value)} onKeyDown={evento => { if (evento.key === 'Enter') { evento.preventDefault(); impostaDurataTimer(timer, evento.currentTarget.value); evento.currentTarget.blur(); } }} style={{ width: 64, padding: '6px 7px', borderRadius: 7, border: `1px solid ${bordo}`, color: '#3b2731', font: 'inherit', background: '#fff' }} /></label><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 11 }}><button type="button" onClick={() => avviaOPausaTimer(timer)} style={{ border: 0, borderRadius: 8, padding: '9px 12px', background: timer.inCorso ? '#9a5d00' : coloreBase, color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>{timer.inCorso ? 'Ⅱ Pausa' : rimanenti === 0 ? '↻ Riavvia' : '▶ Avvia'}</button><button type="button" onClick={() => reimpostaTimer(timer)} style={{ border: `1px solid ${bordo}`, borderRadius: 8, padding: '9px 12px', background: '#fff', color: coloreScuro, fontWeight: 'bold', cursor: 'pointer' }}>↺ Reimposta</button><button type="button" onClick={() => aggiungiMinutiTimer(timer, 5)} style={{ border: 0, borderRadius: 8, padding: '9px 10px', background: coloreBase, color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>+5 min</button><button type="button" onClick={() => aggiungiMinutiTimer(timer, 10)} style={{ border: 0, borderRadius: 8, padding: '9px 10px', background: coloreScuro, color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>+10 min</button></div></article>;
          })}
        </div>
      </section>}

      <section style={{ ...box, marginBottom: 18, padding: 18, background: 'linear-gradient(135deg, #f8fbff, #fff8fb)', border: '1px solid #c9d7ee' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}><div><p style={{ margin: 0, color: '#315f9e', fontWeight: 'bold', fontSize: 11, letterSpacing: 1.1 }}>PROGRAMMA DI OGGI</p><h2 style={{ margin: '5px 0 0', color: '#253d68', fontSize: 21 }}>🕒 Corsi e lezioni private</h2></div><span style={{ padding: '6px 9px', borderRadius: 12, background: '#eaf2ff', color: '#315f9e', fontWeight: 'bold', fontSize: 12 }}>Oggi · {dataOraDashboard.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span></div>
        {corsoInCorso ? <article style={{ marginTop: 14, padding: '14px 16px', borderRadius: 12, background: 'linear-gradient(110deg, #163d73, #2861a6)', color: '#fff', boxShadow: '0 7px 17px rgba(32,82,145,.18)' }}><small style={{ color: '#cfe8ff', fontWeight: 'bold', letterSpacing: .7 }}>● CORSO IN CORSO ORA</small><strong style={{ display: 'block', marginTop: 4, fontSize: 19 }}>{corsoInCorso.corso} · {formattaFasciaDisciplina(corsoInCorso.fascia)}</strong><span style={{ display: 'block', marginTop: 5, color: '#eef8ff', fontWeight: 'bold' }}>🕒 {corsoInCorso.inizio} – {corsoInCorso.fine}</span></article> : <div style={{ marginTop: 14, padding: '13px 15px', borderRadius: 11, background: '#f0f5fb', color: '#51677f' }}><strong>Al momento non c’è un corso in svolgimento.</strong>{impegniSuccessiviOggi[0] && <span> Il prossimo impegno inizia alle {impegniSuccessiviOggi[0].inizio}.</span>}</div>}
        {lezioniPrivateInCorso.length > 0 && <div style={{ marginTop: 14 }}><strong style={{ color: '#6b38a4', fontSize: 13 }}>📅 LEZIONE PRIVATA IN CORSO</strong>{lezioniPrivateInCorso.map(lezione => <div key={lezione.id} style={{ marginTop: 9, padding: '11px 12px', borderRadius: 10, background: 'linear-gradient(110deg, #6b36a3, #8f56c4)', color: '#fff' }}><strong style={{ display: 'block' }}>{lezione.Allievo || 'Allievo'}</strong><span style={{ display: 'block', marginTop: 3, color: '#f7eeff', fontSize: 13 }}>🕒 {lezione.OraInizio} – {lezione.OraFine || oraFineLezionePrivata(lezione.OraInizio)} · M° {lezione.Maestro}</span></div>)}</div>}
        <div style={{ marginTop: 14 }}><strong style={{ color: '#375276', fontSize: 13 }}>DOPO, RESTANO QUESTI IMPEGNI</strong>{impegniSuccessiviOggi.length ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(205px, 1fr))', gap: 9, marginTop: 9 }}>{impegniSuccessiviOggi.map(impegno => impegno.tipo === 'corso' ? <div key={`corso-${impegno.id}`} style={{ padding: '11px 12px', borderRadius: 10, background: '#fff', border: '1px solid #d7e2f0' }}><strong style={{ color: '#24456d' }}>🕒 {impegno.inizio} – {impegno.fine}</strong><span style={{ display: 'block', marginTop: 4, color: '#4b617b', fontWeight: 'bold' }}>{impegno.corso.corso}</span><small style={{ display: 'block', marginTop: 2, color: '#647a92' }}>{formattaFasciaDisciplina(impegno.corso.fascia)}</small></div> : <Link key={`privata-${impegno.id}`} to="/lezioni#agenda-lezioni-private" style={{ padding: '11px 12px', borderRadius: 10, background: '#fbf7ff', border: '1px solid #d8c8ef', textDecoration: 'none' }}><strong style={{ color: '#63369b' }}>🕒 {impegno.inizio} – {impegno.fine}</strong><span style={{ display: 'block', marginTop: 4, color: '#392051', fontWeight: 'bold' }}>📅 Privata · {impegno.lezione.Allievo || 'Allievo'}</span><small style={{ display: 'block', marginTop: 2, color: '#76588f' }}>M° {impegno.lezione.Maestro} · Apri agenda →</small></Link>)}</div> : <p style={{ margin: '7px 0 0', color: '#61748a' }}>Non ci sono altri impegni programmati per oggi.</p>}</div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, maxWidth: 700, marginBottom: 18 }}>
        {!eOperatoreAcademy && <Link to="/allievi#lista-allievi" title="Apri l'anagrafica degli allievi" style={{ ...box, minHeight: 88, padding: 15, borderRadius: 14, borderTop: '4px solid #d42259', textDecoration: 'none', cursor: 'pointer', transition: 'transform .18s ease, box-shadow .18s ease' }}><span style={{ color: '#6c5660', fontSize: 12, fontWeight: 'bold' }}>ALLIEVI DA MODIFICARE</span><div style={{ marginTop: 6, fontSize: 29, fontWeight: 'bold', color: '#b51b4b' }}>{quoteDaVerificare}</div><small style={{ display: 'block', marginTop: 4, color: '#b51b4b', fontWeight: 'bold' }}>Apri anagrafica →</small></Link>}
        {!nascondiPagamentiInRegola && <div style={{ ...box, minHeight: 88, padding: 15, borderRadius: 14, borderTop: '4px solid #22a06b' }}><Link to="/pagamenti" title="Apri Pagamenti e Saldi" style={{ display: 'block', textDecoration: 'none', cursor: 'pointer' }}><span style={{ color: '#3b775b', fontSize: 12, fontWeight: 'bold' }}>✓ PAGAMENTI IN REGOLA</span><div style={{ marginTop: 6, fontSize: 29, fontWeight: 'bold', color: '#14804a' }}>{pagamentiInRegola}</div><small style={{ display: 'block', marginTop: 4, color: '#14804a', fontWeight: 'bold' }}>Apri pagamenti →</small></Link><div style={{ marginTop: 11, paddingTop: 10, borderTop: '1px solid #d9efe2' }}><Link to="/iscrizione-allievo" title="Inserisci un nuovo allievo" style={{ color: '#14804a', fontWeight: 'bold', textDecoration: 'none' }}>＋ Nuovo allievo</Link></div></div>}
        <Link to="/lezioni#agenda-lezioni-private" title="Apri direttamente l'agenda delle lezioni private" style={{ ...box, gridColumn: '1 / -1', padding: 16, borderRadius: 14, borderTop: '4px solid #7950f2', textDecoration: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #fbfaff, #f0edff)' }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}><div><span style={{ color: '#5f3dc4', fontSize: 12, fontWeight: 'bold' }}>📅 LEZIONI PRIVATE IN AGENDA</span><strong style={{ display: 'block', marginTop: 5, color: '#3d287b', fontSize: 18 }}>Consulta le lezioni prenotate</strong></div><span style={{ color: '#5f3dc4', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Apri agenda →</span></div><div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginTop: 13 }}>{riepilogoLezioniPrivate.map(({ maestro }) => <div key={maestro} style={{ padding: '12px 8px', borderRadius: 9, background: '#fff', border: '1px solid #ddd3ff', textAlign: 'center' }}><strong style={{ display: 'block', color: '#4c327f', fontSize: 13 }}>{maestro.split(' ')[0]}</strong><small style={{ display: 'block', marginTop: 4, color: '#6b5d83', fontSize: 10 }}>Agenda docente</small></div>)}</div></Link>
        <Link to="/compleanni" title="Apri la pagina Compleanni" style={{ ...box, position: 'relative', display: 'block', gridColumn: '1 / -1', minHeight: 88, padding: 15, borderRadius: 14, borderTop: '4px solid #ffb020', textDecoration: 'none', cursor: 'pointer', overflow: 'hidden', background: 'radial-gradient(circle at 12% 18%, rgba(255, 212, 59, .58) 0 4px, transparent 5px), radial-gradient(circle at 82% 28%, rgba(255, 107, 107, .48) 0 4px, transparent 5px), radial-gradient(circle at 65% 78%, rgba(76, 111, 255, .40) 0 3px, transparent 4px), linear-gradient(135deg, #fffdf4, #fff6db)' }}>{auguriDaInviare > 0 && <img className="ballerino-compleanno" src="/ballerina-compleanno.png" alt="Ballerina che festeggia" style={{ position: 'absolute', top: 18, right: 32, width: 40, height: 'auto', zIndex: 1, pointerEvents: 'none', filter: 'drop-shadow(0 4px 4px rgba(102, 73, 21, .28))' }} />}<span style={{ color: '#a16207', fontSize: 12, fontWeight: 'bold' }}>🎉 COMPLEANNI OGGI</span><div style={{ marginTop: 6, fontSize: 29, fontWeight: 'bold', color: '#d97706' }}>{compleanni}</div><small style={{ display: 'block', marginTop: 4, color: '#a16207', fontWeight: 'bold' }}>{auguriDaInviare > 0 ? `Auguri da inviare: ${auguriDaInviare} · Apri →` : 'Vedi elenco →'}</small></Link>
      </div>
    </div>
  );
}

function ContattaSviluppatore({ utenteLoggato }) {
  const [descrizione, setDescrizione] = useState('');
  const [screenshot, setScreenshot] = useState('');
  const [nomeScreenshot, setNomeScreenshot] = useState('');
  const [invioInCorso, setInvioInCorso] = useState(false);
  const [messaggio, setMessaggio] = useState('');
  const [errore, setErrore] = useState('');
  const [riscontri, setRiscontri] = useState([]);

  useEffect(() => ascoltaRaccolta('notifiche_staff', snapshot => {
    const idUtente = String(utenteLoggato.id || '').trim();
    const nomeUtente = String(utenteLoggato.username || '').trim().toLocaleLowerCase('it-IT');
    setRiscontri(snapshot.docs
      .map(documento => ({ id: documento.id, ...documento.data() }))
      .filter(notifica => {
        const destinatarioId = String(notifica.destinatarioId || '').trim();
        const destinatarioNome = String(notifica.destinatarioNome || '').trim().toLocaleLowerCase('it-IT');
        return (idUtente && destinatarioId === idUtente) || (!destinatarioId && destinatarioNome === nomeUtente);
      })
      .sort((a, b) => String(b.dataCreazione || '').localeCompare(String(a.dataCreazione || ''))));
  }, erroreNotifiche => console.error('Impossibile caricare i riscontri delle segnalazioni:', erroreNotifiche)), [utenteLoggato.id, utenteLoggato.username]);

  const segnaRiscontroLetto = async riscontro => {
    if (riscontro.letto) return;
    try {
      await updateDoc(doc(db, 'notifiche_staff', riscontro.id), { letto: true, dataLettura: new Date().toISOString() });
    } catch (erroreLettura) {
      console.error('Impossibile segnare il riscontro come letto:', erroreLettura);
    }
  };

  const allegaScreenshot = file => {
    setErrore('');
    if (!file) return;
    if (!file.type.startsWith('image/')) return setErrore('Puoi allegare soltanto un’immagine o uno screenshot.');
    if (file.size > 5 * 1024 * 1024) return setErrore('Lo screenshot supera 5 MB: scegli un file più leggero.');
    const lettore = new FileReader();
    lettore.onload = evento => {
      const immagine = new Image();
      immagine.onload = () => {
        const rapporto = Math.min(1, 1200 / Math.max(immagine.width, immagine.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(immagine.width * rapporto));
        canvas.height = Math.max(1, Math.round(immagine.height * rapporto));
        canvas.getContext('2d').drawImage(immagine, 0, 0, canvas.width, canvas.height);
        const compressa = canvas.toDataURL('image/jpeg', .72);
        if (compressa.length > 700000) return setErrore('Lo screenshot è ancora troppo grande dopo la riduzione. Ritaglialo e riprova.');
        setScreenshot(compressa);
        setNomeScreenshot(file.name || 'screenshot.jpg');
      };
      immagine.src = String(evento.target?.result || '');
    };
    lettore.readAsDataURL(file);
  };

  const inviaSegnalazione = async event => {
    event.preventDefault();
    setErrore(''); setMessaggio('');
    const testo = descrizione.trim();
    if (testo.length < 8) return setErrore('Descrivi il problema con almeno qualche parola, così lo sviluppatore può intervenire.');
    setInvioInCorso(true);
    try {
      await addDoc(collection(db, 'segnalazioni_bug'), {
        mittenteTipo: 'staff',
        mittenteId: utenteLoggato.id || '',
        mittenteNome: utenteLoggato.username,
        mittenteRuolo: utenteLoggato.ruolo || 'operatore',
        descrizione: testo,
        screenshot,
        nomeScreenshot,
        pagina: window.location.pathname,
        origine: 'webapp_segreteria',
        dataCreazione: new Date().toISOString(),
        stato: 'nuova'
      });
      await registraLogOperazione(utenteLoggato.username, 'Inviata segnalazione software allo sviluppatore');
      setDescrizione(''); setScreenshot(''); setNomeScreenshot('');
      setMessaggio('✅ Segnalazione inviata allo sviluppatore.');
    } catch (erroreInvio) {
      console.error(erroreInvio);
      setErrore('Non è stato possibile inviare la segnalazione. Riprova.');
    } finally { setInvioInCorso(false); }
  };

  return <div style={{ color: '#2d1721', maxWidth: 760 }}>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>🛠️ Contatta lo sviluppatore</h2>
    <p style={{ color: '#553744', lineHeight: 1.55 }}>Hai notato un’anomalia nella Web App? Descrivi cosa è successo e, se puoi, allega uno screenshot. L’Admin riceverà la segnalazione con il tuo nome.</p>
    {errore && <p style={{ padding: 12, borderRadius: 10, background: '#fff0f0', border: '1px solid #fecaca', color: '#b42318', fontWeight: 'bold' }}>{errore}</p>}
    {messaggio && <p style={{ padding: 12, borderRadius: 10, background: '#e8f8ed', border: '1px solid #a9dfbb', color: '#146c3a', fontWeight: 'bold' }}>{messaggio}</p>}
    {riscontri.length > 0 && <section style={{ display: 'grid', gap: 9, marginBottom: 16 }}><h3 style={{ margin: 0, color: '#57132c' }}>🔔 Riscontri sulle tue segnalazioni</h3>{riscontri.map(riscontro => <button key={riscontro.id} type="button" onClick={() => segnaRiscontroLetto(riscontro)} style={{ padding: 14, borderRadius: 11, border: `1px solid ${riscontro.letto ? '#d8cbd0' : '#9c36b5'}`, borderLeft: `5px solid ${riscontro.stato === 'risolta' ? '#198754' : '#7e2e99'}`, background: riscontro.letto ? '#faf7f8' : '#f6ecfb', color: '#32101e', textAlign: 'left', cursor: riscontro.letto ? 'default' : 'pointer' }}><strong style={{ display: 'block', color: riscontro.stato === 'risolta' ? '#146c3a' : '#6d287f' }}>{riscontro.titolo}</strong><span style={{ display: 'block', marginTop: 5, lineHeight: 1.45 }}>{riscontro.messaggio}</span><small style={{ display: 'block', marginTop: 7, color: '#765b66' }}>{formattaDataIT(riscontro.dataCreazione)} · {riscontro.letto ? 'Letto' : 'Tocca per segnare come letto'}</small></button>)}</section>}
    <form onSubmit={inviaSegnalazione} style={{ display: 'grid', gap: 14, padding: 20, borderRadius: 16, background: '#fff', border: '1px solid #eadde2', boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
      <label style={{ color: '#57132c', fontWeight: 'bold' }}>Descrivi il problema<textarea value={descrizione} onChange={event => setDescrizione(event.target.value)} rows={6} maxLength={1200} required placeholder="Es. entrando nella pagina… il pulsante non risponde…" style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 7, padding: 12, borderRadius: 10, border: '1px solid #d9b8c5', color: '#2d1721', font: 'inherit', resize: 'vertical' }} /></label>
      <label style={{ display: 'block', padding: 14, borderRadius: 11, border: '1px dashed #c77a99', background: '#fff9fc', color: '#57132c', fontWeight: 'bold', cursor: 'pointer' }}>📎 {nomeScreenshot ? `Screenshot allegato: ${nomeScreenshot}` : 'Allega uno screenshot (facoltativo)'}<small style={{ display: 'block', marginTop: 5, color: '#765b66', fontWeight: 'normal' }}>Massimo 5 MB. L’immagine viene ridotta automaticamente.</small><input type="file" accept="image/*" onChange={event => allegaScreenshot(event.target.files?.[0])} style={{ display: 'none' }} /></label>
      {screenshot && <div><img src={screenshot} alt="Anteprima screenshot" style={{ display: 'block', width: 'min(100%, 420px)', maxHeight: 280, objectFit: 'contain', borderRadius: 10, border: '1px solid #eadde2', background: '#f8f5f6' }} /><button type="button" onClick={() => { setScreenshot(''); setNomeScreenshot(''); }} style={{ marginTop: 8, border: 0, background: 'transparent', color: '#a71243', fontWeight: 'bold', cursor: 'pointer' }}>Rimuovi screenshot</button></div>}
      <button disabled={invioInCorso} type="submit" style={{ justifySelf: 'start', border: 0, borderRadius: 10, padding: '12px 16px', background: '#a71243', color: '#fff', fontWeight: 'bold', cursor: invioInCorso ? 'wait' : 'pointer' }}>{invioInCorso ? 'Invio in corso…' : '📤 Invia allo sviluppatore'}</button>
    </form>
  </div>;
}

function SegnalazioniBug({ utenteLoggato }) {
  const [segnalazioni, setSegnalazioni] = useState([]);
  const [aggiornamentoInCorso, setAggiornamentoInCorso] = useState('');

  useEffect(() => ascoltaRaccolta('segnalazioni_bug', snapshot => {
    setSegnalazioni(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))
      .sort((a, b) => String(b.dataCreazione || '').localeCompare(String(a.dataCreazione || ''))));
  }, errore => console.error('Impossibile caricare le segnalazioni bug:', errore)), []);

  const cambiaStato = async (segnalazione, stato) => {
    setAggiornamentoInCorso(segnalazione.id);
    try {
      await updateDoc(doc(db, 'segnalazioni_bug', segnalazione.id), {
        stato,
        gestitaDa: utenteLoggato.username,
        dataAggiornamento: new Date().toISOString()
      });
      if (segnalazione.stato !== stato) {
        const risolta = stato === 'risolta';
        const titolo = risolta ? '✅ Segnalazione risolta' : '🔎 Segnalazione in verifica';
        const messaggio = risolta
          ? 'La tua segnalazione è stata risolta da DR System Code. Grazie per averci aiutato a migliorare Academy of Arts.'
          : 'DR System Code ha preso in carico la tua segnalazione e sta verificando il problema.';
        if (segnalazione.allievoId) {
          await addDoc(collection(db, 'avvisi_allievi'), {
            allievoId: segnalazione.allievoId,
            titolo,
            messaggio,
            importante: risolta,
            letto: false,
            origine: 'riscontro_segnalazione_bug',
            segnalazioneId: segnalazione.id,
            dataCreazione: new Date().toISOString()
          });
        } else if (segnalazione.mittenteNome) {
          await addDoc(collection(db, 'notifiche_staff'), {
            destinatarioId: segnalazione.mittenteId || '',
            destinatarioNome: segnalazione.mittenteNome,
            titolo,
            messaggio,
            stato,
            letto: false,
            origine: 'riscontro_segnalazione_bug',
            segnalazioneId: segnalazione.id,
            dataCreazione: new Date().toISOString()
          });
        }
      }
    } catch (errore) {
      console.error(errore);
      alert('Non è stato possibile aggiornare la segnalazione.');
    } finally {
      setAggiornamentoInCorso('');
    }
  };

  const etichettaStato = stato => stato === 'risolta' ? 'Risolta' : stato === 'in_verifica' ? 'In verifica' : 'Nuova';
  const coloreStato = stato => stato === 'risolta' ? '#16844a' : stato === 'in_verifica' ? '#7e2e99' : '#c92a2a';
  return <div style={{ color: '#2d1721', maxWidth: 900 }}>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>🐛 Segnalazioni inviate a DR System Code</h2>
    <p style={{ color: '#553744', lineHeight: 1.5 }}>Qui arrivano le segnalazioni inviate dagli allievi e dallo staff della Segreteria. Puoi segnare ciascun problema come in verifica o risolto.</p>
    {segnalazioni.length ? segnalazioni.map(segnalazione => <article key={segnalazione.id} style={{ marginTop: 14, padding: 18, background: '#fff', border: '1px solid #eadde2', borderLeft: `5px solid ${coloreStato(segnalazione.stato)}`, borderRadius: 14, boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start', flexWrap: 'wrap' }}><div><strong style={{ fontSize: 17, color: '#32101e' }}>{segnalazione.mittenteNome || segnalazione.nomeAllievo || 'Mittente non indicato'}</strong><small style={{ display: 'block', marginTop: 4, color: '#765b66' }}>{segnalazione.mittenteTipo === 'staff' ? `Segreteria · ${segnalazione.mittenteRuolo || 'Operatore'} · ` : 'App Allievi · '}{formattaDataIT(segnalazione.dataCreazione)}</small></div><span style={{ padding: '5px 9px', borderRadius: 14, background: `${coloreStato(segnalazione.stato)}18`, color: coloreStato(segnalazione.stato), fontWeight: 'bold', fontSize: 12 }}>{etichettaStato(segnalazione.stato)}</span></div>
      <p style={{ margin: '13px 0', color: '#432332', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{segnalazione.descrizione}</p>
      {segnalazione.screenshot && <a href={segnalazione.screenshot} target="_blank" rel="noreferrer"><img src={segnalazione.screenshot} alt={`Screenshot di ${segnalazione.mittenteNome || segnalazione.nomeAllievo || 'utente'}`} style={{ display: 'block', width: 'min(100%, 360px)', maxHeight: 250, objectFit: 'contain', borderRadius: 10, border: '1px solid #eadde2', background: '#f8f5f6' }} /></a>}
      <small style={{ display: 'block', marginTop: 11, color: '#765b66' }}>{segnalazione.mittenteTipo === 'staff' ? `Pagina segnalata: ${segnalazione.pagina || 'non indicata'}` : `Tema: ${segnalazione.tema || 'Academy'} · Modalità: ${segnalazione.modalita || 'scuro'}`}</small>
      <div style={{ display: 'flex', gap: 9, marginTop: 14, flexWrap: 'wrap' }}><button type="button" disabled={aggiornamentoInCorso === segnalazione.id} onClick={() => cambiaStato(segnalazione, 'in_verifica')} style={{ border: 0, borderRadius: 8, padding: '9px 12px', background: '#7e2e99', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>In verifica</button><button type="button" disabled={aggiornamentoInCorso === segnalazione.id} onClick={() => cambiaStato(segnalazione, 'risolta')} style={{ border: 0, borderRadius: 8, padding: '9px 12px', background: '#198754', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Risolta</button></div>
    </article>) : <div style={{ marginTop: 16, padding: 22, background: '#fff', border: '1px solid #eadde2', borderRadius: 14, color: '#6f6268' }}>Non è arrivata ancora nessuna segnalazione.</div>}
  </div>;
}

function QrAppAllievi() {
  // Il QR deve funzionare anche se la webapp viene aperta in locale o in
  // anteprima: per questo usa sempre il dominio pubblico di Firebase.
  const linkAllievi = 'https://academy-of-arts-allievi.web.app';
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=420x420&margin=18&format=png&data=${encodeURIComponent(linkAllievi)}`;
  const messaggioWhatsApp = `Ciao! Accedi all'App Allievi Academy Of Arts da questo link: ${linkAllievi}`;
  const linkWhatsApp = `https://wa.me/?text=${encodeURIComponent(messaggioWhatsApp)}`;
  const [messaggio, setMessaggio] = useState('');

  const copiaLink = async () => {
    try {
      await navigator.clipboard.writeText(linkAllievi);
      setMessaggio('✓ Link copiato: puoi inviarlo agli allievi su WhatsApp.');
    } catch {
      window.prompt('Copia questo link per gli allievi:', linkAllievi);
    }
  };

  return <div style={{ maxWidth: 850, color: '#2d1721' }}>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>📱 QR App e link Allievi</h2>
    <p style={{ marginTop: 0, color: '#553744', lineHeight: 1.55 }}>Questo è il collegamento ufficiale dell’App Allievi. Il QR viene creato con l’indirizzo attuale della webapp e apre sempre direttamente l’area degli allievi.</p>
    <section style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 320px) 1fr', gap: 24, alignItems: 'center', padding: 24, borderRadius: 16, background: '#fff', border: '1px solid #ead0df', boxShadow: '0 9px 25px rgba(71,22,43,.08)' }}>
      <div style={{ padding: 14, borderRadius: 14, background: '#fff8fc', border: '1px solid #e8bcd4', textAlign: 'center' }}>
        <img src={qrSrc} alt="QR code per l'App Allievi" style={{ display: 'block', width: '100%', maxWidth: 280, margin: '0 auto', borderRadius: 8 }} />
        <strong style={{ display: 'block', marginTop: 10, color: '#8e1d55' }}>QR App Allievi</strong>
      </div>
      <div>
        <strong style={{ display: 'block', color: '#57132c', fontSize: 18 }}>Condividi con gli allievi</strong>
        <p style={{ color: '#553744', lineHeight: 1.5 }}>Possono inquadrare il codice con la fotocamera oppure ricevere il link. Da iPhone devono poi scegliere “Aggiungi a Home” da Safari.</p>
        <label style={{ display: 'block', color: '#57132c', fontSize: 13, fontWeight: 'bold' }}>LINK SEMPRE AGGIORNATO</label>
        <div style={{ marginTop: 6, padding: '11px 12px', borderRadius: 9, background: '#fff5fa', border: '1px solid #e7b7cf', color: '#57132c', fontWeight: 'bold', overflowWrap: 'anywhere' }}>{linkAllievi}</div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginTop: 15 }}>
          <button type="button" onClick={copiaLink} style={{ padding: '10px 13px', border: 0, borderRadius: 8, background: '#a71243', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>📋 Copia link</button>
          <a href={linkWhatsApp} target="_blank" rel="noreferrer" style={{ padding: '10px 13px', borderRadius: 8, background: '#1f9d55', color: '#fff', textDecoration: 'none', fontWeight: 'bold' }}>💬 Invia su WhatsApp</a>
          <a href={linkAllievi} target="_blank" rel="noreferrer" style={{ padding: '10px 13px', borderRadius: 8, background: '#6d287f', color: '#fff', textDecoration: 'none', fontWeight: 'bold' }}>↗ Apri App Allievi</a>
          <button type="button" onClick={() => window.print()} style={{ padding: '10px 13px', border: '1px solid #bc7e9d', borderRadius: 8, background: '#fff', color: '#7c1f4e', fontWeight: 'bold', cursor: 'pointer' }}>🖨️ Stampa QR</button>
        </div>
        {messaggio && <p style={{ margin: '12px 0 0', color: '#176b3a', fontWeight: 'bold' }}>{messaggio}</p>}
      </div>
    </section>
  </div>;
}

function MenuNavigazione({ utenteLoggato, gestisciLogout, avviaPausa, isMasterOrAdmin, isAdmin }) {
  const location = useLocation();
  const pathCorrente = location.pathname;
  const [soccorsoAperto, setSoccorsoAperto] = useState(false);
  const [messaggiNuovi, setMessaggiNuovi] = useState(0);
  const [approvazioniNuove, setApprovazioniNuove] = useState(0);
  const [cancellazioniNuove, setCancellazioniNuove] = useState(0);
  const [bugNuovi, setBugNuovi] = useState(0);
  const [prenotazioniShopNuove, setPrenotazioniShopNuove] = useState(0);
  const [richiesteLezioniNuove, setRichiesteLezioniNuove] = useState(0);
  const [lezioniPrivateNuove, setLezioniPrivateNuove] = useState(0);
  const [compleanniDaGestire, setCompleanniDaGestire] = useState(0);
  const ruolo = String(utenteLoggato.ruolo || '').toLowerCase();
  const eOperatoreAcademy = ruolo === 'operatore' && String(utenteLoggato.username || '').trim().toLocaleLowerCase('it-IT') === 'academyofarts';
  // La postazione Academy deve poter aprire il riepilogo dei compleanni anche
  // se il suo profilo operatore ha permessi più limitati.
  const puoVedere = chiave => utentePuoVedere(utenteLoggato, chiave) || (eOperatoreAcademy && chiave === 'compleanni');
  const docenteLezioniPrivate = utenteGestisceLezioniPrivate(utenteLoggato);
  const gestisceMessaggiAllievi = (ruolo === 'master' || ruolo === 'operatore' || ruolo === 'admin') && puoVedere('comunicazioni');
  // La campanella Comunicazioni deve indicare solo le conversazioni da leggere.
  // Le altre richieste restano segnalate accanto alla loro voce di menu.
  const notificheComunicazioni = messaggiNuovi;
  const [comunicazioniAperte, setComunicazioniAperte] = useState(() => ['/avvisi-allievi', '/messaggi-allievi'].includes(pathCorrente));
  const [carrieraAperta, setCarrieraAperta] = useState(() => ['/carriera-allievi', '/approvazioni-carriera', '/utenti-premiati', '/richieste-cancellazione'].includes(pathCorrente));
  const [lezioniAperte, setLezioniAperte] = useState(() => ['/lezioni', '/richieste-lezioni-private'].includes(pathCorrente));
  const [registroPresenzeAperto, setRegistroPresenzeAperto] = useState(() => ['/presenza-allievi', '/presenze', '/report-presenze'].includes(pathCorrente));
  const ordineMenuPredefinito = {
    gestione: ['dashboard', 'ricerca', 'anagrafica', 'iscrizione', 'presenze', 'corsi', 'compleanni', 'tesseramento', 'qr'],
    servizi: ['pagamenti', 'docenti-scuola', 'lezioni-private', 'shop', 'divise'],
    controllo: ['comunicazioni', 'calendario', 'carriera', 'report', 'log', 'sviluppatore', 'bug', 'amministrazione']
  };
  const etichetteMenuPredefinite = {
    dashboard: 'Dashboard', ricerca: 'Ricerca Allievi', iscrizione: 'Iscrizione Allievo', anagrafica: 'Anagrafica Allievi', presenze: 'Registro presenze', corsi: 'Corsi & Orari', compleanni: 'Compleanni Allievi', tesseramento: 'Modulo Tesseramento', qr: 'QR App e link Allievi',
    pagamenti: 'Pagamenti e Saldi', 'docenti-scuola': 'Docenti della scuola', 'lezioni-private': 'Gestione lezioni private', shop: 'Shop e prenotazioni', divise: 'Divise e Materiali',
    comunicazioni: 'Comunicazioni', carriera: 'Carriera e richieste', calendario: 'Calendario Eventi', report: 'Report Annuale', log: 'Log Operazioni', sviluppatore: 'Contatta lo sviluppatore', bug: 'Bug a DR System Code', amministrazione: 'Amministrazione',
    presenza_allievi: 'Appello Allievi', presenze_oggi: 'Presenza Tot. Giornaliera', report_presenze: 'Report annuale', agenda_private: 'Agenda e inserimento', docenti_private: 'Docenti, orari e impegni', richieste_private: 'Richieste dall’App Allievi', invia_avvisi: 'Invia avvisi', messaggi_allievi: 'Messaggi allievi', aggiungi_carriera: 'Aggiungi alla carriera', approvazioni_carriera: 'Approvazioni', utenti_premiati: 'Utenti premiati', richieste_cancellazione: 'Richieste cancellazione'
  };
  useEffect(() => {
    if (!gestisceMessaggiAllievi) return undefined;
    return ascoltaRaccolta('messaggi_allievi', snapshot => {
      const nuovi = snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })).filter(messaggio => {
        if (messaggio.lettoDaStaff) return false;
        if (ruolo === 'admin') return true;
        if (ruolo === 'master') {
          return messaggio.canale === 'segreteria' || (messaggio.canale === 'maestro' && (
            messaggio.maestroId === utenteLoggato.id ||
            String(messaggio.maestroNome || '').toLowerCase() === String(utenteLoggato.username || '').toLowerCase()
          ));
        }
        return messaggio.canale === 'segreteria';
      });
      setMessaggiNuovi(nuovi.length);
    }, errore => console.error('Impossibile controllare i nuovi messaggi:', errore));
  }, [gestisceMessaggiAllievi, ruolo, utenteLoggato.id, utenteLoggato.username]);

  useEffect(() => {
    if (messaggiNuovi > 0) setComunicazioniAperte(true);
  }, [messaggiNuovi]);

  // La campanella dei compleanni resta accesa finché non vengono inviati gli auguri.
  // Appena l'operatore risponde da WhatsApp, la data viene salvata sull'allievo
  // e questa notifica si aggiorna automaticamente senza dover ricaricare la pagina.
  useEffect(() => {
    return ascoltaRaccolta('allievi', snapshot => {
      const daGestire = snapshot.docs
        .map(documento => documento.data())
        .filter(allievo => isCompleannoOggi(allievo.DataNascita, allievo.CodiceFiscale))
        .filter(allievo => allievo.auguriCompleannoInviatiIl !== dataOggiISO())
        .length;
      setCompleanniDaGestire(daGestire);
    }, errore => console.error('Impossibile controllare i compleanni di oggi:', errore));
  }, []);

  useEffect(() => {
    if (notificheComunicazioni > 0) setComunicazioniAperte(true);
  }, [notificheComunicazioni]);

  useEffect(() => {
    if (ruolo !== 'master' && ruolo !== 'admin') return undefined;
    return ascoltaRaccolta('richieste_carriera', snapshot => {
      setApprovazioniNuove(snapshot.docs.filter(documento => documento.data().stato === 'in_attesa').length);
    }, errore => console.error('Impossibile controllare le approvazioni:', errore));
  }, [ruolo]);

  useEffect(() => {
    if (approvazioniNuove > 0) setCarrieraAperta(true);
  }, [approvazioniNuove]);

  useEffect(() => ascoltaRaccolta('prenotazioni_shop', snapshot => {
    setPrenotazioniShopNuove(snapshot.docs.filter(documento => documento.data().stato === 'in_attesa').length);
  }, errore => console.error('Impossibile controllare le prenotazioni Shop:', errore)), []);

  useEffect(() => {
    if (ruolo !== 'admin' && !docenteLezioniPrivate) return undefined;
    return ascoltaRaccolta('richieste_lezioni_private', snapshot => {
      const nuove = snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })).filter(richiesta => ['in_attesa', 'richiesta_disponibilita'].includes(richiesta.stato) && (ruolo === 'admin' || richiesta.maestroId === utenteLoggato.id || String(richiesta.maestroNome || '').toLowerCase() === String(utenteLoggato.username || '').toLowerCase()));
      setRichiesteLezioniNuove(nuove.length);
    }, errore => console.error('Impossibile controllare le richieste lezioni:', errore));
  }, [ruolo, docenteLezioniPrivate, utenteLoggato.id, utenteLoggato.username]);

  // Le lezioni create direttamente dalla Web App hanno una propria notifica
  // persistente. L'ID del docente evita che l'avviso appaia ad altri maestri.
  useEffect(() => {
    if (ruolo !== 'admin' && !docenteLezioniPrivate) return undefined;
    return ascoltaRaccolta('lezioni_private', snapshot => {
      const nuove = snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })).filter(lezione => {
        if (!lezione.notificaMaestroCreataIl || lezione.lettoDalMaestro) return false;
        if (ruolo === 'admin') return true;
        const idDocente = String(lezione.maestroId || '').trim();
        return idDocente
          ? idDocente === String(utenteLoggato.id || '').trim()
          : String(lezione.Maestro || '').trim().toLocaleLowerCase('it-IT') === String(utenteLoggato.username || '').trim().toLocaleLowerCase('it-IT');
      });
      setLezioniPrivateNuove(nuove.length);
    }, errore => console.error('Impossibile controllare le nuove lezioni private:', errore));
  }, [ruolo, docenteLezioniPrivate, utenteLoggato.id, utenteLoggato.username]);

  const notificheLezioniPrivate = richiesteLezioniNuove + lezioniPrivateNuove;

  useEffect(() => {
    if (notificheLezioniPrivate > 0) setLezioniAperte(true);
  }, [notificheLezioniPrivate]);

  useEffect(() => {
    if (ruolo !== 'admin') return undefined;
    return ascoltaRaccolta('segnalazioni_bug', snapshot => {
      setBugNuovi(snapshot.docs.filter(documento => documento.data().stato === 'nuova').length);
    }, errore => console.error('Impossibile controllare le segnalazioni bug:', errore));
  }, [ruolo]);

  useEffect(() => {
    if (ruolo !== 'master' && ruolo !== 'admin') return undefined;
    return ascoltaRaccolta('richieste_cancellazione_allievi', snapshot => {
      setCancellazioniNuove(snapshot.docs.filter(documento => documento.data().stato === 'in_attesa').length);
    }, errore => console.error('Impossibile controllare le richieste di cancellazione:', errore));
  }, [ruolo]);

  const menuStyle = { 
    width: '278px', 
    background: 'radial-gradient(circle at 8% 0%, rgba(255, 202, 51, .20), transparent 24%), radial-gradient(circle at 95% 44%, rgba(164, 111, 5, .18), transparent 28%), linear-gradient(180deg, #070707 0%, #17120a 48%, #050505 100%)', 
    borderRight: '1px solid rgba(255, 207, 70, .42)',
    color: '#fff8e4', 
    padding: '16px 14px', 
    minHeight: '100vh', 
    display: 'flex', 
    flexDirection: 'column', 
    justifyContent: 'space-between', 
    zIndex: 10, 
    position: 'relative' 
  };
  const percorsoCorrente = `${pathCorrente}${window.location.hash || ''}`;
  const SchedaMenuOrdinabile = ({ sezione, id, children }) => {
    if (!children) return null;
    const indice = (ordineMenuPredefinito[sezione] || []).indexOf(id);
    return <div style={{ order: indice, position: 'relative' }}>{children}</div>;
  };
  const etichettaMenu = id => etichetteMenuPredefinite[id] || id;
  const coloreTrasparente = (colore, trasparenza) => {
    const valore = String(colore || '').replace('#', '');
    if (!/^[0-9a-f]{6}$/i.test(valore)) return colore;
    const numero = Number.parseInt(valore, 16);
    return `rgba(${(numero >> 16) & 255}, ${(numero >> 8) & 255}, ${numero & 255}, ${trasparenza})`;
  };

  const getLinkBoxStyle = (path, coloreAccento = '#fcc419') => {
    const isAttivo = percorsoCorrente === path;
    return {
      color: '#ffffff',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      margin: '6px 0',
      padding: '10px 12px',
      borderRadius: '11px',
      fontSize: '15px',
      lineHeight: 1.25,
      fontWeight: 800,
      fontFamily: 'Montserrat, Trebuchet MS, Arial, sans-serif',
      letterSpacing: '.25px',
      background: isAttivo ? `linear-gradient(115deg, ${coloreAccento} 0%, ${coloreTrasparente(coloreAccento, .76)} 100%)` : `linear-gradient(125deg, ${coloreTrasparente(coloreAccento, .58)} 0%, ${coloreTrasparente(coloreAccento, .25)} 72%, rgba(14,10,13,.83) 100%)`,
      border: `1px solid ${coloreTrasparente(coloreAccento, .86)}`,
      borderLeft: `5px solid ${isAttivo ? '#ffffff' : coloreAccento}`,
      boxShadow: isAttivo ? `0 8px 21px ${coloreTrasparente(coloreAccento, .44)}` : `inset 0 1px 0 ${coloreTrasparente('#ffffff', .18)}, 0 5px 14px ${coloreTrasparente(coloreAccento, .18)}`,
      transition: 'background .2s ease, transform .2s ease, box-shadow .2s ease'
    };
  };

  const getPulsanteMenuStyle = (aperto, attivo, coloreAccento = '#f59e0b') => ({
    width: '100%', color: attivo ? '#171006' : '#fff5d7', display: 'flex', alignItems: 'center', margin: '6px 0', padding: '10px 12px', borderRadius: '11px', fontSize: '15px', lineHeight: 1.25, fontWeight: 800, fontFamily: 'Montserrat, Trebuchet MS, Arial, sans-serif', letterSpacing: '.25px', textAlign: 'left', cursor: 'pointer',
    background: attivo ? `linear-gradient(115deg, ${coloreAccento}, ${coloreTrasparente(coloreAccento, .76)})` : (aperto ? `linear-gradient(125deg, ${coloreTrasparente(coloreAccento, .72)}, ${coloreTrasparente(coloreAccento, .32)})` : `linear-gradient(125deg, ${coloreTrasparente(coloreAccento, .56)}, ${coloreTrasparente(coloreAccento, .22)} 72%, rgba(14,10,13,.83))`), border: `1px solid ${coloreTrasparente(coloreAccento, .86)}`, borderLeft: `5px solid ${attivo ? '#ffffff' : coloreAccento}`, boxShadow: attivo ? `0 8px 21px ${coloreTrasparente(coloreAccento, .44)}` : `inset 0 1px 0 ${coloreTrasparente('#ffffff', .18)}, 0 5px 14px ${coloreTrasparente(coloreAccento, .18)}`, transition: 'background .2s ease, transform .2s ease, box-shadow .2s ease'
  });
  const getSottoLinkStyle = path => ({ ...getLinkBoxStyle(path), margin: '4px 0 4px 12px', padding: '8px 10px', fontSize: '14px', fontWeight: 600, borderRadius: '9px', borderLeft: percorsoCorrente === path ? '3px solid #ffb3ca' : '3px solid rgba(255,255,255,.18)' });
  const titoloSezioneStyle = { display: 'flex', alignItems: 'center', gap: 7, margin: '18px 7px 7px', color: '#ffd866', fontSize: 10, fontWeight: 800, fontFamily: 'Montserrat, Trebuchet MS, Arial, sans-serif', letterSpacing: '1.4px' };
  const iconaMenu = icona => <span aria-hidden="true" style={{ display: 'inline-grid', placeItems: 'center', flex: '0 0 27px', width: 27, height: 27, marginRight: 9, borderRadius: 8, background: 'linear-gradient(145deg, rgba(255,225,128,.26), rgba(255,255,255,.035))', border: '1px solid rgba(255,222,123,.42)', boxShadow: 'inset 0 1px 0 rgba(255,250,225,.22), 0 3px 8px rgba(0,0,0,.16)', fontSize: 15 }}>{icona}</span>;

  return (
    <nav className="no-print" style={menuStyle}>
      <div>
        <h2 style={{ fontSize: '20px', borderBottom: '1px solid rgba(255,213,96,.30)', padding: '0 5px 12px', margin: 0, color: '#ffe38b', fontFamily: 'Cinzel, Georgia, serif', fontWeight: 800, letterSpacing: '.7px', textShadow: '0 2px 12px rgba(255,196,38,.18)' }}>
          Academy Of Arts 💃
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginTop: 8 }}>
          <div title={`Codice tecnico della build: ${VERSIONE_APP}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 12, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,160,190,.34)', color: '#ffb3ca', fontSize: 10, fontWeight: 'bold', letterSpacing: '.4px' }}>◈ AGGIORNATA IL {DATA_VERSIONE_APP}</div>
          <button type="button" onClick={aggiornaPaginaManuale} title="Cerca l’ultima versione e ricarica questa pagina" style={{ border: '1px solid rgba(255,160,190,.55)', borderRadius: 12, padding: '5px 8px', background: 'rgba(165,31,83,.32)', color: '#fff', fontSize: 10, fontWeight: 'bold', cursor: 'pointer' }}>↻ Aggiorna pagina</button>
        </div>
        
        <div style={{ background: 'rgba(255,255,255,.075)', padding: '12px', borderRadius: '10px', marginTop: '15px', marginBottom: '20px', border: '1px solid rgba(255,255,255,.12)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <strong style={{ fontSize: '13px', color: '#ffffff' }}>👤 {utenteLoggato.username}</strong>
              <PallinoBluOnline />
            </div>

            <button 
              onClick={gestisciLogout}
              title="Disconnetti"
              style={{ backgroundColor: '#c92a2a', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Esci
            </button>
          </div>

          <div style={{ fontSize: '10px', color: '#fcc419', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '1px' }}>
            {utenteLoggato.ruolo || 'OPERATORE'}
          </div>
        </div>

        <button 
          onClick={avviaPausa}
          style={{ width: '100%', backgroundColor: '#e67700', color: '#fff', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '15px', textAlign: 'center', fontSize: '17px', boxShadow: '0 4px 10px rgba(230, 119, 0, 0.3)' }}
        >
          ⏸️ Pausa Schermo Intero
        </button>

        <button onClick={() => setSoccorsoAperto(aperto => !aperto)} aria-expanded={soccorsoAperto} style={{ width: '100%', color: '#ffffff', background: soccorsoAperto ? '#991b1b' : 'linear-gradient(135deg, #ef4444, #b91c1c)', border: '1px solid #991b1b', margin: '8px 0', padding: '13px 14px', borderRadius: '8px', fontSize: '17px', fontWeight: 'bold', cursor: 'pointer', textAlign: 'left', boxShadow: '0 5px 13px rgba(220,38,38,.28)' }}>
          ✚ Soccorso <span style={{ float: 'right' }}>{soccorsoAperto ? '▴' : '▾'}</span>
        </button>
        {soccorsoAperto && (
          <div style={{ margin: '-2px 0 10px', padding: '10px', borderRadius: '8px', background: '#fffafa', border: '2px solid #ef4444', boxShadow: '0 4px 12px rgba(220,38,38,.12)' }}>
            <small style={{ display: 'block', marginBottom: 9, color: '#991b1b', fontWeight: 'bold', fontSize: '15px' }}>Pericolo immediato: 112</small>
            {[
              ['Emergenza unica', '112'], ['Ambulanza', '118'], ['Vigili del Fuoco', '115'], ['Polizia', '113'],
              ['Carabinieri Terzigno', '081 529 5398'], ['Emergenza Infanzia', '114']
            ].map(([nome, numero]) => <a key={numero} href={`tel:${numero.replace(/\s/g, '')}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '9px 5px', borderBottom: '1px solid #fee2e2', color: '#334155', textDecoration: 'none', fontSize: '15px' }}><span>{nome}</span><strong style={{ color: '#c24141' }}>{numero}</strong></a>)}
          </div>
        )}
        <div style={titoloSezioneStyle}><span>GESTIONE ALLIEVI</span><span style={{ height: 1, flex: 1, background: 'rgba(255,255,255,.15)' }} /></div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <SchedaMenuOrdinabile sezione="gestione" id="dashboard">{puoVedere('dashboard') && <Link to="/" style={getLinkBoxStyle('/', '#2563eb')}>{iconaMenu('🏠')}{etichettaMenu('dashboard')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="gestione" id="ricerca">{puoVedere('ricerca') && <Link to="/ricerca" style={getLinkBoxStyle('/ricerca', '#06b6d4')}>{iconaMenu('🔍')}{etichettaMenu('ricerca')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="gestione" id="iscrizione">{puoVedere('iscrizione') && <Link to="/iscrizione-allievo" style={getLinkBoxStyle('/iscrizione-allievo', '#22c55e')}>{iconaMenu('📝')}{etichettaMenu('iscrizione')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="gestione" id="anagrafica">{puoVedere('anagrafica') && <Link to="/allievi" style={getLinkBoxStyle('/allievi', '#10b981')}>{iconaMenu('📁')}{etichettaMenu('anagrafica')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="gestione" id="presenze">{(isMasterOrAdmin || ruolo === 'operatore' || ruolo === 'docente') && puoVedere('presenze') && <><button type="button" onClick={() => setRegistroPresenzeAperto(aperto => !aperto)} aria-expanded={registroPresenzeAperto} style={getPulsanteMenuStyle(registroPresenzeAperto, ['/presenza-allievi', '/presenze', '/report-presenze'].includes(pathCorrente), '#f97316')}>{iconaMenu('📋')}{etichettaMenu('presenze')} <span style={{ marginLeft: 'auto' }}>{registroPresenzeAperto ? '▴' : '▾'}</span></button>{registroPresenzeAperto && <div style={{ margin: '-3px 0 7px' }}><Link to="/presenza-allievi" style={getSottoLinkStyle('/presenza-allievi')}>👥 {etichettaMenu('presenza_allievi')}</Link><Link to="/presenze" style={getSottoLinkStyle('/presenze')}>📅 {etichettaMenu('presenze_oggi')}</Link><Link to="/report-presenze" style={getSottoLinkStyle('/report-presenze')}>📊 {etichettaMenu('report_presenze')}</Link></div>}</>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="gestione" id="corsi">{puoVedere('corsi') && <Link to="/corsi" style={getLinkBoxStyle('/corsi', '#8b5cf6')}>{iconaMenu('🎓')}{etichettaMenu('corsi')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="gestione" id="compleanni">{puoVedere('compleanni') && <Link to="/compleanni" style={getLinkBoxStyle('/compleanni', '#ec4899')}>{iconaMenu('🎂')}{etichettaMenu('compleanni')} {compleanniDaGestire > 0 && <span title={`${compleanniDaGestire} ${compleanniDaGestire === 1 ? 'compleanno da festeggiare' : 'compleanni da festeggiare'} oggi`} aria-label="Notifica compleanni" style={{ display: 'inline-grid', placeItems: 'center', minWidth: 24, height: 24, marginLeft: 'auto', borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 14, boxShadow: '0 0 0 3px rgba(224,49,49,.22)', animation: 'academyBellPulse 1.5s ease-in-out infinite' }}>🔔</span>}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="gestione" id="tesseramento">{puoVedere('tesseramento') && <Link to="/tesseramento" style={getLinkBoxStyle('/tesseramento', '#f59e0b')}>{iconaMenu('📑')}{etichettaMenu('tesseramento')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="gestione" id="qr">{(isMasterOrAdmin || ruolo === 'operatore') && puoVedere('qr_allievi') && <Link to="/qr-app-allievi" style={getLinkBoxStyle('/qr-app-allievi', '#0ea5e9')}>{iconaMenu('📱')}{etichettaMenu('qr')}</Link>}</SchedaMenuOrdinabile>
        </div>
        <div style={titoloSezioneStyle}><span>SERVIZI ACADEMY</span><span style={{ height: 1, flex: 1, background: 'rgba(255,255,255,.15)' }} /></div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <SchedaMenuOrdinabile sezione="servizi" id="divise">{puoVedere('divise') && <Link to="/divise" style={getLinkBoxStyle('/divise', '#14b8a6')}>{iconaMenu('👕')}{etichettaMenu('divise')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="servizi" id="shop">{puoVedere('shop') && <Link to="/shop" style={getLinkBoxStyle('/shop', '#d946ef')}>{iconaMenu('🛍️')}{etichettaMenu('shop')} {prenotazioniShopNuove > 0 && <span style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 'auto', borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>{prenotazioniShopNuove}</span>}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="servizi" id="lezioni-private">{puoVedere('lezioni_private') && <><button type="button" onClick={() => setLezioniAperte(aperto => !aperto)} aria-expanded={lezioniAperte} style={getPulsanteMenuStyle(lezioniAperte, ['/lezioni', '/richieste-lezioni-private', '/notifiche-lezioni-private'].includes(pathCorrente), '#ef4444')}>📅 {etichettaMenu('lezioni-private')} {notificheLezioniPrivate > 0 && <span title={`${notificheLezioniPrivate} notifiche di lezioni private da leggere`} style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 5, borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>🔔 {notificheLezioniPrivate}</span>}<span style={{ float: 'right' }}>{lezioniAperte ? '▴' : '▾'}</span></button>{lezioniAperte && <div style={{ margin: '-3px 0 7px' }}><Link to="/lezioni#agenda-lezioni-private" style={getSottoLinkStyle('/lezioni#agenda-lezioni-private')}>🗓️ {etichettaMenu('agenda_private')}</Link><Link to="/lezioni#docenti-lezioni-private" style={getSottoLinkStyle('/lezioni#docenti-lezioni-private')}>👩‍🏫 {etichettaMenu('docenti_private')}</Link>{(ruolo === 'admin' || docenteLezioniPrivate) && <><Link to="/notifiche-lezioni-private" style={getSottoLinkStyle('/notifiche-lezioni-private')}>🔔 Notifiche lezioni private {notificheLezioniPrivate > 0 && <span style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 4, borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>{notificheLezioniPrivate}</span>}</Link><Link to="/richieste-lezioni-private" style={getSottoLinkStyle('/richieste-lezioni-private')}>📨 {etichettaMenu('richieste_private')} {richiesteLezioniNuove > 0 && <span style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 4, borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>{richiesteLezioniNuove}</span>}</Link></>}</div>}</>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="servizi" id="pagamenti">{puoVedere('pagamenti') && <Link to="/pagamenti" style={getLinkBoxStyle('/pagamenti', '#22c55e')}>{iconaMenu('💳')}{etichettaMenu('pagamenti')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="servizi" id="docenti-scuola">{['master', 'admin', 'operatore'].includes(ruolo) && !eOperatoreAcademy && <Link to="/docenti-scuola" style={getLinkBoxStyle('/docenti-scuola', '#7c3aed')}>{iconaMenu('🧑‍🏫')}{isMasterOrAdmin ? etichettaMenu('docenti-scuola') : 'Richiedi docente'}</Link>}</SchedaMenuOrdinabile>
        </div>
        <div style={titoloSezioneStyle}><span>COMUNICAZIONI E CONTROLLO</span><span style={{ height: 1, flex: 1, background: 'rgba(255,255,255,.15)' }} /></div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <SchedaMenuOrdinabile sezione="controllo" id="comunicazioni">{puoVedere('comunicazioni') && <><button type="button" onClick={() => setComunicazioniAperte(aperto => !aperto)} aria-expanded={comunicazioniAperte} style={getPulsanteMenuStyle(comunicazioniAperte, ['/avvisi-allievi', '/messaggi-allievi'].includes(pathCorrente), '#f59e0b')}><span>🔔 {etichettaMenu('comunicazioni')} {notificheComunicazioni > 0 && <span title={`${notificheComunicazioni} notifiche da leggere`} style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 5, borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>{notificheComunicazioni}</span>}</span><span style={{ float: 'right' }}>{comunicazioniAperte ? '▴' : '▾'}</span></button>{comunicazioniAperte && <div style={{ margin: '-3px 0 7px' }}><Link to="/avvisi-allievi" style={getSottoLinkStyle('/avvisi-allievi')}>📣 {etichettaMenu('invia_avvisi')}</Link>{gestisceMessaggiAllievi && <Link to="/messaggi-allievi" style={getSottoLinkStyle('/messaggi-allievi')}>🔔 {etichettaMenu('messaggi_allievi')} {messaggiNuovi > 0 && <span title={`${messaggiNuovi} messaggi da leggere`} style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 4, borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>{messaggiNuovi}</span>}</Link>}</div>}</>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="controllo" id="carriera">{(ruolo === 'operatore' || ruolo === 'master' || ruolo === 'admin') && puoVedere('carriera') && <><button type="button" onClick={() => setCarrieraAperta(aperto => !aperto)} aria-expanded={carrieraAperta} style={getPulsanteMenuStyle(carrieraAperta, ['/carriera-allievi', '/approvazioni-carriera', '/utenti-premiati', '/richieste-cancellazione'].includes(pathCorrente), '#a855f7')}>🏆 {etichettaMenu('carriera')} {(ruolo === 'master' || ruolo === 'admin') && approvazioniNuove > 0 && <span title={`${approvazioniNuove} richieste carriera da approvare`} style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 5, borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>{approvazioniNuove}</span>} <span style={{ float: 'right' }}>{carrieraAperta ? '▴' : '▾'}</span></button>{carrieraAperta && <div style={{ margin: '-3px 0 7px' }}>{(ruolo === 'operatore' || ruolo === 'admin') && <Link to="/carriera-allievi" style={getSottoLinkStyle('/carriera-allievi')}>➕ {etichettaMenu('aggiungi_carriera')}</Link>}{(ruolo === 'master' || ruolo === 'admin') && <Link to="/approvazioni-carriera" style={getSottoLinkStyle('/approvazioni-carriera')}>🔔 {etichettaMenu('approvazioni_carriera')} {approvazioniNuove > 0 && <span title={`${approvazioniNuove} richieste da approvare`} style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 4, borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>{approvazioniNuove}</span>}</Link>}{(ruolo === 'master' || ruolo === 'admin') && <Link to="/utenti-premiati" style={getSottoLinkStyle('/utenti-premiati')}>🏅 {etichettaMenu('utenti_premiati')}</Link>}{(ruolo === 'master' || ruolo === 'admin') && <Link to="/richieste-cancellazione" style={getSottoLinkStyle('/richieste-cancellazione')}>🗑️ {etichettaMenu('richieste_cancellazione')}</Link>}</div>}</>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="controllo" id="calendario">{puoVedere('calendario') && <Link to="/calendario-eventi" style={getLinkBoxStyle('/calendario-eventi', '#3b82f6')}>🗓️ {etichettaMenu('calendario')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="controllo" id="report">{isMasterOrAdmin && puoVedere('report') && <Link to="/report" style={getLinkBoxStyle('/report', '#84cc16')}>📊 {etichettaMenu('report')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="controllo" id="log">{isMasterOrAdmin && <Link to="/logs" style={getLinkBoxStyle('/logs', '#64748b')}>📜 {etichettaMenu('log')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="controllo" id="sviluppatore">{!isAdmin && <Link to="/contatta-sviluppatore" style={getLinkBoxStyle('/contatta-sviluppatore', '#ef4444')}>🛠️ {etichettaMenu('sviluppatore')}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="controllo" id="bug">{isAdmin && <Link to="/segnalazioni-bug" style={getLinkBoxStyle('/segnalazioni-bug', '#e11d48')}>🐛 {etichettaMenu('bug')} {bugNuovi > 0 && <span style={{ display: 'inline-grid', placeItems: 'center', minWidth: 20, height: 20, marginLeft: 4, borderRadius: '50%', background: '#e03131', color: '#fff', fontSize: 11 }}>{bugNuovi}</span>}</Link>}</SchedaMenuOrdinabile>
          <SchedaMenuOrdinabile sezione="controllo" id="amministrazione">{isAdmin && <Link to="/admin" style={getLinkBoxStyle('/admin', '#f43f5e')}>⚙️ {etichettaMenu('amministrazione')}</Link>}</SchedaMenuOrdinabile>
        </div>
      </div>
      <div style={{ marginTop: 24, paddingTop: 15, borderTop: '1px solid rgba(255,255,255,.14)', color: '#cbb5bf', textAlign: 'center', lineHeight: 1.45, fontFamily: 'Arial, Helvetica, sans-serif' }}>
        <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '.2px' }}>Ideata e realizzata da</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, marginTop: 3 }}><img src="/dr-system-code-logo.jpg" alt="DR System Code" style={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 7, border: '1px solid rgba(255,212,59,.65)', boxShadow: '0 3px 9px rgba(0,0,0,.28)' }} /><strong style={{ color: '#ffb3ca', fontSize: 13, letterSpacing: '.2px' }}>DR System Code</strong></div>
        <div style={{ fontSize: 10, marginTop: 2, letterSpacing: '.15px' }}>Academy Of Arts · © 2026</div>
      </div>
    </nav>
  );
}

function AvvisiAllievi({ utenteCorrente }) {
  const [allievi, setAllievi] = useState([]);
  const [allievoId, setAllievoId] = useState('');
  const [titolo, setTitolo] = useState('Avviso dalla segreteria');
  const [messaggio, setMessaggio] = useState('');
  const [importante, setImportante] = useState(true);
  const [invioInCorso, setInvioInCorso] = useState(false);
  const [avvisiInviati, setAvvisiInviati] = useState([]);
  const [eliminazioneAvvisoInCorso, setEliminazioneAvvisoInCorso] = useState('');

  useEffect(() => {
    const caricaAllievi = async () => {
      const snap = await leggiRaccolta('allievi');
      setAllievi(ordinaAllieviPerCognomeNome(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    };
    caricaAllievi();
  }, []);

  useEffect(() => ascoltaRaccolta('avvisi_allievi', snapshot => {
    setAvvisiInviati(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))
      .sort((primo, secondo) => String(secondo.dataCreazione || '').localeCompare(String(primo.dataCreazione || ''))));
  }, errore => console.error('Impossibile caricare gli avvisi inviati:', errore)), []);

  const inviaAvviso = async (event) => {
    event.preventDefault();
    if (!allievoId || !messaggio.trim()) return;
    const allievo = allievi.find(a => a.id === allievoId);
    setInvioInCorso(true);
    try {
      const nuovoAvviso = await addDoc(collection(db, 'avvisi_allievi'), {
        allievoId,
        titolo: titolo.trim() || 'Avviso dalla segreteria',
        messaggio: messaggio.trim(),
        importante,
        letto: false,
        notificaPushInviata: false,
        dataCreazione: new Date().toISOString()
      });
      const rispostaPush = await fetch(URL_SERVIZIO_NOTIFICHE, {
        method: 'POST',
        headers: await intestazioniAutenticate({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ avvisoId: nuovoAvviso.id })
      });
      const esitoPush = await rispostaPush.json().catch(() => ({}));
      if (!rispostaPush.ok || !esitoPush.inviato) {
        throw new Error(esitoPush.errore || esitoPush.motivo || 'Notifica push non inviata.');
      }
      await registraLogOperazione(utenteCorrente, `Inviato avviso a ${allievo ? `${allievo.Cognome} ${allievo.Nome}` : 'allievo'}`);
      setMessaggio('');
      setTitolo('Avviso dalla segreteria');
      setImportante(true);
      alert(esitoPush.saltato
        ? 'Avviso salvato. La notifica non è stata inviata perché l’app dell’allievo è disabilitata.'
        : 'Avviso inviato e notifica arrivata al telefono dell’allievo.');
    } catch (e) {
      console.error(e);
      alert(String(e?.message || '').includes('Nessun telefono registrato')
        ? 'Avviso salvato nell’app. Per ricevere l’avviso sul telefono, l’allievo deve aprire l’App Allievi almeno una volta e consentire le notifiche.'
        : 'Avviso salvato nell’app, ma la notifica sul telefono non è stata inviata.');
    } finally {
      setInvioInCorso(false);
    }
  };

  const eliminaAvviso = async avviso => {
    if (!window.confirm(`Eliminare definitivamente l’avviso “${avviso.titolo || 'Avviso dalla segreteria'}”? L’allievo non potrà più leggerlo.`)) return;
    setEliminazioneAvvisoInCorso(avviso.id);
    try {
      await deleteDoc(doc(db, 'avvisi_allievi', avviso.id));
    } catch (errore) {
      console.error('Impossibile eliminare l’avviso:', errore);
      alert('Non è stato possibile eliminare l’avviso. Riprova.');
    } finally {
      setEliminazioneAvvisoInCorso('');
    }
  };

  return (
    <div style={{ color: '#f8f9fa', maxWidth: 760 }}>
      <h2 style={{ color: '#ff8787', marginTop: 0 }}>🔔 Avvisi Allievi</h2>
      <p style={{ color: '#ced4da' }}>Invia comunicazioni personali: cambi orario, cambio sede, promemoria o informazioni importanti.</p>
      <form onSubmit={inviaAvviso} style={{ display: 'flex', flexDirection: 'column', gap: 14, background: '#353b4c', border: '1px solid #4a5266', borderRadius: 12, padding: 22 }}>
        <label style={{ fontWeight: 'bold' }}>Destinatario</label>
        <select value={allievoId} onChange={e => setAllievoId(e.target.value)} required style={{ padding: 11, borderRadius: 7 }}>
          <option value="">-- Seleziona un allievo --</option>
          {allievi.map(a => <option key={a.id} value={a.id}>{a.Cognome} {a.Nome}</option>)}
        </select>
        <label style={{ fontWeight: 'bold' }}>Titolo avviso</label>
        <input value={titolo} onChange={e => setTitolo(e.target.value)} maxLength={80} style={{ padding: 11, borderRadius: 7 }} />
        <label style={{ fontWeight: 'bold' }}>Messaggio</label>
        <textarea value={messaggio} onChange={e => setMessaggio(e.target.value)} required rows={5} placeholder="Es. La lezione di oggi si svolgerà nella sala B alle ore 18:00." style={{ padding: 11, borderRadius: 7, resize: 'vertical' }} />
        <label style={{ fontWeight: 'bold', color: '#ffd43b' }}><input type="checkbox" checked={importante} onChange={e => setImportante(e.target.checked)} style={{ marginRight: 8 }} />🔔 Segna come avviso importante</label>
        <button disabled={invioInCorso} type="submit" style={{ padding: 12, border: 0, borderRadius: 7, background: '#ff8787', color: '#1f2430', fontWeight: 'bold', cursor: 'pointer' }}>{invioInCorso ? 'Invio…' : 'Invia avviso'}</button>
      </form>
      <section style={{ marginTop: 22, padding: 18, borderRadius: 12, background: '#2b3140', border: '1px solid #4a5266' }}><h3 style={{ margin: 0, color: '#ffd8e2' }}>📣 Avvisi inviati</h3><p style={{ margin: '7px 0 14px', color: '#ced4da', fontSize: 13 }}>Qui puoi eliminare gli avvisi non più utili.</p>{avvisiInviati.length ? <div style={{ display: 'grid', gap: 9 }}>{avvisiInviati.map(avviso => { const destinatario = allievi.find(allievo => allievo.id === avviso.allievoId); return <article key={avviso.id} style={{ padding: 12, borderRadius: 9, background: '#1f2430', borderLeft: `4px solid ${avviso.importante ? '#ff8787' : '#74c0fc'}` }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'start' }}><div><strong>{avviso.titolo || 'Avviso dalla segreteria'}</strong><small style={{ display: 'block', marginTop: 4, color: '#ced4da' }}>A: {destinatario ? `${destinatario.Cognome} ${destinatario.Nome}` : 'Allievo'} · {formattaDataIT(avviso.dataCreazione)}</small></div><button type="button" disabled={eliminazioneAvvisoInCorso === avviso.id} onClick={() => eliminaAvviso(avviso)} style={{ border: '1px solid #ff8787', borderRadius: 7, padding: '6px 8px', background: '#4b1f2a', color: '#fff', cursor: eliminazioneAvvisoInCorso === avviso.id ? 'wait' : 'pointer', fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap' }}>{eliminazioneAvvisoInCorso === avviso.id ? 'Elimino…' : '🗑️ Elimina'}</button></div><p style={{ margin: '8px 0 0', color: '#f8f9fa', fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{avviso.messaggio || ''}</p></article>; })}</div> : <p style={{ margin: 0, color: '#ced4da' }}>Non ci sono avvisi da gestire.</p>}</section>
    </div>
  );
}

function CarrieraAllieviSegreteria({ utenteLoggato }) {
  const amministratore = String(utenteLoggato.ruolo || '').toLowerCase() === 'admin';
  const [allievi, setAllievi] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [allievoId, setAllievoId] = useState('');
  const [tipo, setTipo] = useState('Titolo');
  const [titolo, setTitolo] = useState('');
  const [piazzamento, setPiazzamento] = useState('');
  const [evento, setEvento] = useState('');
  const [luogo, setLuogo] = useState('');
  const [data, setData] = useState(dataOggiISO());
  const [descrizione, setDescrizione] = useState('');
  const [invioInCorso, setInvioInCorso] = useState(false);

  useEffect(() => {
    leggiRaccolta('allievi')
      .then(snapshot => setAllievi(ordinaAllieviPerCognomeNome(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })))))
      .catch(errore => console.error('Impossibile caricare gli allievi:', errore));
    return ascoltaRaccolta('richieste_carriera', snapshot => {
      setRichieste(snapshot.docs
        .map(documento => ({ id: documento.id, ...documento.data() }))
        .filter(richiesta => amministratore || richiesta.operatoreId === utenteLoggato.id)
        .sort((a, b) => String(b.dataRichiesta || '').localeCompare(String(a.dataRichiesta || ''))));
    }, errore => console.error('Impossibile caricare le richieste carriera:', errore));
  }, [amministratore, utenteLoggato.id]);

  const inviaRichiesta = async (event) => {
    event.preventDefault();
    const allievo = allievi.find(elemento => elemento.id === allievoId);
    if (!allievo || !titolo.trim()) return;
    setInvioInCorso(true);
    try {
      await addDoc(collection(db, 'richieste_carriera'), {
        allievoId: allievo.id,
        nomeAllievo: `${allievo.Cognome || ''} ${allievo.Nome || ''}`.trim(),
        tipo,
        titolo: normalizzaTestoCarriera(titolo),
        piazzamento: normalizzaTestoCarriera(piazzamento),
        evento: normalizzaTestoCarriera(evento),
        luogo: normalizzaTestoCarriera(luogo),
        data,
        descrizione: normalizzaTestoCarriera(descrizione),
        operatoreId: utenteLoggato.id,
        operatoreNome: utenteLoggato.username,
        stato: 'in_attesa',
        dataRichiesta: new Date().toISOString()
      });
      await registraLogOperazione(utenteLoggato.username, `Richiesta approvazione carriera per ${allievo.Cognome} ${allievo.Nome}`);
      setAllievoId(''); setTitolo(''); setPiazzamento(''); setEvento(''); setLuogo(''); setData(dataOggiISO()); setDescrizione(''); setTipo('Titolo');
      alert('Richiesta inviata al Master per l’approvazione.');
    } catch (errore) {
      console.error(errore);
      alert('Non è stato possibile inviare la richiesta. Riprova.');
    } finally {
      setInvioInCorso(false);
    }
  };

  const coloreStato = stato => stato === 'approvato' ? '#16844a' : stato === 'non_approvato' ? '#b51b4b' : stato === 'rimossa_dalla_carriera' ? '#6b7280' : '#7e2e99';
  const testoStato = stato => stato === 'approvato' ? 'Approvato' : stato === 'non_approvato' ? 'Non approvato' : stato === 'rimossa_dalla_carriera' ? 'Rimossa dalla carriera · invia una nuova richiesta' : stato === 'in_attesa' ? 'In attesa del Master' : 'Stato da verificare';

  return <div style={{ color: '#2d1721', maxWidth: 860 }}>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>🏆 Aggiungi alla carriera dell’allievo</h2>
    <p style={{ color: '#553744', lineHeight: 1.5 }}>Inserisci un titolo, un piazzamento o un premio. Sarà visibile nella carriera dell’allievo solo dopo l’approvazione di un Master.</p>
    <form onSubmit={inviaRichiesta} style={{ display: 'grid', gap: 13, padding: 20, background: '#fff', border: '1px solid #eadde2', borderRadius: 14, boxShadow: '0 8px 22px rgba(71,22,43,.07)' }}>
      <label>Allievo<select value={allievoId} onChange={event => setAllievoId(event.target.value)} required style={{ display: 'block', width: '100%', marginTop: 6, padding: 11, borderRadius: 8 }}><option value="">Seleziona l’allievo…</option>{allievi.map(allievo => <option key={allievo.id} value={allievo.id}>{allievo.Cognome} {allievo.Nome}</option>)}</select></label>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, .45fr) 1fr', gap: 12 }}><label>Tipo<select value={tipo} onChange={event => setTipo(event.target.value)} style={{ display: 'block', width: '100%', marginTop: 6, padding: 11, borderRadius: 8 }}><option>Titolo</option><option>Piazzamento</option><option>Premio</option></select></label><label>Titolo / riconoscimento<input value={titolo} onChange={event => setTitolo(event.target.value)} required maxLength={120} placeholder="Es. Campione regionale 2026" style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 6, padding: 11, borderRadius: 8 }} /></label></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}><label>Piazzamento (facoltativo)<input value={piazzamento} onChange={event => setPiazzamento(event.target.value)} maxLength={80} placeholder="Es. 1° classificato" style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 6, padding: 11, borderRadius: 8 }} /></label><label>Data<input type="date" value={data} onChange={event => setData(event.target.value)} style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 6, padding: 11, borderRadius: 8 }} /></label></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}><label>Evento o gara (facoltativo)<input value={evento} onChange={event => setEvento(event.target.value)} maxLength={120} placeholder="Es. Campionato mondiale" style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 6, padding: 11, borderRadius: 8 }} /></label><label>📍 Luogo della gara (facoltativo)<input value={luogo} onChange={event => setLuogo(event.target.value)} maxLength={120} placeholder="Es. Roma, Italia" style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 6, padding: 11, borderRadius: 8 }} /></label></div>
      <label>Nota (facoltativa)<textarea value={descrizione} onChange={event => setDescrizione(event.target.value)} rows={3} maxLength={500} placeholder="Dettagli utili per il Master…" style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 6, padding: 11, borderRadius: 8, resize: 'vertical' }} /></label>
      <button type="submit" disabled={invioInCorso} style={{ padding: 12, border: 0, borderRadius: 8, background: '#7e2e99', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>{invioInCorso ? 'Invio…' : 'Invia al Master per approvazione →'}</button>
    </form>
    <h3 style={{ margin: '25px 0 10px', color: '#57132c' }}>{amministratore ? 'Tutte le richieste' : 'Le mie richieste'}</h3>
    {richieste.length ? richieste.map(richiesta => <article key={richiesta.id} style={{ padding: 14, marginTop: 10, borderRadius: 12, background: '#fff', border: '1px solid #eadde2', borderLeft: `4px solid ${coloreStato(richiesta.stato)}` }}><strong>{richiesta.nomeAllievo} · {richiesta.titolo}</strong><span style={{ float: 'right', color: coloreStato(richiesta.stato), fontWeight: 'bold', fontSize: 12 }}>{testoStato(richiesta.stato)}</span><p style={{ margin: '7px 0 0', color: '#5c4650', fontSize: 13 }}>{richiesta.tipo}{richiesta.piazzamento ? ` · ${richiesta.piazzamento}` : ''}{richiesta.evento ? ` · ${richiesta.evento}` : ''}{richiesta.luogo ? ` · 📍 ${richiesta.luogo}` : ''}</p><small style={{ display: 'block', marginTop: 8, color: '#765b66' }}>Inserita da {richiesta.operatoreNome || 'operatore'}</small>{richiesta.masterNome && <small style={{ display: 'block', marginTop: 4, color: '#765b66' }}>Decisione di {richiesta.masterNome} · {formattaDataIT(richiesta.dataDecisione)}</small>}</article>) : <p style={{ color: '#6f6268' }}>Non ci sono ancora richieste.</p>}
  </div>;
}

function ApprovazioniCarriera({ utenteLoggato }) {
  const [richieste, setRichieste] = useState([]);
  const [vociPubblicate, setVociPubblicate] = useState([]);
  const [azioneInCorso, setAzioneInCorso] = useState('');
  const [richiestaInModifica, setRichiestaInModifica] = useState('');
  const [bozza, setBozza] = useState(null);
  const [voceInModifica, setVoceInModifica] = useState('');
  const [bozzaPubblicata, setBozzaPubblicata] = useState(null);

  useEffect(() => ascoltaRaccolta('richieste_carriera', snapshot => {
    setRichieste(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))
      .filter(richiesta => richiesta.stato === 'in_attesa')
      .sort((a, b) => String(a.dataRichiesta || '').localeCompare(String(b.dataRichiesta || ''))));
  }, errore => console.error('Impossibile caricare le approvazioni:', errore)), []);

  useEffect(() => ascoltaRaccolta('carriere_allievi', snapshot => {
    setVociPubblicate(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))
      .sort((a, b) => String(b.dataApprovazione || b.data || '').localeCompare(String(a.dataApprovazione || a.data || ''))));
  }, errore => console.error('Impossibile caricare le voci della carriera:', errore)), []);

  const apriModifica = richiesta => {
    setRichiestaInModifica(richiesta.id);
    setBozza({ tipo: richiesta.tipo || 'Titolo', titolo: richiesta.titolo || '', piazzamento: richiesta.piazzamento || '', evento: richiesta.evento || '', luogo: richiesta.luogo || '', data: richiesta.data || '', descrizione: richiesta.descrizione || '' });
  };

  const decidi = async (richiesta, approvato, revisione = null) => {
    setAzioneInCorso(richiesta.id);
    const ora = new Date().toISOString();
    const datiFinali = { ...richiesta, ...(revisione || {}) };
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'richieste_carriera', richiesta.id), {
        stato: approvato ? 'approvato' : 'non_approvato',
        masterId: utenteLoggato.id,
        masterNome: utenteLoggato.username,
        dataDecisione: ora,
        ...(revisione ? { tipo: datiFinali.tipo, titolo: normalizzaTestoCarriera(datiFinali.titolo), piazzamento: normalizzaTestoCarriera(datiFinali.piazzamento), evento: normalizzaTestoCarriera(datiFinali.evento), luogo: normalizzaTestoCarriera(datiFinali.luogo), data: datiFinali.data, descrizione: normalizzaTestoCarriera(datiFinali.descrizione), modificataDalMaster: true, modificataIl: ora } : {})
      });
      if (approvato) {
        const carrieraRef = doc(collection(db, 'carriere_allievi'));
        batch.set(carrieraRef, {
          allievoId: richiesta.allievoId,
          nomeAllievo: richiesta.nomeAllievo,
          tipo: datiFinali.tipo,
          titolo: normalizzaTestoCarriera(datiFinali.titolo),
          piazzamento: normalizzaTestoCarriera(datiFinali.piazzamento),
          evento: normalizzaTestoCarriera(datiFinali.evento),
          luogo: normalizzaTestoCarriera(datiFinali.luogo),
          data: datiFinali.data || '',
          descrizione: normalizzaTestoCarriera(datiFinali.descrizione),
          richiestaId: richiesta.id,
          approvatoDa: utenteLoggato.username,
          dataApprovazione: ora
        });
      }
      await batch.commit();
      await registraLogOperazione(utenteLoggato.username, `${approvato ? 'Approvata' : 'Non approvata'} richiesta carriera di ${richiesta.nomeAllievo}`);
      setRichiestaInModifica(''); setBozza(null);
    } catch (errore) {
      console.error(errore);
      alert('Non è stato possibile registrare la decisione. Riprova.');
    } finally {
      setAzioneInCorso('');
    }
  };

  const salvaModifiche = async richiesta => {
    if (!bozza?.titolo.trim()) return alert('Inserisci il titolo o riconoscimento.');
    setAzioneInCorso(richiesta.id);
    try {
      await updateDoc(doc(db, 'richieste_carriera', richiesta.id), { tipo: bozza.tipo, titolo: normalizzaTestoCarriera(bozza.titolo), piazzamento: normalizzaTestoCarriera(bozza.piazzamento), evento: normalizzaTestoCarriera(bozza.evento), luogo: normalizzaTestoCarriera(bozza.luogo), data: bozza.data, descrizione: normalizzaTestoCarriera(bozza.descrizione), modificataDalMaster: true, modificataIl: new Date().toISOString(), modificataDaMaster: utenteLoggato.username });
      await registraLogOperazione(utenteLoggato.username, `Modificata richiesta carriera di ${richiesta.nomeAllievo} prima dell’approvazione`);
      setRichiestaInModifica(''); setBozza(null);
    } catch (errore) { console.error(errore); alert('Non è stato possibile salvare le modifiche.'); }
    finally { setAzioneInCorso(''); }
  };

  const apriModificaPubblicata = voce => {
    setVoceInModifica(voce.id);
    setBozzaPubblicata({ tipo: voce.tipo || 'Titolo', titolo: voce.titolo || '', piazzamento: voce.piazzamento || '', evento: voce.evento || '', luogo: voce.luogo || '', data: voce.data || '', descrizione: voce.descrizione || '' });
  };

  const salvaVocePubblicata = async voce => {
    if (!bozzaPubblicata?.titolo.trim()) return alert('Inserisci il titolo o riconoscimento.');
    setAzioneInCorso(voce.id);
    const ora = new Date().toISOString();
    const dati = { tipo: bozzaPubblicata.tipo, titolo: normalizzaTestoCarriera(bozzaPubblicata.titolo), piazzamento: normalizzaTestoCarriera(bozzaPubblicata.piazzamento), evento: normalizzaTestoCarriera(bozzaPubblicata.evento), luogo: normalizzaTestoCarriera(bozzaPubblicata.luogo), data: bozzaPubblicata.data, descrizione: normalizzaTestoCarriera(bozzaPubblicata.descrizione), modificataDa: utenteLoggato.username, modificataIl: ora };
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'carriere_allievi', voce.id), dati);
      if (voce.richiestaId) batch.update(doc(db, 'richieste_carriera', voce.richiestaId), { ...dati, modificataDopoApprovazione: true });
      await batch.commit();
      await registraLogOperazione(utenteLoggato.username, `Modificata voce pubblicata nella carriera di ${voce.nomeAllievo}`);
      setVoceInModifica(''); setBozzaPubblicata(null);
    } catch (errore) { console.error(errore); alert('Non è stato possibile salvare la modifica.'); }
    finally { setAzioneInCorso(''); }
  };

  const eliminaVocePubblicata = async voce => {
    if (!window.confirm(`Eliminare definitivamente “${voce.titolo}” dalla carriera di ${voce.nomeAllievo}?`)) return;
    setAzioneInCorso(voce.id);
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'carriere_allievi', voce.id));
      if (voce.richiestaId) batch.update(doc(db, 'richieste_carriera', voce.richiestaId), { stato: 'rimossa_dalla_carriera', rimossaDa: utenteLoggato.username, dataRimozione: new Date().toISOString() });
      await batch.commit();
      await registraLogOperazione(utenteLoggato.username, `Eliminata voce dalla carriera di ${voce.nomeAllievo}: ${voce.titolo}`);
    } catch (errore) { console.error(errore); alert('Non è stato possibile eliminare la voce.'); }
    finally { setAzioneInCorso(''); }
  };

  return <div style={{ color: '#2d1721', maxWidth: 860 }}>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>🔔 Approvazioni carriera</h2>
    <p style={{ color: '#553744', lineHeight: 1.5 }}>Puoi correggere la richiesta dell’operatore prima di approvarla. Nella carriera dell’allievo verrà inserita soltanto la versione finale approvata.</p>
    {richieste.length ? richieste.map(richiesta => {
      const inModifica = richiestaInModifica === richiesta.id;
      const input = { display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 5, padding: 10, borderRadius: 8, border: '1px solid #d9b8c5', background: '#fff', color: '#2d1721', font: 'inherit' };
      return <article key={richiesta.id} style={{ padding: 18, marginTop: 14, borderRadius: 14, background: '#fff', border: '1px solid #eadde2', borderLeft: '5px solid #7e2e99', boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}><div><strong style={{ fontSize: 18 }}>{richiesta.nomeAllievo}</strong><span style={{ display: 'block', marginTop: 3, color: '#765b66', fontSize: 12 }}>Proposto da {richiesta.operatoreNome || 'operatore'} · {formattaDataIT(richiesta.dataRichiesta)}</span></div><span style={{ height: 'fit-content', padding: '5px 9px', borderRadius: 14, background: '#f3d9f8', color: '#6d2e7e', fontSize: 11, fontWeight: 'bold' }}>{richiesta.tipo}</span></div>
        {inModifica ? <div style={{ display: 'grid', gap: 11, marginTop: 16, padding: 14, borderRadius: 11, background: '#fbf1f5', border: '1px solid #f1d8e2' }}><strong style={{ color: '#57132c' }}>✏️ Correggi prima dell’approvazione</strong><div style={{ display: 'grid', gridTemplateColumns: 'minmax(130px, .4fr) 1fr', gap: 10 }}><label>Tipo<select value={bozza.tipo} onChange={event => setBozza(valore => ({ ...valore, tipo: event.target.value }))} style={input}><option>Titolo</option><option>Piazzamento</option><option>Premio</option></select></label><label>Titolo / riconoscimento<input value={bozza.titolo} onChange={event => setBozza(valore => ({ ...valore, titolo: event.target.value }))} maxLength={120} style={input} /></label></div><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}><label>Piazzamento<input value={bozza.piazzamento} onChange={event => setBozza(valore => ({ ...valore, piazzamento: event.target.value }))} maxLength={80} style={input} /></label><label>Data<input type="date" value={bozza.data} onChange={event => setBozza(valore => ({ ...valore, data: event.target.value }))} style={input} /></label></div><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}><label>Evento / gara<input value={bozza.evento} onChange={event => setBozza(valore => ({ ...valore, evento: event.target.value }))} maxLength={120} style={input} /></label><label>📍 Luogo della gara<input value={bozza.luogo} onChange={event => setBozza(valore => ({ ...valore, luogo: event.target.value }))} maxLength={120} style={input} /></label></div><label>Nota<textarea value={bozza.descrizione} onChange={event => setBozza(valore => ({ ...valore, descrizione: event.target.value }))} rows={3} maxLength={500} style={{ ...input, resize: 'vertical' }} /></label><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}><button type="button" disabled={azioneInCorso === richiesta.id} onClick={() => salvaModifiche(richiesta)} style={{ padding: '10px 12px', border: 0, borderRadius: 8, background: '#7e2e99', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Salva e resta in attesa</button><button type="button" disabled={azioneInCorso === richiesta.id} onClick={() => decidi(richiesta, true, bozza)} style={{ padding: '10px 12px', border: 0, borderRadius: 8, background: '#198754', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Approva modifiche</button><button type="button" onClick={() => { setRichiestaInModifica(''); setBozza(null); }} style={{ padding: '10px 12px', border: '1px solid #d9b8c5', borderRadius: 8, background: '#fff', color: '#57132c', fontWeight: 'bold', cursor: 'pointer' }}>Annulla</button></div></div> : <><h3 style={{ margin: '14px 0 7px', color: '#32101e' }}>{richiesta.titolo}</h3>{richiesta.piazzamento && <p style={{ margin: '6px 0', color: '#432332' }}>🏅 {richiesta.piazzamento}</p>}{richiesta.evento && <p style={{ margin: '6px 0', color: '#432332' }}>🏆 {richiesta.evento}</p>}{richiesta.luogo && <p style={{ margin: '6px 0', color: '#432332' }}>📍 {richiesta.luogo}</p>}{richiesta.data && <p style={{ margin: '6px 0', color: '#432332' }}>📅 {formattaDataIT(richiesta.data)}</p>}{richiesta.descrizione && <p style={{ margin: '10px 0 0', color: '#5c4650', lineHeight: 1.45 }}>{richiesta.descrizione}</p>}<div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}><button type="button" disabled={azioneInCorso === richiesta.id} onClick={() => apriModifica(richiesta)} style={{ padding: 11, border: '1px solid #b58ac3', borderRadius: 8, background: '#f8ebfb', color: '#6d2e7e', fontWeight: 'bold', cursor: 'pointer' }}>✏️ Modifica</button><button type="button" disabled={azioneInCorso === richiesta.id} onClick={() => decidi(richiesta, true)} style={{ flex: 1, minWidth: 140, padding: 11, border: 0, borderRadius: 8, background: '#198754', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Approva</button><button type="button" disabled={azioneInCorso === richiesta.id} onClick={() => decidi(richiesta, false)} style={{ flex: 1, minWidth: 140, padding: 11, border: 0, borderRadius: 8, background: '#c92a2a', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✕ Non approva</button></div></>}</article>;
    }) : <div style={{ marginTop: 18, padding: 24, borderRadius: 14, background: '#fff', border: '1px solid #eadde2', color: '#6f6268' }}>Non ci sono richieste in attesa di approvazione.</div>}
    <h3 style={{ margin: '30px 0 8px', color: '#57132c' }}>📚 Voci già pubblicate</h3>
    <p style={{ margin: '0 0 12px', color: '#553744', lineHeight: 1.45, fontSize: 13 }}>Qui Master e Admin possono correggere oppure eliminare un titolo già visibile nell’App Allievi.</p>
    {vociPubblicate.length ? vociPubblicate.map(voce => {
      const inModifica = voceInModifica === voce.id;
      const input = { display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 5, padding: 10, borderRadius: 8, border: '1px solid #d9b8c5', background: '#fff', color: '#2d1721', font: 'inherit' };
      return <article key={voce.id} style={{ padding: 16, marginTop: 10, borderRadius: 13, background: '#fff', border: '1px solid #eadde2', borderLeft: '5px solid #198754', boxShadow: '0 6px 16px rgba(71,22,43,.06)' }}>
        {inModifica ? <div style={{ display: 'grid', gap: 10 }}><strong style={{ color: '#57132c' }}>✏️ Correggi voce pubblicata · {voce.nomeAllievo}</strong><div style={{ display: 'grid', gridTemplateColumns: 'minmax(130px, .4fr) 1fr', gap: 10 }}><label>Tipo<select value={bozzaPubblicata.tipo} onChange={event => setBozzaPubblicata(valore => ({ ...valore, tipo: event.target.value }))} style={input}><option>Titolo</option><option>Piazzamento</option><option>Premio</option></select></label><label>Titolo / riconoscimento<input value={bozzaPubblicata.titolo} onChange={event => setBozzaPubblicata(valore => ({ ...valore, titolo: event.target.value }))} maxLength={120} style={input} /></label></div><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}><label>Piazzamento<input value={bozzaPubblicata.piazzamento} onChange={event => setBozzaPubblicata(valore => ({ ...valore, piazzamento: event.target.value }))} maxLength={80} style={input} /></label><label>Data<input type="date" value={bozzaPubblicata.data} onChange={event => setBozzaPubblicata(valore => ({ ...valore, data: event.target.value }))} style={input} /></label></div><label>Evento / gara<input value={bozzaPubblicata.evento} onChange={event => setBozzaPubblicata(valore => ({ ...valore, evento: event.target.value }))} maxLength={120} style={input} /></label><label>Nota<textarea value={bozzaPubblicata.descrizione} onChange={event => setBozzaPubblicata(valore => ({ ...valore, descrizione: event.target.value }))} rows={3} maxLength={500} style={{ ...input, resize: 'vertical' }} /></label><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}><button type="button" disabled={azioneInCorso === voce.id} onClick={() => salvaVocePubblicata(voce)} style={{ padding: '10px 12px', border: 0, borderRadius: 8, background: '#198754', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Salva correzione</button><button type="button" onClick={() => { setVoceInModifica(''); setBozzaPubblicata(null); }} style={{ padding: '10px 12px', border: '1px solid #d9b8c5', borderRadius: 8, background: '#fff', color: '#57132c', fontWeight: 'bold', cursor: 'pointer' }}>Annulla</button></div></div> : <><div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}><div><strong style={{ color: '#32101e', fontSize: 16 }}>{voce.nomeAllievo} · {voce.titolo}</strong><span style={{ display: 'block', marginTop: 4, color: '#765b66', fontSize: 12 }}>{voce.tipo}{voce.piazzamento ? ` · ${voce.piazzamento}` : ''}{voce.evento ? ` · ${voce.evento}` : ''}{voce.data ? ` · ${formattaDataIT(voce.data)}` : ''}</span></div><span style={{ height: 'fit-content', padding: '5px 8px', borderRadius: 12, color: '#176b3a', background: '#e8f7ee', fontSize: 11, fontWeight: 'bold' }}>Pubblicata</span></div><div style={{ display: 'flex', gap: 8, marginTop: 13, flexWrap: 'wrap' }}><button type="button" onClick={() => apriModificaPubblicata(voce)} style={{ padding: '9px 11px', border: '1px solid #b58ac3', borderRadius: 8, background: '#f8ebfb', color: '#6d2e7e', fontWeight: 'bold', cursor: 'pointer' }}>✏️ Modifica</button><button type="button" disabled={azioneInCorso === voce.id} onClick={() => eliminaVocePubblicata(voce)} style={{ padding: '9px 11px', border: 0, borderRadius: 8, background: '#c92a2a', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>🗑️ Elimina</button></div></>}</article>;
    }) : <div style={{ padding: 18, borderRadius: 12, background: '#fff', border: '1px solid #eadde2', color: '#6f6268' }}>Non ci sono ancora voci pubblicate.</div>}
  </div>;
}

function UtentiPremiati({ utenteLoggato }) {
  const [voci, setVoci] = useState([]);
  const [ricerca, setRicerca] = useState('');
  const [voceInModifica, setVoceInModifica] = useState('');
  const [bozza, setBozza] = useState(null);
  const [azioneInCorso, setAzioneInCorso] = useState('');

  useEffect(() => ascoltaRaccolta('carriere_allievi', snapshot => {
    setVoci(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }))
      .sort((a, b) => String(b.dataApprovazione || b.data || '').localeCompare(String(a.dataApprovazione || a.data || ''))));
  }, errore => console.error('Impossibile caricare gli utenti premiati:', errore)), []);

  const risultati = voci.filter(voce => `${voce.nomeAllievo || ''} ${voce.titolo || ''} ${voce.evento || ''}`.toLocaleLowerCase('it-IT').includes(ricerca.trim().toLocaleLowerCase('it-IT')));
  const apriModifica = voce => { setVoceInModifica(voce.id); setBozza({ tipo: voce.tipo || 'Titolo', titolo: voce.titolo || '', piazzamento: voce.piazzamento || '', evento: voce.evento || '', luogo: voce.luogo || '', data: voce.data || '', descrizione: voce.descrizione || '' }); };
  const campo = { display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 5, padding: 10, borderRadius: 8, border: '1px solid #d9b8c5', background: '#fff', color: '#2d1721', font: 'inherit' };

  const salva = async voce => {
    if (!bozza?.titolo.trim()) return alert('Inserisci il titolo o riconoscimento.');
    setAzioneInCorso(voce.id);
    const ora = new Date().toISOString();
    const dati = { tipo: bozza.tipo, titolo: normalizzaTestoCarriera(bozza.titolo), piazzamento: normalizzaTestoCarriera(bozza.piazzamento), evento: normalizzaTestoCarriera(bozza.evento), luogo: normalizzaTestoCarriera(bozza.luogo), data: bozza.data, descrizione: normalizzaTestoCarriera(bozza.descrizione), modificataDa: utenteLoggato.username, modificataIl: ora };
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'carriere_allievi', voce.id), dati);
      if (voce.richiestaId) batch.update(doc(db, 'richieste_carriera', voce.richiestaId), { ...dati, modificataDopoApprovazione: true });
      await batch.commit();
      await registraLogOperazione(utenteLoggato.username, `Modificata voce premiata di ${voce.nomeAllievo}`);
      setVoceInModifica(''); setBozza(null);
    } catch (errore) { console.error(errore); alert('Non è stato possibile salvare la modifica.'); }
    finally { setAzioneInCorso(''); }
  };

  const elimina = async voce => {
    if (!window.confirm(`Eliminare “${voce.titolo}” dalla carriera di ${voce.nomeAllievo}?`)) return;
    setAzioneInCorso(voce.id);
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'carriere_allievi', voce.id));
      if (voce.richiestaId) batch.update(doc(db, 'richieste_carriera', voce.richiestaId), { stato: 'rimossa_dalla_carriera', rimossaDa: utenteLoggato.username, dataRimozione: new Date().toISOString() });
      await batch.commit();
      await registraLogOperazione(utenteLoggato.username, `Eliminata voce premiata di ${voce.nomeAllievo}: ${voce.titolo}`);
    } catch (errore) { console.error(errore); alert('Non è stato possibile eliminare la voce.'); }
    finally { setAzioneInCorso(''); }
  };

  return <div style={{ color: '#2d1721', maxWidth: 900 }}>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>🏅 Utenti premiati</h2>
    <p style={{ color: '#553744', lineHeight: 1.5 }}>Elenco delle gare, piazzamenti e premi già pubblicati nella carriera degli allievi. Puoi correggere un errore oppure eliminare una voce.</p>
    <input value={ricerca} onChange={event => setRicerca(event.target.value)} placeholder="Cerca un allievo, premio o gara…" style={{ ...campo, margin: '0 0 14px', maxWidth: 460 }} />
    <p style={{ margin: '0 0 10px', color: '#765b66', fontSize: 13 }}>{risultati.length} {risultati.length === 1 ? 'voce pubblicata' : 'voci pubblicate'}</p>
    {risultati.length ? risultati.map(voce => {
      const inModifica = voceInModifica === voce.id;
      return <article key={voce.id} style={{ padding: 16, marginTop: 10, borderRadius: 13, background: '#fff', border: '1px solid #eadde2', borderLeft: '5px solid #198754', boxShadow: '0 6px 16px rgba(71,22,43,.06)' }}>
        {inModifica ? <div style={{ display: 'grid', gap: 10 }}><strong style={{ color: '#57132c' }}>✏️ Correggi · {voce.nomeAllievo}</strong><div style={{ display: 'grid', gridTemplateColumns: 'minmax(130px, .4fr) 1fr', gap: 10 }}><label>Tipo<select value={bozza.tipo} onChange={event => setBozza(valore => ({ ...valore, tipo: event.target.value }))} style={campo}><option>Titolo</option><option>Piazzamento</option><option>Premio</option></select></label><label>Titolo / riconoscimento<input value={bozza.titolo} onChange={event => setBozza(valore => ({ ...valore, titolo: event.target.value }))} maxLength={120} style={campo} /></label></div><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}><label>Piazzamento<input value={bozza.piazzamento} onChange={event => setBozza(valore => ({ ...valore, piazzamento: event.target.value }))} maxLength={80} style={campo} /></label><label>Data<input type="date" value={bozza.data} onChange={event => setBozza(valore => ({ ...valore, data: event.target.value }))} style={campo} /></label></div><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}><label>Evento / gara<input value={bozza.evento} onChange={event => setBozza(valore => ({ ...valore, evento: event.target.value }))} maxLength={120} style={campo} /></label><label>📍 Luogo della gara<input value={bozza.luogo} onChange={event => setBozza(valore => ({ ...valore, luogo: event.target.value }))} maxLength={120} style={campo} /></label></div><label>Nota<textarea value={bozza.descrizione} onChange={event => setBozza(valore => ({ ...valore, descrizione: event.target.value }))} rows={3} maxLength={500} style={{ ...campo, resize: 'vertical' }} /></label><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}><button type="button" disabled={azioneInCorso === voce.id} onClick={() => salva(voce)} style={{ padding: '10px 12px', border: 0, borderRadius: 8, background: '#198754', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Salva correzione</button><button type="button" onClick={() => { setVoceInModifica(''); setBozza(null); }} style={{ padding: '10px 12px', border: '1px solid #d9b8c5', borderRadius: 8, background: '#fff', color: '#57132c', fontWeight: 'bold', cursor: 'pointer' }}>Annulla</button></div></div> : <><div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}><div><strong style={{ color: '#32101e', fontSize: 17 }}>{voce.nomeAllievo}</strong><h3 style={{ margin: '6px 0 4px', color: '#57132c', fontSize: 16 }}>{voce.titolo}</h3><span style={{ display: 'block', color: '#765b66', fontSize: 13 }}>{voce.tipo}{voce.piazzamento ? ` · ${voce.piazzamento}` : ''}{voce.evento ? ` · ${voce.evento}` : ''}{voce.luogo ? ` · 📍 ${voce.luogo}` : ''}{voce.data ? ` · ${formattaDataIT(voce.data)}` : ''}</span>{voce.descrizione && <p style={{ margin: '8px 0 0', color: '#5c4650', fontSize: 13 }}>{voce.descrizione}</p>}</div><span style={{ height: 'fit-content', padding: '5px 8px', borderRadius: 12, color: '#176b3a', background: '#e8f7ee', fontSize: 11, fontWeight: 'bold' }}>Pubblicata</span></div><div style={{ display: 'flex', gap: 8, marginTop: 13, flexWrap: 'wrap' }}><button type="button" onClick={() => apriModifica(voce)} style={{ padding: '9px 11px', border: '1px solid #b58ac3', borderRadius: 8, background: '#f8ebfb', color: '#6d2e7e', fontWeight: 'bold', cursor: 'pointer' }}>✏️ Modifica</button><button type="button" disabled={azioneInCorso === voce.id} onClick={() => elimina(voce)} style={{ padding: '9px 11px', border: 0, borderRadius: 8, background: '#c92a2a', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>🗑️ Elimina</button></div></>}</article>;
    }) : <div style={{ padding: 20, borderRadius: 12, background: '#fff', border: '1px solid #eadde2', color: '#6f6268' }}>Nessun utente premiato trovato.</div>}
  </div>;
}

function RichiesteCancellazioneAllievi({ utenteLoggato }) {
  const [richieste, setRichieste] = useState([]);
  const [azioneInCorso, setAzioneInCorso] = useState('');

  useEffect(() => ascoltaRaccolta('richieste_cancellazione_allievi', snapshot => {
    setRichieste(snapshot.docs
      .map(documento => ({ id: documento.id, ...documento.data() }))
      .filter(richiesta => richiesta.stato === 'in_attesa')
      .sort((a, b) => String(a.dataRichiesta || '').localeCompare(String(b.dataRichiesta || ''))));
  }, errore => console.error('Impossibile caricare le richieste di cancellazione:', errore)), []);

  const decidi = async (richiesta, approvata) => {
    const verbo = approvata ? 'approvare ed eliminare' : 'rifiutare';
    if (!window.confirm(`Vuoi ${verbo} la richiesta per ${richiesta.nomeAllievo}?${approvata ? ' L’eliminazione sarà definitiva.' : ''}`)) return;
    setAzioneInCorso(richiesta.id);
    const ora = new Date().toISOString();
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'richieste_cancellazione_allievi', richiesta.id), {
        stato: approvata ? 'approvata' : 'non_approvata',
        masterId: utenteLoggato.id,
        masterNome: utenteLoggato.username,
        dataDecisione: ora
      });
      if (approvata) batch.delete(doc(db, 'allievi', richiesta.allievoId));
      await batch.commit();
      await registraLogOperazione(utenteLoggato.username, `${approvata ? 'Approvata ed eseguita' : 'Non approvata'} cancellazione allievo: ${richiesta.nomeAllievo}`);
    } catch (errore) {
      console.error(errore);
      alert('Non è stato possibile registrare la decisione. Riprova.');
    } finally {
      setAzioneInCorso('');
    }
  };

  return <div style={{ color: '#2d1721', maxWidth: 860 }}>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>🗑️ Richieste di cancellazione allievi</h2>
    <p style={{ color: '#553744', lineHeight: 1.5 }}>Le richieste inviate dagli operatori restano qui finché un Master o l’Admin non prende una decisione. L’allievo viene eliminato solo dopo l’approvazione.</p>
    {richieste.length ? richieste.map(richiesta => <article key={richiesta.id} style={{ padding: 18, marginTop: 14, borderRadius: 14, background: '#fff', border: '1px solid #eadde2', borderLeft: '5px solid #c92a2a', boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
      <strong style={{ fontSize: 18, color: '#32101e' }}>{richiesta.nomeAllievo}</strong>
      <span style={{ display: 'block', marginTop: 5, color: '#765b66', fontSize: 13 }}>Richiesta da {richiesta.operatoreNome || 'operatore'} · {formattaDataIT(richiesta.dataRichiesta)}</span>
      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        <button type="button" disabled={azioneInCorso === richiesta.id} onClick={() => decidi(richiesta, true)} style={{ flex: 1, minWidth: 190, padding: 11, border: 0, borderRadius: 8, background: '#c92a2a', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Approva ed elimina</button>
        <button type="button" disabled={azioneInCorso === richiesta.id} onClick={() => decidi(richiesta, false)} style={{ flex: 1, minWidth: 160, padding: 11, border: '1px solid #8d5a6d', borderRadius: 8, background: '#fff', color: '#6d1836', fontWeight: 'bold', cursor: 'pointer' }}>✕ Non approvare</button>
      </div>
    </article>) : <div style={{ marginTop: 18, padding: 24, borderRadius: 14, background: '#fff', border: '1px solid #eadde2', color: '#6f6268' }}>Non ci sono richieste di cancellazione in attesa.</div>}
  </div>;
}

function MessaggiAllieviSegreteria({ utenteLoggato }) {
  const ruolo = String(utenteLoggato.ruolo || '').toLowerCase();
  const canale = ruolo === 'master' ? 'master_e_segreteria' : ruolo === 'admin' ? 'tutti' : 'segreteria';
  const [messaggi, setMessaggi] = useState([]);
  const [risposte, setRisposte] = useState({});
  const [invioInCorso, setInvioInCorso] = useState('');
  const [eliminazioneInCorso, setEliminazioneInCorso] = useState('');
  const emojiRapide = ['😊', '👍', '🎉', '💃', '📅', '✅', '❤️'];

  useEffect(() => {
    return ascoltaRaccolta('messaggi_allievi', snapshot => {
      const visibili = snapshot.docs
        .map(documento => ({ id: documento.id, ...documento.data() }))
        .filter(messaggio => {
          if (canale === 'tutti') return true;
          if (canale === 'master_e_segreteria') {
            return messaggio.canale === 'segreteria' || (messaggio.canale === 'maestro' && (
              messaggio.maestroId === utenteLoggato.id ||
              String(messaggio.maestroNome || '').toLowerCase() === String(utenteLoggato.username || '').toLowerCase()
            ));
          }
          return messaggio.canale === 'segreteria';
        })
        .sort((a, b) => String(b.ultimaData || b.dataCreazione || '').localeCompare(String(a.ultimaData || a.dataCreazione || '')));
      setMessaggi(visibili);
      visibili.filter(messaggio => !messaggio.lettoDaStaff).forEach(messaggio => {
        updateDoc(doc(db, 'messaggi_allievi', messaggio.id), { lettoDaStaff: true }).catch(errore => console.error('Impossibile segnare il messaggio come letto:', errore));
      });
    }, errore => console.error('Impossibile caricare i messaggi degli allievi:', errore));
  }, [canale, utenteLoggato.id, utenteLoggato.username]);

  const inviaRisposta = async (event, messaggio) => {
    event.preventDefault();
    const testo = String(risposte[messaggio.id] || '').trim();
    if (!testo) return;
    setInvioInCorso(messaggio.id);
    const ora = new Date().toISOString();
    try {
      await updateDoc(doc(db, 'messaggi_allievi', messaggio.id), {
        risposte: arrayUnion({
          id: `${Date.now()}_${utenteLoggato.id}`,
          testo,
          autore: utenteLoggato.username,
          ruolo,
          data: ora
        }),
        ultimoMittente: 'staff',
        ultimoTesto: testo,
        ultimaData: ora,
        lettoDaStaff: true,
        lettoDaAllievo: false
      });
      setRisposte(valori => ({ ...valori, [messaggio.id]: '' }));
      intestazioniAutenticate({ 'Content-Type': 'application/json' }).then(intestazioni => fetch(URL_SERVIZIO_MESSAGGIO_ALLIEVO, {
        method: 'POST',
        headers: intestazioni,
        body: JSON.stringify({ messaggioId: messaggio.id })
      })).then(async risposta => {
        const esito = await risposta.json().catch(() => ({}));
        if (!risposta.ok || !esito.inviato) console.warn('Notifica messaggio allievo non inviata:', esito.errore || esito.motivo || 'nessun telefono registrato');
      }).catch(erroreNotifica => console.warn('Servizio notifiche messaggi non raggiungibile:', erroreNotifica));
    } catch (errore) {
      console.error('Impossibile inviare la risposta:', errore);
      alert('Non è stato possibile inviare la risposta. Riprova.');
    } finally {
      setInvioInCorso('');
    }
  };

  const aggiungiEmojiRisposta = (messaggioId, emoji) => {
    setRisposte(valori => ({ ...valori, [messaggioId]: `${valori[messaggioId] || ''}${valori[messaggioId] ? ' ' : ''}${emoji}` }));
  };

  const eliminaChat = async messaggio => {
    const nome = messaggio.nomeAllievo || 'questo allievo';
    if (!window.confirm(`Eliminare definitivamente questa chat con ${nome}? Verranno eliminati il messaggio e tutte le risposte.`)) return;
    setEliminazioneInCorso(messaggio.id);
    try {
      await deleteDoc(doc(db, 'messaggi_allievi', messaggio.id));
    } catch (errore) {
      console.error('Impossibile eliminare la chat:', errore);
      alert('Non è stato possibile eliminare la chat. Riprova.');
    } finally {
      setEliminazioneInCorso('');
    }
  };

  const titolo = canale === 'master_e_segreteria' ? 'Messaggi dei miei allievi e della Segreteria' : canale === 'tutti' ? 'Tutti i messaggi degli allievi' : 'Messaggi alla Segreteria';
  const sottotitolo = canale === 'master_e_segreteria'
    ? 'Qui trovi le domande inviate a te come Maestro e quelle inviate direttamente alla Segreteria.'
    : canale === 'tutti'
      ? 'Come Admin puoi leggere e rispondere sia ai messaggi destinati ai Maestri sia a quelli inviati alla Segreteria.'
      : 'Qui trovi le domande e le richieste inviate direttamente alla Segreteria.';

  return <div style={{ color: '#2d1721', maxWidth: 860 }}>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>💬 {titolo}</h2>
    <p style={{ color: '#553744', lineHeight: 1.5 }}>{sottotitolo}</p>
    {messaggi.length ? messaggi.map(messaggio => { const messaggioPerMaestro = messaggio.canale === 'maestro'; return <article key={messaggio.id} style={{ marginTop: 14, padding: 18, borderRadius: 14, background: '#fff', border: '1px solid #eadde2', borderLeft: `5px solid ${messaggioPerMaestro ? '#9c36b5' : '#d82d62'}`, boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
        <div><strong style={{ fontSize: 17, color: '#32101e' }}>{messaggio.nomeAllievo || 'Allievo'}</strong><span style={{ display: 'block', marginTop: 3, color: '#7d5a68', fontSize: 12 }}>Ricevuto il {formattaDataIT(messaggio.dataCreazione)}</span></div>
        <div style={{ display: 'grid', justifyItems: 'end', gap: 7 }}><span style={{ padding: '5px 9px', borderRadius: 16, background: messaggioPerMaestro ? '#f3d9f8' : '#ffe0e9', color: messaggioPerMaestro ? '#6d2e7e' : '#9b1640', fontSize: 11, fontWeight: 'bold' }}>{messaggioPerMaestro ? `MAESTRO${messaggio.maestroNome ? ` · ${messaggio.maestroNome}` : ''}` : 'SEGRETERIA'}</span><button type="button" disabled={eliminazioneInCorso === messaggio.id} onClick={() => eliminaChat(messaggio)} style={{ border: '1px solid #d43d61', borderRadius: 7, padding: '5px 8px', background: '#fff5f6', color: '#ae1f43', cursor: eliminazioneInCorso === messaggio.id ? 'wait' : 'pointer', fontSize: 11, fontWeight: 'bold' }}>{eliminazioneInCorso === messaggio.id ? 'Elimino…' : '🗑️ Elimina chat'}</button></div>
      </div>
      <p style={{ margin: '14px 0 10px', color: '#28151e', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{messaggio.testo}</p>
      {(messaggio.risposte || []).map((risposta, indice) => <div key={risposta.id || indice} style={{ marginTop: 10, padding: 11, borderRadius: 10, background: '#fbf1f5', border: '1px solid #f1d8e2', color: '#432332' }}><strong>{risposta.autore || 'Segreteria'}</strong><span style={{ color: '#765b66', fontSize: 12 }}> · {formattaDataIT(risposta.data)}</span><br />{risposta.testo}</div>)}
      <form onSubmit={(event) => inviaRisposta(event, messaggio)} style={{ marginTop: 15, display: 'flex', gap: 9, alignItems: 'stretch' }}>
        <div style={{ flex: 1, minWidth: 0 }}><div aria-label="Emoji rapide" style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>{emojiRapide.map(emoji => <button key={emoji} type="button" onClick={() => aggiungiEmojiRisposta(messaggio.id, emoji)} aria-label={`Aggiungi ${emoji}`} style={{ width: 30, height: 30, padding: 0, border: '1px solid #eadde2', borderRadius: 8, background: '#fff7fa', cursor: 'pointer', fontSize: 16 }}>{emoji}</button>)}</div><textarea value={risposte[messaggio.id] || ''} onChange={event => setRisposte(valori => ({ ...valori, [messaggio.id]: event.target.value }))} rows={2} placeholder="Scrivi la risposta all’allievo…" style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 9, resize: 'vertical' }} /></div>
        <button type="submit" disabled={invioInCorso === messaggio.id} style={{ alignSelf: 'stretch', padding: '10px 14px', border: 0, borderRadius: 9, background: messaggioPerMaestro ? '#7e2e99' : '#c61d55', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>{invioInCorso === messaggio.id ? 'Invio…' : 'Rispondi →'}</button>
      </form>
    </article>; }) : <div style={{ marginTop: 18, padding: 24, borderRadius: 14, background: '#fff', border: '1px solid #eadde2', color: '#6f6268' }}>Nessun messaggio da leggere al momento.</div>}
  </div>;
}

function formatoEuroShop(valore) {
  const numero = Number(valore);
  return Number.isFinite(numero) && numero > 0 ? numero.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }) : '—';
}

function formatoTitoloShop(valore) {
  return String(valore || '').trim().toLocaleLowerCase('it-IT').split(/(\s+|[-'’])/).map(parte => {
    if (!parte || /^(\s+|[-'’])$/.test(parte)) return parte;
    return parte.charAt(0).toLocaleUpperCase('it-IT') + parte.slice(1);
  }).join('');
}

function formatoListaShop(valore) {
  return String(valore || '').split(',').map(elemento => elemento.trim().toLocaleUpperCase('it-IT')).filter(Boolean).join(', ');
}

function formatoColoriShop(valore) {
  return String(valore || '').split(',').map(elemento => formatoTitoloShop(elemento)).filter(Boolean).join(', ');
}

function statoShop(stato) {
  const stati = { in_attesa: ['In attesa', '#d97706'], confermata: ['Confermata', '#15803d'], pronta_ritiro: ['Pronta al ritiro', '#2563eb'], consegnata: ['Consegnata', '#6d28d9'], non_disponibile: ['Non disponibile', '#c2410c'] };
  return stati[stato] || stati.in_attesa;
}

function ShopSegreteria({ utenteLoggato }) {
  const formVuoto = { codiceArticolo: '', nome: '', categoria: 'Maglia', descrizione: '', prezzo: '', quantitaDisponibile: '', taglie: '', colori: '', immagineUrl: '', attivo: true };
  const [articoli, setArticoli] = useState([]);
  const [prenotazioni, setPrenotazioni] = useState([]);
  const [form, setForm] = useState(formVuoto);
  const [articoloInModifica, setArticoloInModifica] = useState(null);
  const [fotoArticolo, setFotoArticolo] = useState(null);
  const [anteprimaFoto, setAnteprimaFoto] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState('');
  const [messaggioSuccesso, setMessaggioSuccesso] = useState('');
  const [ricevutaDaStampare, setRicevutaDaStampare] = useState(null);
  const [sezioneShop, setSezioneShop] = useState('articoli');
  const [ricercaCodiceArticolo, setRicercaCodiceArticolo] = useState('');

  useEffect(() => {
    const interrompiArticoli = ascoltaRaccolta('articoli_shop', snapshot => setArticoli(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })).sort((a, b) => String(a.nome || '').localeCompare(String(b.nome || ''), 'it'))), erroreSnapshot => setErrore(`Impossibile leggere l’inventario: ${erroreSnapshot.message}`));
    const interrompiPrenotazioni = ascoltaRaccolta('prenotazioni_shop', snapshot => setPrenotazioni(snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() })).sort((a, b) => String(b.dataCreazione || '').localeCompare(String(a.dataCreazione || '')))), erroreSnapshot => setErrore(`Impossibile leggere le prenotazioni: ${erroreSnapshot.message}`));
    return () => { interrompiArticoli(); interrompiPrenotazioni(); };
  }, []);

  const aggiornaForm = (campo, valore) => setForm(corrente => ({ ...corrente, [campo]: valore }));
  const normalizzaCodiceArticolo = valore => String(valore || '').toUpperCase().trim().replace(/[^A-Z0-9_-]/g, '');
  const generaCodiceArticolo = () => `AOA-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
  const scegliFotoArticolo = file => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return setErrore('Scegli un file immagine.');
    if (file.size > 5 * 1024 * 1024) return setErrore('Foto non caricata: il file supera 5 MB. Scegli una foto più piccola o ritagliala prima di riprovare.');
    setErrore(''); setFotoArticolo(file); setAnteprimaFoto(URL.createObjectURL(file));
  };
  const comprimiFotoArticolo = file => new Promise((resolve, reject) => {
    const lettore = new FileReader();
    lettore.onerror = () => reject(new Error('Impossibile leggere la foto selezionata.'));
    lettore.onload = evento => {
      const immagine = new Image();
      immagine.onerror = () => reject(new Error('La foto selezionata non è valida.'));
      immagine.onload = () => {
        const rapporto = Math.min(1, 800 / Math.max(immagine.width, immagine.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(immagine.width * rapporto));
        canvas.height = Math.max(1, Math.round(immagine.height * rapporto));
        const contesto = canvas.getContext('2d');
        contesto.fillStyle = '#ffffff';
        contesto.fillRect(0, 0, canvas.width, canvas.height);
        contesto.drawImage(immagine, 0, 0, canvas.width, canvas.height);
        const fotoCompressa = canvas.toDataURL('image/jpeg', .68);
        if (fotoCompressa.length > 620000) return reject(new Error('Foto non caricata: è troppo grande anche dopo la riduzione automatica. Ritagliala oppure scegli una foto più leggera.'));
        resolve(fotoCompressa);
      };
      immagine.src = String(evento.target?.result || '');
    };
    lettore.readAsDataURL(file);
  });
  const salvaArticolo = async (event) => {
    event.preventDefault(); setErrore(''); setMessaggioSuccesso('');
    const campiMancanti = [];
    if (!form.nome.trim()) campiMancanti.push('nome articolo');
    if (!form.categoria) campiMancanti.push('categoria');
    if (form.prezzo === '' || !Number.isFinite(Number(form.prezzo)) || Number(form.prezzo) < 0) campiMancanti.push('prezzo');
    if (form.quantitaDisponibile === '' || !Number.isInteger(Number(form.quantitaDisponibile)) || Number(form.quantitaDisponibile) <= 0) campiMancanti.push('quantità disponibile (almeno 1)');
    if (form.attivo !== true) campiMancanti.push('spunta “Pubblica e rendi visibile agli allievi”');
    if (campiMancanti.length) return setErrore(`Per pubblicare l’articolo completa questi campi necessari: ${campiMancanti.join(', ')}.`);
    const codiceArticolo = normalizzaCodiceArticolo(form.codiceArticolo) || generaCodiceArticolo();
    const codiceGiaUsato = articoli.some(articolo => articolo.id !== articoloInModifica?.id && normalizzaCodiceArticolo(articolo.codiceArticolo) === codiceArticolo);
    if (codiceGiaUsato) return setErrore(`Il codice prodotto ${codiceArticolo} è già assegnato a un altro articolo. Generane uno nuovo.`);
    const dati = {
      codiceArticolo, nome: formatoTitoloShop(form.nome), categoria: form.categoria, descrizione: formatoTitoloShop(form.descrizione), immagineUrl: form.immagineUrl.trim(),
      prezzo: Number(form.prezzo || 0), quantitaDisponibile: Number(form.quantitaDisponibile || 0),
      taglie: formatoListaShop(form.taglie).split(', ').filter(Boolean),
      colori: formatoColoriShop(form.colori).split(', ').filter(Boolean),
      attivo: true, pubblicoAllievi: true, aggiornatoIl: new Date().toISOString(), aggiornatoDa: utenteLoggato.username
    };
    setSalvataggio(true);
    try {
      if (fotoArticolo) {
        // La foto viene compressa e salvata insieme all'articolo: così la
        // pubblicazione non resta bloccata se Firebase Storage non risponde.
        dati.immagineUrl = await comprimiFotoArticolo(fotoArticolo);
        dati.immagineDaGalleria = true;
      }
      if (articoloInModifica) await updateDoc(doc(db, 'articoli_shop', articoloInModifica.id), dati);
      else await addDoc(collection(db, 'articoli_shop'), { ...dati, creatoIl: new Date().toISOString() });
      setForm(formVuoto); setArticoloInModifica(null); setFotoArticolo(null); setAnteprimaFoto('');
      setMessaggioSuccesso(articoloInModifica ? '✅ Articolo aggiornato e pubblicato con successo.' : '✅ Articolo caricato e pubblicato con successo.');
    } catch (erroreSalvataggio) { console.error(erroreSalvataggio); setErrore(erroreSalvataggio.message || 'Non è stato possibile salvare l’articolo.'); }
    finally { setSalvataggio(false); }
  };
  const modificaArticolo = articolo => { setSezioneShop('articoli'); setArticoloInModifica(articolo); setFotoArticolo(null); setAnteprimaFoto(articolo.immagineUrl || ''); setForm({ codiceArticolo: articolo.codiceArticolo || '', nome: articolo.nome || '', categoria: articolo.categoria || 'Maglia', descrizione: articolo.descrizione || '', prezzo: articolo.prezzo ?? '', quantitaDisponibile: articolo.quantitaDisponibile ?? '', taglie: (articolo.taglie || []).join(', '), colori: (articolo.colori || []).join(', '), immagineUrl: articolo.immagineUrl || '', attivo: articolo.attivo !== false }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const cambiaDisponibilita = async (articolo, delta) => { const nuovaQuantita = Math.max(0, Number(articolo.quantitaDisponibile || 0) + delta); await updateDoc(doc(db, 'articoli_shop', articolo.id), { quantitaDisponibile: nuovaQuantita, aggiornatoIl: new Date().toISOString(), aggiornatoDa: utenteLoggato.username }); };
  const eliminaArticolo = async articolo => {
    setErrore(''); setMessaggioSuccesso('');
    const prenotazioniAperte = prenotazioni.filter(prenotazione => prenotazione.articoloId === articolo.id && !['consegnata', 'non_disponibile'].includes(prenotazione.stato));
    if (prenotazioniAperte.length) {
      setErrore(`Non puoi eliminare “${articolo.nome}”: ci sono ${prenotazioniAperte.length} prenotazioni ancora da gestire. Completa o segna non disponibile le prenotazioni prima di eliminarlo.`);
      return;
    }
    if (!window.confirm(`Eliminare definitivamente l’articolo “${articolo.nome}” dallo Shop? L’azione non può essere annullata.`)) return;
    try {
      await deleteDoc(doc(db, 'articoli_shop', articolo.id));
      if (articoloInModifica?.id === articolo.id) { setArticoloInModifica(null); setForm(formVuoto); setFotoArticolo(null); setAnteprimaFoto(''); }
      await registraLogOperazione(utenteLoggato.username, `Eliminato articolo Shop: ${articolo.nome}`);
      setMessaggioSuccesso(`✅ Articolo “${articolo.nome}” eliminato dallo Shop.`);
    } catch (erroreEliminazione) {
      console.error('Impossibile eliminare l’articolo:', erroreEliminazione);
      setErrore('Non è stato possibile eliminare l’articolo. Riprova.');
    }
  };
  const inviaNotificaShopAllievo = async (prenotazione, stato) => {
    const articolo = prenotazione.articoloNome || 'articolo richiesto';
    const disponibile = stato === 'pronta_ritiro';
    const titolo = disponibile ? '🛍️ Il tuo articolo è pronto!' : '🧵 Aggiornamento sul tuo ordine';
    const messaggio = disponibile
      ? `Il tuo articolo “${articolo}” è disponibile. Puoi passare in Segreteria per ritirarlo e pagarlo alla consegna. Ti aspettiamo!`
      : `L’articolo “${articolo}” non è disponibile al momento. Lo abbiamo già ordinato per te: non appena rientrerà, ti avviseremo noi. Grazie per la pazienza!`;
    const dataCreazione = new Date().toISOString();
    const avviso = await addDoc(collection(db, 'avvisi_allievi'), {
      allievoId: prenotazione.allievoId,
      titolo,
      messaggio,
      importante: true,
      letto: false,
      tipo: 'shop',
      prenotazioneShopId: prenotazione.id,
      notificaPushInviata: false,
      dataCreazione
    });
    await updateDoc(doc(db, 'prenotazioni_shop', prenotazione.id), { messaggioStato: messaggio, notificaStatoShop: stato, notificaStatoShopIl: dataCreazione });
    try {
      const risposta = await fetch(URL_SERVIZIO_NOTIFICHE, { method: 'POST', headers: await intestazioniAutenticate({ 'Content-Type': 'application/json' }), body: JSON.stringify({ avvisoId: avviso.id }) });
      if (!risposta.ok) throw new Error('Notifica push non inviata');
    } catch (erroreNotifica) {
      // L'avviso resta comunque disponibile nell'app dell'allievo, anche se
      // le notifiche del telefono non sono state ancora abilitate.
      console.warn('Notifica Shop salvata nell’app, ma push non inviata:', erroreNotifica);
    }
  };
  const cambiaStato = async (prenotazione, stato) => {
    setErrore('');
    try {
      if (stato === 'confermata' && !prenotazione.inventarioScaricato) {
        await runTransaction(db, async transazione => {
          const articoloRef = doc(db, 'articoli_shop', prenotazione.articoloId);
          const articoloSnap = await transazione.get(articoloRef);
          if (!articoloSnap.exists()) throw new Error('Articolo non trovato nell’inventario.');
          const disponibilita = Number(articoloSnap.data().quantitaDisponibile || 0);
          if (disponibilita < Number(prenotazione.quantita || 0)) throw new Error('Disponibilità insufficiente per confermare questa prenotazione.');
          transazione.update(articoloRef, { quantitaDisponibile: disponibilita - Number(prenotazione.quantita || 0), aggiornatoIl: new Date().toISOString(), aggiornatoDa: utenteLoggato.username });
          transazione.update(doc(db, 'prenotazioni_shop', prenotazione.id), { stato, inventarioScaricato: true, confermataIl: new Date().toISOString(), gestitaDa: utenteLoggato.username });
        });
      } else {
        const aggiornamento = { stato, gestitaDa: utenteLoggato.username, aggiornataIl: new Date().toISOString() };
        if (stato === 'consegnata') aggiornamento.consegnataIl = new Date().toISOString();
        await updateDoc(doc(db, 'prenotazioni_shop', prenotazione.id), aggiornamento);
      }
      if (stato === 'non_disponibile' || stato === 'pronta_ritiro') await inviaNotificaShopAllievo(prenotazione, stato);
    } catch (erroreStato) { console.error(erroreStato); setErrore(erroreStato.message || 'Non è stato possibile aggiornare la prenotazione.'); }
  };
  const registraPagamento = async prenotazione => { try { await updateDoc(doc(db, 'prenotazioni_shop', prenotazione.id), { pagamentoRegistrato: true, pagatoIl: new Date().toISOString(), pagamentoRegistratoDa: utenteLoggato.username }); } catch (errorePagamento) { console.error(errorePagamento); setErrore('Non è stato possibile registrare il pagamento.'); } };
  const stampaRiepilogo = () => { setRicevutaDaStampare(null); window.setTimeout(() => window.print(), 80); };
  const stampaRicevuta = prenotazione => {
    setRicevutaDaStampare(prenotazione);
    window.setTimeout(() => {
      const chiudiStampa = () => document.body.classList.remove('shop-stampa-ricevuta');
      document.body.classList.add('shop-stampa-ricevuta');
      window.addEventListener('afterprint', chiudiStampa, { once: true });
      window.print();
    }, 80);
  };
  const proteggiEtichetta = valore => String(valore ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  const stampaEtichettaQr = articolo => {
    const codice = normalizzaCodiceArticolo(articolo.codiceArticolo);
    if (!codice) { setErrore('Questo articolo non ha ancora un codice. Premi Modifica, poi Genera codice e Salva modifiche.'); return; }
    const finestra = window.open('', '_blank', 'width=520,height=700');
    if (!finestra) { setErrore('Il browser ha bloccato la stampa dell’etichetta. Consenti le finestre popup e riprova.'); return; }
    const qr = `https://api.qrserver.com/v1/create-qr-code/?size=360x360&data=${encodeURIComponent(codice)}`;
    finestra.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Etichetta ${proteggiEtichetta(codice)}</title><style>body{font-family:Arial,sans-serif;margin:28px;color:#111}.etichetta{width:380px;border:2px solid #bf8b12;border-radius:18px;padding:22px;text-align:center}.marca{font-size:12px;color:#8a6110;font-weight:bold;letter-spacing:1.2px}.codice{font-size:25px;letter-spacing:2px;font-weight:bold;margin:12px 0}.nome{font-size:20px;font-weight:bold;margin-top:6px}.descrizione{color:#555;margin:8px 0 16px;min-height:20px}img{width:250px;height:250px}.nota{font-size:11px;color:#666;margin-top:12px}@media print{body{margin:0}.etichetta{border:1px solid #111}}</style></head><body><div class="etichetta"><div class="marca">ACADEMY OF ARTS · SHOP</div><div class="nome">${proteggiEtichetta(articolo.nome || 'Articolo')}</div><div class="descrizione">${proteggiEtichetta(articolo.descrizione || articolo.categoria || '')}</div><img src="${qr}" alt="QR ${proteggiEtichetta(codice)}"><div class="codice">${proteggiEtichetta(codice)}</div><div class="nota">Inquadra o usa il codice per trovare il prodotto.</div></div><script>window.onload=()=>window.print()</script></body></html>`);
    finestra.document.close();
  };
  const input = { width: '100%', boxSizing: 'border-box', padding: 11, borderRadius: 9, border: '1px solid #d9b8c5', background: '#fff', color: '#2d1721', font: 'inherit' };
  const prenotazioniNuove = prenotazioni.filter(prenotazione => prenotazione.stato === 'in_attesa').length;
  const riepilogoStampabile = prenotazioni.filter(prenotazione => prenotazione.stato !== 'non_disponibile');
  // Le richieste non disponibili sono già state comunicate all'allievo: non
  // richiedono più azioni operative, ma rimangono consultabili separatamente.
  const prenotazioniDaGestire = prenotazioni.filter(prenotazione => !['consegnata', 'non_disponibile'].includes(prenotazione.stato));
  const richiesteNonDisponibili = prenotazioni.filter(prenotazione => prenotazione.stato === 'non_disponibile');
  const consegneCompletate = prenotazioni.filter(prenotazione => prenotazione.stato === 'consegnata');
  const testoRicercaArticolo = normalizzaCodiceArticolo(ricercaCodiceArticolo);
  const articoliFiltratiPerCodice = articoli.filter(articolo => !testoRicercaArticolo || normalizzaCodiceArticolo(articolo.codiceArticolo).includes(testoRicercaArticolo) || String(articolo.nome || '').toUpperCase().includes(testoRicercaArticolo) || String(articolo.descrizione || '').toUpperCase().includes(testoRicercaArticolo));

  return <div style={{ maxWidth: 1150, color: '#2d1721' }}>
    <style>{`#shop-riepilogo-stampa, #shop-ricevuta-stampa { display: none; } @media print { @page { size: A4; margin: 14mm; } body * { visibility: hidden !important; } .shop-stampa-attiva, .shop-stampa-attiva * { visibility: visible !important; } .shop-stampa-attiva { display: block !important; position: fixed; inset: 0; padding: 24px; box-sizing: border-box; background: #fff; color: #111; font-family: Arial, sans-serif; } body.shop-stampa-ricevuta #root { display: none !important; } body.shop-stampa-ricevuta #shop-ricevuta-stampa, body.shop-stampa-ricevuta #shop-ricevuta-stampa * { visibility: visible !important; } body.shop-stampa-ricevuta #shop-ricevuta-stampa { display: block !important; position: static; padding: 0; box-sizing: border-box; background: #fff; color: #111; font-family: Arial, sans-serif; } #shop-riepilogo-stampa table { width: 100%; border-collapse: collapse; margin-top: 16px; } #shop-riepilogo-stampa th, #shop-riepilogo-stampa td { padding: 9px; text-align: left; border-bottom: 1px solid #d1d5db; } #shop-riepilogo-stampa th { background: #f3e8ff; color: #57132c; } }`}</style>
    <h2 style={{ marginTop: 0, color: '#57132c' }}>🛍️ Shop Academy</h2>
    <p style={{ color: '#553744', lineHeight: 1.5 }}>Master, Operatori e Admin possono inserire e gestire gli articoli. Il pagamento è registrato alla consegna.</p>
    {errore && <p style={{ padding: 12, borderRadius: 10, color: '#b42318', background: '#fff0f0', border: '1px solid #fecaca', fontWeight: 'bold' }}>{errore}</p>}
    {messaggioSuccesso && <p style={{ padding: 12, borderRadius: 10, color: '#146c3a', background: '#e8f8ed', border: '1px solid #a9dfbb', fontWeight: 'bold' }}>{messaggioSuccesso}</p>}
    <nav aria-label="Sottomenu Shop" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '18px 0' }}>{[['articoli', '➕ Inserisci singolo prodotto'], ['inventario', '📦 Inventario'], ['prenotazioni', `📋 Prenotazioni${prenotazioniNuove ? ` (${prenotazioniNuove})` : ''}`], ['consegne', `✅ Consegne${consegneCompletate.length ? ` (${consegneCompletate.length})` : ''}`]].map(([chiave, etichetta]) => <button key={chiave} type="button" onClick={() => setSezioneShop(chiave)} style={{ border: sezioneShop === chiave ? '2px solid #57132c' : '1px solid #d9b8c5', borderRadius: 10, padding: '10px 13px', background: sezioneShop === chiave ? '#57132c' : '#fff', color: sezioneShop === chiave ? '#fff' : '#57132c', fontWeight: 'bold', cursor: 'pointer' }}>{etichetta}</button>)}</nav>
    <section hidden={sezioneShop !== 'articoli'} style={{ marginTop: 18, padding: 20, borderRadius: 16, background: '#fff', border: '1px solid #eadde2', boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
      <h3 style={{ marginTop: 0, color: '#57132c' }}>{articoloInModifica ? `Modifica prodotto: ${articoloInModifica.nome}` : '➕ Inserisci un singolo prodotto nello Shop'}</h3>
      {!articoloInModifica && <p style={{ margin: '-5px 0 16px', color: '#765b66', lineHeight: 1.45 }}>Compila questa scheda per aggiungere un prodotto alla volta all’inventario e renderlo visibile agli allievi.</p>}
      <form onSubmit={salvaArticolo} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
        <label>🔫 Codice prodotto <small>(clicca qui e spara il codice)</small><div style={{ display: 'flex', gap: 7, marginTop: 5 }}><input value={form.codiceArticolo} onChange={event => aggiornaForm('codiceArticolo', normalizzaCodiceArticolo(event.target.value))} placeholder="Clicca qui e usa la pistola" style={input} /><button type="button" onClick={() => aggiornaForm('codiceArticolo', generaCodiceArticolo())} style={{ border: 0, borderRadius: 8, padding: '0 10px', background: '#7e2e99', color: '#fff', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }}>Genera</button></div><small style={{ display: 'block', marginTop: 5, color: '#765b66', lineHeight: 1.35 }}>Dopo la scansione inserisci nome e descrizione: il codice resta associato a questo prodotto.</small></label>
        <label>Nome articolo <strong style={{ color: '#b42318' }}>*</strong><input required value={form.nome} onChange={event => aggiornaForm('nome', event.target.value)} onBlur={event => aggiornaForm('nome', formatoTitoloShop(event.target.value))} placeholder="Es. Maglia Academy" style={{ ...input, marginTop: 5 }} /></label>
        <label>Categoria <strong style={{ color: '#b42318' }}>*</strong><select value={form.categoria} onChange={event => aggiornaForm('categoria', event.target.value)} style={{ ...input, marginTop: 5 }}>{['Maglia', 'Body', 'Giubbotto', 'Kit tuta', 'Scarpe', 'Accessorio', 'Altro'].map(categoria => <option key={categoria}>{categoria}</option>)}</select></label>
        <label>Prezzo (€) <strong style={{ color: '#b42318' }}>*</strong><input required type="number" min="0" step="0.01" value={form.prezzo} onChange={event => aggiornaForm('prezzo', event.target.value)} placeholder="Es. 25" style={{ ...input, marginTop: 5 }} /></label>
        <label>Quantità disponibile <strong style={{ color: '#b42318' }}>*</strong><input required type="number" min="1" value={form.quantitaDisponibile} onChange={event => aggiornaForm('quantitaDisponibile', event.target.value)} placeholder="Es. 12" style={{ ...input, marginTop: 5 }} /></label>
        <label>Taglie <small>(separate da virgola)</small><input value={form.taglie} onChange={event => aggiornaForm('taglie', event.target.value)} onBlur={event => aggiornaForm('taglie', formatoListaShop(event.target.value))} placeholder="Es. S, M, L, XL" style={{ ...input, marginTop: 5 }} /></label>
        <label>Colori <small>(separati da virgola)</small><input value={form.colori} onChange={event => aggiornaForm('colori', event.target.value)} onBlur={event => aggiornaForm('colori', formatoColoriShop(event.target.value))} placeholder="Es. Nero, Rosa, Viola" style={{ ...input, marginTop: 5 }} /></label>
        <div style={{ gridColumn: '1 / -1', padding: 13, borderRadius: 11, background: '#fff9fc', border: '1px dashed #c77a99' }}><strong style={{ color: '#57132c' }}>📷 Foto articolo</strong><span style={{ display: 'block', margin: '4px 0 4px', color: '#765b66', fontSize: 13 }}>Puoi scattare una nuova foto oppure scegliere un’immagine già presente nella galleria.</span><span style={{ display: 'block', margin: '0 0 10px', color: '#a71243', fontSize: 12, fontWeight: 'bold' }}>Limite: massimo 5 MB. La foto viene ridotta automaticamente prima della pubblicazione.</span><div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}><label style={{ display: 'inline-block', padding: '10px 13px', borderRadius: 8, background: '#7e2e99', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>📸 Scatta foto<input type="file" accept="image/*" capture="environment" onChange={event => scegliFotoArticolo(event.target.files?.[0])} style={{ display: 'none' }} /></label><label style={{ display: 'inline-block', padding: '10px 13px', borderRadius: 8, background: '#a71243', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>🖼️ Scegli dalla galleria<input type="file" accept="image/*" onChange={event => scegliFotoArticolo(event.target.files?.[0])} style={{ display: 'none' }} /></label></div>{anteprimaFoto && <div style={{ marginTop: 11 }}><img src={anteprimaFoto} alt="Anteprima articolo" style={{ width: 130, height: 100, objectFit: 'cover', borderRadius: 9, border: '1px solid #d9b8c5' }} /><button type="button" onClick={() => { setFotoArticolo(null); setAnteprimaFoto(''); aggiornaForm('immagineUrl', ''); }} style={{ marginLeft: 10, verticalAlign: 'top', border: 0, background: 'transparent', color: '#a71243', fontWeight: 'bold', cursor: 'pointer' }}>Rimuovi foto</button></div>}</div>
        <label style={{ gridColumn: '1 / -1' }}>Oppure link immagine <small>(facoltativo)</small><input type="url" value={form.immagineUrl} onChange={event => aggiornaForm('immagineUrl', event.target.value)} placeholder="https://…" style={{ ...input, marginTop: 5 }} /></label>
        <label style={{ gridColumn: '1 / -1' }}>Descrizione <small>(facoltativa)</small><textarea value={form.descrizione} onChange={event => aggiornaForm('descrizione', event.target.value)} onBlur={event => aggiornaForm('descrizione', formatoTitoloShop(event.target.value))} rows={2} style={{ ...input, marginTop: 5, resize: 'vertical' }} /></label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.attivo} onChange={event => aggiornaForm('attivo', event.target.checked)} />Pubblica e rendi visibile agli allievi <strong style={{ color: '#b42318' }}>*</strong></label>
        <div style={{ display: 'flex', gap: 9, alignItems: 'end', flexWrap: 'wrap' }}><button disabled={salvataggio} type="submit" style={{ border: 0, borderRadius: 9, padding: '11px 15px', background: '#a71243', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>{salvataggio ? 'Salvataggio…' : articoloInModifica ? 'Salva modifiche' : 'Aggiungi articolo'}</button>{articoloInModifica && <button type="button" onClick={() => { setArticoloInModifica(null); setForm(formVuoto); setFotoArticolo(null); setAnteprimaFoto(''); }} style={{ border: '1px solid #d9b8c5', borderRadius: 9, padding: '10px 14px', background: '#fff', color: '#57132c', fontWeight: 'bold', cursor: 'pointer' }}>Annulla</button>}</div>
      </form>
    </section>
    <section hidden={sezioneShop !== 'inventario'} style={{ marginTop: 18, padding: 20, borderRadius: 16, background: '#fff', border: '1px solid #eadde2', boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}><h3 style={{ margin: 0, color: '#57132c' }}>📦 Inventario</h3><button type="button" onClick={stampaRiepilogo} disabled={!riepilogoStampabile.length} style={{ border: 0, borderRadius: 9, padding: '10px 13px', background: riepilogoStampabile.length ? '#57132c' : '#9c8a92', color: '#fff', fontWeight: 'bold', cursor: riepilogoStampabile.length ? 'pointer' : 'not-allowed' }}>🖨️ Stampa riepilogo materiale</button></div>
      <label style={{ display: 'block', margin: '15px 0 12px', color: '#57132c', fontWeight: 'bold' }}>🔫 Cerca o spara codice prodotto<input type="search" value={ricercaCodiceArticolo} onChange={event => setRicercaCodiceArticolo(event.target.value)} placeholder="Clicca qui e usa la pistola: compare il prodotto" style={{ ...input, marginTop: 6, maxWidth: 520 }} /></label>
      {articoli.length ? <div style={{ display: 'grid', gap: 10 }}>{articoliFiltratiPerCodice.map(articolo => <article key={articolo.id} style={{ padding: 14, borderRadius: 12, background: articolo.attivo === false ? '#f6f0f2' : '#fff9fc', border: '1px solid #eadde2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}><div>{articolo.immagineUrl && <img src={articolo.immagineUrl} alt="" style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 8, verticalAlign: 'middle', marginRight: 10 }} />}<strong>{articolo.nome}</strong><span style={{ marginLeft: 8, color: '#7d5a68' }}>· {articolo.categoria || 'Articolo'} · {formatoEuroShop(articolo.prezzo)}</span><small style={{ display: 'block', marginTop: 5, color: '#57132c', fontWeight: 'bold' }}>Codice: {articolo.codiceArticolo || 'Da generare'}</small>{articolo.descrizione && <small style={{ display: 'block', marginTop: 3, color: '#765b66' }}>{articolo.descrizione}</small>}<small style={{ display: 'block', marginTop: 5, color: '#765b66' }}>{(articolo.taglie || []).join(', ') || 'Taglia unica'} · {(articolo.colori || []).join(', ') || 'Colore unico'}{articolo.attivo === false ? ' · Non pubblicato' : Number(articolo.quantitaDisponibile || 0) <= 0 ? ' · Esaurito: non visibile agli allievi' : ' · Pubblicato agli allievi'}</small></div><div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><button type="button" onClick={() => cambiaDisponibilita(articolo, -1)} style={{ border: 0, width: 31, height: 31, borderRadius: 8, background: '#f1d8e2', color: '#7f2443', fontWeight: 'bold', cursor: 'pointer' }}>−</button><strong style={{ minWidth: 96, textAlign: 'center', color: Number(articolo.quantitaDisponibile || 0) > 0 ? '#16844a' : '#c92a2a' }}>{articolo.quantitaDisponibile || 0} pezzi</strong><button type="button" onClick={() => cambiaDisponibilita(articolo, 1)} style={{ border: 0, width: 31, height: 31, borderRadius: 8, background: '#d7f2df', color: '#16844a', fontWeight: 'bold', cursor: 'pointer' }}>+</button><button type="button" onClick={() => stampaEtichettaQr(articolo)} style={{ border: 0, borderRadius: 8, padding: '7px 10px', background: '#7e2e99', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>▣ Stampa QR</button><button type="button" onClick={() => modificaArticolo(articolo)} style={{ border: '1px solid #d9b8c5', borderRadius: 8, padding: '7px 10px', background: '#fff', color: '#57132c', fontWeight: 'bold', cursor: 'pointer' }}>Modifica</button><button type="button" onClick={() => eliminaArticolo(articolo)} style={{ border: 0, borderRadius: 8, padding: '7px 10px', background: '#c92a2a', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>🗑️ Elimina</button></div></article>)}</div> : <p style={{ color: '#6f6268' }}>Non hai ancora inserito articoli nello Shop.</p>}
      {articoli.length > 0 && articoliFiltratiPerCodice.length === 0 && <p style={{ color: '#765b66' }}>Nessun articolo corrisponde al codice letto.</p>}
    </section>
    <section hidden={sezioneShop !== 'prenotazioni'} style={{ marginTop: 18, padding: 20, borderRadius: 16, background: '#fff', border: '1px solid #eadde2', boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}><h3 style={{ margin: 0, color: '#57132c' }}>📋 Prenotazioni da gestire {prenotazioniNuove > 0 && <span style={{ marginLeft: 7, padding: '4px 8px', borderRadius: 12, background: '#ffe4c2', color: '#a04700', fontSize: 12 }}>{prenotazioniNuove} nuove</span>}</h3></div>
      {prenotazioniDaGestire.filter(prenotazione => prenotazione.stato === 'confermata').length > 0 && <div style={{ marginTop: 13, padding: 12, borderRadius: 10, background: '#eef5ff', border: '1px solid #bfdbfe' }}><strong style={{ color: '#1d4ed8' }}>📦 Articoli disponibili per la consegna</strong><div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 9 }}>{prenotazioniDaGestire.filter(prenotazione => prenotazione.stato === 'confermata').map(prenotazione => <button key={`consegna-${prenotazione.id}`} type="button" onClick={() => cambiaStato(prenotazione, 'consegnata')} style={{ border: 0, borderRadius: 8, padding: '9px 11px', background: '#6d28d9', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Consegnato: {prenotazione.nomeAllievo || 'Allievo'} · {prenotazione.articoloNome}</button>)}</div></div>}
      <div id="shop-riepilogo-stampa" className={!ricevutaDaStampare ? 'shop-stampa-attiva' : ''}><h1 style={{ margin: 0, color: '#57132c' }}>Academy Of Arts · Riepilogo Shop</h1><p>Materiale prenotato dagli allievi · Generato il {new Date().toLocaleDateString('it-IT')}</p><table><thead><tr><th>Allievo</th><th>Articolo</th><th>Taglia / Colore</th><th>Quantità</th><th>Stato</th><th>Pagamento</th></tr></thead><tbody>{riepilogoStampabile.map(prenotazione => <tr key={prenotazione.id}><td>{prenotazione.nomeAllievo || 'Allievo'}</td><td>{prenotazione.articoloNome}</td><td>{[prenotazione.taglia, prenotazione.colore].filter(Boolean).join(' · ') || '—'}</td><td>{prenotazione.quantita}</td><td>{statoShop(prenotazione.stato)[0]}</td><td>{prenotazione.pagamentoRegistrato ? 'Pagato alla consegna' : 'Da pagare alla consegna'}</td></tr>)}</tbody></table><p style={{ marginTop: 22, fontWeight: 'bold' }}>Totale pezzi: {riepilogoStampabile.reduce((totale, prenotazione) => totale + Number(prenotazione.quantita || 0), 0)}</p></div>
      {prenotazioniDaGestire.length ? <div style={{ display: 'grid', gap: 11, marginTop: 14 }}>{prenotazioniDaGestire.map(prenotazione => { const [testo, colore] = statoShop(prenotazione.stato); return <article key={prenotazione.id} style={{ padding: 15, borderRadius: 12, borderLeft: `5px solid ${colore}`, background: '#fff9fc', borderTop: '1px solid #eadde2', borderRight: '1px solid #eadde2', borderBottom: '1px solid #eadde2' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}><div><strong style={{ fontSize: 17 }}>{prenotazione.nomeAllievo || 'Allievo'}</strong><span style={{ display: 'block', marginTop: 3, color: '#765b66' }}>#{prenotazione.articoloNome} · {prenotazione.quantita} pezzo/i{prenotazione.taglia ? ` · Taglia ${prenotazione.taglia}` : ''}{prenotazione.colore ? ` · ${prenotazione.colore}` : ''}</span></div><span style={{ color: colore, fontWeight: 'bold' }}>{testo}</span></div>{prenotazione.nota && <p style={{ margin: '10px 0 0', color: '#553744' }}>Nota: {prenotazione.nota}</p>}<p style={{ margin: '10px 0 0', color: '#553744', fontWeight: 'bold' }}>Totale: {formatoEuroShop(prenotazione.totaleStimato)} · 💳 Alla consegna {prenotazione.pagamentoRegistrato ? '· Pagato' : '· Da pagare'}</p><div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>{prenotazione.stato === 'in_attesa' && <><button type="button" onClick={() => cambiaStato(prenotazione, 'confermata')} style={{ border: 0, borderRadius: 8, padding: '9px 11px', background: '#198754', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Conferma e scala inventario</button><button type="button" onClick={() => cambiaStato(prenotazione, 'non_disponibile')} style={{ border: 0, borderRadius: 8, padding: '9px 11px', background: '#c24141', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Non disponibile e avvisa</button></>}{prenotazione.stato === 'confermata' && <button type="button" onClick={() => cambiaStato(prenotazione, 'pronta_ritiro')} style={{ border: 0, borderRadius: 8, padding: '9px 11px', background: '#2563eb', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Disponibile: avvisa per il ritiro</button>}{prenotazione.stato === 'pronta_ritiro' && <><button type="button" onClick={() => cambiaStato(prenotazione, 'consegnata')} style={{ border: 0, borderRadius: 8, padding: '9px 11px', background: '#6d28d9', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✓ Segna consegnata</button>{!prenotazione.pagamentoRegistrato && <button type="button" onClick={() => registraPagamento(prenotazione)} style={{ border: 0, borderRadius: 8, padding: '9px 11px', background: '#a71243', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>💳 Registra pagamento</button>}</>}</div></article>; })}</div> : <p style={{ marginTop: 14, color: '#6f6268' }}>Nessuna prenotazione da gestire.</p>}
      {richiesteNonDisponibili.length > 0 && <section style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid #eadde2' }}><h3 style={{ margin: 0, color: '#9b2c2c', fontSize: 17 }}>🧵 Richieste momentaneamente non disponibili</h3><p style={{ margin: '6px 0 12px', color: '#765b66', fontSize: 13 }}>L’allievo è già stato avvisato. Qui trovi uno storico per sapere chi attende il riassortimento.</p><div style={{ display: 'grid', gap: 9 }}>{richiesteNonDisponibili.map(prenotazione => <article key={`non-disponibile-${prenotazione.id}`} style={{ padding: 13, borderRadius: 11, background: '#fff5f5', borderLeft: '5px solid #c24141' }}><strong style={{ color: '#57132c' }}>{prenotazione.nomeAllievo || 'Allievo'}</strong><span style={{ display: 'block', marginTop: 4, color: '#553744' }}>Ha richiesto: <strong>{prenotazione.articoloNome}</strong>{prenotazione.quantita ? ` · ${prenotazione.quantita} pezzo/i` : ''}{prenotazione.taglia ? ` · Taglia ${prenotazione.taglia}` : ''}{prenotazione.colore ? ` · ${prenotazione.colore}` : ''}</span><small style={{ display: 'block', marginTop: 7, color: '#765b66' }}>Richiesta del {formattaDataIT(prenotazione.dataCreazione || prenotazione.aggiornataIl)} · Non disponibile</small></article>)}</div></section>}
    </section>
    <section hidden={sezioneShop !== 'consegne'} style={{ marginTop: 18, padding: 20, borderRadius: 16, background: '#fff', border: '1px solid #eadde2', boxShadow: '0 7px 20px rgba(71,22,43,.07)' }}>
      <h3 style={{ margin: 0, color: '#57132c' }}>📦 Consegne completate</h3>
      <p style={{ color: '#765b66', margin: '6px 0 14px' }}>Storico degli articoli già consegnati: questi non compaiono più nell’App Allievi.</p>
      {consegneCompletate.length ? <div style={{ display: 'grid', gap: 10 }}>{consegneCompletate.map(prenotazione => <article key={prenotazione.id} style={{ padding: 14, borderRadius: 12, background: '#f5f0fb', borderLeft: '5px solid #6d28d9' }}><strong>{prenotazione.nomeAllievo || 'Allievo'}</strong><span style={{ float: 'right', color: '#6d28d9', fontWeight: 'bold' }}>✓ Consegnata</span><p style={{ margin: '8px 0 0', color: '#553744' }}>{prenotazione.articoloNome} · {prenotazione.quantita} pezzo/i{prenotazione.taglia ? ` · Taglia ${prenotazione.taglia}` : ''}{prenotazione.colore ? ` · ${prenotazione.colore}` : ''}</p><p style={{ margin: '7px 0 0', color: '#553744', fontWeight: 'bold' }}>Totale: {formatoEuroShop(prenotazione.totaleStimato)} · {prenotazione.pagamentoRegistrato ? '💳 Pagato alla consegna' : '💳 Pagamento da registrare'}</p>{!prenotazione.pagamentoRegistrato && <button type="button" onClick={() => registraPagamento(prenotazione)} style={{ marginTop: 10, border: 0, borderRadius: 8, padding: '9px 11px', background: '#a71243', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>💳 Registra pagamento</button>}</article>)}</div> : <p style={{ color: '#6f6268' }}>Nessuna consegna completata.</p>}
      {consegneCompletate.length > 0 && <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #eadde2' }}><strong style={{ color: '#57132c' }}>🧾 Ricevute materiale consegnato</strong><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>{consegneCompletate.map(prenotazione => <button key={`ricevuta-${prenotazione.id}`} type="button" onClick={() => stampaRicevuta(prenotazione)} style={{ border: '1px solid #c5a4df', borderRadius: 8, padding: '9px 11px', background: '#fff', color: '#57132c', fontWeight: 'bold', cursor: 'pointer' }}>🧾 {prenotazione.nomeAllievo || 'Allievo'} · {prenotazione.articoloNome}</button>)}</div></div>}
    </section>
    {typeof document !== 'undefined' && createPortal(<div id="shop-ricevuta-stampa">{ricevutaDaStampare && <><header style={{ display: 'flex', alignItems: 'center', gap: 16, paddingBottom: 18, borderBottom: '3px solid #57132c' }}><img src="/academy-allievi-icon.png" alt="Logo Academy Of Arts" style={{ width: 96, height: 52, objectFit: 'contain' }} /><div><h1 style={{ margin: 0, color: '#57132c', fontFamily: 'Georgia, serif', fontSize: 28 }}>Academy Of Arts</h1><p style={{ margin: '4px 0 0', color: '#8b4261', fontSize: 11, fontWeight: 'bold', letterSpacing: 1.3 }}>TEAM DANCE PROJECT</p></div></header><div style={{ marginTop: 24, padding: '14px 16px', background: '#f7eff8', borderLeft: '5px solid #8e3fa7' }}><h2 style={{ margin: 0, color: '#57132c', fontSize: 22 }}>Ricevuta materiale consegnato</h2><p style={{ margin: '6px 0 0', color: '#614854' }}>Documento di consegna del materiale Academy Of Arts</p></div><section style={{ marginTop: 24, lineHeight: 1.7, fontSize: 16 }}><p><strong>Allievo:</strong> {ricevutaDaStampare.nomeAllievo || 'Allievo'}</p><p><strong>Articolo consegnato:</strong> {ricevutaDaStampare.articoloNome}</p><p><strong>Dettagli:</strong> {[ricevutaDaStampare.taglia ? `Taglia ${ricevutaDaStampare.taglia}` : '', ricevutaDaStampare.colore].filter(Boolean).join(' · ') || '—'}</p><p><strong>Quantità:</strong> {ricevutaDaStampare.quantita} pezzo/i</p><p><strong>Data consegna:</strong> {formattaDataIT(ricevutaDaStampare.consegnataIl || ricevutaDaStampare.aggiornataIl || ricevutaDaStampare.dataCreazione)}</p><p><strong>Modalità di pagamento:</strong> Alla consegna</p></section><footer style={{ marginTop: 42, paddingTop: 18, borderTop: '1px solid #c9b8bd', color: '#614854', fontSize: 13 }}><p style={{ margin: 0 }}>Grazie per aver scelto Academy Of Arts.</p><p style={{ margin: '6px 0 0' }}>Firma per ricevuta: ________________________________________</p></footer></>}</div>, document.body)}
  </div>;
}

function CalendarioEventiSegreteria({ utenteCorrente }) {
  const [allievi, setAllievi] = useState([]);
  const [eventi, setEventi] = useState([]);
  const [destinatarioEvento, setDestinatarioEvento] = useState('tutti');
  const [allievoIds, setAllievoIds] = useState([]);
  const [corsiDestinatari, setCorsiDestinatari] = useState([]);
  const [ricercaDestinatario, setRicercaDestinatario] = useState('');
  const [titolo, setTitolo] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [data, setData] = useState(dataOggiISO());
  const [ora, setOra] = useState('');
  const [luogo, setLuogo] = useState('');
  const [linkMappa, setLinkMappa] = useState('');
  const [allegatoEvento, setAllegatoEvento] = useState(null);
  const [allegatoEsistente, setAllegatoEsistente] = useState(null);
  const [richiedePresenza, setRichiedePresenza] = useState(false);
  const [salvataggio, setSalvataggio] = useState(false);
  const [erroreSalvataggio, setErroreSalvataggio] = useState('');
  const [eventoInModifica, setEventoInModifica] = useState(null);
  const [presenzeEventi, setPresenzeEventi] = useState([]);
  const [novitaApp, setNovitaApp] = useState([]);
  const [titoloNovita, setTitoloNovita] = useState('');
  const [testoNovita, setTestoNovita] = useState('');
  const [urlNovita, setUrlNovita] = useState('');
  const [linkNovita, setLinkNovita] = useState('');
  const [tipoNovita, setTipoNovita] = useState('immagine');
  const [meseVisualizzato, setMeseVisualizzato] = useState(() => {
    const oggi = new Date();
    return new Date(oggi.getFullYear(), oggi.getMonth(), 1);
  });
  const [dataSelezionata, setDataSelezionata] = useState(dataOggiISO());
  const formattaDataOraRisposta = valore => {
    if (!valore) return 'Data non disponibile';
    const dataRisposta = valore?.toDate ? valore.toDate() : new Date(valore);
    if (Number.isNaN(dataRisposta.getTime())) return 'Data non disponibile';
    return dataRisposta.toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  useEffect(() => {
    const caricaAllievi = async () => {
      try {
        const snap = await leggiRaccolta('allievi');
        setAllievi(ordinaAllieviPerCognomeNome(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
      } catch (errore) {
        console.error('Errore caricamento allievi calendario:', errore);
      }
    };
    caricaAllievi();
    const interrompiEventi = ascoltaRaccolta('eventi_allievi', (snap) => {
      setEventi(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => `${a.data || ''}${a.ora || ''}`.localeCompare(`${b.data || ''}${b.ora || ''}`)));
    }, (errore) => console.error('Errore aggiornamento eventi calendario:', errore));
    const interrompiPresenze = ascoltaRaccolta('presenze_eventi', (snap) => {
      setPresenzeEventi(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (errore) => console.error('Errore aggiornamento presenze eventi:', errore));
    const interrompiNovita = ascoltaRaccolta('novita_app_allievi', (snap) => {
      setNovitaApp(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => Number(a.ordine || 0) - Number(b.ordine || 0)));
    }, (errore) => console.error('Errore aggiornamento novità App:', errore));
    return () => { interrompiEventi(); interrompiPresenze(); interrompiNovita(); };
  }, []);

  const pubblicaNovitaApp = async event => {
    event.preventDefault();
    if (!titoloNovita.trim() || !urlNovita.trim()) return alert('Inserisci almeno titolo e collegamento alla foto o al video.');
    try {
      await addDoc(collection(db, 'novita_app_allievi'), { titolo: titoloNovita.trim(), testo: testoNovita.trim(), urlMedia: urlNovita.trim(), link: linkNovita.trim(), tipo: tipoNovita, attivo: true, ordine: novitaApp.length + 1, pubblicataIl: new Date().toISOString(), pubblicataDa: utenteCorrente });
      setTitoloNovita(''); setTestoNovita(''); setUrlNovita(''); setLinkNovita('');
      await registraLogOperazione(utenteCorrente, `Pubblicata novità per App Allievi: ${titoloNovita.trim()}`);
    } catch (errore) { console.error('Errore pubblicazione novità:', errore); alert('Non è stato possibile pubblicare la novità.'); }
  };

  const caricaAllegatoConTimeout = async (file) => {
    const controller = new AbortController();
    const attesaMassima = window.setTimeout(() => controller.abort(), 30000);
    try {
      const dati = new FormData();
      dati.append('file', file, file.name);
      const risposta = await fetch(URL_SERVIZIO_ALLEGATI_EVENTI, { method: 'POST', headers: await intestazioniAutenticate(), body: dati, signal: controller.signal });
      const risultato = await risposta.json().catch(() => ({}));
      if (!risposta.ok) {
        const errore = new Error(risultato.errore || 'Caricamento allegato non riuscito.');
        errore.code = `allegati/${risposta.status}`;
        throw errore;
      }
      return risultato;
    } catch (errore) {
      if (errore?.name === 'AbortError') {
        const erroreTimeout = new Error('Caricamento allegato troppo lento.');
        erroreTimeout.code = 'allegati/upload-timeout';
        throw erroreTimeout;
      }
      throw errore;
    } finally {
      window.clearTimeout(attesaMassima);
    }
  };

  const eliminaAllegatoEvento = (percorso) => {
    // Gli allegati creati in precedenza con Firebase non possono essere rimossi
    // dal piano gratuito; i nuovi allegati R2 sono invece rimossi con l'evento.
    if (!percorso?.startsWith('eventi/')) return;
    intestazioniAutenticate({ 'Content-Type': 'application/json' }).then(intestazioni => fetch(URL_SERVIZIO_ALLEGATI_EVENTI, {
      method: 'DELETE',
      headers: intestazioni,
      body: JSON.stringify({ percorso })
    })).catch(errore => console.warn('Allegato R2 non eliminato:', errore));
  };

  const pubblicaEvento = async (event) => {
    event.preventDefault();
    if (!titolo.trim() || !data || (destinatarioEvento === 'selezionati' && !allievoIds.length) || (destinatarioEvento === 'corsi' && !corsiDestinatari.length)) {
      alert('Inserisci titolo, data e destinatario dell’evento.');
      return;
    }
    const perTutti = destinatarioEvento === 'tutti';
    const destinatariCorso = allievi.filter(allievo => {
      const corsi = Array.isArray(allievo.Corsi) ? allievo.Corsi : (allievo.Corso ? [allievo.Corso] : []);
      return corsi.some(corso => corsiDestinatari.includes(corso));
    });
    setErroreSalvataggio('');
    setSalvataggio(true);
    try {
      let datiAllegato = {};
      if (allegatoEvento) {
        const allegatoCaricato = await caricaAllegatoConTimeout(allegatoEvento);
        datiAllegato = {
          allegatoNome: allegatoCaricato.nome,
          allegatoUrl: allegatoCaricato.url,
          allegatoTipo: allegatoCaricato.tipo,
          allegatoPercorso: allegatoCaricato.percorso
        };
      }
      const datiEvento = {
        titolo: titolo.trim(),
        descrizione: descrizione.trim(),
        data,
        ora,
        luogo: luogo.trim(),
        linkMappa: linkMappa.trim(),
        destinatario: perTutti ? 'tutti' : destinatarioEvento,
        // allievoId resta per compatibilità con gli eventi vecchi già creati.
        allievoId: perTutti || destinatarioEvento === 'corsi' || allievoIds.length !== 1 ? null : allievoIds[0],
        allievoIds: destinatarioEvento === 'selezionati' ? allievoIds : [],
        corsiDestinatari: destinatarioEvento === 'corsi' ? corsiDestinatari : [],
        richiedePresenza,
        ultimaModifica: new Date().toISOString()
      };
      if (eventoInModifica) {
        await updateDoc(doc(db, 'eventi_allievi', eventoInModifica), { ...datiEvento, ...datiAllegato });
        if (allegatoEvento && allegatoEsistente?.percorso) eliminaAllegatoEvento(allegatoEsistente.percorso);
        await registraLogOperazione(utenteCorrente, `Modificato evento calendario: ${titolo.trim()}`);
      } else {
        const nuovoEvento = await addDoc(collection(db, 'eventi_allievi'), {
          ...datiEvento,
          ...datiAllegato,
          creatoDa: utenteCorrente,
          dataCreazione: new Date().toISOString()
        });
        // Notifica con suono ai destinatari (non blocca la pubblicazione se fallisce).
        (async () => {
          try {
            let da = 0;
            for (let giro = 0; giro < 50 && da !== null; giro++) {
              const risposta = await fetch(URL_SERVIZIO_NOTIFICHE.replace('/invia-avviso', '/invia-evento'), {
                method: 'POST',
                headers: await intestazioniAutenticate({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ eventoId: nuovoEvento.id, da })
              });
              const esito = await risposta.json().catch(() => ({}));
              if (!risposta.ok) throw new Error(esito.errore || 'Notifica evento non inviata.');
              da = esito.prossimo ?? null;
            }
          } catch (erroreNotifica) { console.error('Notifica evento non inviata:', erroreNotifica); }
        })();
        await registraLogOperazione(utenteCorrente, `Creato evento calendario: ${titolo.trim()}`);
      }
      setTitolo('');
      setDescrizione('');
      setData(dataOggiISO());
      setOra('');
      setLuogo('');
      setLinkMappa('');
      setAllegatoEvento(null);
      setAllegatoEsistente(null);
      setRichiedePresenza(false);
      setDestinatarioEvento('tutti');
      setAllievoIds([]);
      setCorsiDestinatari([]);
      setRicercaDestinatario('');
      setEventoInModifica(null);
      alert(eventoInModifica ? 'Evento modificato con successo.' : (perTutti ? 'Evento pubblicato per tutti gli allievi.' : destinatarioEvento === 'corsi' ? `Evento pubblicato per ${destinatariCorso.length} allievo${destinatariCorso.length === 1 ? '' : 'i'} dei corsi selezionati.` : `Evento pubblicato per ${allievoIds.length} allievo${allievoIds.length === 1 ? '' : 'i'}.`));
    } catch (errore) {
      console.error('Errore salvataggio evento:', errore);
      const codiceErrore = String(errore?.code || '');
      const messaggioErrore = codiceErrore === 'allegati/413'
        ? 'L’allegato supera 8 MB. Scegli un file più leggero.'
        : codiceErrore === 'allegati/415'
          ? 'Questo formato non è consentito. Puoi allegare foto, PDF e documenti Office.'
          : codiceErrore === 'allegati/507'
            ? '☁️ Cloud pieno: l’archivio allegati ha raggiunto il limite gratuito. Elimina alcuni vecchi allegati prima di caricarne altri.'
          : codiceErrore === 'allegati/upload-timeout'
            ? 'L’evento non è stato pubblicato perché il caricamento dell’allegato non ha risposto entro 30 secondi. Riprova con un file più leggero.'
          : (eventoInModifica ? 'Non è stato possibile modificare l’evento. Riprova.' : 'Non è stato possibile pubblicare l’evento. Riprova.');
      setErroreSalvataggio(messaggioErrore);
      alert(messaggioErrore);
    } finally {
      setSalvataggio(false);
    }
  };

  const avviaModificaEvento = (evento) => {
    setEventoInModifica(evento.id);
    const destinatariEsistenti = Array.isArray(evento.allievoIds)
      ? evento.allievoIds
      : (evento.allievoId ? [evento.allievoId] : []);
    setDestinatarioEvento(evento.destinatario === 'tutti' ? 'tutti' : evento.destinatario === 'corsi' ? 'corsi' : 'selezionati');
    setAllievoIds(destinatariEsistenti);
    setCorsiDestinatari(Array.isArray(evento.corsiDestinatari) ? evento.corsiDestinatari : []);
    setRicercaDestinatario('');
    setTitolo(evento.titolo || '');
    setDescrizione(evento.descrizione || '');
    setData(evento.data || dataOggiISO());
    setOra(evento.ora || '');
    setLuogo(evento.luogo || '');
    setLinkMappa(evento.linkMappa || '');
    setAllegatoEvento(null);
    setAllegatoEsistente(evento.allegatoUrl ? { nome: evento.allegatoNome || 'Allegato', url: evento.allegatoUrl, percorso: evento.allegatoPercorso || '' } : null);
    setRichiedePresenza(Boolean(evento.richiedePresenza));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const annullaModificaEvento = () => {
    setEventoInModifica(null);
    setDestinatarioEvento('tutti');
    setAllievoIds([]);
    setCorsiDestinatari([]);
    setRicercaDestinatario('');
    setTitolo('');
    setDescrizione('');
    setData(dataOggiISO());
    setOra('');
    setLuogo('');
    setLinkMappa('');
    setAllegatoEvento(null);
    setAllegatoEsistente(null);
    setRichiedePresenza(false);
  };

  const eliminaEvento = async (evento) => {
    if (!window.confirm(`Eliminare l’evento “${evento.titolo}”?`)) return;
    try {
      await deleteDoc(doc(db, 'eventi_allievi', evento.id));
      if (evento.allegatoPercorso) eliminaAllegatoEvento(evento.allegatoPercorso);
      await registraLogOperazione(utenteCorrente, `Eliminato evento calendario: ${evento.titolo}`);
    } catch (errore) {
      console.error('Errore eliminazione evento:', errore);
      alert('Non è stato possibile eliminare l’evento.');
    }
  };

  const nomeDestinatario = (evento) => {
    if (evento.destinatario === 'tutti') return '👥 Visibile a tutti gli allievi';
    if (evento.destinatario === 'corsi') {
      const corsi = Array.isArray(evento.corsiDestinatari) ? evento.corsiDestinatari : [];
      return corsi.length <= 2 ? `🎓 Per corsi: ${corsi.join(' · ') || 'non specificati'}` : `🎓 Per ${corsi.length} corsi selezionati`;
    }
    const destinatari = Array.isArray(evento.allievoIds) && evento.allievoIds.length
      ? evento.allievoIds
      : (evento.allievoId ? [evento.allievoId] : []);
    const nomi = destinatari.map(id => {
      const allievo = allievi.find(a => a.id === id);
      return allievo ? `${allievo.Cognome} ${allievo.Nome}` : null;
    }).filter(Boolean);
    if (nomi.length <= 3) return `👥 Per: ${nomi.join(', ') || 'allievi selezionati'}`;
    return `👥 Per ${nomi.length} allievi selezionati`;
  };

  const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const annoCalendario = meseVisualizzato.getFullYear();
  const meseCalendario = meseVisualizzato.getMonth();
  const primoGiornoCalendario = (new Date(annoCalendario, meseCalendario, 1).getDay() + 6) % 7;
  const giorniNelMese = new Date(annoCalendario, meseCalendario + 1, 0).getDate();
  const dataDelGiorno = giorno => `${annoCalendario}-${String(meseCalendario + 1).padStart(2, '0')}-${String(giorno).padStart(2, '0')}`;
  const eventiPerData = eventi.reduce((mappa, evento) => {
    const dataEvento = String(evento.data || '').slice(0, 10);
    if (dataEvento) mappa[dataEvento] = [...(mappa[dataEvento] || []), evento];
    return mappa;
  }, {});
  const eventiDelGiorno = eventiPerData[dataSelezionata] || [];
  const risposteEvento = eventoId => presenzeEventi.filter(presenza => presenza.eventoId === eventoId);
  const urlMappaEvento = evento => {
    const linkCondiviso = String(evento.linkMappa || '').trim();
    if (/^https?:\/\//i.test(linkCondiviso)) return linkCondiviso;
    return '';
  };
  const normalizzaRicerca = valore => String(valore || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .toLowerCase()
    .trim();
  const ricercaNormalizzata = normalizzaRicerca(ricercaDestinatario);
  const allieviFiltrati = allievi.filter(allievo => normalizzaRicerca(`${allievo.Cognome || ''} ${allievo.Nome || ''}`).includes(ricercaNormalizzata));
  const corsiDisponibili = [...new Set(allievi.flatMap(allievo => Array.isArray(allievo.Corsi) ? allievo.Corsi : (allievo.Corso ? [allievo.Corso] : [])))].sort((a, b) => a.localeCompare(b, 'it'));
  const alternaCorsoDestinatario = corso => setCorsiDestinatari(corsi => corsi.includes(corso) ? corsi.filter(voce => voce !== corso) : [...corsi, corso]);
  const aggiungiDestinatario = id => {
    setAllievoIds(ids => ids.includes(id) ? ids : [...ids, id]);
    setRicercaDestinatario('');
  };
  const rimuoviDestinatario = id => setAllievoIds(ids => ids.filter(allievoId => allievoId !== id));

  return (
    <div style={{ color: '#f8f9fa', maxWidth: 850 }}>
      <h2 style={{ color: '#e599f7', marginTop: 0 }}>🗓️ Calendario Eventi</h2>
      <p style={{ color: '#9aa5ba' }}>Inserisci appuntamenti, saggi, chiusure o comunicazioni importanti. Puoi mostrarli a tutti, a singoli allievi oppure a uno o più corsi.</p>
      <section style={{ marginBottom: 24, padding: 18, borderRadius: 12, background: 'linear-gradient(135deg, #3e214d, #172a38)', border: '1px solid #d8a4ff' }}>
        <h3 style={{ margin: 0, color: '#ffe29a' }}>✨ Pubblicità nella Dashboard Allievi</h3>
        <p style={{ margin: '7px 0 14px', color: '#f3d9f8', lineHeight: 1.4 }}>Inserisci il link di una foto, di un video MP4 oppure il normale link YouTube. Le novità scorrono automaticamente nell’App Allievi.</p>
        <form onSubmit={pubblicaNovitaApp} style={{ display: 'grid', gap: 9 }}>
          <input value={titoloNovita} onChange={event => setTitoloNovita(event.target.value)} maxLength={70} placeholder="Titolo, es. Open Day Hip-Hop" style={{ padding: 10, borderRadius: 7 }} />
          <textarea value={testoNovita} onChange={event => setTestoNovita(event.target.value)} maxLength={160} rows={2} placeholder="Breve testo (facoltativo)" style={{ padding: 10, borderRadius: 7, resize: 'vertical' }} />
          <select value={tipoNovita} onChange={event => setTipoNovita(event.target.value)} style={{ padding: 10, borderRadius: 7 }}><option value="immagine">Foto</option><option value="video">Video</option></select>
          <input type="url" value={urlNovita} onChange={event => setUrlNovita(event.target.value)} placeholder="Link foto/MP4 oppure YouTube" style={{ padding: 10, borderRadius: 7 }} />
          <input type="url" value={linkNovita} onChange={event => setLinkNovita(event.target.value)} placeholder="Link da aprire al tocco (facoltativo)" style={{ padding: 10, borderRadius: 7 }} />
          <button type="submit" style={{ justifySelf: 'start', border: 0, borderRadius: 8, padding: '10px 14px', background: '#a84ada', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✨ Pubblica nella Dashboard</button>
        </form>
        {novitaApp.length > 0 && <div style={{ display: 'grid', gap: 7, marginTop: 14 }}>{novitaApp.map(novita => <div key={novita.id} style={{ display: 'flex', gap: 9, justifyContent: 'space-between', alignItems: 'center', padding: '9px 10px', borderRadius: 8, background: 'rgba(0,0,0,.22)', color: '#fff' }}><span><strong>{novita.titolo}</strong><small style={{ display: 'block', color: '#e6d7ed' }}>{novita.tipo === 'video' ? '🎬 Video' : '🖼️ Foto'} · {novita.attivo === false ? 'Nascosta' : 'Visibile'}</small></span><div style={{ display: 'flex', gap: 7 }}><button type="button" onClick={() => updateDoc(doc(db, 'novita_app_allievi', novita.id), { attivo: novita.attivo === false })} style={{ border: 0, borderRadius: 7, padding: '7px 9px', background: '#6d5b79', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>{novita.attivo === false ? 'Mostra' : 'Nascondi'}</button><button type="button" onClick={() => { if (window.confirm(`Eliminare “${novita.titolo}”?`)) deleteDoc(doc(db, 'novita_app_allievi', novita.id)); }} style={{ border: 0, borderRadius: 7, padding: '7px 9px', background: '#c92a2a', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Elimina</button></div></div>)}</div>}
      </section>
      <form onSubmit={pubblicaEvento} style={{ display: 'grid', gap: 14, background: '#353b4c', border: `1px solid ${eventoInModifica ? '#e599f7' : '#4a5266'}`, borderRadius: 12, padding: 22, marginBottom: 24 }}>
        {eventoInModifica && <strong style={{ color: '#e599f7' }}>✏️ Stai modificando un evento</strong>}
        <label style={{ fontWeight: 'bold' }}>Destinatari</label>
        <select value={destinatarioEvento} onChange={e => { const scelta = e.target.value; setDestinatarioEvento(scelta); if (scelta !== 'selezionati') { setAllievoIds([]); setRicercaDestinatario(''); } if (scelta !== 'corsi') setCorsiDestinatari([]); }} style={{ padding: 11, borderRadius: 7 }}>
          <option value="tutti">Tutti gli allievi</option>
          <option value="selezionati">Uno o più allievi selezionati</option>
          <option value="corsi">Uno o più corsi / fasce</option>
        </select>
        {destinatarioEvento === 'selezionati' && <>
          <input value={ricercaDestinatario} onChange={e => setRicercaDestinatario(e.target.value)} placeholder="🔎 Cerca e aggiungi per cognome o nome" style={{ padding: 11, borderRadius: 7 }} />
          {ricercaDestinatario.trim() && <div style={{ display: 'grid', gap: 6, padding: 10, borderRadius: 8, background: '#242b3d', border: '1px solid #9c36b5' }}>
            {allieviFiltrati.length ? allieviFiltrati.slice(0, 8).map(allievo => <button key={allievo.id} type="button" onClick={() => aggiungiDestinatario(allievo.id)} disabled={allievoIds.includes(allievo.id)} style={{ textAlign: 'left', padding: '8px 10px', border: '1px solid rgba(229,153,247,.45)', borderRadius: 7, background: allievoIds.includes(allievo.id) ? '#4a334f' : '#353b4c', color: '#fff', cursor: allievoIds.includes(allievo.id) ? 'default' : 'pointer', fontWeight: 'bold' }}>👤 {allievo.Cognome} {allievo.Nome}{allievoIds.includes(allievo.id) ? ' · già aggiunto' : ''}</button>) : <span style={{ color: '#ffb3ca', fontSize: 13 }}>Nessun allievo trovato.</span>}
            {allieviFiltrati.length > 8 && <span style={{ color: '#e599f7', fontSize: 12 }}>Mostrati i primi 8 risultati: rendi la ricerca più precisa.</span>}
          </div>}
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', minHeight: 28 }}>
            {allievoIds.length ? allievoIds.map(id => {
              const allievo = allievi.find(a => a.id === id);
              return <button key={id} type="button" onClick={() => rimuoviDestinatario(id)} title="Rimuovi allievo" style={{ padding: '7px 10px', borderRadius: 16, border: '1px solid #9c36b5', background: '#f9edf9', color: '#351522', fontWeight: 'bold', cursor: 'pointer' }}>👤 {allievo ? `${allievo.Cognome} ${allievo.Nome}` : 'Allievo'} ×</button>;
            }) : <span style={{ color: '#6c5660', fontSize: 13 }}>Cerca e aggiungi almeno un allievo.</span>}
          </div>
        </>}
        {destinatarioEvento === 'corsi' && <section style={{ display: 'grid', gap: 8, padding: 12, borderRadius: 8, background: '#242b3d', border: '1px solid #9c36b5' }}>
          <strong style={{ color: '#e599f7', fontSize: 14 }}>🎓 Seleziona uno o più corsi</strong>
          <small style={{ color: '#d6c7dd', lineHeight: 1.4 }}>Ogni allievo iscritto ad almeno uno dei corsi selezionati vedrà l’evento nella sua App.</small>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 7 }}>
            {corsiDisponibili.map(corso => <label key={corso} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 9px', borderRadius: 7, cursor: 'pointer', background: corsiDestinatari.includes(corso) ? '#61356b' : '#353b4c', border: `1px solid ${corsiDestinatari.includes(corso) ? '#e599f7' : 'rgba(229,153,247,.35)'}`, color: '#fff', fontWeight: 'bold', fontSize: 13 }}><input type="checkbox" checked={corsiDestinatari.includes(corso)} onChange={() => alternaCorsoDestinatario(corso)} style={{ accentColor: '#e599f7', width: 16, height: 16 }} />{corso.replace(' - ', ' · ')}</label>)}
          </div>
          {!corsiDisponibili.length && <span style={{ color: '#ffb3ca', fontSize: 13 }}>Nessun corso è ancora presente nell’anagrafica.</span>}
          <strong style={{ color: corsiDestinatari.length ? '#b2f2bb' : '#ffb3ca', fontSize: 13 }}>{corsiDestinatari.length ? `✓ ${corsiDestinatari.length} corso/i selezionato/i` : 'Seleziona almeno un corso.'}</strong>
        </section>}
        <label style={{ fontWeight: 'bold' }}>Titolo dell’evento</label>
        <input value={titolo} onChange={e => setTitolo(e.target.value)} maxLength={100} required placeholder="Es. Saggio di fine anno" style={{ padding: 11, borderRadius: 7 }} />
        <label style={{ fontWeight: 'bold' }}>Data dell’evento</label>
        <input type="date" value={data} onChange={e => setData(e.target.value)} required style={{ padding: 11, borderRadius: 7 }} />
        <label style={{ fontWeight: 'bold' }}>Ora (facoltativa)</label>
        <input type="time" value={ora} onChange={e => setOra(e.target.value)} style={{ padding: 11, borderRadius: 7 }} />
        <label style={{ fontWeight: 'bold' }}>📍 Luogo o indirizzo (facoltativo)</label>
        <input value={luogo} onChange={e => setLuogo(e.target.value)} maxLength={160} placeholder="Es. Teatro Comunale, Via Roma 10, Napoli" style={{ padding: 11, borderRadius: 7 }} />
        <label style={{ fontWeight: 'bold' }}>Link Google Maps (facoltativo)</label>
        <input type="url" value={linkMappa} onChange={e => setLinkMappa(e.target.value)} placeholder="Incolla qui il link condiviso da Google Maps" style={{ padding: 11, borderRadius: 7 }} />
        <label style={{ fontWeight: 'bold' }}>Descrizione (facoltativa)</label>
        <textarea value={descrizione} onChange={e => setDescrizione(e.target.value)} rows={3} maxLength={500} placeholder="Es. Teatro comunale, arrivo richiesto alle ore 17:30." style={{ padding: 11, borderRadius: 7, resize: 'vertical' }} />
        <label style={{ fontWeight: 'bold' }}>📎 Allegato per gli allievi (facoltativo)</label>
        <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" onChange={event => { const file = event.target.files?.[0] || null; if (file && file.size > 8 * 1024 * 1024) { alert('Il file supera 8 MB. Scegli un allegato più piccolo.'); event.target.value = ''; return; } setAllegatoEvento(file); }} style={{ padding: 9, borderRadius: 7 }} />
        {allegatoEvento && <small style={{ color: '#e599f7', fontWeight: 'bold' }}>Nuovo allegato: {allegatoEvento.name}</small>}
        {!allegatoEvento && allegatoEsistente && <small style={{ color: '#e599f7' }}>Allegato attuale: <a href={allegatoEsistente.url} target="_blank" rel="noreferrer" style={{ color: '#e599f7', fontWeight: 'bold' }}>{allegatoEsistente.nome}</a></small>}
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, color: '#e599f7', fontWeight: 'bold', cursor: 'pointer' }}>
          <input type="checkbox" checked={richiedePresenza} onChange={e => setRichiedePresenza(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#9c36b5' }} />
          Chiedi conferma della presenza all’allievo
        </label>
        {erroreSalvataggio && <div role="alert" style={{ padding: '10px 12px', borderRadius: 7, background: '#5f1717', border: '1px solid #ff8787', color: '#ffe3e3', fontWeight: 'bold', lineHeight: 1.4 }}>{erroreSalvataggio}</div>}
        <button disabled={salvataggio} type="submit" style={{ padding: 12, border: 0, borderRadius: 7, background: '#9c36b5', color: '#fff', fontWeight: 'bold', cursor: salvataggio ? 'wait' : 'pointer' }}>{salvataggio ? 'Salvataggio…' : (eventoInModifica ? 'Salva modifiche' : 'Pubblica evento nel calendario')}</button>
        {eventoInModifica && <button type="button" onClick={annullaModificaEvento} style={{ padding: 10, border: '1px solid #adb5bd', borderRadius: 7, background: 'transparent', color: '#f8f9fa', fontWeight: 'bold', cursor: 'pointer' }}>Annulla modifica</button>}
      </form>

      <section style={{ background: '#353b4c', border: '1px solid #4a5266', borderRadius: 12, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16 }}>
          <h3 style={{ color: '#e599f7', margin: 0 }}>Calendario eventi</h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={() => setMeseVisualizzato(dataCalendario => new Date(dataCalendario.getFullYear(), dataCalendario.getMonth() - 1, 1))} style={{ border: 0, borderRadius: 8, padding: '6px 10px', background: '#4a5266', color: '#fff', cursor: 'pointer' }}>‹</button>
            <button type="button" onClick={() => setMeseVisualizzato(dataCalendario => new Date(dataCalendario.getFullYear(), dataCalendario.getMonth() + 1, 1))} style={{ border: 0, borderRadius: 8, padding: '6px 10px', background: '#4a5266', color: '#fff', cursor: 'pointer' }}>›</button>
          </div>
        </div>
        <p style={{ margin: '0 0 13px', textAlign: 'center', color: '#fff', fontWeight: 'bold' }}>{nomiMesi[meseCalendario]} {annoCalendario}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 5, textAlign: 'center', fontSize: 11, color: '#ced4da', marginBottom: 5 }}>
          {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(giorno => <span key={giorno}>{giorno}</span>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 5 }}>
          {Array.from({ length: primoGiornoCalendario }).map((_, indice) => <span key={`vuoto-${indice}`} />)}
          {Array.from({ length: giorniNelMese }, (_, indice) => {
            const giorno = indice + 1;
            const dataEvento = dataDelGiorno(giorno);
            const haEventi = Boolean(eventiPerData[dataEvento]?.length);
            const eOggi = dataEvento === dataOggiISO();
            const selezionato = dataEvento === dataSelezionata;
            return <button type="button" key={dataEvento} onClick={() => setDataSelezionata(dataEvento)} title={haEventi ? 'Clicca per vedere l’evento' : ''} style={{ minHeight: 42, display: 'grid', placeItems: 'center', borderRadius: 9, padding: 2, cursor: 'pointer', background: haEventi ? '#9c36b5' : (eOggi ? '#ffb3ca' : '#3d485f'), color: haEventi ? '#fff' : (eOggi ? '#251018' : '#ffffff'), border: selezionato ? '2px solid #fff' : (eOggi ? '2px solid #ffb3ca' : '1px solid rgba(235,242,255,.30)'), fontWeight: haEventi || eOggi ? 'bold' : 'normal' }}><span>{giorno}</span>{haEventi && <small style={{ fontSize: 10 }}>●</small>}</button>;
          })}
        </div>

        <div style={{ marginTop: 18 }}>
          <strong style={{ color: '#fff' }}>{formattaDataIT(dataSelezionata)}</strong>
          {eventiDelGiorno.length ? eventiDelGiorno.map(evento => {
            const risposte = risposteEvento(evento.id);
            const presenti = risposte.filter(presenza => presenza.risposta === 'presente').length;
            const assenti = risposte.filter(presenza => presenza.risposta === 'assente').length;
            return <article key={evento.id} style={{ marginTop: 10, padding: 14, borderRadius: 10, borderLeft: '4px solid #9c36b5', background: '#242b3d' }}>
              <strong style={{ color: '#fff' }}>{evento.ora ? `${evento.ora} — ` : ''}{evento.titolo}</strong>
              <div style={{ color: '#ffb3ca', fontSize: 13, marginTop: 5 }}>{nomeDestinatario(evento)}</div>
              {evento.descrizione && <p style={{ color: '#ced4da', margin: '7px 0 0' }}>{evento.descrizione}</p>}
              {evento.luogo && <div style={{ color: '#fff', marginTop: 8, fontSize: 14 }}>📍 {evento.luogo}</div>}
              {urlMappaEvento(evento) && <a href={urlMappaEvento(evento)} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, color: '#e599f7', fontWeight: 'bold' }}>Apri in Google Maps ↗</a>}
              {evento.allegatoUrl && <a href={evento.allegatoUrl} target="_blank" rel="noreferrer" download={evento.allegatoNome || true} style={{ display: 'inline-block', marginTop: 8, marginLeft: 10, color: '#e599f7', fontWeight: 'bold' }}>📎 Apri allegato: {evento.allegatoNome || 'file'}</a>}
              {evento.richiedePresenza && <div style={{ marginTop: 11, paddingTop: 10, borderTop: '1px solid #4a5266' }}>
                <strong style={{ color: '#e599f7', fontSize: 13 }}>Presenze richieste · {risposte.length} risposta{risposte.length === 1 ? '' : 'e'}</strong>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <span style={{ padding: '5px 9px', borderRadius: 12, background: 'rgba(81,207,102,.18)', color: '#8ce99a', fontSize: 13, fontWeight: 'bold' }}>✓ {presenti} presenti</span>
                  <span style={{ padding: '5px 9px', borderRadius: 12, background: 'rgba(255,135,135,.16)', color: '#ffb4b4', fontSize: 13, fontWeight: 'bold' }}>✕ {assenti} assenti</span>
                </div>
                {risposte.length ? <div style={{ display: 'grid', gap: 7, marginTop: 10 }}>{risposte.map(presenza => {
                  const presente = presenza.risposta === 'presente';
                  return <div key={presenza.id} style={{ padding: '8px 10px', borderRadius: 8, background: presente ? 'rgba(81,207,102,.10)' : 'rgba(255,135,135,.10)', color: presente ? '#8ce99a' : '#ffb4b4', fontSize: 13 }}>
                    <strong>{presenza.nomeAllievo || 'Allievo'}</strong>: {presente ? 'Presente' : 'Non presente'}
                    <small style={{ display: 'block', marginTop: 3, color: '#d9b8e4' }}>Risposta inviata il {formattaDataOraRisposta(presenza.dataRisposta)}</small>
                  </div>;
                })}</div> : <p style={{ margin: '7px 0 0', color: '#ced4da', fontSize: 13 }}>Nessun allievo ha ancora risposto.</p>}
              </div>}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 13 }}>
                <button type="button" onClick={() => avviaModificaEvento(evento)} style={{ padding: '8px 12px', border: 0, borderRadius: 7, background: '#74c0fc', color: '#1f2430', fontWeight: 'bold', cursor: 'pointer' }}>Modifica</button>
                <button type="button" onClick={() => eliminaEvento(evento)} style={{ padding: '8px 12px', border: 0, borderRadius: 7, background: '#ff6b6b', color: '#1f2430', fontWeight: 'bold', cursor: 'pointer' }}>Elimina</button>
              </div>
            </article>;
          }) : <p style={{ margin: '9px 0 0', color: '#ced4da' }}>Nessun evento in questo giorno.</p>}
        </div>
      </section>
    </div>
  );
}

function OperatoriOnline({ utenti }) {
  const operatoriOnline = utenti.filter(utente =>
    utenteStaffAttivo(utente) && String(utente.ruolo || '').toLowerCase() !== 'admin'
  );

  if (!operatoriOnline.length) return null;

  const nomi = operatoriOnline.map(utente => utente.username).filter(Boolean);
  return (
    <aside aria-live="polite" title="Operatori e maestri attualmente nel gestionale" style={{ position: 'fixed', top: 18, right: 22, zIndex: 40, maxWidth: 'min(440px, calc(100vw - 45px))', display: 'flex', alignItems: 'center', gap: 9, padding: '10px 13px', borderRadius: 13, background: 'rgba(28, 11, 22, .95)', border: '1px solid rgba(255, 124, 165, .65)', color: '#fff', boxShadow: '0 10px 27px rgba(55, 7, 30, .28)', fontSize: 12 }}>
      <span aria-hidden="true" style={{ width: 9, height: 9, flex: '0 0 auto', borderRadius: '50%', background: '#3ee285', boxShadow: '0 0 0 4px rgba(62,226,133,.16)' }} />
      <span><strong style={{ color: '#ffbad0' }}>{operatoriOnline.length === 1 ? 'Sta lavorando:' : 'Stanno lavorando:'}</strong> {nomi.join(' · ')}</span>
    </aside>
  );
}

function NotificaVersioneAllievi() {
  return <div role="status" aria-live="polite" style={{ position: 'fixed', left: '50%', bottom: 112, transform: 'translateX(-50%)', zIndex: 5000, padding: '11px 16px', borderRadius: 18, background: 'rgba(20, 104, 58, .96)', border: '1px solid rgba(140, 233, 154, .85)', color: '#fff', fontWeight: 'bold', fontSize: 14, whiteSpace: 'nowrap', boxShadow: '0 10px 24px rgba(0,0,0,.28)' }}>✓ Ultima versione aggiornata</div>;
}

function App() {
  const [modalita, setModalita] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const percorso = window.location.pathname.replace(/\/+$/, '') || '/';
    const hostAllievi = [
      'academy-of-arts-allievi.web.app',
      'academy-of-arts-allievi.firebaseapp.com',
      'allievi.academyofarts.it'
    ].includes(window.location.hostname);
    return Capacitor.isNativePlatform() || hostAllievi || percorso === '/area-allievi' || params.get('mode') === 'allievo'
      ? 'allievo'
      : 'staff';
  });
  const [allievoLoggato, setAllievoLoggato] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('academy_of_arts_allievo_session') || 'null'); }
    catch { return null; }
  });
  const [mostraVersioneAllievi, setMostraVersioneAllievi] = useState(false);
  const [utenti, setUtenti] = useState([]);
  const [sistemaBloccato, setSistemaBloccato] = useState(false);
  const [utenteInPausa, setUtenteInPausa] = useState(null);
  const [connessoWeb, setConnessoWeb] = useState(() => navigator.onLine);
  const [mostraPopupOfflineWeb, setMostraPopupOfflineWeb] = useState(() => !navigator.onLine);
  const [messaggioNotificheStaff, setMessaggioNotificheStaff] = useState('');
  const [messaggiNonLettiLogin, setMessaggiNonLettiLogin] = useState({});
  // Sessione Firebase Authentication: le regole del database la richiedono.
  const [utenteFirebase, setUtenteFirebase] = useState(() => auth.currentUser);
  const [authPronta, setAuthPronta] = useState(false);
  useEffect(() => onAuthStateChanged(auth, utente => {
    setUtenteFirebase(utente);
    setAuthPronta(true);
  }), []);

  useEffect(() => {
    const vaiOnline = () => {
      setConnessoWeb(true);
      setMostraPopupOfflineWeb(false);
    };
    const vaiOffline = () => {
      setConnessoWeb(false);
      setMostraPopupOfflineWeb(true);
    };
    window.addEventListener('online', vaiOnline);
    window.addEventListener('offline', vaiOffline);
    return () => { window.removeEventListener('online', vaiOnline); window.removeEventListener('offline', vaiOffline); };
  }, []);

  // Nell'area allievi usiamo un manifesto dedicato: quando lo studente
  // salva il sito nella Home del telefono, l'icona riapre sempre qui.
  useEffect(() => {
    const manifesto = document.querySelector('link[rel="manifest"]');
    const hrefOriginale = manifesto?.getAttribute('href') || '/manifest.json';
    const iconaAppleEsistente = document.querySelector('link[rel="apple-touch-icon"]');
    const hrefAppleOriginale = iconaAppleEsistente?.getAttribute('href');
    const iconaApple = iconaAppleEsistente || document.createElement('link');
    const abbiamoCreatoIconaApple = !iconaAppleEsistente;

    if (modalita === 'allievo') {
      manifesto?.setAttribute('href', '/manifest-allievi.json');
      iconaApple.setAttribute('rel', 'apple-touch-icon');
      iconaApple.setAttribute('href', '/academy-allievi-icon.png');
      if (abbiamoCreatoIconaApple) document.head.appendChild(iconaApple);
    }

    return () => {
      manifesto?.setAttribute('href', hrefOriginale);
      if (abbiamoCreatoIconaApple) {
        iconaApple.remove();
      } else if (hrefAppleOriginale) {
        iconaApple.setAttribute('href', hrefAppleOriginale);
      }
    };
  }, [modalita]);

  useEffect(() => {
    if (modalita !== 'allievo') return undefined;
    let attivo = true;
    const verificaVersione = async () => {
      const aggiornamentoAvviato = await aggiornaVersioneAllieviSeDisponibile();
      if (!aggiornamentoAvviato && attivo) {
        setMostraVersioneAllievi(true);
        window.setTimeout(() => { if (attivo) setMostraVersioneAllievi(false); }, 2000);
      }
    };
    verificaVersione();
    return () => { attivo = false; };
  }, [modalita]);

  // Web App e Area Allievi usano lo stesso service worker: mantiene le
  // schermate già aperte disponibili anche in assenza di connessione.
  useEffect(() => {
    if (Capacitor.isNativePlatform() || !('serviceWorker' in navigator)) return undefined;

    navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' })
      .catch((errore) => console.error('Impossibile predisporre la modalità offline:', errore));

    return undefined;
  }, []);
  
  const [utenteLoggato, setUtenteLoggato] = useState(() => {
    const salvato = localStorage.getItem('utente_app_data');
    const lastActivity = localStorage.getItem('lastActivity');
    const now = Date.now();

    if (salvato && lastActivity) {
      if (now - parseInt(lastActivity, 10) > TWO_HOURS_MS) {
        localStorage.removeItem('utente_app_data');
        localStorage.removeItem('lastActivity');
        return null;
      }
      return JSON.parse(salvato);
    }
    return null;
  });
  const [aggiornamentoWebDisponibile, setAggiornamentoWebDisponibile] = useState('');

  useEffect(() => {
    const eAdmin = String(utenteLoggato?.ruolo || '').toLowerCase() === 'admin';
    if (modalita !== 'staff' || !utenteLoggato || eAdmin) {
      setAggiornamentoWebDisponibile('');
      return undefined;
    }
    let attivo = true;
    const controllaAggiornamento = async () => {
      const scriptAggiornato = await rilevaVersioneWebDisponibile();
      if (!attivo || !scriptAggiornato) return;
      const chiaveRinvio = `academy_of_arts_aggiornamento_rinviato_${scriptAggiornato}`;
      if (sessionStorage.getItem(chiaveRinvio) !== '1') setAggiornamentoWebDisponibile(scriptAggiornato);
    };
    controllaAggiornamento();
    const intervallo = window.setInterval(controllaAggiornamento, 45000);
    return () => { attivo = false; window.clearInterval(intervallo); };
  }, [modalita, utenteLoggato]);

  useEffect(() => {
    const updateLastActivity = () => {
      if (localStorage.getItem('utente_app_data')) {
        localStorage.setItem('lastActivity', Date.now().toString());
      }
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, updateLastActivity));

    const interval = setInterval(() => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (lastActivity && Date.now() - parseInt(lastActivity, 10) > TWO_HOURS_MS) {
        gestisciLogoutAutomatico();
      }
    }, 60000);

    return () => {
      events.forEach(event => window.removeEventListener(event, updateLastActivity));
      clearInterval(interval);
    };
  }, []);

  const gestisciLogoutAutomatico = async () => {
    let utenteCorrente = null;
    try { utenteCorrente = JSON.parse(localStorage.getItem('utente_app_data')); } catch (e) { utenteCorrente = null; }
    setUtenteLoggato(null);
    setUtenteInPausa(null);
    localStorage.removeItem('utente_app_data');
    localStorage.removeItem('lastActivity');
    if (utenteCorrente && utenteCorrente.id) {
      Promise.allSettled([
        updateDoc(doc(db, 'utenti_sistema', utenteCorrente.id), { online: false }),
        Promise.resolve(registraLogOperazione(utenteCorrente.username, 'Disconnessione automatica per inattività (2 ore)'))
      ]).finally(esciDaFirebase);
    } else {
      esciDaFirebase();
    }
  };

  // Un aggiornamento del browser non deve far perdere la sessione: chi ha
  // premuto "Aggiorna" deve ritrovare la stessa schermata del gestionale.
  // Segniamo soltanto la presenza come non attiva finché l'app non si riapre;
  // le credenziali restano salvate e vengono eliminate esclusivamente con
  // "Esci" oppure dopo due ore di inattività.
  useEffect(() => {
    const logoutAllaChiusura = () => {
      try {
        if (sessionStorage.getItem('aggiornamento_webapp_in_corso') === '1') {
          sessionStorage.removeItem('aggiornamento_webapp_in_corso');
          return;
        }
        const utenteCorrente = JSON.parse(localStorage.getItem('utente_app_data'));
        if (utenteCorrente && utenteCorrente.id) {
          updateDoc(doc(db, 'utenti_sistema', utenteCorrente.id), { online: false })
            .catch(e => console.error('Errore logout alla chiusura:', e));
        }
      } catch (e) {
        console.error(e);
      }
    };

    window.addEventListener('pagehide', logoutAllaChiusura);
    return () => window.removeEventListener('pagehide', logoutAllaChiusura);
  }, []);

  // Chi risultava collegato con la vecchia versione (senza accesso Firebase)
  // deve rientrare una volta: senza sessione Firebase il database non risponde.
  useEffect(() => {
    if (!authPronta) return;
    if (modalita === 'allievo') {
      if (allievoLoggato && !sessioneFirebaseValida(auth.currentUser, { allievoId: allievoLoggato.id })) {
        sessionStorage.removeItem('academy_of_arts_allievo_session');
        setAllievoLoggato(null);
      }
      return;
    }
    if (utenteLoggato && !sessioneFirebaseValida(auth.currentUser, { staffId: utenteLoggato.id, admin: String(utenteLoggato.ruolo || '').toLowerCase() === 'admin' })) {
      setUtenteLoggato(null);
      localStorage.removeItem('utente_app_data');
      localStorage.removeItem('lastActivity');
    }
  }, [authPronta, utenteFirebase, modalita, allievoLoggato, utenteLoggato]);

  const staffAutenticato = Boolean(utenteFirebase && (utenteFirebase.uid.startsWith('staff_') || utenteFirebase.email === EMAIL_RECUPERO_ADMIN));

  // Prima del login l'elenco dei profili arriva dal server, senza password.
  useEffect(() => {
    if (modalita === 'allievo' || staffAutenticato) return undefined;
    let attivo = true;
    const carica = () => caricaProfiliStaff().then(dati => {
      if (!attivo) return;
      const perNome = new Map();
      [...(dati.profili || [])]
        .sort((a, b) => dataUltimaPresenza(b) - dataUltimaPresenza(a))
        .forEach(profilo => {
          const chiave = String(profilo.username || '').trim().toLowerCase();
          if (chiave && !perNome.has(chiave)) perNome.set(chiave, profilo);
        });
      setUtenti(Array.from(perNome.values()));
      setSistemaBloccato(Boolean(dati.sistemaBloccato));
      setMessaggiNonLettiLogin(dati.messaggiNonLetti || {});
    }).catch(errore => console.warn('Elenco profili non disponibile:', errore));
    carica();
    // Ogni 5 minuti e solo se la pagina è visibile: ogni controllo fa leggere
    // al server tutto lo staff e i messaggi ai maestri (prima ogni minuto).
    const intervallo = window.setInterval(() => { if (document.visibilityState === 'visible') carica(); }, 5 * 60 * 1000);
    const alRitorno = () => { if (document.visibilityState === 'visible') carica(); };
    document.addEventListener('visibilitychange', alRitorno);
    return () => { attivo = false; window.clearInterval(intervallo); document.removeEventListener('visibilitychange', alRitorno); };
  }, [modalita, staffAutenticato]);

  useEffect(() => {
    // Gli allievi non hanno bisogno dell'elenco staff: evitiamo letture inutili.
    if (modalita === 'allievo' || !staffAutenticato) return undefined;
    const unsubscribe = ascoltaRaccolta('utenti_sistema', async (snap) => {
      let lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const adminDoc = lista.find(u => u.username === 'Admin');
      if (adminDoc && adminDoc.sistemaBloccato !== undefined) {
        setSistemaBloccato(adminDoc.sistemaBloccato);
      }

      if (lista.length === 0) {
        for (const u of UTENTI_DEFAULT) {
          const docRef = await addDoc(collection(db, 'utenti_sistema'), { ...u, online: false, sistemaBloccato: false });
          lista.push({ id: docRef.id, ...u, online: false });
        }
      } else {
        const batch = writeBatch(db);
        let daAggiornare = false;

        UTENTI_DEFAULT.forEach(defUser => {
          const uTrovato = lista.find(u => 
            [String(defUser.username).trim().toLowerCase(), String(defUser.username).split(' ')[0].trim().toLowerCase()].includes(String(u.username || '').trim().toLowerCase())
          );

          if (uTrovato) {
            const aggiornamenti = {};
            if (uTrovato.username !== defUser.username) aggiornamenti.username = defUser.username;
            if (uTrovato.ruolo !== defUser.ruolo) aggiornamenti.ruolo = defUser.ruolo;
            if (typeof defUser.docenteLezioniPrivate === 'boolean' && uTrovato.docenteLezioniPrivate !== defUser.docenteLezioniPrivate) aggiornamenti.docenteLezioniPrivate = defUser.docenteLezioniPrivate;
            if (Object.keys(aggiornamenti).length) {
              batch.update(doc(db, 'utenti_sistema', uTrovato.id), aggiornamenti);
              daAggiornare = true;
            }
          }
        });

        // La gerarchia Academy prevede soltanto due Master decisionali:
        // Lia Di Martino e Salvatore Renna. Qualsiasi altro profilo rimane
        // operatore/docente, anche se in passato aveva il ruolo tecnico master.
        lista.forEach(utente => {
          if (String(utente.ruolo || '').trim().toLowerCase() === 'master' && !utenteEMasterPrioritario(utente)) {
            batch.update(doc(db, 'utenti_sistema', utente.id), { ruolo: utenteGestisceLezioniPrivate(utente) ? 'docente' : 'operatore' });
            daAggiornare = true;
          }
        });

        if (daAggiornare) await batch.commit();
      }

      // Per il login mostriamo una sola scheda per nome. In caso di un nome
      // ripetuto appena creato, preferiamo sempre il profilo più recente.
      const utentiPerNome = new Map();
      [...lista]
        .sort((a, b) => dataUltimaPresenza(b) - dataUltimaPresenza(a))
        .forEach(utente => {
          const chiave = String(utente.username || '').trim().toLowerCase();
          if (!chiave) return;
          if (utentiPerNome.has(chiave)) return;
          // Le password non devono mai restare in memoria nel browser.
          const { password: _password, passwordAlternative: _alternative, ...utenteSenzaPassword } = utente;
          utentiPerNome.set(chiave, utenteSenzaPassword);
        });
      const utentiUnici = Array.from(utentiPerNome.values());

      setUtenti(utentiUnici);
    }, errore => console.error('Impossibile caricare gli utenti:', errore));

    return () => unsubscribe();
  }, [modalita, staffAutenticato]);

  const toggleBloccoSistema = async () => {
    const nuovoStato = !sistemaBloccato;
    setSistemaBloccato(nuovoStato);
    
    const adminUser = utenti.find(u => u.ruolo === 'admin');
    if (adminUser) {
      await updateDoc(doc(db, 'utenti_sistema', adminUser.id), { sistemaBloccato: nuovoStato });
    }

    await registraLogOperazione(utenteLoggato ? utenteLoggato.username : 'Admin', nuovoStato ? "Messo il sistema in PAUSA" : "Riattivato il sistema");
    alert(nuovoStato ? "⏸️ Sistema messo in pausa!" : "🟢 Sistema riattivato!");
  };

  const gestisciLoginUtente = async (oggettoUtente) => {
    setUtenteLoggato(oggettoUtente);
    setUtenteInPausa(null);
    localStorage.setItem('utente_app_data', JSON.stringify(oggettoUtente));
    localStorage.setItem('lastActivity', Date.now().toString());

    if (oggettoUtente.id) {
      await updateDoc(doc(db, 'utenti_sistema', oggettoUtente.id), {
        online: true,
        lastSeen: new Date().toISOString()
      });
    }
    registraLogOperazione(oggettoUtente.username, 'Accesso eseguito');
  };

  // Il consenso va richiesto con un pulsante: Chrome e Safari non permettono
  // di mostrare la richiesta di autorizzazione automaticamente al login.
  const attivaNotificheLezioniSulTelefono = async () => {
    if (!utenteLoggato?.id) return;
    setMessaggioNotificheStaff('');
    try {
      if (!('serviceWorker' in navigator) || !('Notification' in window) || !(await isSupported())) {
        setMessaggioNotificheStaff('Questo browser non supporta le notifiche. Apri la Web App con Google Chrome aggiornato.');
        return;
      }
      const registrazione = await navigator.serviceWorker.getRegistration('/') || await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      const permesso = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
      if (permesso !== 'granted') {
        setMessaggioNotificheStaff('Per ricevere gli avvisi, abilita le notifiche di Academy Of Arts nelle impostazioni del browser.');
        return;
      }
      const token = await getToken(getMessaging(firebaseApp), { vapidKey: CHIAVE_WEB_PUSH, serviceWorkerRegistration: registrazione });
      if (!token) {
        setMessaggioNotificheStaff('Non riesco a registrare questo telefono. Chiudi e riapri la Web App, poi riprova.');
        return;
      }
      await updateDoc(doc(db, 'utenti_sistema', utenteLoggato.id), {
        tokenNotifichePush: arrayUnion(token),
        ultimoAggiornamentoNotifiche: new Date().toISOString()
      });
      setMessaggioNotificheStaff('✓ Notifiche attive su questo telefono: riceverai le nuove richieste di lezione privata.');
    } catch (errore) {
      console.error('Attivazione notifiche per docenti non riuscita:', errore);
      setMessaggioNotificheStaff('Non è stato possibile attivare le notifiche. Riprova dopo aver riaperto la Web App.');
    }
  };

  const avviaPausa = () => {
    if (utenteLoggato) {
      setUtenteInPausa(utenteLoggato.username);
      setUtenteLoggato(null);
    }
  };

  const gestisciLogout = async () => {
    // Si esce subito anche offline o se il profilo è stato eliminato:
    // l'aggiornamento dello stato online parte dopo, senza bloccare.
    const utenteUscito = utenteLoggato;
    setUtenteLoggato(null);
    setUtenteInPausa(null);
    localStorage.removeItem('utente_app_data');
    localStorage.removeItem('lastActivity');
    if (utenteUscito && utenteUscito.id) {
      Promise.allSettled([
        updateDoc(doc(db, 'utenti_sistema', utenteUscito.id), { online: false })
          .catch(errore => console.warn('Stato offline non aggiornato:', errore)),
        Promise.resolve(registraLogOperazione(utenteUscito.username, 'Disconnessione')).catch(() => {})
      ]).finally(esciDaFirebase);
    } else {
      esciDaFirebase();
    }
  };

  // Mantiene aggiornata la presenza anche quando il gestionale resta aperto
  // per molte ore: gli altri operatori vedono il nome in tempo reale.
  useEffect(() => {
    if (!utenteLoggato?.id) return undefined;

    const aggiornaPresenza = () => {
      updateDoc(doc(db, 'utenti_sistema', utenteLoggato.id), {
        online: true,
        lastSeen: new Date().toISOString()
      }).catch(errore => console.error('Impossibile aggiornare la presenza online:', errore));
    };

    aggiornaPresenza();
    // Ogni 2 minuti (prima 30s): ogni scrittura fa rileggere utenti_sistema a tutti i dispositivi aperti.
    const intervallo = window.setInterval(aggiornaPresenza, 2 * 60 * 1000);
    return () => window.clearInterval(intervallo);
  }, [utenteLoggato]);

  // Tutti gli hook sono stati dichiarati sopra: solo ora è sicuro
  // mostrare una schermata alternativa per l'area riservata agli allievi.
  if (modalita === 'allievo' && allievoLoggato) {
    return <>
      <VistaAppAllievo
        allievo={allievoLoggato}
        onLogout={() => { sessionStorage.removeItem('academy_of_arts_allievo_session'); setAllievoLoggato(null); esciDaFirebase(); }}
      />
      {mostraVersioneAllievi && <NotificaVersioneAllievi />}
    </>;
  }

  if (modalita === 'allievo') {
    return <>
      <LoginAllievo
        onLoginSuccess={a => { sessionStorage.setItem('academy_of_arts_allievo_session', JSON.stringify(a)); setAllievoLoggato(a); }}
        onTornaStaff={() => setModalita('staff')}
      />
      {mostraVersioneAllievi && <NotificaVersioneAllievi />}
    </>;
  }

  if (utenteInPausa) {
    return (
      <div style={{
        backgroundImage: 'radial-gradient(circle at 12% 12%, #e1ebff 0%, transparent 34%), linear-gradient(135deg, #f8fafc, #eaf1fa)',
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', minHeight: '100vh',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '15px', boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%', maxWidth: '480px' }}>
          <HomeDivertente utenteCorrente={utenteInPausa} />
          <Login 
            onLogin={gestisciLoginUtente} 
            utenti={utenti} 
            sistemaBloccato={sistemaBloccato} 
            utenteInPausa={utenteInPausa}
            onAnnullaPausa={() => setUtenteInPausa(null)}
            messaggiNonLettiLogin={messaggiNonLettiLogin}
          />
        </div>
      </div>
    );
  }

  if (!utenteLoggato) {
    return (
      <div style={{
        backgroundImage: "radial-gradient(circle at 78% 83%, rgba(211, 22, 79, .54), transparent 27%), linear-gradient(100deg, rgba(5, 5, 9, .94) 0%, rgba(11, 7, 12, .72) 52%, rgba(17, 3, 10, .51) 100%), url('/sfondo-login-segreteria.webp')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box'
      }}>
        <Login onLogin={gestisciLoginUtente} utenti={utenti} sistemaBloccato={sistemaBloccato} messaggiNonLettiLogin={messaggiNonLettiLogin} />
      </div>
    );
  }

  const isMasterOrAdmin = utenteLoggato.ruolo === 'master' || utenteLoggato.ruolo === 'admin';
  const isAdmin = utenteLoggato.ruolo === 'admin';
  const eOperatoreAcademy = String(utenteLoggato.ruolo || '').toLocaleLowerCase('it-IT') === 'operatore' && String(utenteLoggato.username || '').trim().toLocaleLowerCase('it-IT') === 'academyofarts';
  const puoVedereSezione = chiave => utentePuoVedere(utenteLoggato, chiave) || (eOperatoreAcademy && chiave === 'compleanni');
  const puoGestireMessaggiAllievi = ['master', 'operatore', 'admin'].includes(String(utenteLoggato.ruolo || '').toLowerCase()) && puoVedereSezione('comunicazioni');
  const isOperatore = String(utenteLoggato.ruolo || '').toLowerCase() === 'operatore';
  const isMaster = String(utenteLoggato.ruolo || '').toLowerCase() === 'master';
  const isDocente = String(utenteLoggato.ruolo || '').toLowerCase() === 'docente';
  const docenteLezioniPrivate = utenteGestisceLezioniPrivate(utenteLoggato);

  return (
    <div style={{ 
      display: 'flex', 
      position: 'relative', 
      minHeight: '100vh',
      backgroundColor: '#f8f5f6',
      backgroundImage: 'radial-gradient(circle at 18% 18%, rgba(216, 45, 98, .10) 0%, transparent 34%), radial-gradient(circle at 86% 82%, rgba(255, 176, 32, .08) 0%, transparent 32%)',
      backgroundAttachment: 'fixed'
    }}>
      {!connessoWeb && <div role="status" style={{ position: 'fixed', top: 10, right: 12, zIndex: 9999, padding: '8px 11px', borderRadius: 10, background: '#7f1d1d', color: '#fff', fontSize: 12, fontWeight: 'bold', boxShadow: '0 5px 16px rgba(0,0,0,.22)' }}>📴 Offline · ultima copia salvata</div>}
      {mostraPopupOfflineWeb && <div role="dialog" aria-modal="true" aria-labelledby="titolo-offline-web" style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(39, 12, 27, .64)' }}>
        <section style={{ width: 'min(390px, 100%)', padding: 25, textAlign: 'center', borderRadius: 20, border: '1px solid #e5a6ba', background: '#fff9fc', color: '#2d1721', boxShadow: '0 24px 58px rgba(0,0,0,.32)' }}>
          <div aria-hidden="true" style={{ fontSize: 38, lineHeight: 1 }}>📴</div>
          <h2 id="titolo-offline-web" style={{ margin: '12px 0 8px', fontSize: 23, color: '#6b183a' }}>Sei offline</h2>
          <p style={{ margin: 0, color: '#553744', lineHeight: 1.5 }}>Puoi comunque lavorare con l’ultima copia salvata. Appena torna Internet, i dati verranno sincronizzati automaticamente.</p>
          <button type="button" onClick={() => setMostraPopupOfflineWeb(false)} style={{ marginTop: 19, padding: '11px 22px', border: 0, borderRadius: 10, background: '#8e3fa7', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: 15 }}>Ho capito</button>
        </section>
      </div>}
      <style>{`
        .theme-segreteria [style*="rgb(248, 249, 250)"] { color: #2d1721 !important; }
        .theme-segreteria [style*="rgb(53, 59, 76)"] {
          background: #ffffff !important;
          color: #2d1721 !important;
          border-color: #eadde2 !important;
          box-shadow: 0 9px 24px rgba(71, 22, 43, .07) !important;
        }
        .theme-segreteria [style*="rgb(43, 49, 64)"] {
          background: #fbf5f7 !important;
          color: #3b1c29 !important;
          border-color: #eadde2 !important;
        }
        .theme-segreteria [style*="rgb(74, 82, 102)"] { border-color: #eadde2 !important; }
        .theme-segreteria h2 { color: #57132c !important; }
        .theme-segreteria table { background: #ffffff !important; border: 1px solid #eadde2; }
        .theme-segreteria table thead tr { background: #f5e8ed !important; color: #7f2443 !important; }
        .theme-segreteria table tbody tr { border-color: #f0e4e8 !important; }
        .theme-segreteria table td { color: #4b5563 !important; }
        .theme-segreteria table td strong { color: #111827 !important; }
        .theme-segreteria input:not([type="checkbox"]),
        .theme-segreteria select,
        .theme-segreteria textarea {
          background-color: #fff !important;
          color: #2d1721 !important;
          border-color: #d9b8c5 !important;
        }
        .theme-segreteria input::placeholder,
        .theme-segreteria textarea::placeholder { color: #927783 !important; }
        /* Sui pannelli chiari il testo deve restare sempre leggibile. */
        .theme-segreteria label,
        .theme-segreteria legend { color: #351522 !important; font-weight: 700; }
        .theme-segreteria table th { color: #42162a !important; }
        .theme-segreteria [style*="color: rgb(255, 212, 59)"] { color: #5b1d43 !important; }
        .theme-segreteria [style*="background: rgb(255, 255, 255)"] > p,
        .theme-segreteria [style*="background: rgb(255, 255, 255)"] > small { color: #432332 !important; }
        .theme-segreteria a:not([style*="background"]) { color: #ad1947; }
        @keyframes academyBellPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.14) rotate(7deg); }
        }
        @media (max-width: 700px) {
          .theme-segreteria table th, .theme-segreteria table td { padding-left: 12px !important; padding-right: 12px !important; }
        }
      `}</style>
      {aggiornamentoWebDisponibile && <div role="dialog" aria-modal="true" aria-labelledby="titolo-aggiornamento" style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(23, 8, 16, .58)', backdropFilter: 'blur(3px)' }}><section style={{ width: 'min(100%, 440px)', padding: 26, borderRadius: 18, background: 'linear-gradient(145deg, #fff, #fff3f7)', border: '2px solid #e0527d', boxShadow: '0 22px 60px rgba(0,0,0,.35)', color: '#32101e', textAlign: 'center' }}><div aria-hidden="true" style={{ width: 62, height: 62, margin: '0 auto 12px', display: 'grid', placeItems: 'center', borderRadius: '50%', background: '#ffe0ea', fontSize: 30 }}>🔔</div><h2 id="titolo-aggiornamento" style={{ margin: 0, color: '#8e1d46', fontSize: 22 }}>Nuovo aggiornamento disponibile</h2><p style={{ margin: '12px 0 7px', color: '#432332', fontSize: 16, lineHeight: 1.5 }}>L’Admin ha pubblicato una nuova versione della Web App.</p><p style={{ margin: '0 0 20px', color: '#765b66', fontSize: 13, lineHeight: 1.45 }}>Vuoi applicarla adesso? Il refresh mantiene il tuo accesso, ma salva prima eventuali campi che stai compilando.</p><div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}><button type="button" onClick={() => { sessionStorage.setItem(`academy_of_arts_aggiornamento_rinviato_${aggiornamentoWebDisponibile}`, '1'); setAggiornamentoWebDisponibile(''); }} style={{ minWidth: 130, padding: '11px 15px', borderRadius: 9, border: '1px solid #c9a8b5', background: '#fff', color: '#613a49', fontWeight: 'bold', cursor: 'pointer' }}>Non ora</button><button type="button" onClick={() => applicaAggiornamentoWeb(aggiornamentoWebDisponibile)} style={{ minWidth: 150, padding: '11px 15px', borderRadius: 9, border: 0, background: 'linear-gradient(120deg, #a51f53, #e0527d)', color: '#fff', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 6px 14px rgba(165,31,83,.24)' }}>Sì, aggiorna</button></div></section></div>}
      <MenuNavigazione 
        utenteLoggato={utenteLoggato} 
        gestisciLogout={gestisciLogout} 
        avviaPausa={avviaPausa} 
        isMasterOrAdmin={isMasterOrAdmin} 
        isAdmin={isAdmin} 
      />

      <main className="theme-segreteria" style={{ flex: 1, padding: '30px', fontFamily: 'sans-serif', minHeight: '100vh', paddingRight: '40px', zIndex: 1, position: 'relative' }}>
        <OperatoriOnline utenti={utenti} />
        {docenteLezioniPrivate && <section style={{ margin: '0 0 18px', padding: '13px 15px', borderRadius: 13, border: '1px solid #d7b2e6', background: '#fff9fd', color: '#351522', boxShadow: '0 7px 18px rgba(71,22,43,.06)' }}>
          <strong style={{ display: 'block', color: '#711b67', fontSize: 15 }}>🔔 Avvisi richieste lezioni private</strong>
          <span style={{ display: 'block', marginTop: 4, color: '#553744', fontSize: 13, lineHeight: 1.4 }}>Sul tuo telefono riceverai una notifica quando un allievo invia una nuova richiesta assegnata a te.</span>
          <button type="button" onClick={attivaNotificheLezioniSulTelefono} style={{ marginTop: 10, padding: '9px 12px', border: 0, borderRadius: 8, background: '#8e3fa7', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: 13 }}>📱 Attiva notifiche su questo telefono</button>
          {messaggioNotificheStaff && <p style={{ margin: '9px 0 0', color: messaggioNotificheStaff.startsWith('✓') ? '#176b3a' : '#8e1d46', fontWeight: 'bold', fontSize: 13, lineHeight: 1.4 }}>{messaggioNotificheStaff}</p>}
        </section>}
        <Routes>
          <Route path="/" element={puoVedereSezione('dashboard') ? <DashboardSegreteria utenteLoggato={utenteLoggato} /> : <Navigate to="/presenza-allievi" replace />} />
          <Route path="/allievi" element={puoVedereSezione('anagrafica') ? <Allievi utenteCorrente={utenteLoggato.username} ruoloUtente={utenteLoggato.ruolo} /> : <Navigate to="/" replace />} />
          <Route path="/presenza-allievi" element={(isMasterOrAdmin || isOperatore || isDocente) && puoVedereSezione('presenze') ? <PresenzaAllieviManuale utenteCorrente={utenteLoggato.username} /> : <Navigate to="/" replace />} />
          <Route path="/presenze" element={(isMasterOrAdmin || isOperatore || isDocente) && puoVedereSezione('presenze') ? <RegistroPresenze /> : <Navigate to="/" replace />} />
          <Route path="/report-presenze" element={(isMasterOrAdmin || isOperatore || isDocente) && puoVedereSezione('presenze') ? <ReportFrequenzaPresenze /> : <Navigate to="/" replace />} />
          <Route path="/iscrizione-allievo" element={puoVedereSezione('iscrizione') ? <Allievi utenteCorrente={utenteLoggato.username} ruoloUtente={utenteLoggato.ruolo} modalita="iscrizione" /> : <Navigate to="/" replace />} />
          <Route path="/compleanni" element={puoVedereSezione('compleanni') ? <CompleanniAllievi utenteCorrente={utenteLoggato.username} /> : <Navigate to="/" replace />} />
          <Route path="/ricerca" element={puoVedereSezione('ricerca') ? <Ricerca utenteCorrente={utenteLoggato.username} ruoloUtente={utenteLoggato.ruolo} /> : <Navigate to="/" replace />} />
          <Route path="/tesseramento" element={puoVedereSezione('tesseramento') ? <ModuloTesseramento utenteCorrente={utenteLoggato.username} /> : <Navigate to="/" replace />} />
          <Route path="/qr-app-allievi" element={(isMasterOrAdmin || isOperatore) && puoVedereSezione('qr_allievi') ? <QrAppAllievi /> : <Navigate to="/" replace />} />
          <Route path="/divise" element={puoVedereSezione('divise') ? <Divise utenteCorrente={utenteLoggato.username} /> : <Navigate to="/" replace />} />
          <Route path="/shop" element={puoVedereSezione('shop') ? <ShopSegreteria utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/corsi" element={puoVedereSezione('corsi') ? <Corsi /> : <Navigate to="/" replace />} />
          <Route path="/lezioni" element={puoVedereSezione('lezioni_private') ? <LezioniPrivate utenteCorrente={utenteLoggato.username} utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/notifiche-lezioni-private" element={(isAdmin || docenteLezioniPrivate) && puoVedereSezione('lezioni_private') ? <NotificheLezioniPrivate utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/richieste-lezioni-private" element={(isAdmin || docenteLezioniPrivate) && puoVedereSezione('lezioni_private') ? <RichiesteLezioniPrivate utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/pagamenti" element={puoVedereSezione('pagamenti') ? <Pagamenti utenteCorrente={utenteLoggato.username} /> : <Navigate to="/" replace />} />
          <Route path="/docenti-scuola" element={(isMasterOrAdmin || isOperatore) ? <GestioneDocentiScuola utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/avvisi-allievi" element={puoVedereSezione('comunicazioni') ? <AvvisiAllievi utenteCorrente={utenteLoggato.username} /> : <Navigate to="/" replace />} />
          <Route path="/messaggi-allievi" element={puoGestireMessaggiAllievi ? <MessaggiAllieviSegreteria utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/carriera-allievi" element={(isOperatore || isAdmin) && puoVedereSezione('carriera') ? <CarrieraAllieviSegreteria utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/approvazioni-carriera" element={(isMaster || isAdmin) ? <ApprovazioniCarriera utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/utenti-premiati" element={(isMaster || isAdmin) ? <UtentiPremiati utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/richieste-cancellazione" element={(isMaster || isAdmin) ? <RichiesteCancellazioneAllievi utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/calendario-eventi" element={puoVedereSezione('calendario') ? <CalendarioEventiSegreteria utenteCorrente={utenteLoggato.username} /> : <Navigate to="/" replace />} />
          <Route path="/report" element={isMasterOrAdmin && puoVedereSezione('report') ? <Report utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/logs" element={isMasterOrAdmin ? <Logs utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          <Route path="/contatta-sviluppatore" element={!isAdmin ? <ContattaSviluppatore utenteLoggato={utenteLoggato} /> : <Navigate to="/segnalazioni-bug" replace />} />
          <Route path="/segnalazioni-bug" element={isAdmin ? <SegnalazioniBug utenteLoggato={utenteLoggato} /> : <Navigate to="/" replace />} />
          {isAdmin && <Route path="/admin" element={<AdminPanel utenti={utenti} ricaricaUtenti={() => {}} utenteCorrente={utenteLoggato.username} sistemaBloccato={sistemaBloccato} toggleBloccoSistema={toggleBloccoSistema} />} />}
        </Routes>
      </main>
    </div>
  );
}

export default App;
